// Mehrsprachigkeit ohne Bibliothek.
//
// Sieben Sprachen: Deutsch, Englisch, Französisch, Spanisch, Türkisch,
// Arabisch und vereinfachtes Chinesisch.
//
// Arabisch läuft von rechts nach links — das setzt dir/lang am <html>, damit
// Layout, Formulare und Scrollbalken automatisch spiegeln.
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

export const SPRACHEN = [
  { code: 'de', name: 'Deutsch',  flagge: '🇩🇪', rtl: false },
  { code: 'en', name: 'English',  flagge: '🇬🇧', rtl: false },
  { code: 'fr', name: 'Français', flagge: '🇫🇷', rtl: false },
  { code: 'es', name: 'Español',  flagge: '🇪🇸', rtl: false },
  { code: 'tr', name: 'Türkçe',   flagge: '🇹🇷', rtl: false },
  { code: 'ar', name: 'العربية',  flagge: '🇸🇦', rtl: true  },
  { code: 'zh', name: '简体中文',  flagge: '🇨🇳', rtl: false },
] as const;

export type SprachCode = typeof SPRACHEN[number]['code'];

// Deutsch ist die Referenz. Jeder weitere Sprachblock deckt dieselben Schlüssel ab;
// fehlt einer, fällt die Anzeige auf Deutsch zurück statt leer zu bleiben.
const TEXTE = {
  de: {
    'start.nav.funktionen': 'Funktionen',
    'start.nav.belegkette': 'Belegkette',
    'start.nav.preise': 'Preise',
    'start.kostenlosStarten': 'Kostenlos starten',
    'start.themaDunkel': 'Dunkles Design',
    'start.themaHell': 'Helles Design',
    'start.sprache': 'Sprache',
    'start.hero.marke': 'GoBD-konform · Server in der EU',
    'start.hero.titel1': 'Lager, Kasse und Buchhaltung.',
    'start.hero.titel2': 'Ein System statt fünf.',
    'start.hero.text': 'LagerPro führt Ihren Betrieb vom Angebot bis zur Steuererklärung: Bestände, Verkauf, Belege und Buchhaltung greifen ineinander. Kein Export, kein Übertragen, keine doppelte Pflege.',
    'start.hero.konto': 'Ich habe ein Konto',
    'start.hero.fussnote': 'Keine Kreditkarte nötig · In zwei Minuten eingerichtet · Monatlich kündbar',
    'start.vorschau.umsatz': 'Umsatz heute',
    'start.vorschau.offen': 'Offene Rechnungen',
    'start.vorschau.lagerwert': 'Lagerwert',
    'start.vorschau.knapp': 'Unter Mindestbestand',
    'start.vorschau.gruss': 'Guten Tag, Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'Letzte Belege',
    'start.vorschau.angebot': 'Angebot',
    'start.vorschau.lieferschein': 'Lieferschein',
    'start.vorschau.rechnung': 'Rechnung',
    'start.vertrauen1t': 'GoBD-konform',
    'start.vertrauen1x': 'Lückenlose Nummern, unveränderbare Belege, Hash-gesicherte Protokolle',
    'start.vertrauen2t': 'GDPdU-Export',
    'start.vertrauen2x': 'Datenträgerüberlassung Z3 mit index.xml — die Prüfung kann kommen',
    'start.vertrauen3t': 'Server in der EU',
    'start.vertrauen3x': 'Keine Weitergabe, keine Werbe-Tracker, DSGVO von Anfang an',
    'start.vertrauen4t': '10 Jahre Aufbewahrung',
    'start.vertrauen4x': 'Nach § 147 AO, automatisch — Sie müssen an nichts denken',
    'start.f1t': 'Beleg abfotografieren, fertig',
    'start.f1x': 'Lieferschein oder Rechnung mit dem Handy knipsen — der Assistent liest Positionen, Mengen und Lieferant heraus. Sie prüfen einmal, dann steht die Ware im Lager. Fragen zu Beständen und Zahlen beantwortet er getippt oder gesprochen.',
    'start.f2t': 'Lager im Griff',
    'start.f2x': 'Bestände in Echtzeit, Mindestbestand-Warnung, Wareneingang und Etiketten mit Barcode in fünf Größen.',
    'start.f3t': 'Kasse für den Tresen',
    'start.f3x': 'Kunde wählen, Artikel scannen, kassieren. Barverkauf bleibt möglich, der Bestand bucht sich selbst.',
    'start.f4t': 'Alle Belege',
    'start.f4x': 'Angebot, Auftragsbestätigung, Lieferschein, Rechnung, Gutschrift und Storno — mit Ihrem Logo als PDF.',
    'start.f5t': 'Buchhaltung, die zählt',
    'start.f5x': 'EÜR mit Anlage-Zeilen, Umsatzsteuer-Voranmeldung, Fahrtenbuch und DATEV-Export für Ihren Steuerberater.',
    'start.f6t': 'Retouren ohne Chaos',
    'start.f6x': 'Immer über die Rechnung: Kunde, Beleg, Positionen. Ware geht zurück ins Lager, Guthaben auf das Kundenkonto.',
    'start.f7t': 'Team an Bord',
    'start.f7x': 'Aufgaben mit Timer, Chat mit Benachrichtigungen, Anwesenheit — ohne dass Sie eine zweite App brauchen.',
    'start.funktionen.titel': 'Alles, was der Betrieb täglich braucht',
    'start.funktionen.text': 'Jeder Bereich kennt die Daten der anderen. Verkaufen Sie etwas, sinkt der Bestand, entsteht der Beleg und die Buchhaltung hat die Buchung — im selben Moment.',
    'start.kette.titel': 'Ein Vorgang, vier Belege, kein Abtippen',
    'start.kette.text': 'Aus dem Angebot wird per Klick die Auftragsbestätigung, daraus der Lieferschein, daraus die Rechnung. Positionen und Preise wandern mit. Die Ware wird dabei genau einmal ausgebucht.',
    'start.kette.schritt': 'Schritt',
    'start.kette1t': 'Angebot',
    'start.kette1x': 'Was es kosten wird',
    'start.kette2t': 'Auftrag',
    'start.kette2x': 'Kunde sagt zu',
    'start.kette3t': 'Lieferschein',
    'start.kette3x': 'Ware geht raus',
    'start.kette4t': 'Rechnung',
    'start.kette4x': 'Geld kommt rein',
    'start.kette.fuss': 'Läuft etwas zurück, gibt es die Gutschrift und die Stornorechnung — mit Gegenbuchung, wie das Finanzamt es verlangt.',
    'start.preise.titel': 'Preise ohne Kleingedrucktes',
    'start.preise.text': "Monatlich kündbar, alle Preise zzgl. MwSt. Jeder Tarif startet mit 30 Tagen gratis — ohne Bankdaten.",
    'start.preise.laedt': 'Tarife werden geladen …',
    'start.preise.fehler': 'Tarife gerade nicht abrufbar. Schreiben Sie uns — wir nennen Ihnen die Konditionen.',
    'start.preise.top': 'Meistgewählt',
    'start.preise.proMonat': '/ Monat',
    'start.preise.sofort': "30 Tage gratis testen",
    'start.preise.waehlen': "{tarif} 30 Tage gratis testen",
    'start.ende.titel': 'Heute anfangen, morgen den Überblick haben',
    'start.ende.text': 'Konto anlegen, Artikel erfassen, erste Rechnung schreiben. Ihre bisherigen Daten können Sie später übernehmen.',
    'start.fuss.hinweis': 'Server in der EU · GoBD-konform',
    'nav.support': 'Support',
    'support.vonAnbieter': 'Von LagerPro',
    'support.titel': 'Support',
    'support.untertitel': 'Fragen, Fehler, Wünsche — schreiben Sie uns hier. Der ganze Verlauf bleibt beisammen.',
    'support.neu': 'Neue Anfrage',
    'support.betreff': 'Betreff',
    'support.kategorie': 'Worum geht es?',
    'support.kat.frage': 'Frage',
    'support.kat.fehler': 'Etwas funktioniert nicht',
    'support.kat.wunsch': 'Wunsch oder Idee',
    'support.kat.abrechnung': 'Rechnung und Tarif',
    'support.kat.sonstiges': 'Sonstiges',
    'support.nachricht': 'Nachricht',
    'support.nachrichtPlatzhalter': 'Beschreiben Sie möglichst genau, was passiert ist und was Sie erwartet haben.',
    'support.anhang': 'Screenshot oder Datei anhängen',
    'support.anhangHinweis': 'Bis zu 5 Dateien, je 25 MB',
    'support.senden': 'Anfrage senden',
    'support.gesendet': 'Ihre Anfrage {nummer} ist eingegangen. Sie bekommen eine Mail, sobald es eine Antwort gibt.',
    'support.leer': 'Noch keine Anfragen',
    'support.leerText': 'Wenn etwas nicht klappt oder Sie eine Frage haben — schreiben Sie uns.',
    'support.status.offen': 'Eingegangen',
    'support.status.in_arbeit': 'In Bearbeitung',
    'support.status.wartet': 'Antwort da',
    'support.status.erledigt': 'Erledigt',
    'support.zurueck': 'Alle Anfragen',
    'support.antworten': 'Antworten',
    'support.antwortPlatzhalter': 'Ihre Nachricht …',
    'support.erledigt': 'Als erledigt markieren',
    'support.erledigtHinweis': 'Erledigt. Schreiben Sie einfach weiter, falls doch noch etwas ist — die Anfrage öffnet sich dann wieder.',
    'support.sie': 'Sie',
    'support.team': 'LagerPro Support',
    'support.ungelesen': 'neu',
    'support.glocke': 'Antwort vom Support',
    'support.anhaengen': 'Anhängen',
    'einheit.stueck': 'Stück',
    'einheit.paar': 'Paar',
    'einheit.satz': 'Satz',
    'einheit.paket': 'Paket',
    'einheit.karton': 'Karton',
    'einheit.palette': 'Palette',
    'einheit.rolle': 'Rolle',
    'einheit.flasche': 'Flasche',
    'einheit.dose': 'Dose',
    'einheit.beutel': 'Beutel',
    'einheit.minute': 'Min.',
    'einheit.stunde': 'Std.',
    'einheit.tag': 'Tag',
    'einheit.woche': 'Woche',
    'einheit.monat': 'Monat',
    'einheit.jahr': 'Jahr',
    'einheit.pauschale': 'Pauschale',
    'einheit.gruppe.anzahl': 'Anzahl & Gebinde',
    'einheit.gruppe.gewicht': 'Gewicht',
    'einheit.gruppe.volumen': 'Volumen',
    'einheit.gruppe.laenge': 'Länge & Fläche',
    'einheit.gruppe.zeit': 'Zeit',
    'einheit.gruppe.sonstiges': 'Sonstiges',
    'einheit.andere': 'Andere Einheit …',
    'einheit.eigene': 'Eigene Einheit',
    'einheit.eigenePlatzhalter': 'z. B. Bund, Kiste',
    'positionen.umgerechnet': '= {menge} vom Lager',
    'positionen.passtNicht': 'Der Artikel wird in {einheit} geführt — diese Einheit lässt sich nicht umrechnen.',
    'positionen.jeEinheit': 'je {einheit}',
    'nav.assistent': 'KI-Assistent',
    'assistent.datenschutz': 'Für die Auswertung werden Beleg und Frage an unseren KI-Dienstleister übertragen und dort nur zur Beantwortung verarbeitet — nicht zum Trainieren von Modellen. Ihre Lagerdaten bleiben auf unserem Server.',
    'pruef.titel': '{art} prüfen',
    'pruef.beleg': 'Beleg',
    'pruef.lieferschein': 'Lieferschein',
    'pruef.eingangsrechnung': 'Eingangsrechnung',
    'pruef.gutschrift': 'Gutschrift',
    'pruef.bestellung': 'Bestellung',
    'pruef.kassenbon': 'Kassenbon',
    'pruef.erkannt': '% erkannt',
    'pruef.ohneNummer': 'Ohne Nummer und Datum',
    'pruef.schwerLesbar': 'Der Beleg war schwer zu lesen',
    'pruef.schwerLesbarText': 'Bitte jede Zeile durchgehen. Bei einem unscharfen oder schrägen Foto verwechselt die Erkennung Ziffern. Ein neues Foto bei gutem Licht hilft oft mehr als Korrigieren.',
    'pruef.nichtAusgewertet': 'Nicht ausgewertet',
    'pruef.nurBilder': 'nur Fotos und PDF lassen sich lesen',
    'pruef.vorhandener': 'Vorhandener',
    'pruef.neuAnlegen': 'Neu anlegen',
    'pruef.ohne': 'Ohne',
    'pruef.bitteWaehlen': '— bitte wählen —',
    'pruef.gewaehlt': '{a} von {b} gewählt',
    'pruef.alleUebernehmen': 'Alle übernehmen',
    'pruef.keine': 'Keine',
    'pruef.keinePositionen': 'Auf dem Beleg wurden keine Positionen erkannt.',
    'pruef.warenwert': 'Warenwert der Auswahl',
    'pruef.rechnungsbetrag': 'Rechnungsbetrag',
    'pruef.brauchtBuchhaltung': 'Zum Erfassen der Ausgabe wird die Buchhaltung benötigt — im Tarif Professional enthalten.',
    'pruef.alsAusgabe': 'Betrag als Betriebsausgabe erfassen',
    'pruef.alsAusgabeText': 'Der Beleg hängt als Nachweis daran — so ist die Ausgabe für die Buchhaltung vollständig.',
    'pruef.brutto': 'Bruttobetrag',
    'pruef.steuersatz': 'Steuersatz',
    'pruef.belegnummer': 'Belegnummer',
    'pruef.belegdatum': 'Belegdatum',
    'pruef.lastschrift': 'Lastschrift',
    'pruef.keineWare': 'Keine Ware',
    'pruef.keineWareText': 'Zählt zum Rechnungsbetrag, kommt aber nicht ins Lager.',
    'pruef.grund.keineWare': 'Versand, Gebühr oder Rabatt — kein lagerfähiger Artikel',
    'pruef.privat': 'Privat verauslagt',
    'pruef.erstMitKnopf': 'Gebucht wird erst mit diesem Knopf. Bis dahin ist nichts verändert.',
    'pruef.buchen': 'Übernehmen und buchen',
    'pruef.wirdGebucht': 'Wird gebucht',
    'pruef.zugeordnet': 'Zugeordnet',
    'pruef.vermutet': 'Vermutet',
    'pruef.neu': 'Neu',
    'pruef.schlechtLesbar': 'Schlecht lesbar',
    'pruef.aendern': 'Ändern',
    'pruef.zuklappen': 'Zuklappen',
    'pruef.wirdAngelegt': 'wird als neuer Artikel angelegt',
    'pruef.artikel': 'Artikel',
    'pruef.nichtZugeordnet': '— nicht zugeordnet —',
    'pruef.alsNeuenArtikel': 'Als neuen Artikel anlegen',
    'pruef.einkaufspreis': 'Einkaufspreis',
    'pruef.verkaufspreis': 'Verkaufspreis',
    'pruef.spaeterErgaenzen': 'Kann später ergänzt werden',
    'pruef.ean': 'EAN',
    'pruef.je': 'je',
    'pruef.grund.ean': 'EAN stimmt überein',
    'pruef.grund.nummer': 'Artikelnummer stimmt überein',
    'pruef.grund.name': 'Bezeichnung stimmt genau überein',
    'pruef.grund.ustId': 'USt-IdNr. stimmt überein',
    'pruef.grund.aehnlich': 'Ähnliche Bezeichnung — bitte prüfen',
    'pruef.grund.keiner': 'Kein passender Artikel im Lager',
    'pruef.kat.wareneinkauf': 'Wareneinkauf',
    'pruef.kat.buero': 'Bürobedarf',
    'pruef.kat.fahrzeug': 'Fahrzeugkosten',
    'pruef.kat.werbung': 'Werbekosten',
    'pruef.kat.kommunikation': 'Telefon und Internet',
    'pruef.kat.raumkosten': 'Miete und Raumkosten',
    'pruef.kat.sonstiges': 'Sonstige Betriebsausgaben',
    'assistent.titel': 'KI-Assistent',
    'assistent.rest': 'Diesen Monat frei',
    'assistent.scans': 'Belegscans',
    'assistent.fragen': 'Fragen',
    'assistent.testmodus': 'Testmodus',
    'assistent.testmodusText': 'Es ist kein KI-Zugang hinterlegt. Belege liefern Beispieldaten, Antworten sind Platzhalter. Sobald der Zugang eingetragen ist, arbeitet der Assistent mit Ihren echten Daten.',
    'assistent.verlaufLoeschen': 'Verlauf löschen',
    'assistent.denkt': 'Einen Moment',
    'assistent.platzhalter': 'Fragen oder Beleg fotografieren',
    'assistent.hoertZu': 'Ich höre zu',
    'assistent.sprechen': 'Sprechen',
    'assistent.vorlesen': 'Vorlesen',
    'assistent.stopp': 'Stopp',
    'assistent.senden': 'Senden',
    'assistent.fotografieren': 'Beleg fotografieren',
    'assistent.datei': 'Datei hochladen',
    'assistent.belegAuswerten': 'Beleg auswerten',
    'assistent.wertetAus': 'Beleg wird gelesen',
    'assistent.bestaetigen': 'Bestätigen',
    'assistent.gebucht': 'Gebucht',
    'assistent.verworfen': 'Verworfen',
    'assistent.willkommen': 'Wie kann ich helfen?',
    'assistent.willkommenText': 'Fragen Sie nach Beständen und Zahlen, oder fotografieren Sie einen Lieferschein — die Positionen landen nach Ihrer Prüfung im Lager.',
    'assistent.beispiel1': 'Welche Artikel sind knapp?',
    'assistent.beispiel2': 'Wie viel Umsatz habe ich diesen Monat gemacht?',
    'assistent.beispiel3': 'Welche Rechnungen sind noch offen?',
    'assistent.beispiel4': 'Was ist mein Lagerwert?',
    'allg.entfernen': 'Entfernen',
    'bestaetigung.titel': 'E-Mail-Adresse bestätigen',
    'bestaetigung.text': 'Wir haben Ihnen eine Nachricht geschickt. Bitte öffnen Sie den Link darin, dann geht es weiter.',
    'bestaetigung.warum': 'Ohne bestätigte Adresse erreichen Sie weder Anmeldecodes noch Rechnungen noch die Ankündigung einer Lastschrift.',
    'bestaetigung.schonBestaetigt': 'Ich habe bestätigt — weiter',
    'bestaetigung.erneut': 'Nachricht erneut senden',
    'bestaetigung.erneutGesendet': 'Die Nachricht ist unterwegs.',
    'bestaetigung.versandFehlt': 'Der Versand ist gerade nicht möglich. Bitte wenden Sie sich an den Support.',
    'bestaetigung.fehlgeschlagen': 'Das hat nicht geklappt.',
    'bestaetigung.spam': 'Nichts angekommen? Sehen Sie bitte im Spam-Ordner nach.',
    'bestaetigung.pruefe': 'Einen Moment …',
    'bestaetigung.fertigTitel': 'Adresse bestätigt',
    'bestaetigung.fertigText': 'Ihre E-Mail-Adresse ist bestätigt. Sie können jetzt weiterarbeiten.',
    'bestaetigung.zurApp': 'Zur Anwendung',
    'bestaetigung.abgelaufenTitel': 'Link abgelaufen',
    'bestaetigung.abgelaufenText': 'Dieser Link ist nicht mehr gültig. Melden Sie sich an und fordern Sie eine neue Nachricht an.',
    'bestaetigung.ungueltigTitel': 'Link nicht gültig',
    'bestaetigung.ungueltigText': 'Dieser Link lässt sich nicht einlösen. Möglicherweise ist die Adresse bereits bestätigt.',
    'mandat.pflichtTitel': 'SEPA-Lastschriftmandat',
    'mandat.pflichtText': 'Für die Nutzung wird ein SEPA-Lastschriftmandat benötigt. Es dauert zwei Minuten. Ihre Daten bleiben vollständig erhalten.',
    'mandat.einleitung': 'Sie erteilen das Mandat mit Kontoinhaber und IBAN. Eine Unterschrift auf Papier ist dafür nicht nötig.',
    'mandat.kontoinhaber': 'Kontoinhaber',
    'mandat.kontoinhaberHinweis': 'Genau so, wie der Name bei der Bank hinterlegt ist.',
    'mandat.bicHinweis': 'Im SEPA-Raum nicht erforderlich.',
    'mandat.ibanFalsch': 'Diese IBAN hat keine gültige Prüfsumme.',
    'mandat.wortlautTitel': 'Wortlaut des Mandats',
    'mandat.glaeubiger': 'Gläubiger',
    'mandat.glaeubigerId': 'Gläubiger-ID',
    'mandat.referenz': 'Mandatsreferenz',
    'mandat.referenzSpaeter': 'wird nach dem Erteilen vergeben und vor dem ersten Einzug mitgeteilt',
    'mandat.zustimmung': 'Ich stimme dem oben stehenden Lastschriftmandat zu.',
    'mandat.erteilen': 'Mandat erteilen',
    'mandat.erteilt': 'Das Mandat ist erteilt. Vielen Dank.',
    'mandat.liegtVor': 'Ein gültiges Mandat liegt vor.',
    'mandat.nochNichtBereit': 'Das Lastschriftverfahren wird gerade eingerichtet. Bitte versuchen Sie es später erneut oder wenden Sie sich an den Support.',
    'mandat.ladefehler': 'Die Angaben konnten nicht geladen werden.',
    'mandat.fehlgeschlagen': 'Das Mandat konnte nicht erteilt werden.',
    'allg.optional': 'freiwillig',
    'sperre.titel': 'LagerPro ist gesperrt',
    'sperre.entsperren': 'Entsperren',
    'sperre.abgebrochen': 'Die Prüfung wurde abgebrochen.',
    'geraete.titel': 'Angemeldete Geräte',
    'geraete.hinweis': 'Geräte, auf denen Sie dauerhaft angemeldet bleiben.',
    'geraete.hinweisAdmin': 'Alle Geräte Ihres Betriebs. Bei Verlust oder Ausscheiden hier abmelden.',
    'geraete.keine': 'Kein Gerät dauerhaft angemeldet.',
    'geraete.dieses': 'dieses Gerät',
    'geraete.zuletzt': 'zuletzt',
    'geraete.abmelden': 'Abmelden',
    'geraete.abmeldenFehler': 'Das Gerät ließ sich nicht abmelden.',
    'geraete.ladefehler': 'Die Geräte konnten nicht geladen werden.',
    'geraete.nichtGemerkt': 'Dieses Gerät ist nicht gemerkt. Melden Sie sich einmal mit Passwort an, dann genügt künftig Ihr Fingerabdruck.',
    'geraete.sperreTitel': 'Sperre beim Öffnen',
    'geraete.sperreAn': 'App mit {art} sperren',
    'geraete.sperreText': 'Beim Öffnen und nach fünf Minuten im Hintergrund wird erneut geprüft.',
    'geraete.sperreAusWarnung': 'Ohne Sperre kommt jeder an Ihre Betriebsdaten, der das entsperrte Telefon in die Hand bekommt.',
    'geraete.alleAnderen': "Alle anderen abmelden",
    'geraete.gueltigBis': "angemeldet bis {datum}",
    'geraete.browserTitel': "Im Browser angemeldet",
    'geraete.diese': "diese Anmeldung",
    'geraete.browserHinweis': "Anmeldungen im Browser enden nach 24 Stunden von selbst — oder sofort hier.",
    'app.name': 'LagerPro',
    'app.untertitel': 'Professionelle Lagerverwaltung',

    'benachrichtigung.titel': 'Benachrichtigungen',
    'benachrichtigung.leer': 'Nichts Neues',
    'benachrichtigung.neu': 'Neue Benachrichtigung',
    'benachrichtigung.neueVorgaenge': 'neue Vorgänge',
    'benachrichtigung.anAlle': 'an alle',
    'nav.uebersicht': 'Übersicht',
    'nav.mehr': 'Mehr',
    'allg.rechnung': 'Rechnung',
    'app.gruss': 'Guten Tag, {name}',
    'app.gruppe.tagesgeschaeft': 'Tagesgeschäft',
    'app.gruppe.kontakte': 'Kontakte',
    'app.gruppe.zusammenarbeit': 'Zusammenarbeit',
    'app.gruppe.auswertung': 'Auswertung',
    'app.gruppe.betrieb': 'Betrieb',
    'app.nochmalBeenden': 'Noch einmal drücken zum Beenden',
    'app.keineVerbindung': 'Keine Verbindung — Zahlen fehlen',
    'app.konto': 'Konto',
    'app.darstellung': 'Darstellung',
    'nav.kasse': 'Kasse',
    'nav.lager': 'Lager',
    'nav.belege': 'Belege',
    'nav.rechnungen': 'Rechnungen',
    'nav.kunden': 'Kunden',
    'nav.lieferanten': 'Lieferanten',
    'nav.retouren': 'Retouren',
    'nav.buchhaltung': 'Buchhaltung',
    'nav.ausgaben': 'Ausgaben',
    'nav.bank': 'Bank',
    'nav.fahrtenbuch': 'Fahrtenbuch',
    'nav.aufgaben': "Tafel",
    'nav.chat': 'Chat',
    'nav.team': 'Team',
    'nav.firma': 'Firma',
    'nav.gobd': 'GoBD & Prüfung',
    'nav.tarif': 'Tarif',
    'nav.uebernahme': 'Daten übernehmen',
    'nav.einstellungen': 'Einstellungen',
    'nav.owner': 'Kundensteuerung',
    'nav.mandanten': 'Mandanten',
    'nav.gruppe.verkauf': 'Verkauf',
    'nav.gruppe.waren': 'Waren',
    'nav.gruppe.kontakte': 'Kontakte',
    'nav.gruppe.auswertung': 'Auswertung',
    'nav.gruppe.zusammenarbeit': 'Zusammenarbeit',
    'nav.gruppe.verwaltung': 'Verwaltung',

    'aktion.anlegen': 'Anlegen',
    'aktion.speichern': 'Speichern',
    'aktion.abbrechen': 'Abbrechen',
    'aktion.bearbeiten': 'Bearbeiten',
    'aktion.loeschen': 'Löschen',
    'aktion.schliessen': 'Schließen',
    'aktion.suchen': 'Suchen',
    'aktion.anmelden': 'Anmelden',
    'aktion.abmelden': 'Abmelden',
    'aktion.drucken': 'Drucken',
    'aktion.herunterladen': 'Herunterladen',

    'allg.suche': 'Suche',
    'allg.laedt': 'Lädt',
    'allg.keineTreffer': 'Nichts gefunden',
    'allg.darstellung': 'Darstellung',
    'allg.sprache': 'Sprache',
    'allg.thema.system': 'System',
    'allg.thema.hell': 'Hell',
    'allg.thema.dunkel': 'Dunkel',
    'allg.rolle.inhaber': 'Inhaber',
    'allg.rolle.admin': 'Administrator',
    'allg.rolle.mitarbeiter': 'Mitarbeiter',
    'allg.nichtImTarif': 'Nicht im gebuchten Tarif enthalten',
    'allg.tarif': 'Tarif',
    'allg.menue': 'Menü',
    'allg.schnellzugriff': 'Schnellzugriff',
    'allg.schnellzugriffHinweis': "Seite, Kunde oder Belegnummer …",

    'anmelden.willkommen': 'Willkommen zurück',
    'anmelden.hinweis': 'Melden Sie sich an, um fortzufahren',
    'anmelden.kennung': 'Benutzername oder E-Mail',
    'anmelden.passwort': 'Passwort',
    'anmelden.fehler': 'Anmeldung fehlgeschlagen.',
    'anmelden.keinKonto': 'Noch kein Konto?',
    'anmelden.jetztRegistrieren': 'Kostenlos registrieren',
    'passwort.vergessenLink': "Passwort vergessen?",
    'passwort.vergessenTitel': "Passwort vergessen",
    'passwort.vergessenText': "Geben Sie Ihren Benutzernamen oder Ihre E-Mail-Adresse ein. Wir schicken Ihnen einen Link, mit dem Sie ein neues Passwort festlegen.",
    'passwort.linkSenden': "Link senden",
    'passwort.gesendetTitel': "Schauen Sie in Ihr Postfach",
    'passwort.gesendetText': "Gehört ein Konto mit hinterlegter E-Mail-Adresse zu dieser Angabe, ist der Link unterwegs. Er gilt {min} Minuten. Ohne hinterlegte Adresse hilft Ihnen Ihr Administrator weiter.",
    'passwort.zurAnmeldung': "Zur Anmeldung",
    'passwort.neuTitel': "Neues Passwort festlegen",
    'passwort.fuer': "Für das Konto {name}",
    'passwort.regeln': "Mindestens 8 Zeichen, mit Groß- und Kleinbuchstaben und einer Zahl.",
    'passwort.ungleich': "Die beiden Passwörter stimmen nicht überein.",
    'passwort.speichern': "Passwort speichern",
    'passwort.fertigTitel': "Passwort geändert",
    'passwort.fertigText': "Melden Sie sich jetzt mit dem neuen Passwort an. Alle Geräte wurden abgemeldet.",
    'passwort.abgelaufenTitel': "Link abgelaufen",
    'passwort.abgelaufenText': "Der Link gilt nur 30 Minuten. Fordern Sie einfach einen neuen an.",
    'passwort.ungueltigTitel': "Link ungültig",
    'passwort.ungueltigText': "Dieser Link ist ungültig oder wurde schon benutzt.",
    'passwort.neuAnfordern': "Neuen Link anfordern",
    'passwort.pruefe': "Link wird geprüft …",

    'uebersicht.begruessung': 'Guten Tag',
    'uebersicht.offeneAufgaben': 'Offene Aufgaben',
    'uebersicht.laufendeTimer': 'Laufende Timer',
    'uebersicht.teamOnline': 'Team online',
    'uebersicht.benutzerImTarif': 'Benutzer im Tarif',
    'uebersicht.naechsteAufgaben': 'Nächste Aufgaben',
    'uebersicht.alleAnsehen': 'Alle ansehen',
    'uebersicht.unbegrenzt': 'unbegrenzt',
    'uebersicht.von': 'von',
    'uebersicht.insgesamt': 'insgesamt',
    'uebersicht.keineOffenen': 'Keine offenen Aufgaben.',

    'feld.name': 'Name',
    'feld.nummer': 'Nummer',
    'feld.telefon': 'Telefon',
    'feld.email': 'E-Mail',
    'feld.strasse': 'Straße',
    'feld.plz': 'PLZ',
    'feld.ort': 'Ort',
    'feld.land': 'Land',
    'feld.notiz': 'Notiz',
    'feld.ansprechpartner': 'Ansprechpartner',
    'feld.ustId': 'USt-IdNr.',
    'feld.menge': 'Menge',
    'feld.einzelpreis': 'Einzelpreis',
    'feld.bezeichnung': 'Bezeichnung',
    'feld.artikel': 'Artikel',
    'feld.kunde': 'Kunde',
    'feld.lieferant': 'Lieferant',
    'feld.datum': 'Datum',
    'feld.betrag': 'Betrag',
    'feld.grund': 'Grund',
    'feld.status': 'Status',
    'feld.einheit': 'Einheit',
    'feld.bestand': 'Bestand',
    'feld.gesamt': 'Gesamt',
    'feld.netto': 'Netto',
    'feld.rabatt': 'Rabatt',
    'feld.position': 'Position',
    'feld.positionen': 'Positionen',
    'aktion.hinzufuegen': 'Hinzufügen',
    'aktion.entfernen': 'Entfernen',
    'aktion.zurueck': 'Zurück',
    'aktion.weiter': 'Weiter',
    'kunden.titel': 'Kunden',
    'kunden.neu': 'Kunde anlegen',
    'kunden.nummer': 'Kundennummer',
    'kunden.suche': 'Name, Nummer, E-Mail oder Ort …',
    'kunden.leer': 'Noch keine Kunden',
    'kunden.leerText': 'Legen Sie den ersten Kunden an.',
    'kunden.akte': "Kundenakte",
    'kunden.alle': "Alle Kunden",
    'kunden.nichtGefunden': "Kunde nicht gefunden.",
    'kunden.gespeichert': "Kunde gespeichert.",
    'kunden.geloescht': "{name} wurde gelöscht.",
    'kunden.loeschenFrage': "„{name}“ wirklich löschen?",
    'kunden.einKunde': "1 Kunde",
    'kunden.anzahl': "{n} Kunden",
    'kunden.sortieren': "Sortieren nach",
    'kunden.letzterKaufSort': "Letzter Kauf",
    'kunden.keineKontakt': "Keine Kontaktdaten",
    'kunden.nochNichts': "noch kein Kauf",
    'kunden.zuletzt': "zuletzt {datum}",
    'kunden.rechnungSchreiben': "Rechnung schreiben",
    'kunden.umsatzJahr': "Umsatz {jahr}",
    'kunden.netto': "netto",
    'kunden.vorjahr': "Vorjahr {betrag}",
    'kunden.offen': "Offen",
    'kunden.ueberfaellig': "{betrag} überfällig ({n})",
    'kunden.ueberfaelligKurz': "{betrag} überfällig",
    'kunden.offenAnzahl': "{n} offene Rechnungen",
    'kunden.offenBetrag': "{betrag} offen",
    'kunden.meistgekauft': "Meistgekauft",
    'kunden.malGekauft': "{n}× gekauft",
    'kunden.keineProdukte': "Noch nichts gekauft",
    'kunden.zahlung': "Zahlungsverhalten",
    'kunden.zahltNach': "Ø {n} Tage",
    'kunden.puenktlich': "{n} % pünktlich bezahlt",
    'kunden.nochKeineZahlung': "noch keine Zahlung",
    'kunden.seit': "Kunde seit {datum}",
    'kunden.letzterKauf': "Letzter Kauf {datum}",
    'kunden.gesamt': "Gesamtumsatz {betrag}",
    'kunden.durchschnitt': "Ø Rechnung {betrag}",
    'kunden.guthaben': "Guthaben {betrag}",
    'kunden.verlauf': "Umsatz der letzten 12 Monate",
    'kunden.diagrammLabel': "Umsatz je Monat",
    'kunden.keinUmsatz': "Kein Umsatz in den letzten 12 Monaten",
    'kunden.rechnungenAnzahl': "{n} Rechnungen",
    'kunden.produkte': "Gekaufte Produkte",
    'kunden.kaeufe': "Käufe",
    'kunden.menge': "Menge",
    'kunden.umsatz': "Umsatz",
    'kunden.mehr': "Alle {n} anzeigen",
    'kunden.weniger': "Weniger anzeigen",
    'kunden.dokumente': "Dokumente",
    'kunden.dokumenteHinweis': "{n} insgesamt, neueste zuerst",
    'kunden.alleDokumente': "Alle",
    'kunden.nurOffene': "Offen",
    'kunden.keineDokumente': "Keine Dokumente",
    'kunden.zu': "zu {nummer}",
    'kunden.entwurf': "Entwurf",
    'kunden.faellig': "fällig {datum}",
    'kunden.gueltigBis': "gültig bis {datum}",
    'kunden.ansehen': "Ansehen",
    'kunden.oeffnen': "Öffnen",
    'kunden.art.rechnung': "Rechnung",
    'kunden.art.lieferschein': "Lieferschein",
    'kunden.art.angebot': "Angebot",
    'kunden.art.auftrag': "Auftrag",
    'kunden.art.gutschrift': "Gutschrift",
    'kunden.art.retoure': "Retoure",
    'kunden.arten.rechnung': "Rechnungen",
    'kunden.arten.lieferschein': "Lieferscheine",
    'kunden.arten.angebot': "Angebote",
    'kunden.arten.auftrag': "Aufträge",
    'kunden.arten.gutschrift': "Gutschriften",
    'kunden.arten.retoure': "Retouren",
    'kunden.status.entwurf': "Entwurf",
    'kunden.status.gestellt': "Offen",
    'kunden.status.bezahlt': "Bezahlt",
    'kunden.status.storniert': "Storniert",
    'kunden.status.gesendet': "Gesendet",
    'kunden.status.angenommen': "Angenommen",
    'kunden.status.abgelehnt': "Abgelehnt",
    'kunden.status.abgelaufen': "Abgelaufen",
    'kunden.status.offen': "Offen",
    'kunden.status.inArbeit': "In Arbeit",
    'kunden.status.erledigt': "Erledigt",
    'kunden.status.geliefert': "Geliefert",
    'kunden.status.eingelagert': "Eingelagert",
    'kunden.status.erfasst': "Erfasst",
    'kunden.status.nichtGeliefert': "Nicht geliefert",
    'kunden.status.ausgestellt': "Ausgestellt",
    'kunden.status.teilbezahlt': "Teilbezahlt",
    'lieferanten.titel': 'Lieferanten',
    'lieferanten.neu': 'Lieferant anlegen',
    'lieferanten.nummer': 'Lieferantennummer',
    'lieferanten.suche': 'Name, Ansprechpartner oder Ort …',
    'lieferanten.akte': 'Akte',
    'lieferanten.alle': 'Alle Lieferanten',
    'lieferanten.artikel': 'Artikel',
    'lieferanten.lieferungen': 'Lieferungen',
    'lieferanten.rechnungen': 'Rechnungen',
    'lieferanten.offen': 'Offen',
    'lieferanten.ueberfaellig': 'überfällig',
    'lieferanten.einkaufJahr': 'Einkauf {jahr}',
    'lieferanten.letzteLieferung': 'Letzte Lieferung',
    'lieferanten.keineArtikel': 'Noch keine Artikel von diesem Lieferanten.',
    'lieferanten.keineLieferungen': 'Noch keine Wareneingänge.',
    'lieferanten.keineRechnungen': 'Noch keine Rechnungen.',
    'lieferanten.rechnungErfassen': 'Rechnung erfassen',
    'lieferanten.alsBezahlt': 'Als bezahlt markieren',
    'lieferanten.bezahltAm': 'bezahlt {datum}',
    'lieferanten.faellig': 'fällig {datum}',
    'lieferanten.betrag': 'Betrag (brutto)',
    'lieferanten.rechnungsnummer': 'Rechnungsnummer',
    'lieferanten.rechnungsdatum': 'Rechnungsdatum',
    'lieferanten.faelligAm': 'Fällig am',
    'lieferanten.schonBezahlt': 'Bereits bezahlt',
    'lieferanten.wert': 'Wert',
    'lieferanten.wiederOffen': 'Wieder öffnen',
    'lieferanten.gespeichert': 'Gespeichert.',
    'lieferanten.bearbeiten': 'Bearbeiten',
    'lieferanten.loeschen': 'Löschen',
    'lager.zugang': 'Wareneingang',
    'lager.abgang': 'Abgang',
    'lager.buchen': 'Buchen',
    'lager.rechnungLiegtBei': 'Rechnung des Lieferanten liegt bei',
    'lager.rechnungBezahlt': 'Rechnung ist bereits bezahlt',
    'lager.gebucht': 'Bestand von „{name}“ gebucht.',
    'lager.ohneLieferant': 'ohne Lieferant',
    'lieferanten.leer': 'Noch keine Lieferanten',
    'lieferanten.leerText': 'Legen Sie den ersten Lieferanten an.',
    'allg.andereSuche': 'Andere Suchbegriffe versuchen.',

    'lager.titel': 'Lager',
    'lager.neu': 'Artikel anlegen',
    'lager.artikelnummer': 'Artikelnummer',
    'lager.ean': 'EAN / Barcode',
    'lager.kategorie': 'Kategorie',
    'lager.lagerort': 'Lagerort',
    'lager.einkaufspreis': 'Einkaufspreis (netto)',
    'lager.verkaufspreis': 'Verkaufspreis (netto)',
    'lager.mindestbestand': 'Mindestbestand',
    'lager.anfangsbestand': 'Anfangsbestand',
    'lager.wareneingang': 'Wird als Wareneingang gebucht.',
    'lager.suche': 'Bezeichnung, Nummer oder EAN …',
    'lager.leer': 'Keine Artikel',
    'lager.leerText': 'Legen Sie den ersten Artikel an oder übernehmen Sie Daten aus der alten App.',
    'lager.verlauf': 'Verlauf',
    'lager.keineBewegungen': 'Keine Bewegungen',
    'lager.etikett': 'Etikett',
    'lager.etikettenDrucken': 'Etiketten drucken',
    'lager.format': 'Format',
    'lager.stueckJeArtikel': 'Stück je Artikel',
    'lager.mehrfachHinweis': 'Beim Wareneingang braucht man dasselbe Etikett oft mehrfach.',
    'lager.knapp': 'knapp',
    'lager.auswahl': 'Auswahl',
    'lager.auswahlAufheben': 'Auswahl aufheben',
    'lager.alle': 'Alle',
    'lager.keinLieferant': '— kein Lieferant —',
    'kasse.titel': 'Kasse',
    'kasse.schritt1': 'Schritt 1 von 2 — für wen ist der Verkauf?',
    'kasse.kundeWaehlen': 'Kunde wählen',
    'kasse.kundeSuchen': 'Kunde suchen',
    'kasse.kundeSuche': 'Name, Kundennummer oder Ort …',
    'kasse.kundeWechseln': 'Kunde wechseln',
    'kasse.ohneKonto': 'Ohne Kundenkonto',
    'kasse.historieHinweis': 'Der Verkauf erscheint dann in seiner Rechnungshistorie',
    'kasse.artikelSuchen': 'Artikel suchen',
    'kasse.artikelSuche': 'Bezeichnung, Nummer oder Barcode scannen …',
    'kasse.keinArtikel': 'Kein Artikel gefunden',
    'kasse.warenkorb': 'Warenkorb',
    'kasse.warenkorbLeeren': 'Warenkorb leeren',
    'kasse.leer': 'Noch nichts erfasst',
    'kasse.leerText': 'Artikel links anklicken.',
    'kasse.zahlart': 'Zahlart',
    'kasse.bezahlt': 'Bezahlt?',
    'kasse.vollstaendig': 'Vollständig',
    'kasse.teilweise': 'Teilweise',
    'kasse.nochNicht': 'Noch nicht',
    'kasse.gegeben': 'Gegeben (für Rückgeld)',
    'kasse.rueckgeld': 'Rückgeld: {betrag}',
    'kasse.erhalten': 'Erhalten',
    'kasse.kundePflicht': 'Für einen offenen Betrag bitte einen Kunden wählen — sonst weiß niemand, wer zahlen muss.',
    'kasse.buchen': 'Buchen · {erhalten} erhalten, {offen} offen',
    'kasse.kassieren': 'Kassieren · {betrag}',
    'kasse.offenBis': 'Offen: {betrag} · fällig {datum}',
    'kasse.bezahltMit': 'Bezahlt · {zahlart}',
    'kasse.naechster': 'Nächster Verkauf',
    'kasse.bar': 'Bar',
    'kasse.karte': 'Karte',
    'kasse.ueberweisung': 'Überweisung',
    'kasse.abgeschlossen': 'Verkauf abgeschlossen',
    'kasse.alle': "Alle",
    'kasse.anzahlung': "Anzahlung",
    'kasse.aufRechnung': "Auf Rechnung",
    'kasse.barAnteil': "Bar-Anteil",
    'kasse.barKassieren': "Bar kassieren · {betrag}",
    'kasse.beleg': "Beleg",
    'kasse.betragBrutto': "Betrag (brutto)",
    'kasse.bezahlen': "Bezahlen",
    'kasse.bezahlenBetrag': "Bezahlen · {betrag}",
    'kasse.bonNr': "Bon {n}",
    'kasse.bonRabatt': "Rabatt auf den Bon",
    'kasse.bonVerwerfen': "Bon verwerfen",
    'kasse.codeUnbekannt': "Kein Artikel mit dem Code {code}",
    'kasse.enthaltenSteuer': "enthält {satz} % {name}",
    'kasse.fehlgeschlagen': "Verkauf konnte nicht gebucht werden.",
    'kasse.freiHinweis': "Für alles ohne Artikel: Dienstleistung, Pfand, Sonstiges.",
    'kasse.freiStandard': "Sonstiges",
    'kasse.freierBetrag': "Freier Betrag",
    'kasse.gegebenKurz': "Gegeben",
    'kasse.gespart': "gespart",
    'kasse.geteilt': "Geteilt",
    'kasse.geteiltKassieren': "Kassieren · {bar} bar + {karte} Karte",
    'kasse.haeufig': "Häufig",
    'kasse.kameraFehler': "Kamera nicht verfügbar — bitte Zugriff erlauben oder einen Handscanner benutzen.",
    'kasse.karteBezahlt': "Karte bezahlt · {betrag}",
    'kasse.karteHinweis': "Betrag am Kartenterminal buchen, dann hier bestätigen.",
    'kasse.karteRest': "Rest per Karte",
    'kasse.keinRabatt': "kein",
    'kasse.keineArtikelAngelegt': "Noch keine Artikel angelegt.",
    'kasse.kundeEntfernen': "Kunde entfernen",
    'kasse.kundeZuordnen': "Kunde zuordnen",
    'kasse.laufkunde': "Laufkunde",
    'kasse.leerNeu': "Artikel antippen oder Barcode scannen.",
    'kasse.neuerBon': "Neuer Bon",
    'kasse.nichtAufLager': "„{name}\" ist nicht auf Lager.",
    'kasse.nichtVorraetig': "nicht vorrätig",
    'kasse.nurNoch': "Von „{name}\" sind nur {menge} verfügbar.",
    'kasse.parkenHinweis': "Diesen Bon parken und einen neuen anfangen",
    'kasse.passend': "passend",
    'kasse.positionen': "Positionen: {n}",
    'kasse.preis': "Preis",
    'kasse.preisAendern': "Preis ändern",
    'kasse.rabatt': "Rabatt",
    'kasse.rabattText': "{p} % Rabatt",
    'kasse.rechnungBuchen': "Auf Rechnung buchen · {offen} offen",
    'kasse.rueckgeldGross': "Rückgeld",
    'kasse.scannen': "Barcode scannen",
    'kasse.scannenHinweis': "Code vor die Kamera halten.",
    'kasse.wofuer': "Wofür?",
    'kasse.zuWenig': "Es fehlen noch {betrag}.",

    'belege.titel': 'Belege',
    'belege.angebote': 'Angebote',
    'belege.auftraege': 'Aufträge',
    'belege.lieferscheine': 'Lieferscheine',
    'belege.gutschriften': 'Gutschriften',
    'belege.offeneAngebote': 'Offene Angebote',
    'belege.laufendeAuftraege': 'Laufende Aufträge',
    'belege.lieferscheineOffen': 'Lieferscheine offen',
    'belege.interessent': 'Name des Interessenten',
    'belege.notiz': 'Notiz (erscheint auf dem Beleg)',
    'belege.rabatt': 'Rabatt %',
    'belege.druckenPdf': 'Drucken / PDF',
    'belege.entwurfLoeschen': 'Entwurf löschen',
    'belege.gesamtBrutto': 'Gesamt brutto',
    'belege.einzel': 'Einzel',
    'rechnungen.titel': 'Rechnungen',
    'rechnungen.neu': 'Neue Rechnung',
    'rechnungen.schreiben': 'Rechnung schreiben',
    'rechnungen.stellen': 'Rechnung stellen',
    'rechnungen.entwurfSpeichern': 'Als Entwurf speichern',
    'rechnungen.entwurfHinweis': 'Ein Entwurf bucht noch keinen Bestand ab.',
    'rechnungen.bezahltMarkieren': 'Als bezahlt markieren',
    'rechnungen.teilbezahlt': 'teilweise bezahlt · offen {betrag}',
    'rechnungen.perBank': 'per Bank zugeordnet',
    'rechnungen.zahlungen': 'Zahlungen',
    'rechnungen.nochOffen': 'Noch offen',
    'rechnungen.bankeingang': 'Bankeingang',
    'rechnungen.zahlung': 'Zahlung',
    'rechnungen.zahlart': 'Zahlart',
    'rechnungen.zahlweg.ueberweisung': 'Überweisung',
    'rechnungen.zahlweg.bar': 'Bar',
    'rechnungen.zahlweg.karte': 'Karte',
    'rechnungen.zahlungsziel': 'Zahlungsziel',
    'rechnungen.zielKunde': 'Wie beim Kunden ({tage} Tage)',
    'rechnungen.zielSofort': 'Sofort fällig',
    'rechnungen.zielTage': '{tage} Tage',
    'rechnungen.zielDatum': 'Eigenes Datum',
    'rechnungen.faelligAm': 'Fällig am',
    'rechnungen.bezahlung': 'Bezahlt?',
    'rechnungen.offenLassen': 'Noch nicht bezahlt',
    'rechnungen.schonBezahlt': 'Bereits bezahlt',
    'rechnungen.lieferscheinMit': 'Lieferschein gleich mit erstellen',
    'rechnungen.lieferschein': 'Lieferschein',
    'rechnungen.lieferscheinDrucken': 'Lieferschein drucken',
    'rechnungen.lieferscheinErstellen': 'Lieferschein erstellen',
    'rechnungen.zahlungErfassen': 'Zahlung erfassen',
    'rechnungen.betrag': 'Betrag',
    'rechnungen.zahltag': 'Zahltag',
    'rechnungen.alsBezahltSpeichern': 'Als bezahlt speichern',
    'rechnungen.teilzahlungSpeichern': 'Teilzahlung speichern',
    'rechnungen.ratenHinweis': 'Weniger als offen? Dann bleibt der Rest offen — so lassen sich Raten erfassen.',
    'rechnungen.zahlungEntfernen': 'Zahlung entfernen',
    'rechnungen.sofort': 'sofort fällig',
    'rechnungen.gestelltMitLs': 'Rechnung gestellt. Lieferschein {nummer} liegt zum Drucken bereit.',
    'rechnungen.zahlungGespeichert': 'Zahlung erfasst.',
    'rechnungen.vollBezahlt': 'Die Rechnung ist vollständig bezahlt.',
    'rechnungen.guthaben': 'Kundenguthaben',
    'rechnungen.gutschriftTitel': 'Gutschrift zu dieser Rechnung',
    'rechnungen.gutschriftMenge': 'Menge gutschreiben',
    'rechnungen.gutschriftGrund': 'Grund (steht auf der Gutschrift)',
    'rechnungen.gutschriftStellen': 'Gutschrift stellen',
    'rechnungen.gutschriftHinweis': 'Ist die Rechnung noch offen, wird die Gutschrift damit verrechnet. Sonst wird sie Guthaben des Kunden. Je Rechnung ist eine Gutschrift möglich.',
    'rechnungen.gutschriftErstellt': 'Gutschrift {nummer} gestellt.',
    'rechnungen.gutschriftVorhanden': 'Gutschrift {nummer}',
    'rechnungen.perGutschrift': 'per Gutschrift ausgeglichen',
    'rechnungen.gutschriftDrucken': 'Gutschrift drucken',
    'rechnungen.gutschriftSumme': 'Gutschrift über {betrag}',
    'rechnungen.stornieren': 'Stornieren',
    'rechnungen.gutschriftErstellen': 'Gutschrift erstellen',
    'rechnungen.drucken': 'Beleg drucken / als PDF',
    'rechnungen.stempelBezahlt': 'Bezahlt',
    'rechnungen.stempelAm': 'am {datum}',
    'rechnungen.keinGirocode': 'Kein GiroCode',
    'rechnungen.girocodeText': 'Mit der Banking-App scannen — Betrag und Verwendungszweck sind schon ausgefüllt.',
    'neuigkeiten.titel': 'Was ist neu',
    'neuigkeiten.auchNeu': 'Außerdem neu',
    'neuigkeiten.fortschritt': 'Ihr Fortschritt mit LagerPro',
    'neuigkeiten.updates': 'Updates',
    'neuigkeiten.verbesserungen': 'Verbesserungen',
    'neuigkeiten.seit': 'seit {datum}',
    'neuigkeiten.mailHinweis': 'Neuigkeiten per E-Mail können Sie unter Einstellungen abbestellen.',
    'neuigkeiten.verstanden': 'Alles klar',
    'rechnungen.barverkauf': 'Barverkauf / ohne Kundenkonto',
    'rechnungen.leer': 'Keine Rechnungen',
    'rechnungen.leerText': 'Schreiben Sie die erste Rechnung.',
    'status.alle': 'Alle',
    'status.entwurf': 'Entwurf',
    'status.entwuerfe': 'Entwürfe',
    'status.offen': 'Offen',
    'status.bezahlt': 'Bezahlt',
    'status.storniert': 'Storniert',
    'retouren.titel': 'Retouren',
    'retouren.erfassen': 'Retoure erfassen',
    'retouren.alle': 'Alle Retouren',
    'retouren.leer': 'Noch keine Retouren',
    'retouren.leerText': 'Eine Retoure gehört immer zu einer Rechnung: Kunde wählen, Rechnung wählen, Positionen zurücknehmen.',
    'retouren.schritt1': '1. Kunde',
    'retouren.schritt2': '2. Rechnung',
    'retouren.schritt3': '3. Was kommt zurück?',
    'retouren.grund': 'Grund (optional)',
    'retouren.grundBeispiel': 'z. B. Transportschaden, Fehllieferung',
    'retouren.berechnet': 'Berechnet',
    'retouren.zurueck': 'Zurück',
    'retouren.gutschrift': 'Gutschrift',
    'retouren.einlagern': 'Ware wieder einlagern',
    'retouren.kundeWaehlen': 'Kunde wählen …',

    'team.titel': 'Team',
    'team.mitglieder': 'Mitglieder',
    'team.neu': 'Mitarbeiter anlegen',
    'team.benutzername': 'Benutzername',
    'team.emailOptional': 'E-Mail (optional)',
    'team.rolle': 'Rolle',
    'team.passwortHinweis': 'Mindestens 8 Zeichen. Der Mitarbeiter kann es später ändern.',
    'team.limitErreicht': 'Benutzerlimit erreicht',
    'team.leer': 'Noch keine Mitglieder',
    'team.leerText': 'Legen Sie den ersten Mitarbeiter an.',
    'team.gesperrt': 'Gesperrt',
    'team.alle': "Alle im Team",
    'team.nichtGefunden': "Person nicht gefunden.",
    'team.sie': "Sie",
    'team.online': "Online",
    'team.offline': "Offline",
    'team.bearbeiten': "Profil bearbeiten",
    'team.gespeichert': "Profil gespeichert.",
    'team.kontakt': "Kontakt & Arbeit",
    'team.position': "Position",
    'team.positionBeispiel': "z. B. Lagerleitung",
    'team.abteilung': "Abteilung",
    'team.mobil': "Mobil",
    'team.arbeitszeit': "Arbeitszeit",
    'team.arbeitszeitBeispiel': "z. B. Mo–Fr 8–16 Uhr",
    'team.eintritt': "Eintritt",
    'team.jahre': "{n} Jahre im Team",
    'team.einJahr': "1 Jahr im Team",
    'team.ersteJahr': "im ersten Jahr",
    'team.privat': "Persönlich",
    'team.privatHinweis': "Sehen nur die Person selbst und Administratoren",
    'team.freiwillig': "Freiwillige Angaben — sehen nur die Person selbst und Administratoren",
    'team.geburtstag': "Geburtstag",
    'team.alter': "{n} Jahre",
    'team.anschrift': "Anschrift",
    'team.notfall': "Notfallkontakt",
    'team.notfallName': "Notfallkontakt (Name)",
    'team.notfallTelefon': "Notfallkontakt (Telefon)",
    'team.notfallBeispiel': "z. B. Anna Meyer (Partnerin)",
    'team.notiz': "Interne Notiz",
    'team.notizHinweis': "Nur für Administratoren sichtbar",
    'team.keineAngaben': "Noch keine Angaben",
    'team.offeneAufgaben': "Offene Aufgaben",
    'team.keineAufgaben': "Keine offenen Aufgaben",
    'team.aktualisiert': "Zuletzt geändert am {datum}",
    'team.nurAdminFelder': "Position, Abteilung, Arbeitszeit und Eintritt pflegt ein Administrator.",
    'uebersicht.umsatzHeute': 'Umsatz heute',
    'uebersicht.offeneForderungen': 'Offene Forderungen',
    'uebersicht.lagerwert': 'Lagerwert',
    'uebersicht.unterMindest': 'Unter Mindestbestand',
    'uebersicht.nachbestellen': 'Nachbestellen',
    'uebersicht.ueberfaellig': 'Überfällige Rechnungen',
    'uebersicht.zahlungsziel': 'Zahlungsziel überschritten',
    'uebersicht.umsatzMonat': 'Umsatz nach Monat',
    'uebersicht.schnell': 'Schnell erledigen',
    'uebersicht.kassieren': 'Verkauf kassieren',
    'uebersicht.wareneingang': 'Wareneingang buchen',
    'aufgaben.titel': 'Aufgaben',
    'aufgaben.neu': 'Neue Aufgabe',
    'aufgaben.wasZuTun': 'Was ist zu tun?',
    'aufgaben.ansichtHeute': 'Heute',
    'aufgaben.ansichtBoard': 'Board',
    'aufgaben.ansichtAlle': 'Alle',
    'aufgaben.vomBetrieb': 'Vom Betrieb',
    'aufgaben.vomBetriebHinweis': 'Ergibt sich aus Ihren Daten und verschwindet von selbst, sobald es erledigt ist.',
    'aufgaben.meine': 'Meine Aufgaben',
    'aufgaben.nurMeine': 'Nur meine',
    'aufgaben.ueberfaellig': 'Überfällig',
    'aufgaben.heute': 'Heute',
    'aufgaben.morgen': 'Morgen',
    'aufgaben.dieseWoche': 'Diese Woche',
    'aufgaben.spaeter': 'Später',
    'aufgaben.ohneDatum': 'Ohne Datum',
    'aufgaben.spalteOffen': 'Offen',
    'aufgaben.spalteArbeit': 'In Arbeit',
    'aufgaben.spalteErledigt': 'Erledigt',
    'aufgaben.schnell': 'Neue Aufgabe — z. B. „Morgen Meyer anrufen !hoch @Mia“',
    'aufgaben.schnellHilfe': 'heute · morgen · Montag · 15.11. · !hoch · !niedrig · @Name',
    'aufgaben.oeffnen': 'Öffnen',
    'aufgaben.zurueckstellen': 'Zurückstellen',
    'aufgaben.tage': '{n} Tage',
    'aufgaben.uebernehmen': 'Als Aufgabe übernehmen',
    'aufgaben.beschreibung': 'Beschreibung',
    'aufgaben.faellig': 'Fällig',
    'aufgaben.prioritaet': 'Priorität',
    'aufgaben.niedrig': 'Niedrig',
    'aufgaben.normal': 'Normal',
    'aufgaben.zustaendig': 'Zuständig',
    'aufgaben.niemand': 'Niemand',
    'aufgaben.wiederholung': 'Wiederholung',
    'aufgaben.keine': 'Keine',
    'aufgaben.taeglich': 'Täglich',
    'aufgaben.woechentlich': 'Wöchentlich',
    'aufgaben.monatlich': 'Monatlich',
    'aufgaben.checkliste': 'Checkliste',
    'aufgaben.punktNeu': 'Punkt hinzufügen',
    'aufgaben.loeschen': 'Löschen',
    'aufgaben.loeschenFrage': 'Diese Aufgabe löschen?',
    'aufgaben.timerStart': 'Timer starten',
    'aufgaben.timerStopp': 'Timer stoppen',
    'aufgaben.allesErledigt': 'Nichts Dringendes — der Betrieb läuft.',
    'aufgaben.keineEigenen': 'Keine offenen Aufgaben.',
    'aufgaben.folge': 'Nächste am {datum}',
    'aufgaben.zieheHier': 'Hierher ziehen',
    'aufgaben.erledigtHeute': 'Heute erledigt',
    'aufgaben.schliessen': 'Schließen',
    'aufgaben.aktionGeliefert': 'Als geliefert markieren',
    'aufgaben.aktionBezahlt': 'Als bezahlt markieren',
    'aufgaben.erledigtMeldung': 'Erledigt.',
    'aufgaben.zurueckgestellt': 'Zurückgestellt bis {datum}.',
    'aufgaben.uebernommen': 'Als Aufgabe übernommen.',
    'aufgaben.auto.nachbestellen': '{artikel} nachbestellen',
    'aufgaben.auto.nachbestellenText': 'Bestand {bestand} {einheit}, Mindestbestand {mindestbestand}',
    'aufgaben.auto.bei': 'bei {lieferant}',
    'aufgaben.auto.ausliefern': 'Lieferschein {nummer} ausliefern',
    'aufgaben.auto.ausliefernText': 'an {kunde}',
    'aufgaben.auto.nachfassen': 'Angebot {nummer} nachfassen',
    'aufgaben.auto.nachfassenText': '{kunde} · seit {tage} Tagen ohne Antwort',
    'aufgaben.auto.entwurf': 'Rechnungsentwurf stellen',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · liegt seit {tage} Tagen',
    'aufgaben.auto.support': 'Antwort vom Support lesen',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'Gescannte Belege buchen',
    'aufgaben.auto.belegscanText': '{anzahl} warten auf Ihre Prüfung',
    'aufgaben.auto.mahnen': 'Zahlung anmahnen: {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} offen · {tage} Tage überfällig',
    'aufgaben.auto.bezahlen': 'Lieferant bezahlen: {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · fällig {datum}',
    'aufgaben.auto.zuordnen': 'Zahlungseingänge zuordnen',
    'aufgaben.auto.zuordnenText': '{anzahl} Eingänge warten auf Zuordnung',
    'aufgaben.auto.bank': 'Bank neu verbinden: {bank}',
    'aufgaben.auto.bankAbgelaufen': 'Die Zustimmung ist abgelaufen — es kommen keine Umsätze mehr.',
    'aufgaben.auto.bankEndet': 'Die Zustimmung endet am {datum}.',
    'aufgaben.auto.ustva': 'Umsatzsteuer-Voranmeldung {monat}',
    'aufgaben.auto.ustvaText': 'Zahllast {betrag} · bis {datum} ans Finanzamt',
    'tafel.titel': "Tafel",
    'tafel.untertitel': "Aufgaben, Termine und Fristen auf einen Blick",
    'tafel.ansicht.woche': "Woche",
    'tafel.ansicht.monat': "Monat",
    'tafel.ansicht.liste': "Liste",
    'tafel.neuerZettel': "Neuer Zettel",
    'tafel.zurueck': "Vorherige",
    'tafel.vor': "Nächste",
    'tafel.kw': "KW {n}",
    'tafel.naechste30': "Die nächsten 30 Tage",
    'tafel.art.aufgabe': "Aufgabe",
    'tafel.art.termin': "Termin",
    'tafel.art.frist': "Frist",
    'tafel.liegenGeblieben': "Liegen geblieben",
    'tafel.ablage': "Ablage",
    'tafel.ablageHinweis': "Ohne Tag — bei Gelegenheit",
    'tafel.leerTag': "+ Zettel",
    'tafel.weitere': "+{n} weitere",
    'tafel.erledigt': "Erledigt",
    'tafel.wiederOffen': "Wieder öffnen",
    'tafel.ziehen': "Zum Verschieben auf einen anderen Tag ziehen",
    'tafel.automatisch': "automatisch",
    'tafel.schnell': "Was steht an?",
    'tafel.schnellGross': "Was steht an? z. B. „14:30 Termin Meyer\" oder „Angebot schicken !hoch\"",
    'tafel.schnellHilfe': "heute · morgen · Freitag · 15.11. · 14:30 · !hoch · @Name",
    'tafel.inEinerWoche': "In einer Woche",
    'tafel.artWaehlen': "Art wählen",
    'tafel.bisWann': "Bis wann?",
    'tafel.wann': "Wann?",
    'tafel.von': "Von",
    'tafel.bis': "Bis",
    'tafel.ort': "Ort",
    'tafel.notiz': "Notiz",
    'tafel.mehr': "Weitere Optionen",
    'tafel.anheften': "Anheften",
    'tafel.fristHinweis': "Ergibt sich aus Ihren Daten und verschwindet von selbst, sobald es erledigt ist.",
    'tafel.kurzTitel': "Meine Tafel",
    'tafel.kurzLiegen': "{n} liegen geblieben",
    'tafel.zurTafel': "Zur Tafel",
    'tafel.heuteFrei': "Nichts geplant.",
    'tafel.frist.angebot': "Angebot läuft ab: {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "Lieferung: {nummer}",
    'tafel.frist.lieferungText': "an {kunde}",
    'tafel.frist.zahlungseingang': "Zahlung erwartet: {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "Lieferantenrechnung fällig: {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "Umsatzsteuer-Voranmeldung",
    'tafel.frist.ustvaText': "für {monat}",
    'aufgaben.leer': 'Noch keine Aufgaben',
    'aufgaben.leerText': 'Legen Sie oben die erste an.',
    'aufgaben.hoch': 'Hoch',
    'aufgaben.laeuft': 'läuft',
    'chat.titel': 'Chat',
    'chat.wemSchreiben': 'Wem schreiben?',
    'chat.leer': 'Noch keine Nachrichten',
    'chat.senden': 'Senden',
    'chat.dateiAnhaengen': 'Datei anhängen',
    'chat.anhangEntfernen': 'Anhang entfernen',
    'chat.zurListe': 'Zurück zur Liste',
    'chat.alleImTeam': 'Alle im Team',
    'chat.herunterladenTipp': 'zum Herunterladen tippen',

    'basis.tarifAb': 'Verfügbar ab dem Tarif',
    'basis.tarifWechsel': 'Sie können den Tarif unter „Tarif & Abrechnung“ wechseln.',
    'basis.support': 'Bitte wenden Sie sich an den Support, um diese Funktion freizuschalten.',
    'positionen.hinzufuegen': 'Position hinzufügen',
    'positionen.freiText': '— frei eingeben —',
    'passwort.festlegen': 'Passwort festlegen',
    'passwort.uebermittelt': 'Übermitteltes Passwort',
    'passwort.neu': 'Neues Passwort',
    'passwort.wiederholen': 'Neues Passwort wiederholen',
    'tarif.titel': 'Tarif & Abrechnung',
    'tarif.aktuell': 'Aktueller Tarif',
    'tarif.enthalten': 'Enthalten',
    'tarif.nichtEnthalten': 'Nicht enthalten',
    'tarif.proMonat': '/ Monat',
    'tarif.sonderkondition': 'Sonderkondition',
    'tarif.verlauf': 'Tarifverlauf',
    'tarif.mandat': 'Lastschriftmandat',
    'tarif.angebot': 'Ihr persönliches Angebot',
    'kopie.titel': "Datenkopie",
    'kopie.text': "Eine Kopie Ihrer Daten können Sie ab der Kündigung bis 30 Tage nach Vertragsende anfordern.",
    'kopie.frist': "Möglich bis {datum}.",
    'kopie.anfordern': "Datenkopie anfordern",
    'kopie.angefordert': "Angefordert am {datum}. Sobald die Kopie bereit ist, bekommen Sie eine E-Mail.",
    'kopie.bereit': "Ihre Datenkopie ist bereit: {format}.",
    'kopie.passwort': "Ihr Anmeldepasswort",
    'kopie.passwortHinweis': "Mit diesem Passwort wird die ZIP-Datei verschlüsselt — Sie öffnen sie per Doppelklick mit demselben Passwort.",
    'kopie.herunterladen': "Herunterladen",
    'kopie.geladen': "Heruntergeladen. Bewahren Sie die Datei sicher auf.",
    'kopie.formatLesbar': "PDF und Tabellen (Excel)",
    'kopie.formatWiederherstellung': "Wiederherstellungsdatei",
    'kopie.nurAdmin': "Die Datenkopie fordert ein Administrator Ihres Betriebs an.",
    'abo.agbAnsehen': "AGB ansehen",
    'abo.agbZustimmung': "Ich habe die AGB gelesen und stimme ihnen zu.",
    'abo.aufbewahrung': "Denken Sie an Ihre Aufbewahrungspflichten: Rechnungen und Buchungsbelege 8 Jahre, Bücher und Abschlüsse 10 Jahre.",
    'abo.aufpreisHinweis': "Auf der nächsten Rechnung: {betrag} netto Aufpreis für den Tarifwechsel am {datum}.",
    'abo.bestaetigen': "Wechsel bestätigen",
    'abo.erstZuruecknehmen': "Der Vertrag ist gekündigt. Um den Tarif zu wechseln, nehmen Sie zuerst die Kündigung zurück.",
    'abo.gekuendigt': "Gekündigt zum {datum}. Bis dahin nutzen Sie LagerPro wie gewohnt.",
    'abo.gekuendigtMeldung': "Gekündigt zum {datum}. Eine Bestätigung ist per E-Mail unterwegs.",
    'abo.gewechselt': "Sie nutzen jetzt {plan}.",
    'abo.hochstufen': "Hochstufen",
    'abo.ihrTarif': "Ihr Tarif",
    'abo.jetztKuendigen': "Jetzt kündigen",
    'abo.kostenpflichtig': "Kostenpflichtig wechseln",
    'abo.kuendigen': "Vertrag kündigen",
    'abo.kuendigenText': "Die Kündigung gilt zum {datum}. Bis dahin nutzen Sie LagerPro wie gewohnt; danach können Sie 30 Tage lang eine Kopie Ihrer Daten anfordern.",
    'abo.kuendigenTextTest': "Sie sind im Gratismonat: Der Vertrag endet mit dem Gratismonat am {datum}, berechnet wird nichts.",
    'abo.kuendigenTitel': "Vertrag kündigen",
    'abo.mandatAendern': "Bankverbindung ändern",
    'abo.mandatBisTestende': "Bitte hinterlegen Sie bis dahin ein SEPA-Lastschriftmandat — sonst pausiert LagerPro nach dem Gratismonat.",
    'abo.nurAdmin': "Tarif und Vertrag verwaltet ein Administrator Ihres Betriebs.",
    'abo.runterstufen': "Runterstufen",
    'abo.sonderkondition': "Sie haben eine Sonderkondition. Einen Tarifwechsel klären Sie bitte über den Support.",
    'abo.testDanach': "Danach kostet {plan} {preis} im Monat.",
    'abo.testLaeuft': "Gratismonat: noch {n} Tage, bis einschließlich {datum}.",
    'abo.vorgemerkt': "Wechsel zu {plan} vorgemerkt ab {datum}.",
    'abo.vorgemerktMeldung': "Wechsel zu {plan} ab {datum} vorgemerkt.",
    'abo.vormerkungWeg': "Vormerkung zurückgenommen.",
    'abo.vormerkungZurueck': "Vormerkung zurücknehmen",
    'abo.waehlen': "Wählen",
    'abo.wechselHoch': "Gilt sofort. Ist dieser Monat schon abgerechnet, steht die Differenz für die Resttage auf der nächsten Rechnung. Ab nächstem Monat {preis} im Monat.",
    'abo.wechselRunter': "Gilt ab {datum}. Bis dahin behalten Sie alle Funktionen von {plan}.",
    'abo.wechselTest': "Gilt sofort. Im Gratismonat kostet das nichts; danach {preis} im Monat.",
    'abo.wechselTitel': "Zu {plan} wechseln",
    'abo.wechselnHinweis': "Hochstufen gilt sofort, Runterstufen zum Monatsende.",
    'abo.wechselnTitel': "Tarif wechseln",
    'abo.zurueckMeldung': "Kündigung zurückgenommen — Ihr Vertrag läuft weiter.",
    'abo.zuruecknehmen': "Kündigung zurücknehmen",
    'abo.beendetTitel': "Ihr Vertrag ist beendet",
    'abo.beendetText': "Ihr LagerPro-Vertrag endete am {datum}.",
    'abo.beendetKopie': "Bis {bis} können Sie eine Kopie Ihrer Daten anfordern — danach werden sie gelöscht.",
    'firma.titel': 'Firma',
    'firma.anschrift': 'Anschrift und Kontakt',
    'firma.steuerRegister': 'Steuer und Register',
    'firma.girocode': 'Zahlcode auf die Rechnung drucken',
    'firma.girocodeText': 'Ein QR-Code, den der Kunde mit seiner Banking-App scannt — Empfänger, Betrag und Verwendungszweck sind dann ausgefüllt. Erscheint nur bei offenen Rechnungen auf Überweisung.',
    'firma.ibanFehlt': 'Für den Zahlcode wird eine IBAN gebraucht.',
    'firma.ibanUngueltig': 'Diese IBAN hat keine gültige Prüfsumme. Bitte nachsehen — bis dahin erscheint kein Zahlcode.',
    'firma.bank': 'Bankverbindung',
    'firma.fusstext': 'Fußtext',
    'firma.logo': 'Logo',
    'firma.logoWaehlen': 'Logo auswählen',
    'firma.logoEntfernen': 'Logo entfernen',
    'firma.speichern': 'Firmenangaben speichern',
    'firma.aufRechnung': 'Erscheint unter jeder Rechnung',
    'firma.pflichtangaben': 'Pflichtangaben auf Rechnungen',
    'firma.imFuss': 'Steht im Fuß der Rechnung',
    'passwort.willkommen': 'Willkommen',
    'passwort.warnung': 'Ihr aktuelles Passwort wurde Ihnen übermittelt und ist damit einer weiteren Person bekannt. Bitte legen Sie jetzt ein eigenes fest.',
    'passwort.regel8': 'Mindestens 8 Zeichen',
    'passwort.regelGross': 'Groß- und Kleinbuchstaben',
    'passwort.regelZiffer': 'Mindestens eine Ziffer',
    'passwort.regelGleich': 'Beide Eingaben stimmen überein',
    'passwort.regelAnders': 'Anders als das übermittelte Passwort',
    'passwort.fortfahren': 'Passwort festlegen und fortfahren',
    'lager.anzahlArtikel': 'Artikel',
    'lager.nurKnappe': 'Nur knappe',
    'lager.etiketten': 'Etiketten',

    'zfa.titel': 'Bestätigung',
    'zfa.gesendet': 'Wir haben Ihnen einen Code geschickt an',
    'zfa.code': 'Code aus der E-Mail',
    'zfa.bestaetigen': 'Bestätigen',
    'zfa.gueltig': 'Der Code gilt {min} Minuten.',
    'zfa.andererZugang': 'Andere Anmeldung',
    'zfa.einstellung': 'Zwei-Faktor-Anmeldung',
    'zfa.erklaerung': 'Zusätzlich zum Passwort wird bei jeder Anmeldung ein Code an Ihre E-Mail geschickt. Wer Ihr Passwort kennt, kommt damit trotzdem nicht in Ihren Betrieb.',
    'zfa.aktiv': 'Eingeschaltet',
    'zfa.aus': 'Ausgeschaltet',
    'zfa.einschalten': 'Einschalten',
    'zfa.ausschalten': 'Ausschalten',
    'zfa.mailFrage': 'An welche Adresse sollen die Codes gehen?',
    'zfa.codeSenden': 'Code senden',
    'zfa.passwortFrage': 'Zum Ausschalten bitte Ihr Passwort eingeben',
    'zfa.keinVersand': 'Für dieses System ist noch kein Mailversand eingerichtet. Ohne ihn kann kein Code zugestellt werden.',
    'zfa.eingeschaltet': 'Zwei-Faktor-Anmeldung ist eingeschaltet.',
    'zfa.ausgeschaltet': 'Zwei-Faktor-Anmeldung ist ausgeschaltet.',
    'zfa.methodeApp': "Authenticator-App",
    'zfa.methodeMail': "E-Mail-Code",
    'zfa.erklaerungBeide': "Zusätzlich zum Passwort fragt LagerPro bei jeder Anmeldung nach einem Code — aus einer Authenticator-App auf Ihrem Telefon oder per E-Mail. Wer nur Ihr Passwort kennt, kommt nicht hinein.",
    'zfa.ersatzUebrig': "{n} Ersatzcodes übrig",
    'zfa.mitApp': "Mit Authenticator-App einschalten",
    'zfa.mitMail': "Per E-Mail einschalten",
    'zfa.zurApp': "Zur Authenticator-App wechseln",
    'zfa.ersatzNeu': "Neue Ersatzcodes",
    'zfa.appEmpfohlen': "Empfohlen: die App. Sie funktioniert auch ohne Netz und ohne Mailversand — z. B. Google Authenticator oder Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "Neue Ersatzcodes erzeugen — die alten verfallen sofort.",
    'zfa.passwortWeiter': "Bitte Ihr Passwort eingeben",
    'zfa.weiter': "Weiter",
    'zfa.schritt1': "Authenticator-App auf dem Telefon öffnen (z. B. Google Authenticator) und „Code hinzufügen“ wählen.",
    'zfa.scannen': "Diesen QR-Code scannen.",
    'zfa.schritt3': "Den 6-stelligen Code aus der App unten eintragen.",
    'zfa.manuell': "Kein Scan möglich? Diesen Schlüssel von Hand eingeben:",
    'zfa.appCode': "Code aus der App",
    'zfa.ersatzTitel': "Ihre Ersatzcodes",
    'zfa.ersatzHinweis': "Falls Ihr Telefon verloren geht: Jeder dieser Codes ersetzt einmal den Code aus der App. Sicher aufbewahren (ausdrucken oder im Passwort-Manager) — sie werden nur jetzt angezeigt.",
    'zfa.kopieren': "Kopieren",
    'zfa.kopiert': "Kopiert",
    'zfa.herunterladen': "Als Datei speichern",
    'zfa.gesichert': "Ich habe die Codes sicher aufbewahrt",
    'zfa.fertig': "Fertig",
    'zfa.appText': "Öffnen Sie Ihre Authenticator-App und geben Sie den 6-stelligen Code für LagerPro ein.",
    'zfa.ersatzText': "Geben Sie einen Ihrer Ersatzcodes ein. Jeder gilt nur einmal.",
    'zfa.ersatzCode': "Ersatzcode",
    'zfa.ersatzNutzen': "Telefon nicht zur Hand? Ersatzcode verwenden",
    'zfa.appCodeNutzen': "Code aus der App verwenden",
    'nav.app': "App",
    'start.nav.app': "App",
    'desktop.titel': "LagerPro als App",
    'desktop.text': "Eigenes Fenster, eigenes Symbol im Dock oder in der Taskleiste, startet mit einem Klick. Einmal anmelden — danach bleiben Sie 90 Tage angemeldet.",
    'desktop.inApp': "Sie nutzen die Desktop-App",
    'desktop.inAppText': "Version {version}. Die Oberfläche aktualisiert sich von selbst.",
    'desktop.alsWebApp': "LagerPro ist auf diesem Rechner als App installiert.",
    'desktop.installiert': "Installiert — LagerPro finden Sie jetzt bei Ihren Programmen.",
    'desktop.direktTitel': "Direkt aus dem Browser installieren",
    'desktop.schnell': "Sekunden",
    'desktop.direktText': "Ohne Download, ohne Sicherheitswarnung, immer aktuell.",
    'desktop.safari': "In Safari: Menü „Ablage“ → „Zum Dock hinzufügen“ (ab macOS Sonoma).",
    'desktop.firefox': "Firefox kann keine Web-Apps installieren — bitte Chrome oder Edge verwenden oder unten herunterladen.",
    'desktop.chromeLeiste': "Im Adressfeld rechts auf das Symbol „Installieren“ klicken — oder Menü ⋮ → „LagerPro installieren“.",
    'desktop.andererBrowser': "Am einfachsten mit Chrome, Edge oder Safari — oder unten die App herunterladen.",
    'desktop.jetztInstallieren': "Jetzt installieren",
    'desktop.downloadTitel': "Oder herunterladen",
    'desktop.version': "Version {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 und 11, 64-Bit",
    'desktop.macApple': "Mac mit Apple-Chip",
    'desktop.macAppleUnter': "M1, M2, M3, M4 · ab 2020",
    'desktop.macIntel': "Mac mit Intel-Chip",
    'desktop.macIntelUnter': "ältere Macs bis 2020",
    'desktop.passtZuIhnen': "Passt zu diesem Rechner",
    'desktop.laden': "Herunterladen",
    'desktop.bald': "Folgt in Kürze",
    'desktop.warnungTitel': "Warnung beim ersten Öffnen?",
    'desktop.warnungText': "Die Downloads sind noch nicht bei Microsoft und Apple registriert. Deshalb fragt Ihr Rechner beim ersten Start einmal nach — danach nie wieder.",
    'desktop.warnungWindows': "Bei „Der Computer wurde durch Windows geschützt“ auf „Weitere Informationen“ und dann „Trotzdem ausführen“ klicken.",
    'desktop.warnungMac': "LagerPro in den Ordner „Programme“ ziehen. Beim ersten Öffnen erscheint ein Hinweis — dann unter „Systemeinstellungen → Datenschutz & Sicherheit“ auf „Dennoch öffnen“ klicken (ältere macOS: Rechtsklick auf LagerPro → „Öffnen“).",
    'desktop.anmeldung': "Bei beiden Wegen: einmal mit Passwort (und Zwei-Faktor-Code) anmelden, danach 90 Tage ohne Eingabe. Abmelden beendet die gemerkte Anmeldung sofort.",
    'desktop.mehrInfos': "Alle Infos zur App",

    'erinnerung.titel': 'Zahlungserinnerungen',
    'erinnerung.erklaerung': 'Ist eine Rechnung überfällig, schickt LagerPro Ihrem Kunden automatisch eine freundliche Erinnerung — in Ihrem Namen, mit Ihrer Adresse als Antwortweg.',
    'erinnerung.nachTagen': 'Erinnern nach … Tagen Überfälligkeit',
    'erinnerung.wiederholung': 'Erneut nach … Tagen',
    'erinnerung.maxAnzahl': 'Höchstens … Erinnerungen je Rechnung',
    'erinnerung.faellig': 'Beim nächsten Lauf betroffen',
    'erinnerung.jetzt': 'Jetzt senden',
    'erinnerung.keineFaellig': 'Zurzeit ist keine Rechnung überfällig.',
    'erinnerung.firmenmailFehlt': 'Hinterlegen Sie unter „Firma" Ihre E-Mail-Adresse — sonst können Ihre Kunden auf die Erinnerung nicht antworten.',
    'erinnerung.keineFuerKunden': 'Keine Zahlungserinnerungen an diesen Kunden',
  },

  en: {
    'start.nav.funktionen': 'Features',
    'start.nav.belegkette': 'Document chain',
    'start.nav.preise': 'Pricing',
    'start.kostenlosStarten': 'Start for free',
    'start.themaDunkel': 'Dark theme',
    'start.themaHell': 'Light theme',
    'start.sprache': 'Language',
    'start.hero.marke': 'GoBD-compliant · Servers in the EU',
    'start.hero.titel1': 'Inventory, point of sale and books.',
    'start.hero.titel2': 'One system instead of five.',
    'start.hero.text': 'LagerPro runs your business from the first quote to the tax return: stock, sales, documents and accounting all feed each other. No exports, no retyping, nothing kept in two places.',
    'start.hero.konto': 'I have an account',
    'start.hero.fussnote': 'No credit card · Set up in two minutes · Cancel monthly',
    'start.vorschau.umsatz': 'Revenue today',
    'start.vorschau.offen': 'Open invoices',
    'start.vorschau.lagerwert': 'Stock value',
    'start.vorschau.knapp': 'Below minimum',
    'start.vorschau.gruss': 'Good morning, Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'Recent documents',
    'start.vorschau.angebot': 'Quote',
    'start.vorschau.lieferschein': 'Delivery note',
    'start.vorschau.rechnung': 'Invoice',
    'start.vertrauen1t': 'GoBD-compliant',
    'start.vertrauen1x': 'Gapless numbering, immutable documents, hash-chained audit logs — the German standard for bookkeeping',
    'start.vertrauen2t': 'GDPdU export',
    'start.vertrauen2x': 'Z3 data handover with index.xml — ready whenever the tax auditor asks',
    'start.vertrauen3t': 'Servers in the EU',
    'start.vertrauen3x': 'Nothing passed on, no ad trackers, GDPR from day one',
    'start.vertrauen4t': '10-year retention',
    'start.vertrauen4x': 'As § 147 of the German Fiscal Code requires, automatically — nothing for you to remember',
    'start.f1t': 'Photograph the document, done',
    'start.f1x': 'Snap a delivery note or invoice with your phone — the assistant reads out the line items, quantities and supplier. You check it once, then the goods are in stock. It answers questions about stock and figures, typed or spoken.',
    'start.f2t': 'Stock under control',
    'start.f2x': 'Live stock levels, low-stock warnings, goods receipt and barcode labels in five sizes.',
    'start.f3t': 'A till for the counter',
    'start.f3x': 'Pick the customer, scan the items, take the money. Cash still works, and stock books itself.',
    'start.f4t': 'Every document',
    'start.f4x': 'Quote, order confirmation, delivery note, invoice, credit note and cancellation — as PDF with your logo.',
    'start.f5t': 'Books that add up',
    'start.f5x': 'Cash-basis accounts with the right form lines, VAT returns, mileage log and DATEV export for your accountant.',
    'start.f6t': 'Returns without the mess',
    'start.f6x': 'Always through the invoice: customer, document, line items. Goods go back to stock, credit goes to the customer account.',
    'start.f7t': 'The team on board',
    'start.f7x': 'Tasks with timers, chat with notifications, presence — without needing a second app.',
    'start.funktionen.titel': 'Everything the business needs daily',
    'start.funktionen.text': 'Each area knows what the others know. Sell something and stock drops, the document appears and accounting has the entry — all in the same moment.',
    'start.kette.titel': 'One job, four documents, no retyping',
    'start.kette.text': 'A click turns the quote into an order confirmation, that into a delivery note, that into an invoice. Line items and prices travel with it. The goods leave stock exactly once.',
    'start.kette.schritt': 'Step',
    'start.kette1t': 'Quote',
    'start.kette1x': 'What it will cost',
    'start.kette2t': 'Order',
    'start.kette2x': 'Customer says yes',
    'start.kette3t': 'Delivery note',
    'start.kette3x': 'Goods go out',
    'start.kette4t': 'Invoice',
    'start.kette4x': 'Money comes in',
    'start.kette.fuss': 'If something comes back, there is the credit note and the cancellation invoice — with the matching counter-entry the tax office expects.',
    'start.preise.titel': 'Pricing without the small print',
    'start.preise.text': "Cancel monthly, all prices plus VAT. Every plan starts with 30 days free — no bank details.",
    'start.preise.laedt': 'Loading plans …',
    'start.preise.fehler': 'Plans are not available right now. Write to us and we will quote you the terms.',
    'start.preise.top': 'Most chosen',
    'start.preise.proMonat': '/ month',
    'start.preise.sofort': "Try free for 30 days",
    'start.preise.waehlen': "Try {tarif} free for 30 days",
    'start.ende.titel': 'Start today, have the overview tomorrow',
    'start.ende.text': 'Create an account, enter your items, write the first invoice. You can bring your existing data across later.',
    'start.fuss.hinweis': 'Servers in the EU · GoBD-compliant',
    'nav.support': 'Support',
    'support.vonAnbieter': 'From LagerPro',
    'support.titel': 'Support',
    'support.untertitel': 'Questions, problems, wishes — write to us here. The whole conversation stays in one place.',
    'support.neu': 'New request',
    'support.betreff': 'Subject',
    'support.kategorie': 'What is it about?',
    'support.kat.frage': 'Question',
    'support.kat.fehler': 'Something is not working',
    'support.kat.wunsch': 'Wish or idea',
    'support.kat.abrechnung': 'Billing and plan',
    'support.kat.sonstiges': 'Other',
    'support.nachricht': 'Message',
    'support.nachrichtPlatzhalter': 'Describe as precisely as you can what happened and what you expected.',
    'support.anhang': 'Attach a screenshot or file',
    'support.anhangHinweis': 'Up to 5 files, 25 MB each',
    'support.senden': 'Send request',
    'support.gesendet': 'Your request {nummer} has arrived. You will get an email as soon as there is a reply.',
    'support.leer': 'No requests yet',
    'support.leerText': 'If something does not work or you have a question — write to us.',
    'support.status.offen': 'Received',
    'support.status.in_arbeit': 'In progress',
    'support.status.wartet': 'Reply waiting',
    'support.status.erledigt': 'Resolved',
    'support.zurueck': 'All requests',
    'support.antworten': 'Reply',
    'support.antwortPlatzhalter': 'Your message …',
    'support.erledigt': 'Mark as resolved',
    'support.erledigtHinweis': 'Resolved. Just keep writing if something is still open — the request will reopen.',
    'support.sie': 'You',
    'support.team': 'LagerPro Support',
    'support.ungelesen': 'new',
    'support.glocke': 'Reply from support',
    'support.anhaengen': 'Attach',
    'einheit.stueck': 'Piece',
    'einheit.paar': 'Pair',
    'einheit.satz': 'Set',
    'einheit.paket': 'Pack',
    'einheit.karton': 'Carton',
    'einheit.palette': 'Pallet',
    'einheit.rolle': 'Roll',
    'einheit.flasche': 'Bottle',
    'einheit.dose': 'Can',
    'einheit.beutel': 'Bag',
    'einheit.minute': 'min',
    'einheit.stunde': 'hr',
    'einheit.tag': 'Day',
    'einheit.woche': 'Week',
    'einheit.monat': 'Month',
    'einheit.jahr': 'Year',
    'einheit.pauschale': 'Flat rate',
    'einheit.gruppe.anzahl': 'Count & packaging',
    'einheit.gruppe.gewicht': 'Weight',
    'einheit.gruppe.volumen': 'Volume',
    'einheit.gruppe.laenge': 'Length & area',
    'einheit.gruppe.zeit': 'Time',
    'einheit.gruppe.sonstiges': 'Other',
    'einheit.andere': 'Other unit …',
    'einheit.eigene': 'Custom unit',
    'einheit.eigenePlatzhalter': 'e.g. bunch, crate',
    'positionen.umgerechnet': '= {menge} from stock',
    'positionen.passtNicht': 'This item is kept in {einheit} — this unit cannot be converted.',
    'positionen.jeEinheit': 'per {einheit}',
    'nav.assistent': 'AI assistant',
    'assistent.datenschutz': 'For processing, the document and your question are sent to our AI provider and used only to answer them — never to train models. Your inventory data stays on our server.',
    'pruef.titel': 'Check {art}',
    'pruef.beleg': 'document',
    'pruef.lieferschein': 'delivery note',
    'pruef.eingangsrechnung': 'purchase invoice',
    'pruef.gutschrift': 'credit note',
    'pruef.bestellung': 'purchase order',
    'pruef.kassenbon': 'receipt',
    'pruef.erkannt': '% recognised',
    'pruef.ohneNummer': 'No number or date',
    'pruef.schwerLesbar': 'This document was hard to read',
    'pruef.schwerLesbarText': 'Please go through every line. On a blurred or skewed photo the recognition confuses digits. A fresh photo in good light usually beats correcting.',
    'pruef.nichtAusgewertet': 'Not processed',
    'pruef.nurBilder': 'only photos and PDF can be read',
    'pruef.vorhandener': 'Existing',
    'pruef.neuAnlegen': 'Create new',
    'pruef.ohne': 'None',
    'pruef.bitteWaehlen': '— please choose —',
    'pruef.gewaehlt': '{a} of {b} selected',
    'pruef.alleUebernehmen': 'Select all',
    'pruef.keine': 'None',
    'pruef.keinePositionen': 'No line items were recognised on this document.',
    'pruef.warenwert': 'Value of the selection',
    'pruef.rechnungsbetrag': 'Invoice total',
    'pruef.brauchtBuchhaltung': 'Recording the expense requires accounting — included from the Professional plan.',
    'pruef.alsAusgabe': 'Record amount as a business expense',
    'pruef.alsAusgabeText': 'The document is attached as proof, so the expense is complete for the books.',
    'pruef.brutto': 'Gross amount',
    'pruef.steuersatz': 'Tax rate',
    'pruef.belegnummer': 'Document number',
    'pruef.belegdatum': 'Document date',
    'pruef.lastschrift': 'Direct debit',
    'pruef.keineWare': 'Not stock',
    'pruef.keineWareText': 'Counts towards the invoice total but does not enter inventory.',
    'pruef.grund.keineWare': 'Shipping, fee or discount — not a stockable item',
    'pruef.privat': 'Paid privately',
    'pruef.erstMitKnopf': 'Nothing is booked until you press this button.',
    'pruef.buchen': 'Accept and book',
    'pruef.wirdGebucht': 'Booking',
    'pruef.zugeordnet': 'Matched',
    'pruef.vermutet': 'Likely',
    'pruef.neu': 'New',
    'pruef.schlechtLesbar': 'Hard to read',
    'pruef.aendern': 'Change',
    'pruef.zuklappen': 'Collapse',
    'pruef.wirdAngelegt': 'will be created as a new item',
    'pruef.artikel': 'Item',
    'pruef.nichtZugeordnet': '— not matched —',
    'pruef.alsNeuenArtikel': 'Create as a new item',
    'pruef.einkaufspreis': 'Purchase price',
    'pruef.verkaufspreis': 'Sales price',
    'pruef.spaeterErgaenzen': 'Can be added later',
    'pruef.ean': 'EAN',
    'pruef.je': 'per',
    'pruef.grund.ean': 'EAN matches',
    'pruef.grund.nummer': 'Item number matches',
    'pruef.grund.name': 'Name matches exactly',
    'pruef.grund.ustId': 'VAT ID matches',
    'pruef.grund.aehnlich': 'Similar name — please check',
    'pruef.grund.keiner': 'No matching item in stock',
    'pruef.kat.wareneinkauf': 'Goods purchased',
    'pruef.kat.buero': 'Office supplies',
    'pruef.kat.fahrzeug': 'Vehicle costs',
    'pruef.kat.werbung': 'Advertising',
    'pruef.kat.kommunikation': 'Phone and internet',
    'pruef.kat.raumkosten': 'Rent and premises',
    'pruef.kat.sonstiges': 'Other business expenses',
    'assistent.titel': 'AI assistant',
    'assistent.rest': 'Left this month',
    'assistent.scans': 'document scans',
    'assistent.fragen': 'questions',
    'assistent.testmodus': 'Test mode',
    'assistent.testmodusText': 'No AI access is configured. Documents return sample data and answers are placeholders. Once access is set up, the assistant works with your real data.',
    'assistent.verlaufLoeschen': 'Clear history',
    'assistent.denkt': 'One moment',
    'assistent.platzhalter': 'Ask a question or photograph a document',
    'assistent.hoertZu': 'Listening',
    'assistent.sprechen': 'Speak',
    'assistent.vorlesen': 'Read aloud',
    'assistent.stopp': 'Stop',
    'assistent.senden': 'Send',
    'assistent.fotografieren': 'Photograph document',
    'assistent.datei': 'Upload file',
    'assistent.belegAuswerten': 'Read document',
    'assistent.wertetAus': 'Reading document',
    'assistent.bestaetigen': 'Confirm',
    'assistent.gebucht': 'Booked',
    'assistent.verworfen': 'Discarded',
    'assistent.willkommen': 'How can I help?',
    'assistent.willkommenText': 'Ask about stock and figures, or photograph a delivery note — the lines go into inventory once you have checked them.',
    'assistent.beispiel1': 'Which items are running low?',
    'assistent.beispiel2': 'What is my revenue this month?',
    'assistent.beispiel3': 'Which invoices are still open?',
    'assistent.beispiel4': 'What is my inventory worth?',
    'allg.entfernen': 'Remove',
    'bestaetigung.titel': 'Confirm your email address',
    'bestaetigung.text': 'We have sent you a message. Open the link inside and you are through.',
    'bestaetigung.warum': 'Without a confirmed address you receive no sign-in codes, no invoices and no notice before a direct debit.',
    'bestaetigung.schonBestaetigt': 'I have confirmed — continue',
    'bestaetigung.erneut': 'Send the message again',
    'bestaetigung.erneutGesendet': 'The message is on its way.',
    'bestaetigung.versandFehlt': 'Sending is not possible right now. Please contact support.',
    'bestaetigung.fehlgeschlagen': 'That did not work.',
    'bestaetigung.spam': 'Nothing arrived? Please check your spam folder.',
    'bestaetigung.pruefe': 'One moment …',
    'bestaetigung.fertigTitel': 'Address confirmed',
    'bestaetigung.fertigText': 'Your email address is confirmed. You can carry on.',
    'bestaetigung.zurApp': 'Go to the app',
    'bestaetigung.abgelaufenTitel': 'Link expired',
    'bestaetigung.abgelaufenText': 'This link is no longer valid. Sign in and request a new message.',
    'bestaetigung.ungueltigTitel': 'Link not valid',
    'bestaetigung.ungueltigText': 'This link cannot be used. The address may already be confirmed.',
    'mandat.pflichtTitel': 'SEPA direct debit mandate',
    'mandat.pflichtText': 'A SEPA direct debit mandate is required to continue. It takes two minutes. Your data remains untouched.',
    'mandat.einleitung': 'You grant the mandate with the account holder and IBAN. No paper signature is needed.',
    'mandat.kontoinhaber': 'Account holder',
    'mandat.kontoinhaberHinweis': 'Exactly as the name is held at your bank.',
    'mandat.bicHinweis': 'Not required within SEPA.',
    'mandat.ibanFalsch': 'This IBAN fails its check digits.',
    'mandat.wortlautTitel': 'Wording of the mandate',
    'mandat.glaeubiger': 'Creditor',
    'mandat.glaeubigerId': 'Creditor ID',
    'mandat.referenz': 'Mandate reference',
    'mandat.referenzSpaeter': 'assigned once granted and sent to you before the first collection',
    'mandat.zustimmung': 'I agree to the direct debit mandate stated above.',
    'mandat.erteilen': 'Grant mandate',
    'mandat.erteilt': 'The mandate is granted. Thank you.',
    'mandat.liegtVor': 'A valid mandate is on file.',
    'mandat.nochNichtBereit': 'Direct debit is still being set up. Please try again later or contact support.',
    'mandat.ladefehler': 'The details could not be loaded.',
    'mandat.fehlgeschlagen': 'The mandate could not be granted.',
    'allg.optional': 'optional',
    'sperre.titel': 'LagerPro is locked',
    'sperre.entsperren': 'Unlock',
    'sperre.abgebrochen': 'The check was cancelled.',
    'geraete.titel': 'Signed-in devices',
    'geraete.hinweis': 'Devices where you stay signed in.',
    'geraete.hinweisAdmin': 'All devices in your company. Sign one out here if it is lost or someone leaves.',
    'geraete.keine': 'No device stays signed in.',
    'geraete.dieses': 'this device',
    'geraete.zuletzt': 'last seen',
    'geraete.abmelden': 'Sign out',
    'geraete.abmeldenFehler': 'The device could not be signed out.',
    'geraete.ladefehler': 'The devices could not be loaded.',
    'geraete.nichtGemerkt': 'This device is not remembered. Sign in once with your password and your fingerprint will be enough from then on.',
    'geraete.sperreTitel': 'Lock on opening',
    'geraete.sperreAn': 'Lock the app with {art}',
    'geraete.sperreText': 'Checked on opening and after five minutes in the background.',
    'geraete.sperreAusWarnung': 'Without the lock, anyone holding your unlocked phone reaches your company data.',
    'geraete.alleAnderen': "Sign out all others",
    'geraete.gueltigBis': "signed in until {datum}",
    'geraete.browserTitel': "Signed in in a browser",
    'geraete.diese': "this session",
    'geraete.browserHinweis': "Browser sign-ins end by themselves after 24 hours — or right away here.",
    'app.name': 'LagerPro',
    'app.untertitel': 'Professional warehouse management',
    'benachrichtigung.titel': 'Notifications',
    'benachrichtigung.leer': 'Nothing new',
    'benachrichtigung.neu': 'New notification',
    'benachrichtigung.neueVorgaenge': 'new items',
    'benachrichtigung.anAlle': 'to everyone',
    'nav.uebersicht': 'Dashboard',
    'nav.mehr': 'More',
    'allg.rechnung': 'invoice',
    'app.gruss': 'Hello, {name}',
    'app.gruppe.tagesgeschaeft': 'Daily work',
    'app.gruppe.kontakte': 'Contacts',
    'app.gruppe.zusammenarbeit': 'Collaboration',
    'app.gruppe.auswertung': 'Reports',
    'app.gruppe.betrieb': 'Business',
    'app.nochmalBeenden': 'Press again to exit',
    'app.keineVerbindung': 'No connection — figures unavailable',
    'app.konto': 'Account',
    'app.darstellung': 'Appearance',
    'nav.kasse': 'Point of sale',
    'nav.lager': 'Inventory',
    'nav.belege': 'Documents',
    'nav.rechnungen': 'Invoices',
    'nav.kunden': 'Customers',
    'nav.lieferanten': 'Suppliers',
    'nav.retouren': 'Returns',
    'nav.buchhaltung': 'Accounting',
    'nav.ausgaben': 'Expenses',
    'nav.bank': 'Bank',
    'nav.fahrtenbuch': 'Mileage log',
    'nav.aufgaben': "Board",
    'nav.chat': 'Chat',
    'nav.team': 'Team',
    'nav.firma': 'Company',
    'nav.gobd': 'GoBD & audit',
    'nav.tarif': 'Plan',
    'nav.uebernahme': 'Import data',
    'nav.einstellungen': 'Settings',
    'nav.owner': 'Customer control',
    'nav.mandanten': 'Tenants',
    'nav.gruppe.verkauf': 'Sales',
    'nav.gruppe.waren': 'Goods',
    'nav.gruppe.kontakte': 'Contacts',
    'nav.gruppe.auswertung': 'Reports',
    'nav.gruppe.zusammenarbeit': 'Collaboration',
    'nav.gruppe.verwaltung': 'Administration',
    'aktion.anlegen': 'Create',
    'aktion.speichern': 'Save',
    'aktion.abbrechen': 'Cancel',
    'aktion.bearbeiten': 'Edit',
    'aktion.loeschen': 'Delete',
    'aktion.schliessen': 'Close',
    'aktion.suchen': 'Search',
    'aktion.anmelden': 'Sign in',
    'aktion.abmelden': 'Sign out',
    'aktion.drucken': 'Print',
    'aktion.herunterladen': 'Download',
    'allg.suche': 'Search',
    'allg.laedt': 'Loading',
    'allg.keineTreffer': 'Nothing found',
    'allg.darstellung': 'Appearance',
    'allg.sprache': 'Language',
    'allg.thema.system': 'System',
    'allg.thema.hell': 'Light',
    'allg.thema.dunkel': 'Dark',
    'allg.rolle.inhaber': 'Owner',
    'allg.rolle.admin': 'Administrator',
    'allg.rolle.mitarbeiter': 'Employee',
    'allg.nichtImTarif': 'Not included in your plan',
    'allg.tarif': 'Plan',
    'allg.menue': 'Menu',
    'allg.schnellzugriff': 'Quick access',
    'allg.schnellzugriffHinweis': "Page, customer or document number …",
    'anmelden.willkommen': 'Welcome back',
    'anmelden.hinweis': 'Sign in to continue',
    'anmelden.kennung': 'Username or email',
    'anmelden.passwort': 'Password',
    'anmelden.fehler': 'Sign-in failed.',
    'anmelden.keinKonto': 'No account yet?',
    'anmelden.jetztRegistrieren': 'Register for free',
    'passwort.vergessenLink': "Forgot password?",
    'passwort.vergessenTitel': "Forgot password",
    'passwort.vergessenText': "Enter your username or email address. We will send you a link to set a new password.",
    'passwort.linkSenden': "Send link",
    'passwort.gesendetTitel': "Check your inbox",
    'passwort.gesendetText': "If an account with an email address matches, the link is on its way. It is valid for {min} minutes. Without an email address on file, your administrator can help.",
    'passwort.zurAnmeldung': "Back to sign-in",
    'passwort.neuTitel': "Set a new password",
    'passwort.fuer': "For the account {name}",
    'passwort.regeln': "At least 8 characters, with upper- and lowercase letters and a number.",
    'passwort.ungleich': "The two passwords do not match.",
    'passwort.speichern': "Save password",
    'passwort.fertigTitel': "Password changed",
    'passwort.fertigText': "Sign in now with your new password. All devices have been signed out.",
    'passwort.abgelaufenTitel': "Link expired",
    'passwort.abgelaufenText': "The link is only valid for 30 minutes. Simply request a new one.",
    'passwort.ungueltigTitel': "Invalid link",
    'passwort.ungueltigText': "This link is invalid or has already been used.",
    'passwort.neuAnfordern': "Request a new link",
    'passwort.pruefe': "Checking link …",
    'uebersicht.begruessung': 'Hello',
    'uebersicht.offeneAufgaben': 'Open tasks',
    'uebersicht.laufendeTimer': 'Running timers',
    'uebersicht.teamOnline': 'Team online',
    'uebersicht.benutzerImTarif': 'Users in plan',
    'uebersicht.naechsteAufgaben': 'Next tasks',
    'uebersicht.alleAnsehen': 'View all',
    'uebersicht.unbegrenzt': 'unlimited',
    'uebersicht.von': 'of',
    'uebersicht.insgesamt': 'total',
    'uebersicht.keineOffenen': 'No open tasks.',

    'feld.name': 'Name',
    'feld.nummer': 'Number',
    'feld.telefon': 'Phone',
    'feld.email': 'Email',
    'feld.strasse': 'Street',
    'feld.plz': 'Postcode',
    'feld.ort': 'City',
    'feld.land': 'Country',
    'feld.notiz': 'Note',
    'feld.ansprechpartner': 'Contact',
    'feld.ustId': 'VAT ID',
    'feld.menge': 'Quantity',
    'feld.einzelpreis': 'Unit price',
    'feld.bezeichnung': 'Description',
    'feld.artikel': 'Item',
    'feld.kunde': 'Customer',
    'feld.lieferant': 'Supplier',
    'feld.datum': 'Date',
    'feld.betrag': 'Amount',
    'feld.grund': 'Reason',
    'feld.status': 'Status',
    'feld.einheit': 'Unit',
    'feld.bestand': 'Stock',
    'feld.gesamt': 'Total',
    'feld.netto': 'Net',
    'feld.rabatt': 'Discount',
    'feld.position': 'Line',
    'feld.positionen': 'Lines',
    'aktion.hinzufuegen': 'Add',
    'aktion.entfernen': 'Remove',
    'aktion.zurueck': 'Back',
    'aktion.weiter': 'Continue',
    'kunden.titel': 'Customers',
    'kunden.neu': 'New customer',
    'kunden.nummer': 'Customer number',
    'kunden.suche': 'Name, number, email or city …',
    'kunden.leer': 'No customers yet',
    'kunden.leerText': 'Add your first customer.',
    'kunden.akte': "Customer file",
    'kunden.alle': "All customers",
    'kunden.nichtGefunden': "Customer not found.",
    'kunden.gespeichert': "Customer saved.",
    'kunden.geloescht': "{name} was deleted.",
    'kunden.loeschenFrage': "Really delete “{name}”?",
    'kunden.einKunde': "1 customer",
    'kunden.anzahl': "{n} customers",
    'kunden.sortieren': "Sort by",
    'kunden.letzterKaufSort': "Last purchase",
    'kunden.keineKontakt': "No contact details",
    'kunden.nochNichts': "no purchase yet",
    'kunden.zuletzt': "last {datum}",
    'kunden.rechnungSchreiben': "Write invoice",
    'kunden.umsatzJahr': "Revenue {jahr}",
    'kunden.netto': "net",
    'kunden.vorjahr': "previous year {betrag}",
    'kunden.offen': "Outstanding",
    'kunden.ueberfaellig': "{betrag} overdue ({n})",
    'kunden.ueberfaelligKurz': "{betrag} overdue",
    'kunden.offenAnzahl': "{n} open invoices",
    'kunden.offenBetrag': "{betrag} open",
    'kunden.meistgekauft': "Most bought",
    'kunden.malGekauft': "bought {n}×",
    'kunden.keineProdukte': "Nothing bought yet",
    'kunden.zahlung': "Payment behaviour",
    'kunden.zahltNach': "avg. {n} days",
    'kunden.puenktlich': "{n} % paid on time",
    'kunden.nochKeineZahlung': "no payment yet",
    'kunden.seit': "Customer since {datum}",
    'kunden.letzterKauf': "Last purchase {datum}",
    'kunden.gesamt': "Total revenue {betrag}",
    'kunden.durchschnitt': "Avg. invoice {betrag}",
    'kunden.guthaben': "Credit {betrag}",
    'kunden.verlauf': "Revenue, last 12 months",
    'kunden.diagrammLabel': "Revenue per month",
    'kunden.keinUmsatz': "No revenue in the last 12 months",
    'kunden.rechnungenAnzahl': "{n} invoices",
    'kunden.produkte': "Products bought",
    'kunden.kaeufe': "Purchases",
    'kunden.menge': "Quantity",
    'kunden.umsatz': "Revenue",
    'kunden.mehr': "Show all {n}",
    'kunden.weniger': "Show less",
    'kunden.dokumente': "Documents",
    'kunden.dokumenteHinweis': "{n} in total, newest first",
    'kunden.alleDokumente': "All",
    'kunden.nurOffene': "Open",
    'kunden.keineDokumente': "No documents",
    'kunden.zu': "for {nummer}",
    'kunden.entwurf': "Draft",
    'kunden.faellig': "due {datum}",
    'kunden.gueltigBis': "valid until {datum}",
    'kunden.ansehen': "View",
    'kunden.oeffnen': "Open",
    'kunden.art.rechnung': "Invoice",
    'kunden.art.lieferschein': "Delivery note",
    'kunden.art.angebot': "Quote",
    'kunden.art.auftrag': "Order",
    'kunden.art.gutschrift': "Credit note",
    'kunden.art.retoure': "Return",
    'kunden.arten.rechnung': "Invoices",
    'kunden.arten.lieferschein': "Delivery notes",
    'kunden.arten.angebot': "Quotes",
    'kunden.arten.auftrag': "Orders",
    'kunden.arten.gutschrift': "Credit notes",
    'kunden.arten.retoure': "Returns",
    'kunden.status.entwurf': "Draft",
    'kunden.status.gestellt': "Open",
    'kunden.status.bezahlt': "Paid",
    'kunden.status.storniert': "Cancelled",
    'kunden.status.gesendet': "Sent",
    'kunden.status.angenommen': "Accepted",
    'kunden.status.abgelehnt': "Declined",
    'kunden.status.abgelaufen': "Expired",
    'kunden.status.offen': "Open",
    'kunden.status.inArbeit': "In progress",
    'kunden.status.erledigt': "Done",
    'kunden.status.geliefert': "Delivered",
    'kunden.status.eingelagert': "Restocked",
    'kunden.status.erfasst': "Recorded",
    'kunden.status.nichtGeliefert': "Not delivered",
    'kunden.status.ausgestellt': "Issued",
    'kunden.status.teilbezahlt': "Partly paid",
    'lieferanten.titel': 'Suppliers',
    'lieferanten.neu': 'New supplier',
    'lieferanten.nummer': 'Supplier number',
    'lieferanten.suche': 'Name, contact or city …',
    'lieferanten.akte': 'File',
    'lieferanten.alle': 'All suppliers',
    'lieferanten.artikel': 'Products',
    'lieferanten.lieferungen': 'Deliveries',
    'lieferanten.rechnungen': 'Invoices',
    'lieferanten.offen': 'Open',
    'lieferanten.ueberfaellig': 'overdue',
    'lieferanten.einkaufJahr': 'Purchases {jahr}',
    'lieferanten.letzteLieferung': 'Last delivery',
    'lieferanten.keineArtikel': 'No products from this supplier yet.',
    'lieferanten.keineLieferungen': 'No goods receipts yet.',
    'lieferanten.keineRechnungen': 'No invoices yet.',
    'lieferanten.rechnungErfassen': 'Record invoice',
    'lieferanten.alsBezahlt': 'Mark as paid',
    'lieferanten.bezahltAm': 'paid {datum}',
    'lieferanten.faellig': 'due {datum}',
    'lieferanten.betrag': 'Amount (gross)',
    'lieferanten.rechnungsnummer': 'Invoice number',
    'lieferanten.rechnungsdatum': 'Invoice date',
    'lieferanten.faelligAm': 'Due on',
    'lieferanten.schonBezahlt': 'Already paid',
    'lieferanten.wert': 'Value',
    'lieferanten.wiederOffen': 'Reopen',
    'lieferanten.gespeichert': 'Saved.',
    'lieferanten.bearbeiten': 'Edit',
    'lieferanten.loeschen': 'Delete',
    'lager.zugang': 'Goods receipt',
    'lager.abgang': 'Removal',
    'lager.buchen': 'Book',
    'lager.rechnungLiegtBei': 'Supplier invoice is included',
    'lager.rechnungBezahlt': 'Invoice is already paid',
    'lager.gebucht': 'Stock of “{name}” booked.',
    'lager.ohneLieferant': 'no supplier',
    'lieferanten.leer': 'No suppliers yet',
    'lieferanten.leerText': 'Add your first supplier.',
    'allg.andereSuche': 'Try different search terms.',

    'lager.titel': 'Warehouse',
    'lager.neu': 'New item',
    'lager.artikelnummer': 'Item number',
    'lager.ean': 'EAN / barcode',
    'lager.kategorie': 'Category',
    'lager.lagerort': 'Location',
    'lager.einkaufspreis': 'Purchase price (net)',
    'lager.verkaufspreis': 'Sales price (net)',
    'lager.mindestbestand': 'Minimum stock',
    'lager.anfangsbestand': 'Opening stock',
    'lager.wareneingang': 'Booked as goods receipt.',
    'lager.suche': 'Description, number or EAN …',
    'lager.leer': 'No items',
    'lager.leerText': 'Add your first item or import data from the old app.',
    'lager.verlauf': 'History',
    'lager.keineBewegungen': 'No movements',
    'lager.etikett': 'Label',
    'lager.etikettenDrucken': 'Print labels',
    'lager.format': 'Format',
    'lager.stueckJeArtikel': 'Copies per item',
    'lager.mehrfachHinweis': 'You often need the same label several times when receiving goods.',
    'lager.knapp': 'low',
    'lager.auswahl': 'Selection',
    'lager.auswahlAufheben': 'Clear selection',
    'lager.alle': 'All',
    'lager.keinLieferant': '— no supplier —',
    'kasse.titel': 'Point of sale',
    'kasse.schritt1': 'Step 1 of 2 — who is this sale for?',
    'kasse.kundeWaehlen': 'Choose customer',
    'kasse.kundeSuchen': 'Search customer',
    'kasse.kundeSuche': 'Name, customer number or city …',
    'kasse.kundeWechseln': 'Change customer',
    'kasse.ohneKonto': 'Without customer account',
    'kasse.historieHinweis': 'The sale then appears in their invoice history',
    'kasse.artikelSuchen': 'Search items',
    'kasse.artikelSuche': 'Description, number or scan barcode …',
    'kasse.keinArtikel': 'No item found',
    'kasse.warenkorb': 'Cart',
    'kasse.warenkorbLeeren': 'Empty cart',
    'kasse.leer': 'Nothing added yet',
    'kasse.leerText': 'Click an item on the left.',
    'kasse.zahlart': 'Payment method',
    'kasse.bezahlt': 'Paid?',
    'kasse.vollstaendig': 'In full',
    'kasse.teilweise': 'Partly',
    'kasse.nochNicht': 'Not yet',
    'kasse.gegeben': 'Tendered (for change)',
    'kasse.rueckgeld': 'Change: {betrag}',
    'kasse.erhalten': 'Received',
    'kasse.kundePflicht': 'Please choose a customer for an open amount — otherwise nobody knows who owes it.',
    'kasse.buchen': 'Book · {erhalten} received, {offen} open',
    'kasse.kassieren': 'Check out · {betrag}',
    'kasse.offenBis': 'Open: {betrag} · due {datum}',
    'kasse.bezahltMit': 'Paid · {zahlart}',
    'kasse.naechster': 'Next sale',
    'kasse.bar': 'Cash',
    'kasse.karte': 'Card',
    'kasse.ueberweisung': 'Bank transfer',
    'kasse.abgeschlossen': 'Sale completed',
    'kasse.alle': "All",
    'kasse.anzahlung': "Deposit",
    'kasse.aufRechnung': "On account",
    'kasse.barAnteil': "Cash part",
    'kasse.barKassieren': "Take cash · {betrag}",
    'kasse.beleg': "Receipt",
    'kasse.betragBrutto': "Amount (gross)",
    'kasse.bezahlen': "Pay",
    'kasse.bezahlenBetrag': "Pay · {betrag}",
    'kasse.bonNr': "Ticket {n}",
    'kasse.bonRabatt': "Discount on ticket",
    'kasse.bonVerwerfen': "Discard ticket",
    'kasse.codeUnbekannt': "No item with code {code}",
    'kasse.enthaltenSteuer': "incl. {satz} % {name}",
    'kasse.fehlgeschlagen': "The sale could not be booked.",
    'kasse.freiHinweis': "For anything without an item: service, deposit, other.",
    'kasse.freiStandard': "Other",
    'kasse.freierBetrag': "Custom amount",
    'kasse.gegebenKurz': "Given",
    'kasse.gespart': "saved",
    'kasse.geteilt': "Split",
    'kasse.geteiltKassieren': "Charge · {bar} cash + {karte} card",
    'kasse.haeufig': "Frequent",
    'kasse.kameraFehler': "Camera unavailable — allow access or use a hand scanner.",
    'kasse.karteBezahlt': "Card paid · {betrag}",
    'kasse.karteHinweis': "Charge the amount on the card terminal, then confirm here.",
    'kasse.karteRest': "Rest by card",
    'kasse.keinRabatt': "none",
    'kasse.keineArtikelAngelegt': "No items created yet.",
    'kasse.kundeEntfernen': "Remove customer",
    'kasse.kundeZuordnen': "Assign customer",
    'kasse.laufkunde': "Walk-in customer",
    'kasse.leerNeu': "Tap an item or scan a barcode.",
    'kasse.neuerBon': "New ticket",
    'kasse.nichtAufLager': "\"{name}\" is out of stock.",
    'kasse.nichtVorraetig': "out of stock",
    'kasse.nurNoch': "Only {menge} of \"{name}\" available.",
    'kasse.parkenHinweis': "Park this ticket and start a new one",
    'kasse.passend': "exact",
    'kasse.positionen': "Items: {n}",
    'kasse.preis': "Price",
    'kasse.preisAendern': "Change price",
    'kasse.rabatt': "Discount",
    'kasse.rabattText': "{p} % discount",
    'kasse.rechnungBuchen': "Book on account · {offen} open",
    'kasse.rueckgeldGross': "Change",
    'kasse.scannen': "Scan barcode",
    'kasse.scannenHinweis': "Hold the code in front of the camera.",
    'kasse.wofuer': "What for?",
    'kasse.zuWenig': "{betrag} still missing.",

    'belege.titel': 'Documents',
    'belege.angebote': 'Quotes',
    'belege.auftraege': 'Orders',
    'belege.lieferscheine': 'Delivery notes',
    'belege.gutschriften': 'Credit notes',
    'belege.offeneAngebote': 'Open quotes',
    'belege.laufendeAuftraege': 'Active orders',
    'belege.lieferscheineOffen': 'Pending deliveries',
    'belege.interessent': 'Prospect name',
    'belege.notiz': 'Note (appears on the document)',
    'belege.rabatt': 'Discount %',
    'belege.druckenPdf': 'Print / PDF',
    'belege.entwurfLoeschen': 'Delete draft',
    'belege.gesamtBrutto': 'Total gross',
    'belege.einzel': 'Unit',
    'rechnungen.titel': 'Invoices',
    'rechnungen.neu': 'New invoice',
    'rechnungen.schreiben': 'Create invoice',
    'rechnungen.stellen': 'Issue invoice',
    'rechnungen.entwurfSpeichern': 'Save as draft',
    'rechnungen.entwurfHinweis': 'A draft does not reduce stock yet.',
    'rechnungen.bezahltMarkieren': 'Mark as paid',
    'rechnungen.teilbezahlt': 'partly paid · {betrag} open',
    'rechnungen.perBank': 'matched via bank',
    'rechnungen.zahlungen': 'Payments',
    'rechnungen.nochOffen': 'Still open',
    'rechnungen.bankeingang': 'Bank receipt',
    'rechnungen.zahlung': 'Payment',
    'rechnungen.zahlart': 'Payment method',
    'rechnungen.zahlweg.ueberweisung': 'Bank transfer',
    'rechnungen.zahlweg.bar': 'Cash',
    'rechnungen.zahlweg.karte': 'Card',
    'rechnungen.zahlungsziel': 'Payment terms',
    'rechnungen.zielKunde': 'As for customer ({tage} days)',
    'rechnungen.zielSofort': 'Due immediately',
    'rechnungen.zielTage': '{tage} days',
    'rechnungen.zielDatum': 'Custom date',
    'rechnungen.faelligAm': 'Due on',
    'rechnungen.bezahlung': 'Paid?',
    'rechnungen.offenLassen': 'Not paid yet',
    'rechnungen.schonBezahlt': 'Already paid',
    'rechnungen.lieferscheinMit': 'Also create delivery note',
    'rechnungen.lieferschein': 'Delivery note',
    'rechnungen.lieferscheinDrucken': 'Print delivery note',
    'rechnungen.lieferscheinErstellen': 'Create delivery note',
    'rechnungen.zahlungErfassen': 'Record payment',
    'rechnungen.betrag': 'Amount',
    'rechnungen.zahltag': 'Payment date',
    'rechnungen.alsBezahltSpeichern': 'Save as paid',
    'rechnungen.teilzahlungSpeichern': 'Save partial payment',
    'rechnungen.ratenHinweis': 'Less than the open amount? The rest stays open — this is how instalments are recorded.',
    'rechnungen.zahlungEntfernen': 'Remove payment',
    'rechnungen.sofort': 'due immediately',
    'rechnungen.gestelltMitLs': 'Invoice issued. Delivery note {nummer} is ready to print.',
    'rechnungen.zahlungGespeichert': 'Payment recorded.',
    'rechnungen.vollBezahlt': 'The invoice is fully paid.',
    'rechnungen.guthaben': 'Customer credit',
    'rechnungen.gutschriftTitel': 'Credit note for this invoice',
    'rechnungen.gutschriftMenge': 'Quantity to credit',
    'rechnungen.gutschriftGrund': 'Reason (shown on the credit note)',
    'rechnungen.gutschriftStellen': 'Issue credit note',
    'rechnungen.gutschriftHinweis': 'If the invoice is still open, the credit note is offset against it. Otherwise it becomes customer credit. One credit note per invoice.',
    'rechnungen.gutschriftErstellt': 'Credit note {nummer} issued.',
    'rechnungen.gutschriftVorhanden': 'Credit note {nummer}',
    'rechnungen.perGutschrift': 'settled by credit note',
    'rechnungen.gutschriftDrucken': 'Print credit note',
    'rechnungen.gutschriftSumme': 'Credit note for {betrag}',
    'rechnungen.stornieren': 'Cancel invoice',
    'rechnungen.gutschriftErstellen': 'Create credit note',
    'rechnungen.drucken': 'Print document / PDF',
    'rechnungen.stempelBezahlt': 'Paid',
    'rechnungen.stempelAm': 'on {datum}',
    'rechnungen.keinGirocode': 'No GiroCode',
    'rechnungen.girocodeText': 'Scan with a banking app — amount and reference are filled in.',
    'neuigkeiten.titel': "What's new",
    'neuigkeiten.auchNeu': 'Also new',
    'neuigkeiten.fortschritt': 'Your progress with LagerPro',
    'neuigkeiten.updates': 'updates',
    'neuigkeiten.verbesserungen': 'improvements',
    'neuigkeiten.seit': 'since {datum}',
    'neuigkeiten.mailHinweis': 'You can turn off news emails in Settings.',
    'neuigkeiten.verstanden': 'Got it',
    'rechnungen.barverkauf': 'Cash sale / no customer account',
    'rechnungen.leer': 'No invoices',
    'rechnungen.leerText': 'Create your first invoice.',
    'status.alle': 'All',
    'status.entwurf': 'Draft',
    'status.entwuerfe': 'Drafts',
    'status.offen': 'Open',
    'status.bezahlt': 'Paid',
    'status.storniert': 'Cancelled',
    'retouren.titel': 'Returns',
    'retouren.erfassen': 'Record return',
    'retouren.alle': 'All returns',
    'retouren.leer': 'No returns yet',
    'retouren.leerText': 'A return always belongs to an invoice: choose customer, choose invoice, take back items.',
    'retouren.schritt1': '1. Customer',
    'retouren.schritt2': '2. Invoice',
    'retouren.schritt3': '3. What is coming back?',
    'retouren.grund': 'Reason (optional)',
    'retouren.grundBeispiel': 'e.g. transport damage, wrong delivery',
    'retouren.berechnet': 'Invoiced',
    'retouren.zurueck': 'Returning',
    'retouren.gutschrift': 'Credit note',
    'retouren.einlagern': 'Put goods back into stock',
    'retouren.kundeWaehlen': 'Choose customer …',

    'team.titel': 'Team',
    'team.mitglieder': 'Members',
    'team.neu': 'Add employee',
    'team.benutzername': 'Username',
    'team.emailOptional': 'Email (optional)',
    'team.rolle': 'Role',
    'team.passwortHinweis': 'At least 8 characters. The employee can change it later.',
    'team.limitErreicht': 'User limit reached',
    'team.leer': 'No members yet',
    'team.leerText': 'Add your first employee.',
    'team.gesperrt': 'Blocked',
    'team.alle': "Whole team",
    'team.nichtGefunden': "Person not found.",
    'team.sie': "you",
    'team.online': "Online",
    'team.offline': "Offline",
    'team.bearbeiten': "Edit profile",
    'team.gespeichert': "Profile saved.",
    'team.kontakt': "Contact & work",
    'team.position': "Position",
    'team.positionBeispiel': "e.g. Warehouse manager",
    'team.abteilung': "Department",
    'team.mobil': "Mobile",
    'team.arbeitszeit': "Working hours",
    'team.arbeitszeitBeispiel': "e.g. Mon–Fri 8am–4pm",
    'team.eintritt': "Start date",
    'team.jahre': "{n} years on the team",
    'team.einJahr': "1 year on the team",
    'team.ersteJahr': "in the first year",
    'team.privat': "Personal",
    'team.privatHinweis': "Visible only to the person and administrators",
    'team.freiwillig': "Optional — visible only to the person and administrators",
    'team.geburtstag': "Birthday",
    'team.alter': "{n} years",
    'team.anschrift': "Address",
    'team.notfall': "Emergency contact",
    'team.notfallName': "Emergency contact (name)",
    'team.notfallTelefon': "Emergency contact (phone)",
    'team.notfallBeispiel': "e.g. Anna Meyer (partner)",
    'team.notiz': "Internal note",
    'team.notizHinweis': "Visible to administrators only",
    'team.keineAngaben': "Nothing entered yet",
    'team.offeneAufgaben': "Open tasks",
    'team.keineAufgaben': "No open tasks",
    'team.aktualisiert': "Last changed on {datum}",
    'team.nurAdminFelder': "Position, department, hours and start date are maintained by an administrator.",
    'uebersicht.umsatzHeute': 'Revenue today',
    'uebersicht.offeneForderungen': 'Outstanding receivables',
    'uebersicht.lagerwert': 'Stock value',
    'uebersicht.unterMindest': 'Below minimum stock',
    'uebersicht.nachbestellen': 'Reorder',
    'uebersicht.ueberfaellig': 'Overdue invoices',
    'uebersicht.zahlungsziel': 'Payment term exceeded',
    'uebersicht.umsatzMonat': 'Revenue by month',
    'uebersicht.schnell': 'Quick actions',
    'uebersicht.kassieren': 'Take payment',
    'uebersicht.wareneingang': 'Book goods receipt',
    'aufgaben.titel': 'Tasks',
    'aufgaben.neu': 'New task',
    'aufgaben.wasZuTun': 'What needs doing?',
    'aufgaben.ansichtHeute': 'Today',
    'aufgaben.ansichtBoard': 'Board',
    'aufgaben.ansichtAlle': 'All',
    'aufgaben.vomBetrieb': 'From your business',
    'aufgaben.vomBetriebHinweis': 'Derived from your data and disappears by itself once done.',
    'aufgaben.meine': 'My tasks',
    'aufgaben.nurMeine': 'Mine only',
    'aufgaben.ueberfaellig': 'Overdue',
    'aufgaben.heute': 'Today',
    'aufgaben.morgen': 'Tomorrow',
    'aufgaben.dieseWoche': 'This week',
    'aufgaben.spaeter': 'Later',
    'aufgaben.ohneDatum': 'No date',
    'aufgaben.spalteOffen': 'Open',
    'aufgaben.spalteArbeit': 'In progress',
    'aufgaben.spalteErledigt': 'Done',
    'aufgaben.schnell': 'New task — e.g. “Tomorrow call Meyer !high @Mia”',
    'aufgaben.schnellHilfe': 'today · tomorrow · Monday · 15.11. · !high · !low · @Name',
    'aufgaben.oeffnen': 'Open',
    'aufgaben.zurueckstellen': 'Snooze',
    'aufgaben.tage': '{n} days',
    'aufgaben.uebernehmen': 'Turn into a task',
    'aufgaben.beschreibung': 'Description',
    'aufgaben.faellig': 'Due',
    'aufgaben.prioritaet': 'Priority',
    'aufgaben.niedrig': 'Low',
    'aufgaben.normal': 'Normal',
    'aufgaben.zustaendig': 'Assignee',
    'aufgaben.niemand': 'Nobody',
    'aufgaben.wiederholung': 'Repeat',
    'aufgaben.keine': 'None',
    'aufgaben.taeglich': 'Daily',
    'aufgaben.woechentlich': 'Weekly',
    'aufgaben.monatlich': 'Monthly',
    'aufgaben.checkliste': 'Checklist',
    'aufgaben.punktNeu': 'Add item',
    'aufgaben.loeschen': 'Delete',
    'aufgaben.loeschenFrage': 'Delete this task?',
    'aufgaben.timerStart': 'Start timer',
    'aufgaben.timerStopp': 'Stop timer',
    'aufgaben.allesErledigt': 'Nothing urgent — business is running.',
    'aufgaben.keineEigenen': 'No open tasks.',
    'aufgaben.folge': 'Next on {datum}',
    'aufgaben.zieheHier': 'Drop here',
    'aufgaben.erledigtHeute': 'Done today',
    'aufgaben.schliessen': 'Close',
    'aufgaben.aktionGeliefert': 'Mark as delivered',
    'aufgaben.aktionBezahlt': 'Mark as paid',
    'aufgaben.erledigtMeldung': 'Done.',
    'aufgaben.zurueckgestellt': 'Snoozed until {datum}.',
    'aufgaben.uebernommen': 'Turned into a task.',
    'aufgaben.auto.nachbestellen': 'Reorder {artikel}',
    'aufgaben.auto.nachbestellenText': 'Stock {bestand} {einheit}, minimum {mindestbestand}',
    'aufgaben.auto.bei': 'from {lieferant}',
    'aufgaben.auto.ausliefern': 'Deliver note {nummer}',
    'aufgaben.auto.ausliefernText': 'to {kunde}',
    'aufgaben.auto.nachfassen': 'Follow up quote {nummer}',
    'aufgaben.auto.nachfassenText': '{kunde} · no reply for {tage} days',
    'aufgaben.auto.entwurf': 'Issue invoice draft',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · waiting {tage} days',
    'aufgaben.auto.support': 'Read support reply',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'Book scanned receipts',
    'aufgaben.auto.belegscanText': '{anzahl} waiting for your review',
    'aufgaben.auto.mahnen': 'Send payment reminder: {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} open · {tage} days overdue',
    'aufgaben.auto.bezahlen': 'Pay supplier: {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · due {datum}',
    'aufgaben.auto.zuordnen': 'Match incoming payments',
    'aufgaben.auto.zuordnenText': '{anzahl} payments waiting',
    'aufgaben.auto.bank': 'Reconnect bank: {bank}',
    'aufgaben.auto.bankAbgelaufen': 'Consent expired — no more transactions arrive.',
    'aufgaben.auto.bankEndet': 'Consent ends on {datum}.',
    'aufgaben.auto.ustva': 'VAT return {monat}',
    'aufgaben.auto.ustvaText': 'Payable {betrag} · due {datum} to the tax office',
    'tafel.titel': "Board",
    'tafel.untertitel': "Tasks, appointments and deadlines at a glance",
    'tafel.ansicht.woche': "Week",
    'tafel.ansicht.monat': "Month",
    'tafel.ansicht.liste': "List",
    'tafel.neuerZettel': "New note",
    'tafel.zurueck': "Previous",
    'tafel.vor': "Next",
    'tafel.kw': "Week {n}",
    'tafel.naechste30': "The next 30 days",
    'tafel.art.aufgabe': "Task",
    'tafel.art.termin': "Appointment",
    'tafel.art.frist': "Deadline",
    'tafel.liegenGeblieben': "Left over",
    'tafel.ablage': "Tray",
    'tafel.ablageHinweis': "No day — whenever there is time",
    'tafel.leerTag': "+ Note",
    'tafel.weitere': "+{n} more",
    'tafel.erledigt': "Done",
    'tafel.wiederOffen': "Reopen",
    'tafel.ziehen': "Drag to move to another day",
    'tafel.automatisch': "automatic",
    'tafel.schnell': "What needs doing?",
    'tafel.schnellGross': "What needs doing? e.g. \"14:30 meeting Meyer\" or \"send quote !high\"",
    'tafel.schnellHilfe': "today · tomorrow · Friday · 15.11. · 14:30 · !high · @name",
    'tafel.inEinerWoche': "In a week",
    'tafel.artWaehlen': "Choose type",
    'tafel.bisWann': "By when?",
    'tafel.wann': "When?",
    'tafel.von': "From",
    'tafel.bis': "To",
    'tafel.ort': "Place",
    'tafel.notiz': "Note",
    'tafel.mehr': "More options",
    'tafel.anheften': "Pin",
    'tafel.fristHinweis': "Comes from your data and disappears by itself once it is done.",
    'tafel.kurzTitel': "My board",
    'tafel.kurzLiegen': "{n} left over",
    'tafel.zurTafel': "Open board",
    'tafel.heuteFrei': "Nothing planned.",
    'tafel.frist.angebot': "Quote expires: {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "Delivery: {nummer}",
    'tafel.frist.lieferungText': "to {kunde}",
    'tafel.frist.zahlungseingang': "Payment expected: {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "Supplier invoice due: {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "VAT return",
    'tafel.frist.ustvaText': "for {monat}",
    'aufgaben.leer': 'No tasks yet',
    'aufgaben.leerText': 'Add the first one above.',
    'aufgaben.hoch': 'High',
    'aufgaben.laeuft': 'running',
    'chat.titel': 'Chat',
    'chat.wemSchreiben': 'Who to message?',
    'chat.leer': 'No messages yet',
    'chat.senden': 'Send',
    'chat.dateiAnhaengen': 'Attach file',
    'chat.anhangEntfernen': 'Remove attachment',
    'chat.zurListe': 'Back to list',
    'chat.alleImTeam': 'Everyone',
    'chat.herunterladenTipp': 'tap to download',

    'basis.tarifAb': 'Available from the plan',
    'basis.tarifWechsel': 'You can change your plan under “Plan & billing”.',
    'basis.support': 'Please contact support to unlock this feature.',
    'positionen.hinzufuegen': 'Add line',
    'positionen.freiText': '— free text —',
    'passwort.festlegen': 'Set password',
    'passwort.uebermittelt': 'Password you received',
    'passwort.neu': 'New password',
    'passwort.wiederholen': 'Repeat new password',
    'tarif.titel': 'Plan & billing',
    'tarif.aktuell': 'Current plan',
    'tarif.enthalten': 'Included',
    'tarif.nichtEnthalten': 'Not included',
    'tarif.proMonat': '/ month',
    'tarif.sonderkondition': 'Special rate',
    'tarif.verlauf': 'Plan history',
    'tarif.mandat': 'Direct debit mandate',
    'tarif.angebot': 'Your personal offer',
    'kopie.titel': "Data copy",
    'kopie.text': "You can request a copy of your data from cancellation until 30 days after the contract ends.",
    'kopie.frist': "Available until {datum}.",
    'kopie.anfordern': "Request data copy",
    'kopie.angefordert': "Requested on {datum}. You will receive an email once the copy is ready.",
    'kopie.bereit': "Your data copy is ready: {format}.",
    'kopie.passwort': "Your sign-in password",
    'kopie.passwortHinweis': "This password encrypts the ZIP file — open it by double-clicking with the same password.",
    'kopie.herunterladen': "Download",
    'kopie.geladen': "Downloaded. Keep the file in a safe place.",
    'kopie.formatLesbar': "PDF and tables (Excel)",
    'kopie.formatWiederherstellung': "Restore file",
    'kopie.nurAdmin': "An administrator of your business requests the data copy.",
    'abo.agbAnsehen': "View terms",
    'abo.agbZustimmung': "I have read and accept the terms and conditions.",
    'abo.aufbewahrung': "Remember your retention obligations: invoices and accounting records 8 years, books and financial statements 10 years.",
    'abo.aufpreisHinweis': "On your next invoice: {betrag} net surcharge for the plan change on {datum}.",
    'abo.bestaetigen': "Confirm change",
    'abo.erstZuruecknehmen': "Your contract has been cancelled. To change plans, withdraw the cancellation first.",
    'abo.gekuendigt': "Cancelled as of {datum}. Until then, you can use LagerPro as usual.",
    'abo.gekuendigtMeldung': "Cancelled as of {datum}. A confirmation is on its way by email.",
    'abo.gewechselt': "You are now on {plan}.",
    'abo.hochstufen': "Upgrade",
    'abo.ihrTarif': "Your plan",
    'abo.jetztKuendigen': "Cancel now",
    'abo.kostenpflichtig': "Change with costs",
    'abo.kuendigen': "Cancel contract",
    'abo.kuendigenText': "The cancellation takes effect on {datum}. Until then, use LagerPro as usual; afterwards you can request a copy of your data for 30 days.",
    'abo.kuendigenTextTest': "You are in your free month: the contract ends with it on {datum}; nothing is charged.",
    'abo.kuendigenTitel': "Cancel contract",
    'abo.mandatAendern': "Change bank details",
    'abo.mandatBisTestende': "Please set up a SEPA direct debit mandate by then — otherwise LagerPro pauses after the free month.",
    'abo.nurAdmin': "Plan and contract are managed by an administrator of your business.",
    'abo.runterstufen': "Downgrade",
    'abo.sonderkondition': "You have a special arrangement. Please contact support to change your plan.",
    'abo.testDanach': "After that, {plan} costs {preis} per month.",
    'abo.testLaeuft': "Free month: {n} days left, until {datum} inclusive.",
    'abo.vorgemerkt': "Change to {plan} scheduled from {datum}.",
    'abo.vorgemerktMeldung': "Change to {plan} scheduled from {datum}.",
    'abo.vormerkungWeg': "Scheduled change withdrawn.",
    'abo.vormerkungZurueck': "Withdraw scheduled change",
    'abo.waehlen': "Choose",
    'abo.wechselHoch': "Takes effect immediately. If this month has already been invoiced, the difference for the remaining days is added to the next invoice. From next month {preis} per month.",
    'abo.wechselRunter': "Takes effect on {datum}. Until then you keep all features of {plan}.",
    'abo.wechselTest': "Takes effect immediately. Free during the free month; afterwards {preis} per month.",
    'abo.wechselTitel': "Switch to {plan}",
    'abo.wechselnHinweis': "Upgrades take effect immediately, downgrades at the end of the month.",
    'abo.wechselnTitel': "Change plan",
    'abo.zurueckMeldung': "Cancellation withdrawn — your contract continues.",
    'abo.zuruecknehmen': "Withdraw cancellation",
    'abo.beendetTitel': "Your contract has ended",
    'abo.beendetText': "Your LagerPro contract ended on {datum}.",
    'abo.beendetKopie': "You can request a copy of your data until {bis} — after that it will be deleted.",
    'firma.titel': 'Company',
    'firma.anschrift': 'Address and contact',
    'firma.steuerRegister': 'Tax and registration',
    'firma.girocode': 'Print a payment code on invoices',
    'firma.girocodeText': 'A QR code the customer scans with their banking app — payee, amount and reference are filled in for them. Shown only on unpaid invoices due by bank transfer.',
    'firma.ibanFehlt': 'The payment code needs an IBAN.',
    'firma.ibanUngueltig': 'This IBAN fails its check digits. Please review it — until then no payment code is printed.',
    'firma.bank': 'Bank details',
    'firma.fusstext': 'Footer text',
    'firma.logo': 'Logo',
    'firma.logoWaehlen': 'Choose logo',
    'firma.logoEntfernen': 'Remove logo',
    'firma.speichern': 'Save company details',
    'firma.aufRechnung': 'Appears on every invoice',
    'firma.pflichtangaben': 'Mandatory invoice details',
    'firma.imFuss': 'Shown in the invoice footer',
    'passwort.willkommen': 'Welcome',
    'passwort.warnung': 'Your current password was handed to you and is therefore known to someone else. Please set your own now.',
    'passwort.regel8': 'At least 8 characters',
    'passwort.regelGross': 'Upper and lower case',
    'passwort.regelZiffer': 'At least one digit',
    'passwort.regelGleich': 'Both entries match',
    'passwort.regelAnders': 'Different from the password you received',
    'passwort.fortfahren': 'Set password and continue',
    'lager.anzahlArtikel': 'items',
    'lager.nurKnappe': 'Low stock only',
    'lager.etiketten': 'Labels',

    'zfa.titel': 'Confirmation',
    'zfa.gesendet': 'We sent you a code to',
    'zfa.code': 'Code from the email',
    'zfa.bestaetigen': 'Confirm',
    'zfa.gueltig': 'The code is valid for {min} minutes.',
    'zfa.andererZugang': 'Different login',
    'zfa.einstellung': 'Two-factor sign-in',
    'zfa.erklaerung': 'In addition to your password, a code is sent to your email at every sign-in. Knowing your password alone is then not enough.',
    'zfa.aktiv': 'On',
    'zfa.aus': 'Off',
    'zfa.einschalten': 'Turn on',
    'zfa.ausschalten': 'Turn off',
    'zfa.mailFrage': 'Which address should receive the codes?',
    'zfa.codeSenden': 'Send code',
    'zfa.passwortFrage': 'Enter your password to turn it off',
    'zfa.keinVersand': 'No mail delivery is set up for this system yet. Without it no code can be delivered.',
    'zfa.eingeschaltet': 'Two-factor sign-in is on.',
    'zfa.ausgeschaltet': 'Two-factor sign-in is off.',
    'zfa.methodeApp': "Authenticator app",
    'zfa.methodeMail': "Email code",
    'zfa.erklaerungBeide': "In addition to your password, LagerPro asks for a code at every sign-in — from an authenticator app on your phone or by email. Knowing your password alone is not enough.",
    'zfa.ersatzUebrig': "{n} backup codes left",
    'zfa.mitApp': "Turn on with authenticator app",
    'zfa.mitMail': "Turn on by email",
    'zfa.zurApp': "Switch to authenticator app",
    'zfa.ersatzNeu': "New backup codes",
    'zfa.appEmpfohlen': "Recommended: the app. It works offline and without email — e.g. Google Authenticator or Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "Create new backup codes — the old ones stop working immediately.",
    'zfa.passwortWeiter': "Please enter your password",
    'zfa.weiter': "Continue",
    'zfa.schritt1': "Open an authenticator app on your phone (e.g. Google Authenticator) and choose “Add code”.",
    'zfa.scannen': "Scan this QR code.",
    'zfa.schritt3': "Enter the 6-digit code from the app below.",
    'zfa.manuell': "Can’t scan? Enter this key manually:",
    'zfa.appCode': "Code from the app",
    'zfa.ersatzTitel': "Your backup codes",
    'zfa.ersatzHinweis': "If you lose your phone: each code replaces the app code once. Keep them safe (print or password manager) — they are shown only now.",
    'zfa.kopieren': "Copy",
    'zfa.kopiert': "Copied",
    'zfa.herunterladen': "Save as file",
    'zfa.gesichert': "I have stored the codes safely",
    'zfa.fertig': "Done",
    'zfa.appText': "Open your authenticator app and enter the 6-digit code for LagerPro.",
    'zfa.ersatzText': "Enter one of your backup codes. Each works only once.",
    'zfa.ersatzCode': "Backup code",
    'zfa.ersatzNutzen': "Phone not at hand? Use a backup code",
    'zfa.appCodeNutzen': "Use the code from the app",
    'nav.app': "App",
    'start.nav.app': "App",
    'desktop.titel': "LagerPro as an app",
    'desktop.text': "Its own window and icon in the Dock or taskbar, starts with one click. Sign in once — then you stay signed in for 90 days.",
    'desktop.inApp': "You are using the desktop app",
    'desktop.inAppText': "Version {version}. The interface updates itself.",
    'desktop.alsWebApp': "LagerPro is installed as an app on this computer.",
    'desktop.installiert': "Installed — you will now find LagerPro among your apps.",
    'desktop.direktTitel': "Install directly from the browser",
    'desktop.schnell': "seconds",
    'desktop.direktText': "No download, no security warning, always up to date.",
    'desktop.safari': "In Safari: menu “File” → “Add to Dock” (macOS Sonoma or later).",
    'desktop.firefox': "Firefox cannot install web apps — please use Chrome or Edge, or download below.",
    'desktop.chromeLeiste': "Click the “Install” icon on the right of the address bar — or menu ⋮ → “Install LagerPro”.",
    'desktop.andererBrowser': "Easiest with Chrome, Edge or Safari — or download the app below.",
    'desktop.jetztInstallieren': "Install now",
    'desktop.downloadTitel': "Or download",
    'desktop.version': "Version {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 and 11, 64-bit",
    'desktop.macApple': "Mac with Apple chip",
    'desktop.macAppleUnter': "M1, M2, M3, M4 · from 2020",
    'desktop.macIntel': "Mac with Intel chip",
    'desktop.macIntelUnter': "older Macs up to 2020",
    'desktop.passtZuIhnen': "Matches this computer",
    'desktop.laden': "Download",
    'desktop.bald': "Coming soon",
    'desktop.warnungTitel': "Warning when opening for the first time?",
    'desktop.warnungText': "The downloads are not yet registered with Microsoft and Apple, so your computer asks once at first launch — never again after that.",
    'desktop.warnungWindows': "At “Windows protected your PC” click “More info”, then “Run anyway”.",
    'desktop.warnungMac': "Drag LagerPro into “Applications”. When a notice appears on first open, go to “System Settings → Privacy & Security” and click “Open Anyway” (older macOS: right-click LagerPro → “Open”).",
    'desktop.anmeldung': "Either way: sign in once with your password (and two-factor code), then 90 days without typing. Signing out ends the remembered sign-in immediately.",
    'desktop.mehrInfos': "Everything about the app",

    'erinnerung.titel': 'Payment reminders',
    'erinnerung.erklaerung': 'If an invoice is overdue, LagerPro automatically sends your customer a polite reminder — in your name, with your address as the reply path.',
    'erinnerung.nachTagen': 'Remind after … days overdue',
    'erinnerung.wiederholung': 'Repeat after … days',
    'erinnerung.maxAnzahl': 'At most … reminders per invoice',
    'erinnerung.faellig': 'Due at the next run',
    'erinnerung.jetzt': 'Send now',
    'erinnerung.keineFaellig': 'No invoice is overdue right now.',
    'erinnerung.firmenmailFehlt': 'Add your email under “Company” — otherwise your customers cannot reply to the reminder.',
    'erinnerung.keineFuerKunden': 'No payment reminders to this customer',
  },

  fr: {
    'start.nav.funktionen': 'Fonctions',
    'start.nav.belegkette': 'Chaîne de documents',
    'start.nav.preise': 'Tarifs',
    'start.kostenlosStarten': 'Commencer gratuitement',
    'start.themaDunkel': 'Thème sombre',
    'start.themaHell': 'Thème clair',
    'start.sprache': 'Langue',
    'start.hero.marke': 'Conforme GoBD · Serveurs dans l’UE',
    'start.hero.titel1': 'Stock, caisse et comptabilité.',
    'start.hero.titel2': 'Un système au lieu de cinq.',
    'start.hero.text': 'LagerPro conduit votre entreprise du devis à la déclaration fiscale : stocks, ventes, documents et comptabilité s\'alimentent mutuellement. Aucun export, aucune ressaisie, rien à tenir à jour deux fois.',
    'start.hero.konto': 'J\'ai un compte',
    'start.hero.fussnote': 'Sans carte bancaire · Installé en deux minutes · Résiliable chaque mois',
    'start.vorschau.umsatz': 'Chiffre d\'affaires du jour',
    'start.vorschau.offen': 'Factures impayées',
    'start.vorschau.lagerwert': 'Valeur du stock',
    'start.vorschau.knapp': 'Sous le seuil',
    'start.vorschau.gruss': 'Bonjour, Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'Documents récents',
    'start.vorschau.angebot': 'Devis',
    'start.vorschau.lieferschein': 'Bon de livraison',
    'start.vorschau.rechnung': 'Facture',
    'start.vertrauen1t': 'Conforme GoBD',
    'start.vertrauen1x': 'Numérotation sans trou, documents inaltérables, journaux chaînés par empreinte — la norme comptable allemande',
    'start.vertrauen2t': 'Export GDPdU',
    'start.vertrauen2x': 'Remise de données Z3 avec index.xml — prêt quand le contrôle arrive',
    'start.vertrauen3t': 'Serveurs dans l’UE',
    'start.vertrauen3x': 'Aucune transmission, aucun traceur publicitaire, RGPD dès le premier jour',
    'start.vertrauen4t': 'Conservation 10 ans',
    'start.vertrauen4x': 'Selon le § 147 du code fiscal allemand, automatiquement — vous n\'avez rien à y penser',
    'start.f1t': 'Photographier le document, c’est tout',
    'start.f1x': 'Photographiez un bon de livraison ou une facture avec le téléphone — l\'assistant en extrait les lignes, les quantités et le fournisseur. Vous vérifiez une fois, la marchandise est en stock. Il répond à vos questions sur les stocks et les chiffres, par écrit ou à la voix.',
    'start.f2t': 'Le stock en main',
    'start.f2x': 'Stocks en temps réel, alerte de seuil, réception de marchandises et étiquettes code-barres en cinq formats.',
    'start.f3t': 'Une caisse pour le comptoir',
    'start.f3x': 'Choisir le client, scanner les articles, encaisser. Le paiement en espèces reste possible et le stock se décrémente seul.',
    'start.f4t': 'Tous les documents',
    'start.f4x': 'Devis, confirmation de commande, bon de livraison, facture, avoir et annulation — en PDF à votre logo.',
    'start.f5t': 'Une comptabilité qui tient',
    'start.f5x': 'Comptabilité de trésorerie avec les lignes du formulaire, déclaration de TVA, carnet de bord et export DATEV pour votre comptable.',
    'start.f6t': 'Des retours sans désordre',
    'start.f6x': 'Toujours via la facture : client, document, lignes. La marchandise retourne au stock, l\'avoir va au compte client.',
    'start.f7t': 'L\'équipe à bord',
    'start.f7x': 'Tâches avec minuteur, messagerie avec notifications, présence — sans deuxième application.',
    'start.funktionen.titel': 'Tout ce dont l\'entreprise a besoin chaque jour',
    'start.funktionen.text': 'Chaque domaine connaît les données des autres. Vous vendez, le stock baisse, le document naît et la comptabilité a son écriture — au même instant.',
    'start.kette.titel': 'Une affaire, quatre documents, aucune ressaisie',
    'start.kette.text': 'Un clic transforme le devis en confirmation de commande, puis en bon de livraison, puis en facture. Lignes et prix suivent. La marchandise ne sort du stock qu\'une seule fois.',
    'start.kette.schritt': 'Étape',
    'start.kette1t': 'Devis',
    'start.kette1x': 'Ce que cela coûtera',
    'start.kette2t': 'Commande',
    'start.kette2x': 'Le client accepte',
    'start.kette3t': 'Bon de livraison',
    'start.kette3x': 'La marchandise part',
    'start.kette4t': 'Facture',
    'start.kette4x': 'L\'argent rentre',
    'start.kette.fuss': 'Si quelque chose revient, il y a l\'avoir et la facture d\'annulation — avec la contre-écriture exigée par le fisc.',
    'start.preise.titel': 'Des tarifs sans petits caractères',
    'start.preise.text': "Résiliable chaque mois, tous les prix hors TVA. Chaque offre commence par 30 jours gratuits — sans coordonnées bancaires.",
    'start.preise.laedt': 'Chargement des forfaits …',
    'start.preise.fehler': 'Forfaits momentanément indisponibles. Écrivez-nous, nous vous indiquerons les conditions.',
    'start.preise.top': 'Le plus choisi',
    'start.preise.proMonat': '/ mois',
    'start.preise.sofort': "Essayer 30 jours gratuitement",
    'start.preise.waehlen': "Essayer {tarif} 30 jours gratuitement",
    'start.ende.titel': 'Commencer aujourd\'hui, avoir la vue d\'ensemble demain',
    'start.ende.text': 'Créez le compte, saisissez les articles, écrivez la première facture. Vos données existantes pourront être reprises plus tard.',
    'start.fuss.hinweis': 'Serveurs dans l’UE · Conforme GoBD',
    'nav.support': 'Assistance',
    'support.vonAnbieter': 'De LagerPro',
    'support.titel': 'Assistance',
    'support.untertitel': 'Questions, problèmes, souhaits — écrivez-nous ici. Tout l\'échange reste au même endroit.',
    'support.neu': 'Nouvelle demande',
    'support.betreff': 'Objet',
    'support.kategorie': 'De quoi s’agit-il ?',
    'support.kat.frage': 'Question',
    'support.kat.fehler': 'Quelque chose ne fonctionne pas',
    'support.kat.wunsch': 'Souhait ou idée',
    'support.kat.abrechnung': 'Facturation et forfait',
    'support.kat.sonstiges': 'Autre',
    'support.nachricht': 'Message',
    'support.nachrichtPlatzhalter': 'Décrivez aussi précisément que possible ce qui s\'est passé et ce que vous attendiez.',
    'support.anhang': 'Joindre une capture d\'écran ou un fichier',
    'support.anhangHinweis': 'Jusqu\'à 5 fichiers, 25 Mo chacun',
    'support.senden': 'Envoyer la demande',
    'support.gesendet': 'Votre demande {nummer} est bien arrivée. Vous recevrez un e-mail dès qu’une réponse sera disponible.',
    'support.leer': 'Aucune demande pour le moment',
    'support.leerText': 'Si quelque chose ne fonctionne pas ou si vous avez une question, écrivez-nous.',
    'support.status.offen': 'Reçue',
    'support.status.in_arbeit': 'En cours',
    'support.status.wartet': 'Réponse disponible',
    'support.status.erledigt': 'Résolue',
    'support.zurueck': 'Toutes les demandes',
    'support.antworten': 'Répondre',
    'support.antwortPlatzhalter': 'Votre message …',
    'support.erledigt': 'Marquer comme résolue',
    'support.erledigtHinweis': 'Résolue. Si quelque chose reste ouvert, écrivez simplement — la demande sera rouverte.',
    'support.sie': 'Vous',
    'support.team': 'Assistance LagerPro',
    'support.ungelesen': 'nouveau',
    'support.glocke': 'Réponse de l\'assistance',
    'support.anhaengen': 'Joindre',
    'einheit.stueck': 'Pièce',
    'einheit.paar': 'Paire',
    'einheit.satz': 'Lot',
    'einheit.paket': 'Paquet',
    'einheit.karton': 'Carton',
    'einheit.palette': 'Palette',
    'einheit.rolle': 'Rouleau',
    'einheit.flasche': 'Bouteille',
    'einheit.dose': 'Boîte',
    'einheit.beutel': 'Sachet',
    'einheit.minute': 'min',
    'einheit.stunde': 'h',
    'einheit.tag': 'Jour',
    'einheit.woche': 'Semaine',
    'einheit.monat': 'Mois',
    'einheit.jahr': 'An',
    'einheit.pauschale': 'Forfait',
    'einheit.gruppe.anzahl': 'Quantité et conditionnement',
    'einheit.gruppe.gewicht': 'Poids',
    'einheit.gruppe.volumen': 'Volume',
    'einheit.gruppe.laenge': 'Longueur et surface',
    'einheit.gruppe.zeit': 'Temps',
    'einheit.gruppe.sonstiges': 'Autres',
    'einheit.andere': 'Autre unité …',
    'einheit.eigene': 'Unité personnalisée',
    'einheit.eigenePlatzhalter': 'p. ex. botte, caisse',
    'positionen.umgerechnet': '= {menge} du stock',
    'positionen.passtNicht': 'Cet article est géré en {einheit} — cette unité n\'est pas convertible.',
    'positionen.jeEinheit': 'par {einheit}',
    'nav.assistent': 'Assistant IA',
    'assistent.datenschutz': 'Pour l\'analyse, le document et votre question sont transmis à notre prestataire IA et utilisés uniquement pour y répondre — jamais pour entraîner des modèles. Vos données de stock restent sur notre serveur.',
    'pruef.titel': 'Vérifier {art}',
    'pruef.beleg': 'le document',
    'pruef.lieferschein': 'le bon de livraison',
    'pruef.eingangsrechnung': 'la facture d\'achat',
    'pruef.gutschrift': 'l\'avoir',
    'pruef.bestellung': 'la commande',
    'pruef.kassenbon': 'le ticket de caisse',
    'pruef.erkannt': '% reconnu',
    'pruef.ohneNummer': 'Sans numéro ni date',
    'pruef.schwerLesbar': 'Ce document était difficile à lire',
    'pruef.schwerLesbarText': 'Vérifiez chaque ligne. Sur une photo floue ou de travers, la reconnaissance confond les chiffres. Une nouvelle photo bien éclairée vaut souvent mieux qu\'une correction.',
    'pruef.nichtAusgewertet': 'Non analysé',
    'pruef.nurBilder': 'seules les photos et les PDF sont lisibles',
    'pruef.vorhandener': 'Existant',
    'pruef.neuAnlegen': 'Créer',
    'pruef.ohne': 'Aucun',
    'pruef.bitteWaehlen': '— choisissez —',
    'pruef.gewaehlt': '{a} sur {b} sélectionnées',
    'pruef.alleUebernehmen': 'Tout sélectionner',
    'pruef.keine': 'Aucune',
    'pruef.keinePositionen': 'Aucune ligne n\'a été reconnue sur ce document.',
    'pruef.warenwert': 'Valeur de la sélection',
    'pruef.rechnungsbetrag': 'Montant de la facture',
    'pruef.brauchtBuchhaltung': 'L\'enregistrement de la dépense nécessite la comptabilité — incluse à partir du forfait Professional.',
    'pruef.alsAusgabe': 'Enregistrer le montant comme charge',
    'pruef.alsAusgabeText': 'Le document reste attaché comme justificatif : la dépense est complète pour la comptabilité.',
    'pruef.brutto': 'Montant TTC',
    'pruef.steuersatz': 'Taux de TVA',
    'pruef.belegnummer': 'Numéro de pièce',
    'pruef.belegdatum': 'Date de la pièce',
    'pruef.lastschrift': 'Prélèvement',
    'pruef.keineWare': 'Hors stock',
    'pruef.keineWareText': 'Compte dans le total de la facture mais n’entre pas en stock.',
    'pruef.grund.keineWare': 'Frais de port, frais ou remise — article non stockable',
    'pruef.privat': 'Avancé à titre privé',
    'pruef.erstMitKnopf': 'Rien n\'est enregistré avant ce bouton.',
    'pruef.buchen': 'Valider et enregistrer',
    'pruef.wirdGebucht': 'Enregistrement',
    'pruef.zugeordnet': 'Associé',
    'pruef.vermutet': 'Probable',
    'pruef.neu': 'Nouveau',
    'pruef.schlechtLesbar': 'Peu lisible',
    'pruef.aendern': 'Modifier',
    'pruef.zuklappen': 'Replier',
    'pruef.wirdAngelegt': 'sera créé comme nouvel article',
    'pruef.artikel': 'Article',
    'pruef.nichtZugeordnet': '— non associé —',
    'pruef.alsNeuenArtikel': 'Créer comme nouvel article',
    'pruef.einkaufspreis': 'Prix d\'achat',
    'pruef.verkaufspreis': 'Prix de vente',
    'pruef.spaeterErgaenzen': 'Peut être ajouté plus tard',
    'pruef.ean': 'EAN',
    'pruef.je': 'par',
    'pruef.grund.ean': 'L\'EAN correspond',
    'pruef.grund.nummer': 'La référence article correspond',
    'pruef.grund.name': 'La désignation correspond exactement',
    'pruef.grund.ustId': 'Le numéro de TVA correspond',
    'pruef.grund.aehnlich': 'Désignation proche — à vérifier',
    'pruef.grund.keiner': 'Aucun article correspondant en stock',
    'pruef.kat.wareneinkauf': 'Achats de marchandises',
    'pruef.kat.buero': 'Fournitures de bureau',
    'pruef.kat.fahrzeug': 'Frais de véhicule',
    'pruef.kat.werbung': 'Publicité',
    'pruef.kat.kommunikation': 'Téléphone et internet',
    'pruef.kat.raumkosten': 'Loyer et locaux',
    'pruef.kat.sonstiges': 'Autres charges',
    'assistent.titel': 'Assistant IA',
    'assistent.rest': 'Restant ce mois-ci',
    'assistent.scans': 'scans de documents',
    'assistent.fragen': 'questions',
    'assistent.testmodus': 'Mode test',
    'assistent.testmodusText': 'Aucun accès IA n\'est configuré. Les documents renvoient des données d\'exemple et les réponses sont fictives. Une fois l\'accès configuré, l\'assistant travaille sur vos données réelles.',
    'assistent.verlaufLoeschen': 'Effacer l\'historique',
    'assistent.denkt': 'Un instant',
    'assistent.platzhalter': 'Posez une question ou photographiez un document',
    'assistent.hoertZu': 'J\'écoute',
    'assistent.sprechen': 'Parler',
    'assistent.vorlesen': 'Lire à voix haute',
    'assistent.stopp': 'Arrêter',
    'assistent.senden': 'Envoyer',
    'assistent.fotografieren': 'Photographier un document',
    'assistent.datei': 'Téléverser un fichier',
    'assistent.belegAuswerten': 'Analyser le document',
    'assistent.wertetAus': 'Lecture du document',
    'assistent.bestaetigen': 'Confirmer',
    'assistent.gebucht': 'Enregistré',
    'assistent.verworfen': 'Rejeté',
    'assistent.willkommen': 'Comment puis-je aider ?',
    'assistent.willkommenText': 'Posez des questions sur les stocks et les chiffres, ou photographiez un bon de livraison — les lignes entrent en stock après votre vérification.',
    'assistent.beispiel1': 'Quels articles sont bientôt épuisés ?',
    'assistent.beispiel2': 'Quel est mon chiffre d\'affaires ce mois-ci ?',
    'assistent.beispiel3': 'Quelles factures sont encore impayées ?',
    'assistent.beispiel4': 'Quelle est la valeur de mon stock ?',
    'allg.entfernen': 'Retirer',
    'bestaetigung.titel': 'Confirmez votre adresse e-mail',
    'bestaetigung.text': 'Nous vous avons envoyé un message. Ouvrez le lien qu\'il contient pour continuer.',
    'bestaetigung.warum': 'Sans adresse confirmée, vous ne recevez ni codes de connexion, ni factures, ni l\'avis préalable à un prélèvement.',
    'bestaetigung.schonBestaetigt': 'J\'ai confirmé — continuer',
    'bestaetigung.erneut': 'Renvoyer le message',
    'bestaetigung.erneutGesendet': 'Le message est parti.',
    'bestaetigung.versandFehlt': 'L\'envoi est impossible pour le moment. Contactez le support.',
    'bestaetigung.fehlgeschlagen': 'Cela n\'a pas fonctionné.',
    'bestaetigung.spam': 'Rien reçu ? Vérifiez vos indésirables.',
    'bestaetigung.pruefe': 'Un instant …',
    'bestaetigung.fertigTitel': 'Adresse confirmée',
    'bestaetigung.fertigText': 'Votre adresse e-mail est confirmée. Vous pouvez continuer.',
    'bestaetigung.zurApp': 'Vers l\'application',
    'bestaetigung.abgelaufenTitel': 'Lien expiré',
    'bestaetigung.abgelaufenText': 'Ce lien n\'est plus valable. Connectez-vous et demandez un nouveau message.',
    'bestaetigung.ungueltigTitel': 'Lien non valable',
    'bestaetigung.ungueltigText': 'Ce lien ne peut pas être utilisé. L\'adresse est peut-être déjà confirmée.',
    'mandat.pflichtTitel': 'Mandat de prélèvement SEPA',
    'mandat.pflichtText': 'Un mandat de prélèvement SEPA est nécessaire pour continuer. Cela prend deux minutes. Vos données restent intactes.',
    'mandat.einleitung': 'Vous accordez le mandat avec le titulaire du compte et l\'IBAN. Aucune signature papier n\'est requise.',
    'mandat.kontoinhaber': 'Titulaire du compte',
    'mandat.kontoinhaberHinweis': 'Exactement tel que le nom figure à votre banque.',
    'mandat.bicHinweis': 'Non requis dans l\'espace SEPA.',
    'mandat.ibanFalsch': 'Cet IBAN a une clé de contrôle invalide.',
    'mandat.wortlautTitel': 'Texte du mandat',
    'mandat.glaeubiger': 'Créancier',
    'mandat.glaeubigerId': 'ICS du créancier',
    'mandat.referenz': 'Référence du mandat',
    'mandat.referenzSpaeter': 'attribuée après signature et communiquée avant le premier prélèvement',
    'mandat.zustimmung': 'J\'accepte le mandat de prélèvement ci-dessus.',
    'mandat.erteilen': 'Accorder le mandat',
    'mandat.erteilt': 'Le mandat est accordé. Merci.',
    'mandat.liegtVor': 'Un mandat valide est enregistré.',
    'mandat.nochNichtBereit': 'Le prélèvement est en cours de configuration. Réessayez plus tard ou contactez le support.',
    'mandat.ladefehler': 'Les informations n\'ont pas pu être chargées.',
    'mandat.fehlgeschlagen': 'Le mandat n\'a pas pu être accordé.',
    'allg.optional': 'facultatif',
    'sperre.titel': 'LagerPro est verrouillé',
    'sperre.entsperren': 'Déverrouiller',
    'sperre.abgebrochen': 'La vérification a été annulée.',
    'geraete.titel': 'Appareils connectés',
    'geraete.hinweis': 'Appareils sur lesquels vous restez connecté.',
    'geraete.hinweisAdmin': 'Tous les appareils de l\'entreprise. Déconnectez-en un en cas de perte ou de départ.',
    'geraete.keine': 'Aucun appareil connecté en permanence.',
    'geraete.dieses': 'cet appareil',
    'geraete.zuletzt': 'vu',
    'geraete.abmelden': 'Déconnecter',
    'geraete.abmeldenFehler': 'L\'appareil n\'a pas pu être déconnecté.',
    'geraete.ladefehler': 'Les appareils n\'ont pas pu être chargés.',
    'geraete.nichtGemerkt': 'Cet appareil n\'est pas mémorisé. Connectez-vous une fois avec votre mot de passe, ensuite votre empreinte suffira.',
    'geraete.sperreTitel': 'Verrouillage à l\'ouverture',
    'geraete.sperreAn': 'Verrouiller l\'application avec {art}',
    'geraete.sperreText': 'Vérifié à l\'ouverture et après cinq minutes en arrière-plan.',
    'geraete.sperreAusWarnung': 'Sans verrouillage, quiconque tient votre téléphone déverrouillé accède aux données de l\'entreprise.',
    'geraete.alleAnderen': "Déconnecter tous les autres",
    'geraete.gueltigBis': "connecté jusqu’au {datum}",
    'geraete.browserTitel': "Connecté dans un navigateur",
    'geraete.diese': "cette session",
    'geraete.browserHinweis': "Les connexions navigateur expirent seules après 24 heures — ou immédiatement ici.",
    'app.name': 'LagerPro',
    'app.untertitel': 'Gestion d\u2019entrep\u00f4t professionnelle',
    'benachrichtigung.titel': 'Notifications',
    'benachrichtigung.leer': 'Rien de nouveau',
    'benachrichtigung.neu': 'Nouvelle notification',
    'benachrichtigung.neueVorgaenge': 'nouveaux éléments',
    'benachrichtigung.anAlle': 'à tous',
    'nav.uebersicht': 'Tableau de bord',
    'nav.mehr': 'Plus',
    'allg.rechnung': 'facture',
    'app.gruss': 'Bonjour, {name}',
    'app.gruppe.tagesgeschaeft': 'Activité du jour',
    'app.gruppe.kontakte': 'Contacts',
    'app.gruppe.zusammenarbeit': 'Collaboration',
    'app.gruppe.auswertung': 'Analyses',
    'app.gruppe.betrieb': 'Entreprise',
    'app.nochmalBeenden': 'Appuyez à nouveau pour quitter',
    'app.keineVerbindung': 'Pas de connexion — chiffres indisponibles',
    'app.konto': 'Compte',
    'app.darstellung': 'Apparence',
    'nav.kasse': 'Caisse',
    'nav.lager': 'Stock',
    'nav.belege': 'Documents',
    'nav.rechnungen': 'Factures',
    'nav.kunden': 'Clients',
    'nav.lieferanten': 'Fournisseurs',
    'nav.retouren': 'Retours',
    'nav.buchhaltung': 'Comptabilit\u00e9',
    'nav.ausgaben': 'Dépenses',
    'nav.bank': 'Banque',
    'nav.fahrtenbuch': 'Carnet de bord',
    'nav.aufgaben': "Tableau",
    'nav.chat': 'Messagerie',
    'nav.team': '\u00c9quipe',
    'nav.firma': 'Entreprise',
    'nav.gobd': 'GoBD & contrôle',
    'nav.tarif': 'Abonnement',
    'nav.uebernahme': 'Importer les donn\u00e9es',
    'nav.einstellungen': 'Paramètres',
    'nav.owner': 'Gestion clients',
    'nav.mandanten': 'Locataires',
    'nav.gruppe.verkauf': 'Ventes',
    'nav.gruppe.waren': 'Marchandises',
    'nav.gruppe.kontakte': 'Contacts',
    'nav.gruppe.auswertung': 'Rapports',
    'nav.gruppe.zusammenarbeit': 'Collaboration',
    'nav.gruppe.verwaltung': 'Administration',
    'aktion.anlegen': 'Cr\u00e9er',
    'aktion.speichern': 'Enregistrer',
    'aktion.abbrechen': 'Annuler',
    'aktion.bearbeiten': 'Modifier',
    'aktion.loeschen': 'Supprimer',
    'aktion.schliessen': 'Fermer',
    'aktion.suchen': 'Rechercher',
    'aktion.anmelden': 'Se connecter',
    'aktion.abmelden': 'Se d\u00e9connecter',
    'aktion.drucken': 'Imprimer',
    'aktion.herunterladen': 'T\u00e9l\u00e9charger',
    'allg.suche': 'Recherche',
    'allg.laedt': 'Chargement',
    'allg.keineTreffer': 'Aucun r\u00e9sultat',
    'allg.darstellung': 'Apparence',
    'allg.sprache': 'Langue',
    'allg.thema.system': 'Syst\u00e8me',
    'allg.thema.hell': 'Clair',
    'allg.thema.dunkel': 'Sombre',
    'allg.rolle.inhaber': 'Propri\u00e9taire',
    'allg.rolle.admin': 'Administrateur',
    'allg.rolle.mitarbeiter': 'Employ\u00e9',
    'allg.nichtImTarif': 'Non inclus dans votre abonnement',
    'allg.tarif': 'Abonnement',
    'allg.menue': 'Menu',
    'allg.schnellzugriff': 'Acc\u00e8s rapide',
    'allg.schnellzugriffHinweis': "Page, client ou n° de document …",
    'anmelden.willkommen': 'Bon retour',
    'anmelden.hinweis': 'Connectez-vous pour continuer',
    'anmelden.kennung': 'Nom d\u2019utilisateur ou e-mail',
    'anmelden.passwort': 'Mot de passe',
    'anmelden.fehler': '\u00c9chec de la connexion.',
    'anmelden.keinKonto': 'Pas encore de compte ?',
    'anmelden.jetztRegistrieren': 'S’inscrire gratuitement',
    'passwort.vergessenLink': "Mot de passe oublié ?",
    'passwort.vergessenTitel': "Mot de passe oublié",
    'passwort.vergessenText': "Saisissez votre nom d’utilisateur ou votre adresse e-mail. Nous vous enverrons un lien pour définir un nouveau mot de passe.",
    'passwort.linkSenden': "Envoyer le lien",
    'passwort.gesendetTitel': "Consultez votre boîte mail",
    'passwort.gesendetText': "Si un compte avec une adresse e-mail correspond, le lien est en route. Il est valable {min} minutes. Sans adresse enregistrée, votre administrateur peut vous aider.",
    'passwort.zurAnmeldung': "Retour à la connexion",
    'passwort.neuTitel': "Définir un nouveau mot de passe",
    'passwort.fuer': "Pour le compte {name}",
    'passwort.regeln': "Au moins 8 caractères, avec majuscules, minuscules et un chiffre.",
    'passwort.ungleich': "Les deux mots de passe ne correspondent pas.",
    'passwort.speichern': "Enregistrer le mot de passe",
    'passwort.fertigTitel': "Mot de passe modifié",
    'passwort.fertigText': "Connectez-vous maintenant avec le nouveau mot de passe. Tous les appareils ont été déconnectés.",
    'passwort.abgelaufenTitel': "Lien expiré",
    'passwort.abgelaufenText': "Le lien n’est valable que 30 minutes. Demandez-en simplement un nouveau.",
    'passwort.ungueltigTitel': "Lien invalide",
    'passwort.ungueltigText': "Ce lien est invalide ou a déjà été utilisé.",
    'passwort.neuAnfordern': "Demander un nouveau lien",
    'passwort.pruefe': "Vérification du lien …",
    'uebersicht.begruessung': 'Bonjour',
    'uebersicht.offeneAufgaben': 'T\u00e2ches ouvertes',
    'uebersicht.laufendeTimer': 'Minuteurs actifs',
    'uebersicht.teamOnline': '\u00c9quipe en ligne',
    'uebersicht.benutzerImTarif': 'Utilisateurs inclus',
    'uebersicht.naechsteAufgaben': 'Prochaines t\u00e2ches',
    'uebersicht.alleAnsehen': 'Tout voir',
    'uebersicht.unbegrenzt': 'illimit\u00e9',
    'uebersicht.von': 'sur',
    'uebersicht.insgesamt': 'au total',
    'uebersicht.keineOffenen': 'Aucune t\u00e2che ouverte.',

    'feld.name': 'Nom',
    'feld.nummer': 'Numéro',
    'feld.telefon': 'Téléphone',
    'feld.email': 'E-mail',
    'feld.strasse': 'Rue',
    'feld.plz': 'Code postal',
    'feld.ort': 'Ville',
    'feld.land': 'Pays',
    'feld.notiz': 'Note',
    'feld.ansprechpartner': 'Interlocuteur',
    'feld.ustId': 'N° TVA',
    'feld.menge': 'Quantité',
    'feld.einzelpreis': 'Prix unitaire',
    'feld.bezeichnung': 'Désignation',
    'feld.artikel': 'Article',
    'feld.kunde': 'Client',
    'feld.lieferant': 'Fournisseur',
    'feld.datum': 'Date',
    'feld.betrag': 'Montant',
    'feld.grund': 'Motif',
    'feld.status': 'Statut',
    'feld.einheit': 'Unité',
    'feld.bestand': 'Stock',
    'feld.gesamt': 'Total',
    'feld.netto': 'Net',
    'feld.rabatt': 'Remise',
    'feld.position': 'Ligne',
    'feld.positionen': 'Lignes',
    'aktion.hinzufuegen': 'Ajouter',
    'aktion.entfernen': 'Retirer',
    'aktion.zurueck': 'Retour',
    'aktion.weiter': 'Continuer',
    'kunden.titel': 'Clients',
    'kunden.neu': 'Nouveau client',
    'kunden.nummer': 'N° client',
    'kunden.suche': 'Nom, numéro, e-mail ou ville …',
    'kunden.leer': 'Aucun client',
    'kunden.leerText': 'Créez votre premier client.',
    'kunden.akte': "Fiche client",
    'kunden.alle': "Tous les clients",
    'kunden.nichtGefunden': "Client introuvable.",
    'kunden.gespeichert': "Client enregistré.",
    'kunden.geloescht': "{name} a été supprimé.",
    'kunden.loeschenFrage': "Vraiment supprimer « {name} » ?",
    'kunden.einKunde': "1 client",
    'kunden.anzahl': "{n} clients",
    'kunden.sortieren': "Trier par",
    'kunden.letzterKaufSort': "Dernier achat",
    'kunden.keineKontakt': "Aucune coordonnée",
    'kunden.nochNichts': "aucun achat",
    'kunden.zuletzt': "dernier {datum}",
    'kunden.rechnungSchreiben': "Créer une facture",
    'kunden.umsatzJahr': "Chiffre d'affaires {jahr}",
    'kunden.netto': "HT",
    'kunden.vorjahr': "année préc. {betrag}",
    'kunden.offen': "Impayé",
    'kunden.ueberfaellig': "{betrag} en retard ({n})",
    'kunden.ueberfaelligKurz': "{betrag} en retard",
    'kunden.offenAnzahl': "{n} factures impayées",
    'kunden.offenBetrag': "{betrag} impayé",
    'kunden.meistgekauft': "Le plus acheté",
    'kunden.malGekauft': "acheté {n}×",
    'kunden.keineProdukte': "Aucun achat",
    'kunden.zahlung': "Comportement de paiement",
    'kunden.zahltNach': "moy. {n} jours",
    'kunden.puenktlich': "{n} % payés à temps",
    'kunden.nochKeineZahlung': "aucun paiement",
    'kunden.seit': "Client depuis le {datum}",
    'kunden.letzterKauf': "Dernier achat {datum}",
    'kunden.gesamt': "CA total {betrag}",
    'kunden.durchschnitt': "Facture moy. {betrag}",
    'kunden.guthaben': "Avoir {betrag}",
    'kunden.verlauf': "CA des 12 derniers mois",
    'kunden.diagrammLabel': "CA par mois",
    'kunden.keinUmsatz': "Aucun CA sur 12 mois",
    'kunden.rechnungenAnzahl': "{n} factures",
    'kunden.produkte': "Produits achetés",
    'kunden.kaeufe': "Achats",
    'kunden.menge': "Quantité",
    'kunden.umsatz': "CA",
    'kunden.mehr': "Afficher les {n}",
    'kunden.weniger': "Afficher moins",
    'kunden.dokumente': "Documents",
    'kunden.dokumenteHinweis': "{n} au total, plus récents en premier",
    'kunden.alleDokumente': "Tous",
    'kunden.nurOffene': "Impayés",
    'kunden.keineDokumente': "Aucun document",
    'kunden.zu': "pour {nummer}",
    'kunden.entwurf': "Brouillon",
    'kunden.faellig': "échéance {datum}",
    'kunden.gueltigBis': "valable jusqu'au {datum}",
    'kunden.ansehen': "Voir",
    'kunden.oeffnen': "Ouvrir",
    'kunden.art.rechnung': "Facture",
    'kunden.art.lieferschein': "Bon de livraison",
    'kunden.art.angebot': "Devis",
    'kunden.art.auftrag': "Commande",
    'kunden.art.gutschrift': "Avoir",
    'kunden.art.retoure': "Retour",
    'kunden.arten.rechnung': "Factures",
    'kunden.arten.lieferschein': "Bons de livraison",
    'kunden.arten.angebot': "Devis",
    'kunden.arten.auftrag': "Commandes",
    'kunden.arten.gutschrift': "Avoirs",
    'kunden.arten.retoure': "Retours",
    'kunden.status.entwurf': "Brouillon",
    'kunden.status.gestellt': "Émise",
    'kunden.status.bezahlt': "Payée",
    'kunden.status.storniert': "Annulée",
    'kunden.status.gesendet': "Envoyé",
    'kunden.status.angenommen': "Accepté",
    'kunden.status.abgelehnt': "Refusé",
    'kunden.status.abgelaufen': "Expiré",
    'kunden.status.offen': "Ouverte",
    'kunden.status.inArbeit': "En cours",
    'kunden.status.erledigt': "Terminé",
    'kunden.status.geliefert': "Livré",
    'kunden.status.eingelagert': "Remis en stock",
    'kunden.status.erfasst': "Enregistré",
    'kunden.status.nichtGeliefert': "Non livré",
    'kunden.status.ausgestellt': "Émis",
    'kunden.status.teilbezahlt': "Payée en partie",
    'lieferanten.titel': 'Fournisseurs',
    'lieferanten.neu': 'Nouveau fournisseur',
    'lieferanten.nummer': 'N° fournisseur',
    'lieferanten.suche': 'Nom, contact ou ville …',
    'lieferanten.akte': 'Dossier',
    'lieferanten.alle': 'Tous les fournisseurs',
    'lieferanten.artikel': 'Articles',
    'lieferanten.lieferungen': 'Livraisons',
    'lieferanten.rechnungen': 'Factures',
    'lieferanten.offen': 'Ouvert',
    'lieferanten.ueberfaellig': 'en retard',
    'lieferanten.einkaufJahr': 'Achats {jahr}',
    'lieferanten.letzteLieferung': 'Dernière livraison',
    'lieferanten.keineArtikel': 'Aucun article de ce fournisseur.',
    'lieferanten.keineLieferungen': 'Aucune entrée de marchandise.',
    'lieferanten.keineRechnungen': 'Aucune facture.',
    'lieferanten.rechnungErfassen': 'Saisir une facture',
    'lieferanten.alsBezahlt': 'Marquer comme payée',
    'lieferanten.bezahltAm': 'payée le {datum}',
    'lieferanten.faellig': 'échéance {datum}',
    'lieferanten.betrag': 'Montant (TTC)',
    'lieferanten.rechnungsnummer': 'Numéro de facture',
    'lieferanten.rechnungsdatum': 'Date de facture',
    'lieferanten.faelligAm': 'Échéance',
    'lieferanten.schonBezahlt': 'Déjà payée',
    'lieferanten.wert': 'Valeur',
    'lieferanten.wiederOffen': 'Rouvrir',
    'lieferanten.gespeichert': 'Enregistré.',
    'lieferanten.bearbeiten': 'Modifier',
    'lieferanten.loeschen': 'Supprimer',
    'lager.zugang': 'Entrée de marchandise',
    'lager.abgang': 'Sortie',
    'lager.buchen': 'Comptabiliser',
    'lager.rechnungLiegtBei': 'La facture du fournisseur est jointe',
    'lager.rechnungBezahlt': 'La facture est déjà payée',
    'lager.gebucht': 'Stock de « {name} » comptabilisé.',
    'lager.ohneLieferant': 'sans fournisseur',
    'lieferanten.leer': 'Aucun fournisseur',
    'lieferanten.leerText': 'Créez votre premier fournisseur.',
    'allg.andereSuche': 'Essayez d’autres termes.',

    'lager.titel': 'Entrepôt',
    'lager.neu': 'Nouvel article',
    'lager.artikelnummer': 'Référence',
    'lager.ean': 'EAN / code-barres',
    'lager.kategorie': 'Catégorie',
    'lager.lagerort': 'Emplacement',
    'lager.einkaufspreis': 'Prix d’achat (HT)',
    'lager.verkaufspreis': 'Prix de vente (HT)',
    'lager.mindestbestand': 'Stock minimum',
    'lager.anfangsbestand': 'Stock initial',
    'lager.wareneingang': 'Comptabilisé comme entrée de marchandises.',
    'lager.suche': 'Désignation, numéro ou EAN …',
    'lager.leer': 'Aucun article',
    'lager.leerText': 'Créez votre premier article ou importez vos données.',
    'lager.verlauf': 'Historique',
    'lager.keineBewegungen': 'Aucun mouvement',
    'lager.etikett': 'Étiquette',
    'lager.etikettenDrucken': 'Imprimer les étiquettes',
    'lager.format': 'Format',
    'lager.stueckJeArtikel': 'Exemplaires par article',
    'lager.mehrfachHinweis': 'À la réception, la même étiquette sert souvent plusieurs fois.',
    'lager.knapp': 'faible',
    'lager.auswahl': 'Sélection',
    'lager.auswahlAufheben': 'Annuler la sélection',
    'lager.alle': 'Tous',
    'lager.keinLieferant': '— aucun fournisseur —',
    'kasse.titel': 'Caisse',
    'kasse.schritt1': 'Étape 1 sur 2 — pour qui est cette vente ?',
    'kasse.kundeWaehlen': 'Choisir un client',
    'kasse.kundeSuchen': 'Rechercher un client',
    'kasse.kundeSuche': 'Nom, n° client ou ville …',
    'kasse.kundeWechseln': 'Changer de client',
    'kasse.ohneKonto': 'Sans compte client',
    'kasse.historieHinweis': 'La vente apparaîtra dans son historique de factures',
    'kasse.artikelSuchen': 'Rechercher un article',
    'kasse.artikelSuche': 'Désignation, numéro ou scanner le code-barres …',
    'kasse.keinArtikel': 'Aucun article trouvé',
    'kasse.warenkorb': 'Panier',
    'kasse.warenkorbLeeren': 'Vider le panier',
    'kasse.leer': 'Rien pour l’instant',
    'kasse.leerText': 'Cliquez sur un article à gauche.',
    'kasse.zahlart': 'Mode de paiement',
    'kasse.bezahlt': 'Payé ?',
    'kasse.vollstaendig': 'Intégralement',
    'kasse.teilweise': 'En partie',
    'kasse.nochNicht': 'Pas encore',
    'kasse.gegeben': 'Remis (pour la monnaie)',
    'kasse.rueckgeld': 'À rendre : {betrag}',
    'kasse.erhalten': 'Reçu',
    'kasse.kundePflicht': 'Pour un montant ouvert, choisissez un client — sinon personne ne sait qui doit payer.',
    'kasse.buchen': 'Enregistrer · {erhalten} reçu, {offen} ouvert',
    'kasse.kassieren': 'Encaisser · {betrag}',
    'kasse.offenBis': 'Ouvert : {betrag} · échéance {datum}',
    'kasse.bezahltMit': 'Payé · {zahlart}',
    'kasse.naechster': 'Vente suivante',
    'kasse.bar': 'Espèces',
    'kasse.karte': 'Carte',
    'kasse.ueberweisung': 'Virement',
    'kasse.abgeschlossen': 'Vente terminée',
    'kasse.alle': "Tout",
    'kasse.anzahlung': "Acompte",
    'kasse.aufRechnung': "Sur facture",
    'kasse.barAnteil': "Part espèces",
    'kasse.barKassieren': "Encaisser en espèces · {betrag}",
    'kasse.beleg': "Ticket",
    'kasse.betragBrutto': "Montant (TTC)",
    'kasse.bezahlen': "Payer",
    'kasse.bezahlenBetrag': "Payer · {betrag}",
    'kasse.bonNr': "Ticket {n}",
    'kasse.bonRabatt': "Remise sur le ticket",
    'kasse.bonVerwerfen': "Abandonner le ticket",
    'kasse.codeUnbekannt': "Aucun article avec le code {code}",
    'kasse.enthaltenSteuer': "dont {satz} % {name}",
    'kasse.fehlgeschlagen': "La vente n’a pas pu être enregistrée.",
    'kasse.freiHinweis': "Pour tout ce qui n’est pas un article : service, consigne, divers.",
    'kasse.freiStandard': "Divers",
    'kasse.freierBetrag': "Montant libre",
    'kasse.gegebenKurz': "Donné",
    'kasse.gespart': "économisé",
    'kasse.geteilt': "Partagé",
    'kasse.geteiltKassieren': "Encaisser · {bar} espèces + {karte} carte",
    'kasse.haeufig': "Fréquent",
    'kasse.kameraFehler': "Caméra indisponible — autorisez l’accès ou utilisez une douchette.",
    'kasse.karteBezahlt': "Carte payée · {betrag}",
    'kasse.karteHinweis': "Débitez le montant sur le terminal, puis confirmez ici.",
    'kasse.karteRest': "Reste par carte",
    'kasse.keinRabatt': "aucune",
    'kasse.keineArtikelAngelegt': "Aucun article créé.",
    'kasse.kundeEntfernen': "Retirer le client",
    'kasse.kundeZuordnen': "Associer un client",
    'kasse.laufkunde': "Client de passage",
    'kasse.leerNeu': "Touchez un article ou scannez un code-barres.",
    'kasse.neuerBon': "Nouveau ticket",
    'kasse.nichtAufLager': "« {name} » n’est pas en stock.",
    'kasse.nichtVorraetig': "épuisé",
    'kasse.nurNoch': "Seulement {menge} de « {name} » disponible(s).",
    'kasse.parkenHinweis': "Mettre ce ticket en attente et en commencer un nouveau",
    'kasse.passend': "juste",
    'kasse.positionen': "Lignes : {n}",
    'kasse.preis': "Prix",
    'kasse.preisAendern': "Modifier le prix",
    'kasse.rabatt': "Remise",
    'kasse.rabattText': "remise {p} %",
    'kasse.rechnungBuchen': "Enregistrer sur facture · {offen} dû",
    'kasse.rueckgeldGross': "Monnaie à rendre",
    'kasse.scannen': "Scanner un code-barres",
    'kasse.scannenHinweis': "Placez le code devant la caméra.",
    'kasse.wofuer': "Pour quoi ?",
    'kasse.zuWenig': "Il manque encore {betrag}.",

    'belege.titel': 'Documents',
    'belege.angebote': 'Devis',
    'belege.auftraege': 'Commandes',
    'belege.lieferscheine': 'Bons de livraison',
    'belege.gutschriften': 'Avoirs',
    'belege.offeneAngebote': 'Devis en cours',
    'belege.laufendeAuftraege': 'Commandes en cours',
    'belege.lieferscheineOffen': 'Livraisons en attente',
    'belege.interessent': 'Nom du prospect',
    'belege.notiz': 'Note (figure sur le document)',
    'belege.rabatt': 'Remise %',
    'belege.druckenPdf': 'Imprimer / PDF',
    'belege.entwurfLoeschen': 'Supprimer le brouillon',
    'belege.gesamtBrutto': 'Total TTC',
    'belege.einzel': 'Unité',
    'rechnungen.titel': 'Factures',
    'rechnungen.neu': 'Nouvelle facture',
    'rechnungen.schreiben': 'Créer une facture',
    'rechnungen.stellen': 'Émettre la facture',
    'rechnungen.entwurfSpeichern': 'Enregistrer comme brouillon',
    'rechnungen.entwurfHinweis': 'Un brouillon ne décrémente pas encore le stock.',
    'rechnungen.bezahltMarkieren': 'Marquer comme payée',
    'rechnungen.teilbezahlt': 'partiellement payée · reste {betrag}',
    'rechnungen.perBank': 'rapprochée via la banque',
    'rechnungen.zahlungen': 'Paiements',
    'rechnungen.nochOffen': 'Reste à payer',
    'rechnungen.bankeingang': 'Encaissement bancaire',
    'rechnungen.zahlung': 'Paiement',
    'rechnungen.zahlart': 'Mode de paiement',
    'rechnungen.zahlweg.ueberweisung': 'Virement',
    'rechnungen.zahlweg.bar': 'Espèces',
    'rechnungen.zahlweg.karte': 'Carte',
    'rechnungen.zahlungsziel': 'Délai de paiement',
    'rechnungen.zielKunde': 'Comme pour le client ({tage} jours)',
    'rechnungen.zielSofort': 'Payable immédiatement',
    'rechnungen.zielTage': '{tage} jours',
    'rechnungen.zielDatum': 'Date personnalisée',
    'rechnungen.faelligAm': 'Échéance le',
    'rechnungen.bezahlung': 'Payée ?',
    'rechnungen.offenLassen': 'Pas encore payée',
    'rechnungen.schonBezahlt': 'Déjà payée',
    'rechnungen.lieferscheinMit': 'Créer aussi le bon de livraison',
    'rechnungen.lieferschein': 'Bon de livraison',
    'rechnungen.lieferscheinDrucken': 'Imprimer le bon de livraison',
    'rechnungen.lieferscheinErstellen': 'Créer le bon de livraison',
    'rechnungen.zahlungErfassen': 'Enregistrer un paiement',
    'rechnungen.betrag': 'Montant',
    'rechnungen.zahltag': 'Date de paiement',
    'rechnungen.alsBezahltSpeichern': 'Enregistrer comme payée',
    'rechnungen.teilzahlungSpeichern': 'Enregistrer le paiement partiel',
    'rechnungen.ratenHinweis': 'Moins que le montant dû ? Le reste reste ouvert — c’est ainsi que l’on saisit des échéances.',
    'rechnungen.zahlungEntfernen': 'Supprimer le paiement',
    'rechnungen.sofort': 'payable immédiatement',
    'rechnungen.gestelltMitLs': 'Facture émise. Le bon de livraison {nummer} est prêt à imprimer.',
    'rechnungen.zahlungGespeichert': 'Paiement enregistré.',
    'rechnungen.vollBezahlt': 'La facture est entièrement payée.',
    'rechnungen.guthaben': 'Avoir client',
    'rechnungen.gutschriftTitel': 'Avoir pour cette facture',
    'rechnungen.gutschriftMenge': 'Quantité à créditer',
    'rechnungen.gutschriftGrund': 'Motif (figure sur l’avoir)',
    'rechnungen.gutschriftStellen': 'Émettre l’avoir',
    'rechnungen.gutschriftHinweis': 'Si la facture est encore ouverte, l’avoir est imputé dessus. Sinon, il devient un crédit client. Un avoir par facture.',
    'rechnungen.gutschriftErstellt': 'Avoir {nummer} émis.',
    'rechnungen.gutschriftVorhanden': 'Avoir {nummer}',
    'rechnungen.perGutschrift': 'soldée par avoir',
    'rechnungen.gutschriftDrucken': 'Imprimer l’avoir',
    'rechnungen.gutschriftSumme': 'Avoir de {betrag}',
    'rechnungen.stornieren': 'Annuler',
    'rechnungen.gutschriftErstellen': 'Créer un avoir',
    'rechnungen.drucken': 'Imprimer / PDF',
    'rechnungen.stempelBezahlt': 'Payé',
    'rechnungen.stempelAm': 'le {datum}',
    'rechnungen.keinGirocode': 'Pas de GiroCode',
    'rechnungen.girocodeText': 'Scanner avec l’appli bancaire — montant et référence sont déjà remplis.',
    'neuigkeiten.titel': 'Quoi de neuf',
    'neuigkeiten.auchNeu': 'Également nouveau',
    'neuigkeiten.fortschritt': 'Votre progression avec LagerPro',
    'neuigkeiten.updates': 'mises à jour',
    'neuigkeiten.verbesserungen': 'améliorations',
    'neuigkeiten.seit': 'depuis le {datum}',
    'neuigkeiten.mailHinweis': 'Vous pouvez désactiver les e-mails d’actualités dans les Paramètres.',
    'neuigkeiten.verstanden': 'Compris',
    'rechnungen.barverkauf': 'Vente au comptant / sans compte',
    'rechnungen.leer': 'Aucune facture',
    'rechnungen.leerText': 'Créez votre première facture.',
    'status.alle': 'Tous',
    'status.entwurf': 'Brouillon',
    'status.entwuerfe': 'Brouillons',
    'status.offen': 'En cours',
    'status.bezahlt': 'Payée',
    'status.storniert': 'Annulée',
    'retouren.titel': 'Retours',
    'retouren.erfassen': 'Enregistrer un retour',
    'retouren.alle': 'Tous les retours',
    'retouren.leer': 'Aucun retour',
    'retouren.leerText': 'Un retour se rattache toujours à une facture : choisir le client, la facture, puis les articles.',
    'retouren.schritt1': '1. Client',
    'retouren.schritt2': '2. Facture',
    'retouren.schritt3': '3. Que revient-il ?',
    'retouren.grund': 'Motif (facultatif)',
    'retouren.grundBeispiel': 'p. ex. dommage de transport, erreur de livraison',
    'retouren.berechnet': 'Facturé',
    'retouren.zurueck': 'Retour',
    'retouren.gutschrift': 'Avoir',
    'retouren.einlagern': 'Remettre en stock',
    'retouren.kundeWaehlen': 'Choisir un client …',

    'team.titel': 'Équipe',
    'team.mitglieder': 'Membres',
    'team.neu': 'Ajouter un employé',
    'team.benutzername': 'Nom d’utilisateur',
    'team.emailOptional': 'E-mail (facultatif)',
    'team.rolle': 'Rôle',
    'team.passwortHinweis': '8 caractères minimum. L’employé pourra le modifier.',
    'team.limitErreicht': 'Limite d’utilisateurs atteinte',
    'team.leer': 'Aucun membre',
    'team.leerText': 'Ajoutez votre premier employé.',
    'team.gesperrt': 'Bloqué',
    'team.alle': "Toute l’équipe",
    'team.nichtGefunden': "Personne introuvable.",
    'team.sie': "vous",
    'team.online': "En ligne",
    'team.offline': "Hors ligne",
    'team.bearbeiten': "Modifier le profil",
    'team.gespeichert': "Profil enregistré.",
    'team.kontakt': "Contact & travail",
    'team.position': "Poste",
    'team.positionBeispiel': "p. ex. Responsable entrepôt",
    'team.abteilung': "Service",
    'team.mobil': "Portable",
    'team.arbeitszeit': "Horaires",
    'team.arbeitszeitBeispiel': "p. ex. lun.–ven. 8 h–16 h",
    'team.eintritt': "Entrée",
    'team.jahre': "{n} ans dans l’équipe",
    'team.einJahr': "1 an dans l’équipe",
    'team.ersteJahr': "première année",
    'team.privat': "Personnel",
    'team.privatHinweis': "Visible uniquement par la personne et les administrateurs",
    'team.freiwillig': "Facultatif — visible uniquement par la personne et les administrateurs",
    'team.geburtstag': "Anniversaire",
    'team.alter': "{n} ans",
    'team.anschrift': "Adresse",
    'team.notfall': "Contact d’urgence",
    'team.notfallName': "Contact d’urgence (nom)",
    'team.notfallTelefon': "Contact d’urgence (téléphone)",
    'team.notfallBeispiel': "p. ex. Anna Meyer (conjointe)",
    'team.notiz': "Note interne",
    'team.notizHinweis': "Visible uniquement par les administrateurs",
    'team.keineAngaben': "Aucune information",
    'team.offeneAufgaben': "Tâches ouvertes",
    'team.keineAufgaben': "Aucune tâche ouverte",
    'team.aktualisiert': "Modifié le {datum}",
    'team.nurAdminFelder': "Poste, service, horaires et date d’entrée sont gérés par un administrateur.",
    'uebersicht.umsatzHeute': 'CA du jour',
    'uebersicht.offeneForderungen': 'Créances en cours',
    'uebersicht.lagerwert': 'Valeur du stock',
    'uebersicht.unterMindest': 'Sous le stock minimum',
    'uebersicht.nachbestellen': 'À recommander',
    'uebersicht.ueberfaellig': 'Factures en retard',
    'uebersicht.zahlungsziel': 'Délai de paiement dépassé',
    'uebersicht.umsatzMonat': 'CA par mois',
    'uebersicht.schnell': 'Actions rapides',
    'uebersicht.kassieren': 'Encaisser une vente',
    'uebersicht.wareneingang': 'Saisir une entrée',
    'aufgaben.titel': 'Tâches',
    'aufgaben.neu': 'Nouvelle tâche',
    'aufgaben.wasZuTun': 'Que faut-il faire ?',
    'aufgaben.ansichtHeute': 'Aujourd’hui',
    'aufgaben.ansichtBoard': 'Tableau',
    'aufgaben.ansichtAlle': 'Toutes',
    'aufgaben.vomBetrieb': 'De votre entreprise',
    'aufgaben.vomBetriebHinweis': 'Déduit de vos données, disparaît de lui-même une fois fait.',
    'aufgaben.meine': 'Mes tâches',
    'aufgaben.nurMeine': 'Seulement les miennes',
    'aufgaben.ueberfaellig': 'En retard',
    'aufgaben.heute': 'Aujourd’hui',
    'aufgaben.morgen': 'Demain',
    'aufgaben.dieseWoche': 'Cette semaine',
    'aufgaben.spaeter': 'Plus tard',
    'aufgaben.ohneDatum': 'Sans date',
    'aufgaben.spalteOffen': 'Ouvert',
    'aufgaben.spalteArbeit': 'En cours',
    'aufgaben.spalteErledigt': 'Terminé',
    'aufgaben.schnell': 'Nouvelle tâche — p. ex. « Demain appeler Meyer !haute @Mia »',
    'aufgaben.schnellHilfe': 'aujourd’hui · demain · lundi · 15.11. · !haute · !basse · @Nom',
    'aufgaben.oeffnen': 'Ouvrir',
    'aufgaben.zurueckstellen': 'Reporter',
    'aufgaben.tage': '{n} jours',
    'aufgaben.uebernehmen': 'Transformer en tâche',
    'aufgaben.beschreibung': 'Description',
    'aufgaben.faellig': 'Échéance',
    'aufgaben.prioritaet': 'Priorité',
    'aufgaben.niedrig': 'Basse',
    'aufgaben.normal': 'Normale',
    'aufgaben.zustaendig': 'Responsable',
    'aufgaben.niemand': 'Personne',
    'aufgaben.wiederholung': 'Répétition',
    'aufgaben.keine': 'Aucune',
    'aufgaben.taeglich': 'Quotidienne',
    'aufgaben.woechentlich': 'Hebdomadaire',
    'aufgaben.monatlich': 'Mensuelle',
    'aufgaben.checkliste': 'Liste de contrôle',
    'aufgaben.punktNeu': 'Ajouter un point',
    'aufgaben.loeschen': 'Supprimer',
    'aufgaben.loeschenFrage': 'Supprimer cette tâche ?',
    'aufgaben.timerStart': 'Démarrer le chrono',
    'aufgaben.timerStopp': 'Arrêter le chrono',
    'aufgaben.allesErledigt': 'Rien d’urgent — tout roule.',
    'aufgaben.keineEigenen': 'Aucune tâche ouverte.',
    'aufgaben.folge': 'Prochaine le {datum}',
    'aufgaben.zieheHier': 'Déposer ici',
    'aufgaben.erledigtHeute': 'Terminé aujourd’hui',
    'aufgaben.schliessen': 'Fermer',
    'aufgaben.aktionGeliefert': 'Marquer comme livré',
    'aufgaben.aktionBezahlt': 'Marquer comme payée',
    'aufgaben.erledigtMeldung': 'Terminé.',
    'aufgaben.zurueckgestellt': 'Reporté jusqu’au {datum}.',
    'aufgaben.uebernommen': 'Transformé en tâche.',
    'aufgaben.auto.nachbestellen': 'Recommander {artikel}',
    'aufgaben.auto.nachbestellenText': 'Stock {bestand} {einheit}, minimum {mindestbestand}',
    'aufgaben.auto.bei': 'chez {lieferant}',
    'aufgaben.auto.ausliefern': 'Livrer le bon {nummer}',
    'aufgaben.auto.ausliefernText': 'à {kunde}',
    'aufgaben.auto.nachfassen': 'Relancer le devis {nummer}',
    'aufgaben.auto.nachfassenText': '{kunde} · sans réponse depuis {tage} jours',
    'aufgaben.auto.entwurf': 'Émettre le brouillon de facture',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · en attente depuis {tage} jours',
    'aufgaben.auto.support': 'Lire la réponse du support',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'Comptabiliser les pièces scannées',
    'aufgaben.auto.belegscanText': '{anzahl} en attente de vérification',
    'aufgaben.auto.mahnen': 'Relancer le paiement : {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} dû · {tage} jours de retard',
    'aufgaben.auto.bezahlen': 'Payer le fournisseur : {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · échéance {datum}',
    'aufgaben.auto.zuordnen': 'Rapprocher les encaissements',
    'aufgaben.auto.zuordnenText': '{anzahl} encaissements en attente',
    'aufgaben.auto.bank': 'Reconnecter la banque : {bank}',
    'aufgaben.auto.bankAbgelaufen': 'Le consentement a expiré — plus aucune opération n’arrive.',
    'aufgaben.auto.bankEndet': 'Le consentement se termine le {datum}.',
    'aufgaben.auto.ustva': 'Déclaration de TVA {monat}',
    'aufgaben.auto.ustvaText': 'À payer {betrag} · avant le {datum}',
    'tafel.titel': "Tableau",
    'tafel.untertitel': "Tâches, rendez-vous et échéances en un coup d’œil",
    'tafel.ansicht.woche': "Semaine",
    'tafel.ansicht.monat': "Mois",
    'tafel.ansicht.liste': "Liste",
    'tafel.neuerZettel': "Nouvelle note",
    'tafel.zurueck': "Précédent",
    'tafel.vor': "Suivant",
    'tafel.kw': "Sem. {n}",
    'tafel.naechste30': "Les 30 prochains jours",
    'tafel.art.aufgabe': "Tâche",
    'tafel.art.termin': "Rendez-vous",
    'tafel.art.frist': "Échéance",
    'tafel.liegenGeblieben': "En retard",
    'tafel.ablage': "Bac",
    'tafel.ablageHinweis': "Sans jour — quand il y a le temps",
    'tafel.leerTag': "+ Note",
    'tafel.weitere': "+{n} de plus",
    'tafel.erledigt': "Terminé",
    'tafel.wiederOffen': "Rouvrir",
    'tafel.ziehen': "Glisser pour déplacer vers un autre jour",
    'tafel.automatisch': "automatique",
    'tafel.schnell': "Qu’y a-t-il à faire ?",
    'tafel.schnellGross': "Qu’y a-t-il à faire ? p. ex. « 14:30 rdv Meyer » ou « envoyer devis !haute »",
    'tafel.schnellHilfe': "aujourd’hui · demain · vendredi · 15.11. · 14:30 · !haute · @nom",
    'tafel.inEinerWoche': "Dans une semaine",
    'tafel.artWaehlen': "Choisir le type",
    'tafel.bisWann': "Pour quand ?",
    'tafel.wann': "Quand ?",
    'tafel.von': "De",
    'tafel.bis': "À",
    'tafel.ort': "Lieu",
    'tafel.notiz': "Note",
    'tafel.mehr': "Plus d’options",
    'tafel.anheften': "Épingler",
    'tafel.fristHinweis': "Issu de vos données, disparaît tout seul une fois réglé.",
    'tafel.kurzTitel': "Mon tableau",
    'tafel.kurzLiegen': "{n} en retard",
    'tafel.zurTafel': "Ouvrir le tableau",
    'tafel.heuteFrei': "Rien de prévu.",
    'tafel.frist.angebot': "Le devis expire : {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "Livraison : {nummer}",
    'tafel.frist.lieferungText': "à {kunde}",
    'tafel.frist.zahlungseingang': "Paiement attendu : {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "Facture fournisseur due : {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "Déclaration de TVA",
    'tafel.frist.ustvaText': "pour {monat}",
    'aufgaben.leer': 'Aucune tâche',
    'aufgaben.leerText': 'Créez la première ci-dessus.',
    'aufgaben.hoch': 'Haute',
    'aufgaben.laeuft': 'en cours',
    'chat.titel': 'Messagerie',
    'chat.wemSchreiben': 'À qui écrire ?',
    'chat.leer': 'Aucun message',
    'chat.senden': 'Envoyer',
    'chat.dateiAnhaengen': 'Joindre un fichier',
    'chat.anhangEntfernen': 'Retirer la pièce jointe',
    'chat.zurListe': 'Retour à la liste',
    'chat.alleImTeam': 'Toute l’équipe',
    'chat.herunterladenTipp': 'appuyer pour télécharger',

    'basis.tarifAb': 'Disponible à partir de l’offre',
    'basis.tarifWechsel': 'Vous pouvez changer d’offre sous « Offre et facturation ».',
    'basis.support': 'Contactez le support pour activer cette fonction.',
    'positionen.hinzufuegen': 'Ajouter une ligne',
    'positionen.freiText': '— saisie libre —',
    'passwort.festlegen': 'Définir le mot de passe',
    'passwort.uebermittelt': 'Mot de passe reçu',
    'passwort.neu': 'Nouveau mot de passe',
    'passwort.wiederholen': 'Répéter le nouveau mot de passe',
    'tarif.titel': 'Offre et facturation',
    'tarif.aktuell': 'Offre actuelle',
    'tarif.enthalten': 'Inclus',
    'tarif.nichtEnthalten': 'Non inclus',
    'tarif.proMonat': '/ mois',
    'tarif.sonderkondition': 'Condition spéciale',
    'tarif.verlauf': 'Historique des offres',
    'tarif.mandat': 'Mandat de prélèvement',
    'tarif.angebot': 'Votre offre personnelle',
    'kopie.titel': "Copie des données",
    'kopie.text': "Vous pouvez demander une copie de vos données à partir de la résiliation et jusqu’à 30 jours après la fin du contrat.",
    'kopie.frist': "Possible jusqu’au {datum}.",
    'kopie.anfordern': "Demander une copie",
    'kopie.angefordert': "Demandée le {datum}. Vous recevrez un e-mail dès que la copie sera prête.",
    'kopie.bereit': "Votre copie des données est prête : {format}.",
    'kopie.passwort': "Votre mot de passe de connexion",
    'kopie.passwortHinweis': "Ce mot de passe chiffre le fichier ZIP — ouvrez-le par double-clic avec le même mot de passe.",
    'kopie.herunterladen': "Télécharger",
    'kopie.geladen': "Téléchargé. Conservez le fichier en lieu sûr.",
    'kopie.formatLesbar': "PDF et tableaux (Excel)",
    'kopie.formatWiederherstellung': "Fichier de restauration",
    'kopie.nurAdmin': "Un administrateur de votre entreprise demande la copie des données.",
    'abo.agbAnsehen': "Voir les CGV",
    'abo.agbZustimmung': "J’ai lu les CGV et je les accepte.",
    'abo.aufbewahrung': "Pensez à vos obligations de conservation : factures et pièces comptables 8 ans, livres et comptes annuels 10 ans.",
    'abo.aufpreisHinweis': "Sur la prochaine facture : {betrag} HT de supplément pour le changement d’offre du {datum}.",
    'abo.bestaetigen': "Confirmer le changement",
    'abo.erstZuruecknehmen': "Le contrat est résilié. Pour changer d’offre, annulez d’abord la résiliation.",
    'abo.gekuendigt': "Résilié au {datum}. D’ici là, utilisez LagerPro normalement.",
    'abo.gekuendigtMeldung': "Résilié au {datum}. Une confirmation vous est envoyée par e-mail.",
    'abo.gewechselt': "Vous utilisez maintenant {plan}.",
    'abo.hochstufen': "Passer à l’offre supérieure",
    'abo.ihrTarif': "Votre offre",
    'abo.jetztKuendigen': "Résilier maintenant",
    'abo.kostenpflichtig': "Changer (payant)",
    'abo.kuendigen': "Résilier le contrat",
    'abo.kuendigenText': "La résiliation prend effet le {datum}. D’ici là, utilisez LagerPro normalement ; ensuite, vous pouvez demander une copie de vos données pendant 30 jours.",
    'abo.kuendigenTextTest': "Vous êtes dans votre mois gratuit : le contrat prend fin avec lui le {datum}, rien n’est facturé.",
    'abo.kuendigenTitel': "Résilier le contrat",
    'abo.mandatAendern': "Modifier les coordonnées bancaires",
    'abo.mandatBisTestende': "Veuillez déposer un mandat de prélèvement SEPA d’ici là — sinon LagerPro se met en pause après le mois gratuit.",
    'abo.nurAdmin': "L’offre et le contrat sont gérés par un administrateur de votre entreprise.",
    'abo.runterstufen': "Passer à l’offre inférieure",
    'abo.sonderkondition': "Vous bénéficiez de conditions particulières. Pour changer d’offre, contactez le support.",
    'abo.testDanach': "Ensuite, {plan} coûte {preis} par mois.",
    'abo.testLaeuft': "Mois gratuit : encore {n} jours, jusqu’au {datum} inclus.",
    'abo.vorgemerkt': "Passage à {plan} prévu à partir du {datum}.",
    'abo.vorgemerktMeldung': "Passage à {plan} prévu à partir du {datum}.",
    'abo.vormerkungWeg': "Changement prévu annulé.",
    'abo.vormerkungZurueck': "Annuler le changement prévu",
    'abo.waehlen': "Choisir",
    'abo.wechselHoch': "Prend effet immédiatement. Si ce mois est déjà facturé, la différence pour les jours restants figure sur la prochaine facture. Dès le mois prochain {preis} par mois.",
    'abo.wechselRunter': "Prend effet le {datum}. D’ici là, vous conservez toutes les fonctions de {plan}.",
    'abo.wechselTest': "Prend effet immédiatement. Gratuit pendant le mois gratuit ; ensuite {preis} par mois.",
    'abo.wechselTitel': "Passer à {plan}",
    'abo.wechselnHinweis': "La montée en gamme est immédiate, la descente en fin de mois.",
    'abo.wechselnTitel': "Changer d’offre",
    'abo.zurueckMeldung': "Résiliation annulée — votre contrat continue.",
    'abo.zuruecknehmen': "Annuler la résiliation",
    'abo.beendetTitel': "Votre contrat a pris fin",
    'abo.beendetText': "Votre contrat LagerPro a pris fin le {datum}.",
    'abo.beendetKopie': "Vous pouvez demander une copie de vos données jusqu’au {bis} — elles seront ensuite supprimées.",
    'firma.titel': 'Entreprise',
    'firma.anschrift': 'Adresse et contact',
    'firma.steuerRegister': 'Fiscalité et registre',
    'firma.girocode': 'Imprimer un code de paiement sur les factures',
    'firma.girocodeText': 'Un QR code que le client scanne avec son application bancaire — bénéficiaire, montant et référence sont déjà remplis. Affiché uniquement sur les factures impayées par virement.',
    'firma.ibanFehlt': 'Le code de paiement nécessite un IBAN.',
    'firma.ibanUngueltig': 'Cet IBAN a une clé de contrôle invalide. Vérifiez-le — en attendant, aucun code n\'est imprimé.',
    'firma.bank': 'Coordonnées bancaires',
    'firma.fusstext': 'Texte de pied de page',
    'firma.logo': 'Logo',
    'firma.logoWaehlen': 'Choisir un logo',
    'firma.logoEntfernen': 'Retirer le logo',
    'firma.speichern': 'Enregistrer les données',
    'firma.aufRechnung': 'Figure sur chaque facture',
    'firma.pflichtangaben': 'Mentions obligatoires',
    'firma.imFuss': 'Affiché en pied de facture',
    'passwort.willkommen': 'Bienvenue',
    'passwort.warnung': 'Votre mot de passe actuel vous a été transmis et est donc connu d’une autre personne. Définissez le vôtre maintenant.',
    'passwort.regel8': '8 caractères minimum',
    'passwort.regelGross': 'Majuscules et minuscules',
    'passwort.regelZiffer': 'Au moins un chiffre',
    'passwort.regelGleich': 'Les deux saisies correspondent',
    'passwort.regelAnders': 'Différent du mot de passe reçu',
    'passwort.fortfahren': 'Définir et continuer',
    'lager.anzahlArtikel': 'articles',
    'lager.nurKnappe': 'Stock faible seulement',
    'lager.etiketten': 'Étiquettes',

    'zfa.titel': 'Confirmation',
    'zfa.gesendet': 'Nous vous avons envoyé un code à',
    'zfa.code': 'Code reçu par e-mail',
    'zfa.bestaetigen': 'Confirmer',
    'zfa.gueltig': 'Le code est valable {min} minutes.',
    'zfa.andererZugang': 'Autre connexion',
    'zfa.einstellung': 'Connexion à deux facteurs',
    'zfa.erklaerung': 'En plus du mot de passe, un code est envoyé par e-mail à chaque connexion. Connaître le mot de passe ne suffit alors plus.',
    'zfa.aktiv': 'Activé',
    'zfa.aus': 'Désactivé',
    'zfa.einschalten': 'Activer',
    'zfa.ausschalten': 'Désactiver',
    'zfa.mailFrage': 'À quelle adresse envoyer les codes ?',
    'zfa.codeSenden': 'Envoyer le code',
    'zfa.passwortFrage': 'Saisissez votre mot de passe pour désactiver',
    'zfa.keinVersand': 'Aucun envoi d’e-mails n’est configuré. Sans cela, aucun code ne peut être livré.',
    'zfa.eingeschaltet': 'La connexion à deux facteurs est activée.',
    'zfa.ausgeschaltet': 'La connexion à deux facteurs est désactivée.',
    'zfa.methodeApp': "Application d’authentification",
    'zfa.methodeMail': "Code par e-mail",
    'zfa.erklaerungBeide': "En plus du mot de passe, LagerPro demande un code à chaque connexion — depuis une application d’authentification sur votre téléphone ou par e-mail. Le mot de passe seul ne suffit plus.",
    'zfa.ersatzUebrig': "{n} codes de secours restants",
    'zfa.mitApp': "Activer avec une application",
    'zfa.mitMail': "Activer par e-mail",
    'zfa.zurApp': "Passer à l’application",
    'zfa.ersatzNeu': "Nouveaux codes de secours",
    'zfa.appEmpfohlen': "Recommandé : l’application. Elle fonctionne hors ligne et sans e-mail — p. ex. Google Authenticator ou Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "Créer de nouveaux codes de secours — les anciens deviennent invalides.",
    'zfa.passwortWeiter': "Veuillez saisir votre mot de passe",
    'zfa.weiter': "Continuer",
    'zfa.schritt1': "Ouvrez une application d’authentification (p. ex. Google Authenticator) et choisissez « Ajouter un code ».",
    'zfa.scannen': "Scannez ce code QR.",
    'zfa.schritt3': "Saisissez ci-dessous le code à 6 chiffres de l’application.",
    'zfa.manuell': "Impossible de scanner ? Saisissez cette clé :",
    'zfa.appCode': "Code de l’application",
    'zfa.ersatzTitel': "Vos codes de secours",
    'zfa.ersatzHinweis': "Si vous perdez votre téléphone : chaque code remplace une fois le code de l’application. Conservez-les en sécurité — ils ne s’affichent qu’une fois.",
    'zfa.kopieren': "Copier",
    'zfa.kopiert': "Copié",
    'zfa.herunterladen': "Enregistrer",
    'zfa.gesichert': "J’ai conservé les codes en lieu sûr",
    'zfa.fertig': "Terminé",
    'zfa.appText': "Ouvrez votre application d’authentification et saisissez le code à 6 chiffres pour LagerPro.",
    'zfa.ersatzText': "Saisissez un de vos codes de secours. Chacun ne sert qu’une fois.",
    'zfa.ersatzCode': "Code de secours",
    'zfa.ersatzNutzen': "Pas de téléphone ? Utiliser un code de secours",
    'zfa.appCodeNutzen': "Utiliser le code de l’application",
    'nav.app': "Application",
    'start.nav.app': "Application",
    'desktop.titel': "LagerPro en application",
    'desktop.text': "Sa propre fenêtre et icône dans le Dock ou la barre des tâches, démarre en un clic. Connectez-vous une fois — vous restez connecté 90 jours.",
    'desktop.inApp': "Vous utilisez l’application de bureau",
    'desktop.inAppText': "Version {version}. L’interface se met à jour toute seule.",
    'desktop.alsWebApp': "LagerPro est installé comme application sur cet ordinateur.",
    'desktop.installiert': "Installé — LagerPro se trouve maintenant parmi vos applications.",
    'desktop.direktTitel': "Installer directement depuis le navigateur",
    'desktop.schnell': "secondes",
    'desktop.direktText': "Sans téléchargement, sans avertissement, toujours à jour.",
    'desktop.safari': "Dans Safari : menu « Fichier » → « Ajouter au Dock » (macOS Sonoma ou plus récent).",
    'desktop.firefox': "Firefox ne peut pas installer d’applications web — utilisez Chrome ou Edge, ou téléchargez ci-dessous.",
    'desktop.chromeLeiste': "Cliquez sur l’icône « Installer » dans la barre d’adresse — ou menu ⋮ → « Installer LagerPro ».",
    'desktop.andererBrowser': "Le plus simple avec Chrome, Edge ou Safari — ou téléchargez l’application ci-dessous.",
    'desktop.jetztInstallieren': "Installer",
    'desktop.downloadTitel': "Ou télécharger",
    'desktop.version': "Version {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 et 11, 64 bits",
    'desktop.macApple': "Mac avec puce Apple",
    'desktop.macAppleUnter': "M1, M2, M3, M4 · depuis 2020",
    'desktop.macIntel': "Mac avec puce Intel",
    'desktop.macIntelUnter': "Mac plus anciens jusqu’en 2020",
    'desktop.passtZuIhnen': "Adapté à cet ordinateur",
    'desktop.laden': "Télécharger",
    'desktop.bald': "Bientôt disponible",
    'desktop.warnungTitel': "Avertissement à la première ouverture ?",
    'desktop.warnungText': "Les téléchargements ne sont pas encore enregistrés auprès de Microsoft et Apple ; votre ordinateur demande donc une fois au premier lancement.",
    'desktop.warnungWindows': "Sur « Windows a protégé votre ordinateur », cliquez sur « Informations complémentaires » puis « Exécuter quand même ».",
    'desktop.warnungMac': "Glissez LagerPro dans « Applications ». Si un message apparaît, allez dans « Réglages Système → Confidentialité et sécurité » et cliquez sur « Ouvrir quand même » (macOS plus ancien : clic droit → « Ouvrir »).",
    'desktop.anmeldung': "Dans les deux cas : connexion une fois avec mot de passe (et code à deux facteurs), puis 90 jours sans saisie. La déconnexion y met fin immédiatement.",
    'desktop.mehrInfos': "Tout sur l’application",

    'erinnerung.titel': 'Relances de paiement',
    'erinnerung.erklaerung': 'Si une facture est en retard, LagerPro envoie automatiquement une relance polie à votre client — en votre nom, avec votre adresse en réponse.',
    'erinnerung.nachTagen': 'Relancer après … jours de retard',
    'erinnerung.wiederholung': 'Répéter après … jours',
    'erinnerung.maxAnzahl': 'Au plus … relances par facture',
    'erinnerung.faellig': 'Concerné au prochain passage',
    'erinnerung.jetzt': 'Envoyer maintenant',
    'erinnerung.keineFaellig': 'Aucune facture en retard actuellement.',
    'erinnerung.firmenmailFehlt': 'Indiquez votre e-mail sous « Entreprise » — sinon vos clients ne peuvent pas répondre.',
    'erinnerung.keineFuerKunden': 'Aucune relance à ce client',
  },

  es: {
    'start.nav.funktionen': 'Funciones',
    'start.nav.belegkette': 'Cadena de documentos',
    'start.nav.preise': 'Precios',
    'start.kostenlosStarten': 'Empezar gratis',
    'start.themaDunkel': 'Tema oscuro',
    'start.themaHell': 'Tema claro',
    'start.sprache': 'Idioma',
    'start.hero.marke': 'Conforme a GoBD · Servidores en la UE',
    'start.hero.titel1': 'Almacén, caja y contabilidad.',
    'start.hero.titel2': 'Un sistema en vez de cinco.',
    'start.hero.text': 'LagerPro lleva su negocio desde el presupuesto hasta la declaración de impuestos: existencias, ventas, documentos y contabilidad se alimentan entre sí. Sin exportar, sin volver a teclear, sin mantener nada dos veces.',
    'start.hero.konto': 'Ya tengo una cuenta',
    'start.hero.fussnote': 'Sin tarjeta de crédito · Listo en dos minutos · Cancelable cada mes',
    'start.vorschau.umsatz': 'Ventas de hoy',
    'start.vorschau.offen': 'Facturas pendientes',
    'start.vorschau.lagerwert': 'Valor del almacén',
    'start.vorschau.knapp': 'Bajo mínimos',
    'start.vorschau.gruss': 'Buenos días, Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'Documentos recientes',
    'start.vorschau.angebot': 'Presupuesto',
    'start.vorschau.lieferschein': 'Albarán',
    'start.vorschau.rechnung': 'Factura',
    'start.vertrauen1t': 'Conforme a GoBD',
    'start.vertrauen1x': 'Numeración sin huecos, documentos inalterables, registros encadenados por hash — el estándar contable alemán',
    'start.vertrauen2t': 'Exportación GDPdU',
    'start.vertrauen2x': 'Entrega de datos Z3 con index.xml — listo para cuando llegue la inspección',
    'start.vertrauen3t': 'Servidores en la UE',
    'start.vertrauen3x': 'Sin cesión de datos, sin rastreadores publicitarios, RGPD desde el primer día',
    'start.vertrauen4t': '10 años de conservación',
    'start.vertrauen4x': 'Según el § 147 de la ley tributaria alemana, automáticamente — usted no tiene que acordarse de nada',
    'start.f1t': 'Fotografíe el documento y listo',
    'start.f1x': 'Haga una foto del albarán o la factura con el móvil: el asistente extrae las líneas, las cantidades y el proveedor. Usted lo revisa una vez y la mercancía queda en el almacén. También responde preguntas sobre existencias y cifras, escritas o habladas.',
    'start.f2t': 'El almacén bajo control',
    'start.f2x': 'Existencias en tiempo real, aviso de stock mínimo, entrada de mercancía y etiquetas con código de barras en cinco tamaños.',
    'start.f3t': 'Una caja para el mostrador',
    'start.f3x': 'Elegir cliente, escanear artículos, cobrar. El efectivo sigue valiendo y el stock se descuenta solo.',
    'start.f4t': 'Todos los documentos',
    'start.f4x': 'Presupuesto, confirmación de pedido, albarán, factura, abono y anulación — en PDF con su logotipo.',
    'start.f5t': 'Contabilidad que cuadra',
    'start.f5x': 'Cuenta de ingresos y gastos con las líneas del formulario, declaración de IVA, libro de viajes y exportación DATEV para su asesor.',
    'start.f6t': 'Devoluciones sin caos',
    'start.f6x': 'Siempre a través de la factura: cliente, documento, líneas. La mercancía vuelve al almacén y el saldo a la cuenta del cliente.',
    'start.f7t': 'El equipo a bordo',
    'start.f7x': 'Tareas con cronómetro, chat con avisos, presencia — sin necesitar una segunda aplicación.',
    'start.funktionen.titel': 'Todo lo que el negocio necesita a diario',
    'start.funktionen.text': 'Cada área conoce los datos de las demás. Si vende algo, baja el stock, nace el documento y la contabilidad tiene el apunte — en el mismo instante.',
    'start.kette.titel': 'Un trabajo, cuatro documentos, nada que teclear',
    'start.kette.text': 'Un clic convierte el presupuesto en confirmación de pedido, esa en albarán y ese en factura. Líneas y precios viajan con ellos. La mercancía sale del almacén exactamente una vez.',
    'start.kette.schritt': 'Paso',
    'start.kette1t': 'Presupuesto',
    'start.kette1x': 'Lo que costará',
    'start.kette2t': 'Pedido',
    'start.kette2x': 'El cliente acepta',
    'start.kette3t': 'Albarán',
    'start.kette3x': 'La mercancía sale',
    'start.kette4t': 'Factura',
    'start.kette4x': 'Entra el dinero',
    'start.kette.fuss': 'Si algo vuelve, están el abono y la factura de anulación — con el asiento contrario que exige Hacienda.',
    'start.preise.titel': 'Precios sin letra pequeña',
    'start.preise.text': "Cancelable mensualmente, todos los precios sin IVA. Cada plan empieza con 30 días gratis, sin datos bancarios.",
    'start.preise.laedt': 'Cargando planes …',
    'start.preise.fehler': 'Los planes no están disponibles ahora mismo. Escríbanos y le indicamos las condiciones.',
    'start.preise.top': 'El más elegido',
    'start.preise.proMonat': '/ mes',
    'start.preise.sofort': "Probar 30 días gratis",
    'start.preise.waehlen': "Probar {tarif} 30 días gratis",
    'start.ende.titel': 'Empezar hoy, tener la visión mañana',
    'start.ende.text': 'Cree la cuenta, introduzca los artículos, escriba la primera factura. Sus datos anteriores puede traerlos después.',
    'start.fuss.hinweis': 'Servidores en la UE · Conforme a GoBD',
    'nav.support': 'Soporte',
    'support.vonAnbieter': 'De LagerPro',
    'support.titel': 'Soporte',
    'support.untertitel': 'Preguntas, errores, deseos: escríbanos aquí. Toda la conversación queda en un mismo sitio.',
    'support.neu': 'Nueva solicitud',
    'support.betreff': 'Asunto',
    'support.kategorie': '¿De qué se trata?',
    'support.kat.frage': 'Pregunta',
    'support.kat.fehler': 'Algo no funciona',
    'support.kat.wunsch': 'Deseo o idea',
    'support.kat.abrechnung': 'Facturación y plan',
    'support.kat.sonstiges': 'Otros',
    'support.nachricht': 'Mensaje',
    'support.nachrichtPlatzhalter': 'Describa con la mayor precisión posible qué ha pasado y qué esperaba.',
    'support.anhang': 'Adjuntar captura o archivo',
    'support.anhangHinweis': 'Hasta 5 archivos, 25 MB cada uno',
    'support.senden': 'Enviar solicitud',
    'support.gesendet': 'Su solicitud {nummer} ha llegado. Recibirá un correo en cuanto haya respuesta.',
    'support.leer': 'Aún no hay solicitudes',
    'support.leerText': 'Si algo no funciona o tiene una pregunta, escríbanos.',
    'support.status.offen': 'Recibida',
    'support.status.in_arbeit': 'En curso',
    'support.status.wartet': 'Respuesta disponible',
    'support.status.erledigt': 'Resuelta',
    'support.zurueck': 'Todas las solicitudes',
    'support.antworten': 'Responder',
    'support.antwortPlatzhalter': 'Su mensaje …',
    'support.erledigt': 'Marcar como resuelta',
    'support.erledigtHinweis': 'Resuelta. Si aún queda algo pendiente, siga escribiendo y la solicitud se reabrirá.',
    'support.sie': 'Usted',
    'support.team': 'Soporte de LagerPro',
    'support.ungelesen': 'nuevo',
    'support.glocke': 'Respuesta del soporte',
    'support.anhaengen': 'Adjuntar',
    'einheit.stueck': 'Unidad',
    'einheit.paar': 'Par',
    'einheit.satz': 'Juego',
    'einheit.paket': 'Paquete',
    'einheit.karton': 'Caja',
    'einheit.palette': 'Palé',
    'einheit.rolle': 'Rollo',
    'einheit.flasche': 'Botella',
    'einheit.dose': 'Lata',
    'einheit.beutel': 'Bolsa',
    'einheit.minute': 'min',
    'einheit.stunde': 'h',
    'einheit.tag': 'Día',
    'einheit.woche': 'Semana',
    'einheit.monat': 'Mes',
    'einheit.jahr': 'Año',
    'einheit.pauschale': 'Tarifa plana',
    'einheit.gruppe.anzahl': 'Cantidad y envase',
    'einheit.gruppe.gewicht': 'Peso',
    'einheit.gruppe.volumen': 'Volumen',
    'einheit.gruppe.laenge': 'Longitud y superficie',
    'einheit.gruppe.zeit': 'Tiempo',
    'einheit.gruppe.sonstiges': 'Otros',
    'einheit.andere': 'Otra unidad …',
    'einheit.eigene': 'Unidad propia',
    'einheit.eigenePlatzhalter': 'p. ej. manojo, cajón',
    'positionen.umgerechnet': '= {menge} del almacén',
    'positionen.passtNicht': 'Este artículo se lleva en {einheit}; esta unidad no se puede convertir.',
    'positionen.jeEinheit': 'por {einheit}',
    'nav.assistent': 'Asistente de IA',
    'assistent.datenschutz': 'Para el análisis, el documento y su pregunta se envían a nuestro proveedor de IA y se usan solo para responder, nunca para entrenar modelos. Sus datos de almacén permanecen en nuestro servidor.',
    'pruef.titel': 'Revisar {art}',
    'pruef.beleg': 'el documento',
    'pruef.lieferschein': 'el albarán',
    'pruef.eingangsrechnung': 'la factura de compra',
    'pruef.gutschrift': 'el abono',
    'pruef.bestellung': 'el pedido',
    'pruef.kassenbon': 'el tique',
    'pruef.erkannt': '% reconocido',
    'pruef.ohneNummer': 'Sin número ni fecha',
    'pruef.schwerLesbar': 'Este documento era difícil de leer',
    'pruef.schwerLesbarText': 'Revise cada línea. En una foto borrosa o torcida el reconocimiento confunde cifras. Una foto nueva con buena luz suele ayudar más que corregir.',
    'pruef.nichtAusgewertet': 'No analizado',
    'pruef.nurBilder': 'solo se pueden leer fotos y PDF',
    'pruef.vorhandener': 'Existente',
    'pruef.neuAnlegen': 'Crear nuevo',
    'pruef.ohne': 'Ninguno',
    'pruef.bitteWaehlen': '— elija —',
    'pruef.gewaehlt': '{a} de {b} seleccionadas',
    'pruef.alleUebernehmen': 'Seleccionar todo',
    'pruef.keine': 'Ninguna',
    'pruef.keinePositionen': 'No se reconoció ninguna línea en el documento.',
    'pruef.warenwert': 'Valor de la selección',
    'pruef.rechnungsbetrag': 'Importe de la factura',
    'pruef.brauchtBuchhaltung': 'Para registrar el gasto se necesita la contabilidad, incluida desde el plan Professional.',
    'pruef.alsAusgabe': 'Registrar el importe como gasto',
    'pruef.alsAusgabeText': 'El documento queda adjunto como justificante, así el gasto está completo para la contabilidad.',
    'pruef.brutto': 'Importe bruto',
    'pruef.steuersatz': 'Tipo impositivo',
    'pruef.belegnummer': 'Número de documento',
    'pruef.belegdatum': 'Fecha del documento',
    'pruef.lastschrift': 'Domiciliación',
    'pruef.keineWare': 'Sin existencias',
    'pruef.keineWareText': 'Cuenta para el importe de la factura, pero no entra en el almacén.',
    'pruef.grund.keineWare': 'Envío, comisión o descuento — no es un artículo almacenable',
    'pruef.privat': 'Adelantado en privado',
    'pruef.erstMitKnopf': 'Nada se registra hasta pulsar este botón.',
    'pruef.buchen': 'Aceptar y registrar',
    'pruef.wirdGebucht': 'Registrando',
    'pruef.zugeordnet': 'Asignado',
    'pruef.vermutet': 'Probable',
    'pruef.neu': 'Nuevo',
    'pruef.schlechtLesbar': 'Poco legible',
    'pruef.aendern': 'Cambiar',
    'pruef.zuklappen': 'Contraer',
    'pruef.wirdAngelegt': 'se creará como artículo nuevo',
    'pruef.artikel': 'Artículo',
    'pruef.nichtZugeordnet': '— sin asignar —',
    'pruef.alsNeuenArtikel': 'Crear como artículo nuevo',
    'pruef.einkaufspreis': 'Precio de compra',
    'pruef.verkaufspreis': 'Precio de venta',
    'pruef.spaeterErgaenzen': 'Se puede añadir después',
    'pruef.ean': 'EAN',
    'pruef.je': 'por',
    'pruef.grund.ean': 'El EAN coincide',
    'pruef.grund.nummer': 'La referencia coincide',
    'pruef.grund.name': 'La denominación coincide exactamente',
    'pruef.grund.ustId': 'El NIF-IVA coincide',
    'pruef.grund.aehnlich': 'Denominación parecida — revíselo',
    'pruef.grund.keiner': 'No hay artículo equivalente en el almacén',
    'pruef.kat.wareneinkauf': 'Compra de mercancía',
    'pruef.kat.buero': 'Material de oficina',
    'pruef.kat.fahrzeug': 'Gastos de vehículo',
    'pruef.kat.werbung': 'Publicidad',
    'pruef.kat.kommunikation': 'Teléfono e internet',
    'pruef.kat.raumkosten': 'Alquiler y local',
    'pruef.kat.sonstiges': 'Otros gastos de explotación',
    'assistent.titel': 'Asistente de IA',
    'assistent.rest': 'Disponible este mes',
    'assistent.scans': 'escaneos de documentos',
    'assistent.fragen': 'preguntas',
    'assistent.testmodus': 'Modo de prueba',
    'assistent.testmodusText': 'No hay acceso de IA configurado. Los documentos devuelven datos de ejemplo y las respuestas son provisionales. Cuando el acceso esté configurado, el asistente trabajará con sus datos reales.',
    'assistent.verlaufLoeschen': 'Borrar historial',
    'assistent.denkt': 'Un momento',
    'assistent.platzhalter': 'Pregunte o fotografíe un documento',
    'assistent.hoertZu': 'Escuchando',
    'assistent.sprechen': 'Hablar',
    'assistent.vorlesen': 'Leer en voz alta',
    'assistent.stopp': 'Parar',
    'assistent.senden': 'Enviar',
    'assistent.fotografieren': 'Fotografiar documento',
    'assistent.datei': 'Subir archivo',
    'assistent.belegAuswerten': 'Analizar documento',
    'assistent.wertetAus': 'Leyendo el documento',
    'assistent.bestaetigen': 'Confirmar',
    'assistent.gebucht': 'Registrado',
    'assistent.verworfen': 'Descartado',
    'assistent.willkommen': '¿En qué puedo ayudar?',
    'assistent.willkommenText': 'Pregunte por existencias y cifras, o fotografíe un albarán: las líneas entran en el almacén una vez que las revise.',
    'assistent.beispiel1': '¿Qué artículos están por agotarse?',
    'assistent.beispiel2': '¿Cuánto he facturado este mes?',
    'assistent.beispiel3': '¿Qué facturas siguen pendientes?',
    'assistent.beispiel4': '¿Cuál es el valor de mi almacén?',
    'allg.entfernen': 'Quitar',
    'bestaetigung.titel': 'Confirme su dirección de correo',
    'bestaetigung.text': 'Le hemos enviado un mensaje. Abra el enlace que contiene y podrá continuar.',
    'bestaetigung.warum': 'Sin una dirección confirmada no recibirá códigos de acceso, ni facturas, ni el aviso previo a una domiciliación.',
    'bestaetigung.schonBestaetigt': 'Ya lo he confirmado — continuar',
    'bestaetigung.erneut': 'Enviar el mensaje otra vez',
    'bestaetigung.erneutGesendet': 'El mensaje va en camino.',
    'bestaetigung.versandFehlt': 'Ahora mismo no es posible enviarlo. Contacte con el soporte.',
    'bestaetigung.fehlgeschlagen': 'No ha funcionado.',
    'bestaetigung.spam': '¿No ha llegado nada? Revise la carpeta de correo no deseado.',
    'bestaetigung.pruefe': 'Un momento …',
    'bestaetigung.fertigTitel': 'Dirección confirmada',
    'bestaetigung.fertigText': 'Su dirección de correo está confirmada. Puede continuar.',
    'bestaetigung.zurApp': 'Ir a la aplicación',
    'bestaetigung.abgelaufenTitel': 'Enlace caducado',
    'bestaetigung.abgelaufenText': 'Este enlace ya no es válido. Inicie sesión y solicite un mensaje nuevo.',
    'bestaetigung.ungueltigTitel': 'Enlace no válido',
    'bestaetigung.ungueltigText': 'Este enlace no se puede usar. Puede que la dirección ya esté confirmada.',
    'mandat.pflichtTitel': 'Mandato de adeudo SEPA',
    'mandat.pflichtText': 'Para continuar se necesita un mandato de adeudo SEPA. Son dos minutos. Sus datos permanecen intactos.',
    'mandat.einleitung': 'Otorga el mandato con el titular de la cuenta y el IBAN. No hace falta firma en papel.',
    'mandat.kontoinhaber': 'Titular de la cuenta',
    'mandat.kontoinhaberHinweis': 'Exactamente como consta el nombre en su banco.',
    'mandat.bicHinweis': 'No necesario dentro de la zona SEPA.',
    'mandat.ibanFalsch': 'Este IBAN no supera el dígito de control.',
    'mandat.wortlautTitel': 'Texto del mandato',
    'mandat.glaeubiger': 'Acreedor',
    'mandat.glaeubigerId': 'Identificador del acreedor',
    'mandat.referenz': 'Referencia del mandato',
    'mandat.referenzSpaeter': 'se asigna al otorgarlo y se le comunica antes del primer cobro',
    'mandat.zustimmung': 'Acepto el mandato de adeudo indicado arriba.',
    'mandat.erteilen': 'Otorgar mandato',
    'mandat.erteilt': 'El mandato está otorgado. Gracias.',
    'mandat.liegtVor': 'Hay un mandato válido registrado.',
    'mandat.nochNichtBereit': 'El sistema de adeudo se está configurando. Inténtelo más tarde o contacte con el soporte.',
    'mandat.ladefehler': 'No se han podido cargar los datos.',
    'mandat.fehlgeschlagen': 'No se ha podido otorgar el mandato.',
    'allg.optional': 'opcional',
    'sperre.titel': 'LagerPro está bloqueado',
    'sperre.entsperren': 'Desbloquear',
    'sperre.abgebrochen': 'La comprobación se canceló.',
    'geraete.titel': 'Dispositivos con sesión',
    'geraete.hinweis': 'Dispositivos en los que permanece con la sesión iniciada.',
    'geraete.hinweisAdmin': 'Todos los dispositivos de la empresa. Cierre la sesión de uno si se pierde o alguien se va.',
    'geraete.keine': 'Ningún dispositivo con sesión permanente.',
    'geraete.dieses': 'este dispositivo',
    'geraete.zuletzt': 'visto',
    'geraete.abmelden': 'Cerrar sesión',
    'geraete.abmeldenFehler': 'No se pudo cerrar la sesión del dispositivo.',
    'geraete.ladefehler': 'No se pudieron cargar los dispositivos.',
    'geraete.nichtGemerkt': 'Este dispositivo no está recordado. Inicie sesión una vez con su contraseña y luego bastará su huella.',
    'geraete.sperreTitel': 'Bloqueo al abrir',
    'geraete.sperreAn': 'Bloquear la aplicación con {art}',
    'geraete.sperreText': 'Se comprueba al abrir y tras cinco minutos en segundo plano.',
    'geraete.sperreAusWarnung': 'Sin bloqueo, cualquiera que tenga su teléfono desbloqueado accede a los datos de la empresa.',
    'geraete.alleAnderen': "Cerrar las demás sesiones",
    'geraete.gueltigBis': "conectado hasta {datum}",
    'geraete.browserTitel': "Conectado en un navegador",
    'geraete.diese': "esta sesión",
    'geraete.browserHinweis': "Las sesiones del navegador terminan solas tras 24 horas — o al instante aquí.",
    'app.name': 'LagerPro',
    'app.untertitel': 'Gesti\u00f3n profesional de almac\u00e9n',
    'benachrichtigung.titel': 'Notificaciones',
    'benachrichtigung.leer': 'Nada nuevo',
    'benachrichtigung.neu': 'Nueva notificación',
    'benachrichtigung.neueVorgaenge': 'elementos nuevos',
    'benachrichtigung.anAlle': 'a todos',
    'nav.uebersicht': 'Panel',
    'nav.mehr': 'Más',
    'allg.rechnung': 'factura',
    'app.gruss': 'Hola, {name}',
    'app.gruppe.tagesgeschaeft': 'Trabajo diario',
    'app.gruppe.kontakte': 'Contactos',
    'app.gruppe.zusammenarbeit': 'Colaboración',
    'app.gruppe.auswertung': 'Análisis',
    'app.gruppe.betrieb': 'Empresa',
    'app.nochmalBeenden': 'Pulse de nuevo para salir',
    'app.keineVerbindung': 'Sin conexión — faltan las cifras',
    'app.konto': 'Cuenta',
    'app.darstellung': 'Apariencia',
    'nav.kasse': 'Caja',
    'nav.lager': 'Almac\u00e9n',
    'nav.belege': 'Documentos',
    'nav.rechnungen': 'Facturas',
    'nav.kunden': 'Clientes',
    'nav.lieferanten': 'Proveedores',
    'nav.retouren': 'Devoluciones',
    'nav.buchhaltung': 'Contabilidad',
    'nav.ausgaben': 'Gastos',
    'nav.bank': 'Banco',
    'nav.fahrtenbuch': 'Libro de ruta',
    'nav.aufgaben': "Pizarra",
    'nav.chat': 'Chat',
    'nav.team': 'Equipo',
    'nav.firma': 'Empresa',
    'nav.gobd': 'GoBD y auditoría',
    'nav.tarif': 'Plan',
    'nav.uebernahme': 'Importar datos',
    'nav.einstellungen': 'Ajustes',
    'nav.owner': 'Gestión de clientes',
    'nav.mandanten': 'Inquilinos',
    'nav.gruppe.verkauf': 'Ventas',
    'nav.gruppe.waren': 'Mercanc\u00edas',
    'nav.gruppe.kontakte': 'Contactos',
    'nav.gruppe.auswertung': 'Informes',
    'nav.gruppe.zusammenarbeit': 'Colaboraci\u00f3n',
    'nav.gruppe.verwaltung': 'Administraci\u00f3n',
    'aktion.anlegen': 'Crear',
    'aktion.speichern': 'Guardar',
    'aktion.abbrechen': 'Cancelar',
    'aktion.bearbeiten': 'Editar',
    'aktion.loeschen': 'Eliminar',
    'aktion.schliessen': 'Cerrar',
    'aktion.suchen': 'Buscar',
    'aktion.anmelden': 'Iniciar sesi\u00f3n',
    'aktion.abmelden': 'Cerrar sesi\u00f3n',
    'aktion.drucken': 'Imprimir',
    'aktion.herunterladen': 'Descargar',
    'allg.suche': 'Buscar',
    'allg.laedt': 'Cargando',
    'allg.keineTreffer': 'Sin resultados',
    'allg.darstellung': 'Apariencia',
    'allg.sprache': 'Idioma',
    'allg.thema.system': 'Sistema',
    'allg.thema.hell': 'Claro',
    'allg.thema.dunkel': 'Oscuro',
    'allg.rolle.inhaber': 'Propietario',
    'allg.rolle.admin': 'Administrador',
    'allg.rolle.mitarbeiter': 'Empleado',
    'allg.nichtImTarif': 'No incluido en su plan',
    'allg.tarif': 'Plan',
    'allg.menue': 'Men\u00fa',
    'allg.schnellzugriff': 'Acceso r\u00e1pido',
    'allg.schnellzugriffHinweis': "Página, cliente o n.º de documento …",
    'anmelden.willkommen': 'Bienvenido de nuevo',
    'anmelden.hinweis': 'Inicie sesi\u00f3n para continuar',
    'anmelden.kennung': 'Usuario o correo electr\u00f3nico',
    'anmelden.passwort': 'Contrase\u00f1a',
    'anmelden.fehler': 'Error al iniciar sesi\u00f3n.',
    'anmelden.keinKonto': '¿Aún no tiene cuenta?',
    'anmelden.jetztRegistrieren': 'Registrarse gratis',
    'passwort.vergessenLink': "¿Olvidó la contraseña?",
    'passwort.vergessenTitel': "Contraseña olvidada",
    'passwort.vergessenText': "Introduzca su nombre de usuario o correo electrónico. Le enviaremos un enlace para establecer una nueva contraseña.",
    'passwort.linkSenden': "Enviar enlace",
    'passwort.gesendetTitel': "Revise su bandeja de entrada",
    'passwort.gesendetText': "Si hay una cuenta con correo que coincide, el enlace está en camino. Es válido {min} minutos. Sin correo registrado, su administrador puede ayudarle.",
    'passwort.zurAnmeldung': "Volver al inicio de sesión",
    'passwort.neuTitel': "Establecer nueva contraseña",
    'passwort.fuer': "Para la cuenta {name}",
    'passwort.regeln': "Al menos 8 caracteres, con mayúsculas, minúsculas y un número.",
    'passwort.ungleich': "Las dos contraseñas no coinciden.",
    'passwort.speichern': "Guardar contraseña",
    'passwort.fertigTitel': "Contraseña cambiada",
    'passwort.fertigText': "Inicie sesión ahora con la nueva contraseña. Se cerró la sesión en todos los dispositivos.",
    'passwort.abgelaufenTitel': "Enlace caducado",
    'passwort.abgelaufenText': "El enlace solo es válido 30 minutos. Solicite uno nuevo.",
    'passwort.ungueltigTitel': "Enlace no válido",
    'passwort.ungueltigText': "Este enlace no es válido o ya se ha usado.",
    'passwort.neuAnfordern': "Solicitar un nuevo enlace",
    'passwort.pruefe': "Comprobando el enlace …",
    'uebersicht.begruessung': 'Hola',
    'uebersicht.offeneAufgaben': 'Tareas abiertas',
    'uebersicht.laufendeTimer': 'Cron\u00f3metros activos',
    'uebersicht.teamOnline': 'Equipo en l\u00ednea',
    'uebersicht.benutzerImTarif': 'Usuarios en el plan',
    'uebersicht.naechsteAufgaben': 'Pr\u00f3ximas tareas',
    'uebersicht.alleAnsehen': 'Ver todo',
    'uebersicht.unbegrenzt': 'ilimitado',
    'uebersicht.von': 'de',
    'uebersicht.insgesamt': 'en total',
    'uebersicht.keineOffenen': 'No hay tareas abiertas.',

    'feld.name': 'Nombre',
    'feld.nummer': 'Número',
    'feld.telefon': 'Teléfono',
    'feld.email': 'Correo',
    'feld.strasse': 'Calle',
    'feld.plz': 'C.P.',
    'feld.ort': 'Ciudad',
    'feld.land': 'País',
    'feld.notiz': 'Nota',
    'feld.ansprechpartner': 'Contacto',
    'feld.ustId': 'NIF-IVA',
    'feld.menge': 'Cantidad',
    'feld.einzelpreis': 'Precio unitario',
    'feld.bezeichnung': 'Descripción',
    'feld.artikel': 'Artículo',
    'feld.kunde': 'Cliente',
    'feld.lieferant': 'Proveedor',
    'feld.datum': 'Fecha',
    'feld.betrag': 'Importe',
    'feld.grund': 'Motivo',
    'feld.status': 'Estado',
    'feld.einheit': 'Unidad',
    'feld.bestand': 'Existencias',
    'feld.gesamt': 'Total',
    'feld.netto': 'Neto',
    'feld.rabatt': 'Descuento',
    'feld.position': 'Línea',
    'feld.positionen': 'Líneas',
    'aktion.hinzufuegen': 'Añadir',
    'aktion.entfernen': 'Quitar',
    'aktion.zurueck': 'Atrás',
    'aktion.weiter': 'Continuar',
    'kunden.titel': 'Clientes',
    'kunden.neu': 'Nuevo cliente',
    'kunden.nummer': 'N° de cliente',
    'kunden.suche': 'Nombre, número, correo o ciudad …',
    'kunden.leer': 'Aún no hay clientes',
    'kunden.leerText': 'Cree su primer cliente.',
    'kunden.akte': "Ficha del cliente",
    'kunden.alle': "Todos los clientes",
    'kunden.nichtGefunden': "Cliente no encontrado.",
    'kunden.gespeichert': "Cliente guardado.",
    'kunden.geloescht': "{name} se ha eliminado.",
    'kunden.loeschenFrage': "¿Eliminar «{name}» de verdad?",
    'kunden.einKunde': "1 cliente",
    'kunden.anzahl': "{n} clientes",
    'kunden.sortieren': "Ordenar por",
    'kunden.letzterKaufSort': "Última compra",
    'kunden.keineKontakt': "Sin datos de contacto",
    'kunden.nochNichts': "sin compras",
    'kunden.zuletzt': "última {datum}",
    'kunden.rechnungSchreiben': "Crear factura",
    'kunden.umsatzJahr': "Ventas {jahr}",
    'kunden.netto': "neto",
    'kunden.vorjahr': "año anterior {betrag}",
    'kunden.offen': "Pendiente",
    'kunden.ueberfaellig': "{betrag} vencido ({n})",
    'kunden.ueberfaelligKurz': "{betrag} vencido",
    'kunden.offenAnzahl': "{n} facturas pendientes",
    'kunden.offenBetrag': "{betrag} pendiente",
    'kunden.meistgekauft': "Más comprado",
    'kunden.malGekauft': "comprado {n}×",
    'kunden.keineProdukte': "Aún no ha comprado",
    'kunden.zahlung': "Comportamiento de pago",
    'kunden.zahltNach': "media {n} días",
    'kunden.puenktlich': "{n} % pagado a tiempo",
    'kunden.nochKeineZahlung': "sin pagos aún",
    'kunden.seit': "Cliente desde {datum}",
    'kunden.letzterKauf': "Última compra {datum}",
    'kunden.gesamt': "Ventas totales {betrag}",
    'kunden.durchschnitt': "Factura media {betrag}",
    'kunden.guthaben': "Saldo a favor {betrag}",
    'kunden.verlauf': "Ventas de los últimos 12 meses",
    'kunden.diagrammLabel': "Ventas por mes",
    'kunden.keinUmsatz': "Sin ventas en los últimos 12 meses",
    'kunden.rechnungenAnzahl': "{n} facturas",
    'kunden.produkte': "Productos comprados",
    'kunden.kaeufe': "Compras",
    'kunden.menge': "Cantidad",
    'kunden.umsatz': "Ventas",
    'kunden.mehr': "Mostrar los {n}",
    'kunden.weniger': "Mostrar menos",
    'kunden.dokumente': "Documentos",
    'kunden.dokumenteHinweis': "{n} en total, los más recientes primero",
    'kunden.alleDokumente': "Todos",
    'kunden.nurOffene': "Pendientes",
    'kunden.keineDokumente': "Sin documentos",
    'kunden.zu': "de {nummer}",
    'kunden.entwurf': "Borrador",
    'kunden.faellig': "vence {datum}",
    'kunden.gueltigBis': "válido hasta {datum}",
    'kunden.ansehen': "Ver",
    'kunden.oeffnen': "Abrir",
    'kunden.art.rechnung': "Factura",
    'kunden.art.lieferschein': "Albarán",
    'kunden.art.angebot': "Presupuesto",
    'kunden.art.auftrag': "Pedido",
    'kunden.art.gutschrift': "Abono",
    'kunden.art.retoure': "Devolución",
    'kunden.arten.rechnung': "Facturas",
    'kunden.arten.lieferschein': "Albaranes",
    'kunden.arten.angebot': "Presupuestos",
    'kunden.arten.auftrag': "Pedidos",
    'kunden.arten.gutschrift': "Abonos",
    'kunden.arten.retoure': "Devoluciones",
    'kunden.status.entwurf': "Borrador",
    'kunden.status.gestellt': "Emitida",
    'kunden.status.bezahlt': "Pagada",
    'kunden.status.storniert': "Anulada",
    'kunden.status.gesendet': "Enviado",
    'kunden.status.angenommen': "Aceptado",
    'kunden.status.abgelehnt': "Rechazado",
    'kunden.status.abgelaufen': "Caducado",
    'kunden.status.offen': "Abierto",
    'kunden.status.inArbeit': "En curso",
    'kunden.status.erledigt': "Completado",
    'kunden.status.geliefert': "Entregado",
    'kunden.status.eingelagert': "Reingresado",
    'kunden.status.erfasst': "Registrado",
    'kunden.status.nichtGeliefert': "No entregado",
    'kunden.status.ausgestellt': "Emitido",
    'kunden.status.teilbezahlt': "Pagada en parte",
    'lieferanten.titel': 'Proveedores',
    'lieferanten.neu': 'Nuevo proveedor',
    'lieferanten.nummer': 'N° de proveedor',
    'lieferanten.suche': 'Nombre, contacto o ciudad …',
    'lieferanten.akte': 'Ficha',
    'lieferanten.alle': 'Todos los proveedores',
    'lieferanten.artikel': 'Artículos',
    'lieferanten.lieferungen': 'Entregas',
    'lieferanten.rechnungen': 'Facturas',
    'lieferanten.offen': 'Pendiente',
    'lieferanten.ueberfaellig': 'vencido',
    'lieferanten.einkaufJahr': 'Compras {jahr}',
    'lieferanten.letzteLieferung': 'Última entrega',
    'lieferanten.keineArtikel': 'Aún no hay artículos de este proveedor.',
    'lieferanten.keineLieferungen': 'Aún no hay entradas de mercancía.',
    'lieferanten.keineRechnungen': 'Aún no hay facturas.',
    'lieferanten.rechnungErfassen': 'Registrar factura',
    'lieferanten.alsBezahlt': 'Marcar como pagada',
    'lieferanten.bezahltAm': 'pagada {datum}',
    'lieferanten.faellig': 'vence {datum}',
    'lieferanten.betrag': 'Importe (bruto)',
    'lieferanten.rechnungsnummer': 'Número de factura',
    'lieferanten.rechnungsdatum': 'Fecha de factura',
    'lieferanten.faelligAm': 'Vence el',
    'lieferanten.schonBezahlt': 'Ya pagada',
    'lieferanten.wert': 'Valor',
    'lieferanten.wiederOffen': 'Reabrir',
    'lieferanten.gespeichert': 'Guardado.',
    'lieferanten.bearbeiten': 'Editar',
    'lieferanten.loeschen': 'Eliminar',
    'lager.zugang': 'Entrada de mercancía',
    'lager.abgang': 'Salida',
    'lager.buchen': 'Registrar',
    'lager.rechnungLiegtBei': 'Se adjunta la factura del proveedor',
    'lager.rechnungBezahlt': 'La factura ya está pagada',
    'lager.gebucht': 'Stock de «{name}» registrado.',
    'lager.ohneLieferant': 'sin proveedor',
    'lieferanten.leer': 'Aún no hay proveedores',
    'lieferanten.leerText': 'Cree su primer proveedor.',
    'allg.andereSuche': 'Pruebe otros términos.',

    'lager.titel': 'Almacén',
    'lager.neu': 'Nuevo artículo',
    'lager.artikelnummer': 'N° de artículo',
    'lager.ean': 'EAN / código de barras',
    'lager.kategorie': 'Categoría',
    'lager.lagerort': 'Ubicación',
    'lager.einkaufspreis': 'Precio de compra (neto)',
    'lager.verkaufspreis': 'Precio de venta (neto)',
    'lager.mindestbestand': 'Existencias mínimas',
    'lager.anfangsbestand': 'Existencias iniciales',
    'lager.wareneingang': 'Se registra como entrada de mercancía.',
    'lager.suche': 'Descripción, número o EAN …',
    'lager.leer': 'Sin artículos',
    'lager.leerText': 'Cree su primer artículo o importe sus datos.',
    'lager.verlauf': 'Historial',
    'lager.keineBewegungen': 'Sin movimientos',
    'lager.etikett': 'Etiqueta',
    'lager.etikettenDrucken': 'Imprimir etiquetas',
    'lager.format': 'Formato',
    'lager.stueckJeArtikel': 'Copias por artículo',
    'lager.mehrfachHinweis': 'Al recibir mercancía suele hacer falta la misma etiqueta varias veces.',
    'lager.knapp': 'bajo',
    'lager.auswahl': 'Selección',
    'lager.auswahlAufheben': 'Quitar selección',
    'lager.alle': 'Todos',
    'lager.keinLieferant': '— sin proveedor —',
    'kasse.titel': 'Caja',
    'kasse.schritt1': 'Paso 1 de 2 — ¿para quién es la venta?',
    'kasse.kundeWaehlen': 'Elegir cliente',
    'kasse.kundeSuchen': 'Buscar cliente',
    'kasse.kundeSuche': 'Nombre, n° de cliente o ciudad …',
    'kasse.kundeWechseln': 'Cambiar cliente',
    'kasse.ohneKonto': 'Sin cuenta de cliente',
    'kasse.historieHinweis': 'La venta aparecerá en su historial de facturas',
    'kasse.artikelSuchen': 'Buscar artículo',
    'kasse.artikelSuche': 'Descripción, número o escanear código …',
    'kasse.keinArtikel': 'No se encontró ningún artículo',
    'kasse.warenkorb': 'Carrito',
    'kasse.warenkorbLeeren': 'Vaciar carrito',
    'kasse.leer': 'Aún no hay nada',
    'kasse.leerText': 'Haga clic en un artículo a la izquierda.',
    'kasse.zahlart': 'Forma de pago',
    'kasse.bezahlt': '¿Pagado?',
    'kasse.vollstaendig': 'Completo',
    'kasse.teilweise': 'En parte',
    'kasse.nochNicht': 'Todavía no',
    'kasse.gegeben': 'Entregado (para el cambio)',
    'kasse.rueckgeld': 'Cambio: {betrag}',
    'kasse.erhalten': 'Recibido',
    'kasse.kundePflicht': 'Para un importe pendiente elija un cliente — si no, nadie sabe quién debe pagar.',
    'kasse.buchen': 'Registrar · {erhalten} recibido, {offen} pendiente',
    'kasse.kassieren': 'Cobrar · {betrag}',
    'kasse.offenBis': 'Pendiente: {betrag} · vence {datum}',
    'kasse.bezahltMit': 'Pagado · {zahlart}',
    'kasse.naechster': 'Siguiente venta',
    'kasse.bar': 'Efectivo',
    'kasse.karte': 'Tarjeta',
    'kasse.ueberweisung': 'Transferencia',
    'kasse.abgeschlossen': 'Venta finalizada',
    'kasse.alle': "Todo",
    'kasse.anzahlung': "Anticipo",
    'kasse.aufRechnung': "A cuenta",
    'kasse.barAnteil': "Parte en efectivo",
    'kasse.barKassieren': "Cobrar en efectivo · {betrag}",
    'kasse.beleg': "Ticket",
    'kasse.betragBrutto': "Importe (bruto)",
    'kasse.bezahlen': "Pagar",
    'kasse.bezahlenBetrag': "Pagar · {betrag}",
    'kasse.bonNr': "Ticket {n}",
    'kasse.bonRabatt': "Descuento en el ticket",
    'kasse.bonVerwerfen': "Descartar ticket",
    'kasse.codeUnbekannt': "Ningún artículo con el código {code}",
    'kasse.enthaltenSteuer': "incl. {satz} % {name}",
    'kasse.fehlgeschlagen': "No se pudo registrar la venta.",
    'kasse.freiHinweis': "Para todo lo que no es un artículo: servicio, depósito, otros.",
    'kasse.freiStandard': "Otros",
    'kasse.freierBetrag': "Importe libre",
    'kasse.gegebenKurz': "Entregado",
    'kasse.gespart': "ahorrado",
    'kasse.geteilt': "Dividido",
    'kasse.geteiltKassieren': "Cobrar · {bar} efectivo + {karte} tarjeta",
    'kasse.haeufig': "Frecuentes",
    'kasse.kameraFehler': "Cámara no disponible: permita el acceso o use un lector manual.",
    'kasse.karteBezahlt': "Tarjeta pagada · {betrag}",
    'kasse.karteHinweis': "Cobre el importe en el terminal y confirme aquí.",
    'kasse.karteRest': "Resto con tarjeta",
    'kasse.keinRabatt': "ninguno",
    'kasse.keineArtikelAngelegt': "Aún no hay artículos.",
    'kasse.kundeEntfernen': "Quitar cliente",
    'kasse.kundeZuordnen': "Asignar cliente",
    'kasse.laufkunde': "Cliente de paso",
    'kasse.leerNeu': "Toque un artículo o escanee un código.",
    'kasse.neuerBon': "Nuevo ticket",
    'kasse.nichtAufLager': "«{name}» no está en stock.",
    'kasse.nichtVorraetig': "agotado",
    'kasse.nurNoch': "Solo hay {menge} de «{name}».",
    'kasse.parkenHinweis': "Aparcar este ticket y empezar uno nuevo",
    'kasse.passend': "exacto",
    'kasse.positionen': "Líneas: {n}",
    'kasse.preis': "Precio",
    'kasse.preisAendern': "Cambiar precio",
    'kasse.rabatt': "Descuento",
    'kasse.rabattText': "{p} % de descuento",
    'kasse.rechnungBuchen': "Registrar a cuenta · {offen} pendiente",
    'kasse.rueckgeldGross': "Cambio",
    'kasse.scannen': "Escanear código",
    'kasse.scannenHinweis': "Ponga el código delante de la cámara.",
    'kasse.wofuer': "¿Para qué?",
    'kasse.zuWenig': "Faltan {betrag}.",

    'belege.titel': 'Documentos',
    'belege.angebote': 'Presupuestos',
    'belege.auftraege': 'Pedidos',
    'belege.lieferscheine': 'Albaranes',
    'belege.gutschriften': 'Abonos',
    'belege.offeneAngebote': 'Presupuestos abiertos',
    'belege.laufendeAuftraege': 'Pedidos en curso',
    'belege.lieferscheineOffen': 'Albaranes pendientes',
    'belege.interessent': 'Nombre del interesado',
    'belege.notiz': 'Nota (aparece en el documento)',
    'belege.rabatt': 'Descuento %',
    'belege.druckenPdf': 'Imprimir / PDF',
    'belege.entwurfLoeschen': 'Eliminar borrador',
    'belege.gesamtBrutto': 'Total bruto',
    'belege.einzel': 'Unitario',
    'rechnungen.titel': 'Facturas',
    'rechnungen.neu': 'Nueva factura',
    'rechnungen.schreiben': 'Crear factura',
    'rechnungen.stellen': 'Emitir factura',
    'rechnungen.entwurfSpeichern': 'Guardar como borrador',
    'rechnungen.entwurfHinweis': 'Un borrador aún no descuenta existencias.',
    'rechnungen.bezahltMarkieren': 'Marcar como pagada',
    'rechnungen.teilbezahlt': 'pagada parcialmente · pendiente {betrag}',
    'rechnungen.perBank': 'conciliada por banco',
    'rechnungen.zahlungen': 'Pagos',
    'rechnungen.nochOffen': 'Pendiente',
    'rechnungen.bankeingang': 'Ingreso bancario',
    'rechnungen.zahlung': 'Pago',
    'rechnungen.zahlart': 'Forma de pago',
    'rechnungen.zahlweg.ueberweisung': 'Transferencia',
    'rechnungen.zahlweg.bar': 'Efectivo',
    'rechnungen.zahlweg.karte': 'Tarjeta',
    'rechnungen.zahlungsziel': 'Plazo de pago',
    'rechnungen.zielKunde': 'Como el cliente ({tage} días)',
    'rechnungen.zielSofort': 'Pago inmediato',
    'rechnungen.zielTage': '{tage} días',
    'rechnungen.zielDatum': 'Fecha propia',
    'rechnungen.faelligAm': 'Vence el',
    'rechnungen.bezahlung': '¿Pagada?',
    'rechnungen.offenLassen': 'Aún no pagada',
    'rechnungen.schonBezahlt': 'Ya pagada',
    'rechnungen.lieferscheinMit': 'Crear también el albarán',
    'rechnungen.lieferschein': 'Albarán',
    'rechnungen.lieferscheinDrucken': 'Imprimir albarán',
    'rechnungen.lieferscheinErstellen': 'Crear albarán',
    'rechnungen.zahlungErfassen': 'Registrar pago',
    'rechnungen.betrag': 'Importe',
    'rechnungen.zahltag': 'Fecha de pago',
    'rechnungen.alsBezahltSpeichern': 'Guardar como pagada',
    'rechnungen.teilzahlungSpeichern': 'Guardar pago parcial',
    'rechnungen.ratenHinweis': '¿Menos que lo pendiente? El resto queda abierto — así se registran los plazos.',
    'rechnungen.zahlungEntfernen': 'Eliminar pago',
    'rechnungen.sofort': 'pago inmediato',
    'rechnungen.gestelltMitLs': 'Factura emitida. El albarán {nummer} está listo para imprimir.',
    'rechnungen.zahlungGespeichert': 'Pago registrado.',
    'rechnungen.vollBezahlt': 'La factura está totalmente pagada.',
    'rechnungen.guthaben': 'Saldo a favor del cliente',
    'rechnungen.gutschriftTitel': 'Nota de crédito de esta factura',
    'rechnungen.gutschriftMenge': 'Cantidad a abonar',
    'rechnungen.gutschriftGrund': 'Motivo (aparece en la nota)',
    'rechnungen.gutschriftStellen': 'Emitir nota de crédito',
    'rechnungen.gutschriftHinweis': 'Si la factura sigue abierta, la nota se compensa con ella. Si no, pasa a saldo a favor del cliente. Una nota por factura.',
    'rechnungen.gutschriftErstellt': 'Nota de crédito {nummer} emitida.',
    'rechnungen.gutschriftVorhanden': 'Nota de crédito {nummer}',
    'rechnungen.perGutschrift': 'saldada con nota de crédito',
    'rechnungen.gutschriftDrucken': 'Imprimir nota de crédito',
    'rechnungen.gutschriftSumme': 'Nota de crédito por {betrag}',
    'rechnungen.stornieren': 'Anular',
    'rechnungen.gutschriftErstellen': 'Crear abono',
    'rechnungen.drucken': 'Imprimir / PDF',
    'rechnungen.stempelBezahlt': 'Pagado',
    'rechnungen.stempelAm': 'el {datum}',
    'rechnungen.keinGirocode': 'Sin GiroCode',
    'rechnungen.girocodeText': 'Escanear con la app del banco: importe y concepto ya aparecen rellenados.',
    'neuigkeiten.titel': 'Novedades',
    'neuigkeiten.auchNeu': 'También nuevo',
    'neuigkeiten.fortschritt': 'Su progreso con LagerPro',
    'neuigkeiten.updates': 'actualizaciones',
    'neuigkeiten.verbesserungen': 'mejoras',
    'neuigkeiten.seit': 'desde el {datum}',
    'neuigkeiten.mailHinweis': 'Puede desactivar los correos de novedades en Ajustes.',
    'neuigkeiten.verstanden': 'Entendido',
    'rechnungen.barverkauf': 'Venta al contado / sin cuenta',
    'rechnungen.leer': 'Sin facturas',
    'rechnungen.leerText': 'Cree su primera factura.',
    'status.alle': 'Todos',
    'status.entwurf': 'Borrador',
    'status.entwuerfe': 'Borradores',
    'status.offen': 'Pendiente',
    'status.bezahlt': 'Pagada',
    'status.storniert': 'Anulada',
    'retouren.titel': 'Devoluciones',
    'retouren.erfassen': 'Registrar devolución',
    'retouren.alle': 'Todas las devoluciones',
    'retouren.leer': 'Aún no hay devoluciones',
    'retouren.leerText': 'Una devolución siempre pertenece a una factura: elija cliente, factura y artículos.',
    'retouren.schritt1': '1. Cliente',
    'retouren.schritt2': '2. Factura',
    'retouren.schritt3': '3. ¿Qué se devuelve?',
    'retouren.grund': 'Motivo (opcional)',
    'retouren.grundBeispiel': 'p. ej. daño en transporte, entrega errónea',
    'retouren.berechnet': 'Facturado',
    'retouren.zurueck': 'Devolver',
    'retouren.gutschrift': 'Abono',
    'retouren.einlagern': 'Devolver al almacén',
    'retouren.kundeWaehlen': 'Elegir cliente …',

    'team.titel': 'Equipo',
    'team.mitglieder': 'Miembros',
    'team.neu': 'Añadir empleado',
    'team.benutzername': 'Usuario',
    'team.emailOptional': 'Correo (opcional)',
    'team.rolle': 'Rol',
    'team.passwortHinweis': 'Mínimo 8 caracteres. El empleado podrá cambiarla.',
    'team.limitErreicht': 'Límite de usuarios alcanzado',
    'team.leer': 'Aún no hay miembros',
    'team.leerText': 'Añada su primer empleado.',
    'team.gesperrt': 'Bloqueado',
    'team.alle': "Todo el equipo",
    'team.nichtGefunden': "Persona no encontrada.",
    'team.sie': "usted",
    'team.online': "En línea",
    'team.offline': "Desconectado",
    'team.bearbeiten': "Editar perfil",
    'team.gespeichert': "Perfil guardado.",
    'team.kontakt': "Contacto y trabajo",
    'team.position': "Puesto",
    'team.positionBeispiel': "p. ej. Jefe de almacén",
    'team.abteilung': "Departamento",
    'team.mobil': "Móvil",
    'team.arbeitszeit': "Horario",
    'team.arbeitszeitBeispiel': "p. ej. lun–vie 8–16 h",
    'team.eintritt': "Fecha de alta",
    'team.jahre': "{n} años en el equipo",
    'team.einJahr': "1 año en el equipo",
    'team.ersteJahr': "primer año",
    'team.privat': "Personal",
    'team.privatHinweis': "Solo la persona y los administradores lo ven",
    'team.freiwillig': "Opcional — solo la persona y los administradores lo ven",
    'team.geburtstag': "Cumpleaños",
    'team.alter': "{n} años",
    'team.anschrift': "Dirección",
    'team.notfall': "Contacto de emergencia",
    'team.notfallName': "Contacto de emergencia (nombre)",
    'team.notfallTelefon': "Contacto de emergencia (teléfono)",
    'team.notfallBeispiel': "p. ej. Anna Meyer (pareja)",
    'team.notiz': "Nota interna",
    'team.notizHinweis': "Solo visible para administradores",
    'team.keineAngaben': "Sin datos",
    'team.offeneAufgaben': "Tareas abiertas",
    'team.keineAufgaben': "Sin tareas abiertas",
    'team.aktualisiert': "Modificado el {datum}",
    'team.nurAdminFelder': "Puesto, departamento, horario y fecha de alta los gestiona un administrador.",
    'uebersicht.umsatzHeute': 'Ingresos hoy',
    'uebersicht.offeneForderungen': 'Cuentas por cobrar',
    'uebersicht.lagerwert': 'Valor del almacén',
    'uebersicht.unterMindest': 'Bajo mínimo',
    'uebersicht.nachbestellen': 'Reponer',
    'uebersicht.ueberfaellig': 'Facturas vencidas',
    'uebersicht.zahlungsziel': 'Plazo de pago superado',
    'uebersicht.umsatzMonat': 'Ingresos por mes',
    'uebersicht.schnell': 'Acciones rápidas',
    'uebersicht.kassieren': 'Cobrar venta',
    'uebersicht.wareneingang': 'Registrar entrada',
    'aufgaben.titel': 'Tareas',
    'aufgaben.neu': 'Nueva tarea',
    'aufgaben.wasZuTun': '¿Qué hay que hacer?',
    'aufgaben.ansichtHeute': 'Hoy',
    'aufgaben.ansichtBoard': 'Tablero',
    'aufgaben.ansichtAlle': 'Todas',
    'aufgaben.vomBetrieb': 'De su negocio',
    'aufgaben.vomBetriebHinweis': 'Se deriva de sus datos y desaparece sola en cuanto está hecha.',
    'aufgaben.meine': 'Mis tareas',
    'aufgaben.nurMeine': 'Solo las mías',
    'aufgaben.ueberfaellig': 'Vencidas',
    'aufgaben.heute': 'Hoy',
    'aufgaben.morgen': 'Mañana',
    'aufgaben.dieseWoche': 'Esta semana',
    'aufgaben.spaeter': 'Más tarde',
    'aufgaben.ohneDatum': 'Sin fecha',
    'aufgaben.spalteOffen': 'Abierta',
    'aufgaben.spalteArbeit': 'En curso',
    'aufgaben.spalteErledigt': 'Hecha',
    'aufgaben.schnell': 'Nueva tarea — p. ej. «Mañana llamar a Meyer !alta @Mia»',
    'aufgaben.schnellHilfe': 'hoy · mañana · lunes · 15.11. · !alta · !baja · @Nombre',
    'aufgaben.oeffnen': 'Abrir',
    'aufgaben.zurueckstellen': 'Posponer',
    'aufgaben.tage': '{n} días',
    'aufgaben.uebernehmen': 'Convertir en tarea',
    'aufgaben.beschreibung': 'Descripción',
    'aufgaben.faellig': 'Vence',
    'aufgaben.prioritaet': 'Prioridad',
    'aufgaben.niedrig': 'Baja',
    'aufgaben.normal': 'Normal',
    'aufgaben.zustaendig': 'Responsable',
    'aufgaben.niemand': 'Nadie',
    'aufgaben.wiederholung': 'Repetición',
    'aufgaben.keine': 'Ninguna',
    'aufgaben.taeglich': 'Diaria',
    'aufgaben.woechentlich': 'Semanal',
    'aufgaben.monatlich': 'Mensual',
    'aufgaben.checkliste': 'Lista de control',
    'aufgaben.punktNeu': 'Añadir punto',
    'aufgaben.loeschen': 'Eliminar',
    'aufgaben.loeschenFrage': '¿Eliminar esta tarea?',
    'aufgaben.timerStart': 'Iniciar temporizador',
    'aufgaben.timerStopp': 'Detener temporizador',
    'aufgaben.allesErledigt': 'Nada urgente — el negocio funciona.',
    'aufgaben.keineEigenen': 'No hay tareas abiertas.',
    'aufgaben.folge': 'Siguiente el {datum}',
    'aufgaben.zieheHier': 'Soltar aquí',
    'aufgaben.erledigtHeute': 'Hecho hoy',
    'aufgaben.schliessen': 'Cerrar',
    'aufgaben.aktionGeliefert': 'Marcar como entregado',
    'aufgaben.aktionBezahlt': 'Marcar como pagada',
    'aufgaben.erledigtMeldung': 'Hecho.',
    'aufgaben.zurueckgestellt': 'Pospuesto hasta {datum}.',
    'aufgaben.uebernommen': 'Convertido en tarea.',
    'aufgaben.auto.nachbestellen': 'Volver a pedir {artikel}',
    'aufgaben.auto.nachbestellenText': 'Stock {bestand} {einheit}, mínimo {mindestbestand}',
    'aufgaben.auto.bei': 'a {lieferant}',
    'aufgaben.auto.ausliefern': 'Entregar albarán {nummer}',
    'aufgaben.auto.ausliefernText': 'a {kunde}',
    'aufgaben.auto.nachfassen': 'Hacer seguimiento del presupuesto {nummer}',
    'aufgaben.auto.nachfassenText': '{kunde} · sin respuesta desde hace {tage} días',
    'aufgaben.auto.entwurf': 'Emitir borrador de factura',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · esperando {tage} días',
    'aufgaben.auto.support': 'Leer respuesta del soporte',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'Registrar recibos escaneados',
    'aufgaben.auto.belegscanText': '{anzahl} esperan su revisión',
    'aufgaben.auto.mahnen': 'Reclamar el pago: {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} pendiente · {tage} días de retraso',
    'aufgaben.auto.bezahlen': 'Pagar al proveedor: {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · vence {datum}',
    'aufgaben.auto.zuordnen': 'Asignar cobros',
    'aufgaben.auto.zuordnenText': '{anzahl} cobros pendientes',
    'aufgaben.auto.bank': 'Volver a conectar el banco: {bank}',
    'aufgaben.auto.bankAbgelaufen': 'El consentimiento ha caducado — ya no llegan movimientos.',
    'aufgaben.auto.bankEndet': 'El consentimiento termina el {datum}.',
    'aufgaben.auto.ustva': 'Declaración de IVA {monat}',
    'aufgaben.auto.ustvaText': 'A pagar {betrag} · hasta el {datum}',
    'tafel.titel': "Pizarra",
    'tafel.untertitel': "Tareas, citas y plazos de un vistazo",
    'tafel.ansicht.woche': "Semana",
    'tafel.ansicht.monat': "Mes",
    'tafel.ansicht.liste': "Lista",
    'tafel.neuerZettel': "Nueva nota",
    'tafel.zurueck': "Anterior",
    'tafel.vor': "Siguiente",
    'tafel.kw': "Sem. {n}",
    'tafel.naechste30': "Los próximos 30 días",
    'tafel.art.aufgabe': "Tarea",
    'tafel.art.termin': "Cita",
    'tafel.art.frist': "Plazo",
    'tafel.liegenGeblieben': "Pendiente",
    'tafel.ablage': "Bandeja",
    'tafel.ablageHinweis': "Sin día — cuando haya tiempo",
    'tafel.leerTag': "+ Nota",
    'tafel.weitere': "+{n} más",
    'tafel.erledigt': "Hecho",
    'tafel.wiederOffen': "Reabrir",
    'tafel.ziehen': "Arrastrar para mover a otro día",
    'tafel.automatisch': "automático",
    'tafel.schnell': "¿Qué hay que hacer?",
    'tafel.schnellGross': "¿Qué hay que hacer? p. ej. «14:30 cita Meyer» o «enviar presupuesto !alta»",
    'tafel.schnellHilfe': "hoy · mañana · viernes · 15.11. · 14:30 · !alta · @nombre",
    'tafel.inEinerWoche': "En una semana",
    'tafel.artWaehlen': "Elegir tipo",
    'tafel.bisWann': "¿Hasta cuándo?",
    'tafel.wann': "¿Cuándo?",
    'tafel.von': "De",
    'tafel.bis': "A",
    'tafel.ort': "Lugar",
    'tafel.notiz': "Nota",
    'tafel.mehr': "Más opciones",
    'tafel.anheften': "Fijar",
    'tafel.fristHinweis': "Sale de sus datos y desaparece solo cuando está resuelto.",
    'tafel.kurzTitel': "Mi pizarra",
    'tafel.kurzLiegen': "{n} pendientes",
    'tafel.zurTafel': "Ir a la pizarra",
    'tafel.heuteFrei': "Nada previsto.",
    'tafel.frist.angebot': "Vence el presupuesto: {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "Entrega: {nummer}",
    'tafel.frist.lieferungText': "a {kunde}",
    'tafel.frist.zahlungseingang': "Pago esperado: {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "Factura de proveedor vence: {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "Declaración de IVA",
    'tafel.frist.ustvaText': "de {monat}",
    'aufgaben.leer': 'Aún no hay tareas',
    'aufgaben.leerText': 'Cree la primera arriba.',
    'aufgaben.hoch': 'Alta',
    'aufgaben.laeuft': 'en curso',
    'chat.titel': 'Chat',
    'chat.wemSchreiben': '¿A quién escribir?',
    'chat.leer': 'Aún no hay mensajes',
    'chat.senden': 'Enviar',
    'chat.dateiAnhaengen': 'Adjuntar archivo',
    'chat.anhangEntfernen': 'Quitar adjunto',
    'chat.zurListe': 'Volver a la lista',
    'chat.alleImTeam': 'Todo el equipo',
    'chat.herunterladenTipp': 'toque para descargar',

    'basis.tarifAb': 'Disponible desde el plan',
    'basis.tarifWechsel': 'Puede cambiar el plan en «Plan y facturación».',
    'basis.support': 'Contacte con soporte para activar esta función.',
    'positionen.hinzufuegen': 'Añadir línea',
    'positionen.freiText': '— texto libre —',
    'passwort.festlegen': 'Establecer contraseña',
    'passwort.uebermittelt': 'Contraseña recibida',
    'passwort.neu': 'Nueva contraseña',
    'passwort.wiederholen': 'Repita la nueva contraseña',
    'tarif.titel': 'Plan y facturación',
    'tarif.aktuell': 'Plan actual',
    'tarif.enthalten': 'Incluido',
    'tarif.nichtEnthalten': 'No incluido',
    'tarif.proMonat': '/ mes',
    'tarif.sonderkondition': 'Condición especial',
    'tarif.verlauf': 'Historial de planes',
    'tarif.mandat': 'Mandato de domiciliación',
    'tarif.angebot': 'Su oferta personal',
    'kopie.titel': "Copia de datos",
    'kopie.text': "Puede solicitar una copia de sus datos desde la cancelación hasta 30 días después del fin del contrato.",
    'kopie.frist': "Posible hasta el {datum}.",
    'kopie.anfordern': "Solicitar copia de datos",
    'kopie.angefordert': "Solicitada el {datum}. Recibirá un correo cuando la copia esté lista.",
    'kopie.bereit': "Su copia de datos está lista: {format}.",
    'kopie.passwort': "Su contraseña de acceso",
    'kopie.passwortHinweis': "Esta contraseña cifra el archivo ZIP: ábralo con doble clic y la misma contraseña.",
    'kopie.herunterladen': "Descargar",
    'kopie.geladen': "Descargado. Guarde el archivo en un lugar seguro.",
    'kopie.formatLesbar': "PDF y tablas (Excel)",
    'kopie.formatWiederherstellung': "Archivo de restauración",
    'kopie.nurAdmin': "La copia de datos la solicita un administrador de su empresa.",
    'abo.agbAnsehen': "Ver condiciones",
    'abo.agbZustimmung': "He leído las condiciones generales y las acepto.",
    'abo.aufbewahrung': "Recuerde sus obligaciones de conservación: facturas y justificantes contables 8 años, libros y cuentas anuales 10 años.",
    'abo.aufpreisHinweis': "En la próxima factura: {betrag} netos de recargo por el cambio de plan del {datum}.",
    'abo.bestaetigen': "Confirmar cambio",
    'abo.erstZuruecknehmen': "El contrato está cancelado. Para cambiar de plan, retire primero la cancelación.",
    'abo.gekuendigt': "Cancelado a partir del {datum}. Hasta entonces, use LagerPro con normalidad.",
    'abo.gekuendigtMeldung': "Cancelado a partir del {datum}. Le enviamos una confirmación por correo.",
    'abo.gewechselt': "Ahora usa {plan}.",
    'abo.hochstufen': "Subir de plan",
    'abo.ihrTarif': "Su plan",
    'abo.jetztKuendigen': "Cancelar ahora",
    'abo.kostenpflichtig': "Cambiar con coste",
    'abo.kuendigen': "Cancelar contrato",
    'abo.kuendigenText': "La cancelación surte efecto el {datum}. Hasta entonces, use LagerPro con normalidad; después puede solicitar una copia de sus datos durante 30 días.",
    'abo.kuendigenTextTest': "Está en su mes gratuito: el contrato termina con él el {datum} y no se cobra nada.",
    'abo.kuendigenTitel': "Cancelar contrato",
    'abo.mandatAendern': "Cambiar datos bancarios",
    'abo.mandatBisTestende': "Configure antes un mandato de domiciliación SEPA; si no, LagerPro se pausa tras el mes gratuito.",
    'abo.nurAdmin': "El plan y el contrato los gestiona un administrador de su empresa.",
    'abo.runterstufen': "Bajar de plan",
    'abo.sonderkondition': "Tiene una condición especial. Para cambiar de plan, contacte con soporte.",
    'abo.testDanach': "Después, {plan} cuesta {preis} al mes.",
    'abo.testLaeuft': "Mes gratuito: quedan {n} días, hasta el {datum} incluido.",
    'abo.vorgemerkt': "Cambio a {plan} programado desde el {datum}.",
    'abo.vorgemerktMeldung': "Cambio a {plan} programado desde el {datum}.",
    'abo.vormerkungWeg': "Cambio programado retirado.",
    'abo.vormerkungZurueck': "Retirar cambio programado",
    'abo.waehlen': "Elegir",
    'abo.wechselHoch': "Efecto inmediato. Si este mes ya está facturado, la diferencia por los días restantes figura en la próxima factura. Desde el mes que viene {preis} al mes.",
    'abo.wechselRunter': "Efecto a partir del {datum}. Hasta entonces conserva todas las funciones de {plan}.",
    'abo.wechselTest': "Efecto inmediato. Gratis durante el mes gratuito; después {preis} al mes.",
    'abo.wechselTitel': "Cambiar a {plan}",
    'abo.wechselnHinweis': "Subir de plan es inmediato, bajar es a fin de mes.",
    'abo.wechselnTitel': "Cambiar de plan",
    'abo.zurueckMeldung': "Cancelación retirada: su contrato continúa.",
    'abo.zuruecknehmen': "Retirar cancelación",
    'abo.beendetTitel': "Su contrato ha finalizado",
    'abo.beendetText': "Su contrato de LagerPro finalizó el {datum}.",
    'abo.beendetKopie': "Puede solicitar una copia de sus datos hasta el {bis}; después se eliminarán.",
    'firma.titel': 'Empresa',
    'firma.anschrift': 'Dirección y contacto',
    'firma.steuerRegister': 'Impuestos y registro',
    'firma.girocode': 'Imprimir un código de pago en las facturas',
    'firma.girocodeText': 'Un código QR que el cliente escanea con su app bancaria: beneficiario, importe y concepto ya rellenados. Solo aparece en facturas pendientes por transferencia.',
    'firma.ibanFehlt': 'El código de pago necesita un IBAN.',
    'firma.ibanUngueltig': 'Este IBAN no supera el dígito de control. Revíselo; hasta entonces no se imprime ningún código.',
    'firma.bank': 'Datos bancarios',
    'firma.fusstext': 'Texto de pie',
    'firma.logo': 'Logotipo',
    'firma.logoWaehlen': 'Elegir logotipo',
    'firma.logoEntfernen': 'Quitar logotipo',
    'firma.speichern': 'Guardar datos',
    'firma.aufRechnung': 'Aparece en cada factura',
    'firma.pflichtangaben': 'Datos obligatorios en facturas',
    'firma.imFuss': 'Se muestra al pie de la factura',
    'passwort.willkommen': 'Bienvenido',
    'passwort.warnung': 'Su contraseña actual le fue entregada y por tanto la conoce otra persona. Establezca la suya ahora.',
    'passwort.regel8': 'Mínimo 8 caracteres',
    'passwort.regelGross': 'Mayúsculas y minúsculas',
    'passwort.regelZiffer': 'Al menos un dígito',
    'passwort.regelGleich': 'Ambas entradas coinciden',
    'passwort.regelAnders': 'Distinta de la contraseña recibida',
    'passwort.fortfahren': 'Establecer y continuar',
    'lager.anzahlArtikel': 'artículos',
    'lager.nurKnappe': 'Solo bajo mínimo',
    'lager.etiketten': 'Etiquetas',

    'zfa.titel': 'Confirmación',
    'zfa.gesendet': 'Le hemos enviado un código a',
    'zfa.code': 'Código del correo',
    'zfa.bestaetigen': 'Confirmar',
    'zfa.gueltig': 'El código es válido {min} minutos.',
    'zfa.andererZugang': 'Otro acceso',
    'zfa.einstellung': 'Acceso en dos pasos',
    'zfa.erklaerung': 'Además de la contraseña, se envía un código a su correo en cada acceso. Conocer la contraseña ya no basta.',
    'zfa.aktiv': 'Activado',
    'zfa.aus': 'Desactivado',
    'zfa.einschalten': 'Activar',
    'zfa.ausschalten': 'Desactivar',
    'zfa.mailFrage': '¿A qué dirección deben ir los códigos?',
    'zfa.codeSenden': 'Enviar código',
    'zfa.passwortFrage': 'Introduzca su contraseña para desactivar',
    'zfa.keinVersand': 'Aún no hay envío de correo configurado. Sin él no se puede entregar ningún código.',
    'zfa.eingeschaltet': 'El acceso en dos pasos está activado.',
    'zfa.ausgeschaltet': 'El acceso en dos pasos está desactivado.',
    'zfa.methodeApp': "App de autenticación",
    'zfa.methodeMail': "Código por correo",
    'zfa.erklaerungBeide': "Además de la contraseña, LagerPro pide un código en cada inicio de sesión — de una app de autenticación en su móvil o por correo. La contraseña sola ya no basta.",
    'zfa.ersatzUebrig': "Quedan {n} códigos de respaldo",
    'zfa.mitApp': "Activar con app de autenticación",
    'zfa.mitMail': "Activar por correo",
    'zfa.zurApp': "Cambiar a la app de autenticación",
    'zfa.ersatzNeu': "Nuevos códigos de respaldo",
    'zfa.appEmpfohlen': "Recomendado: la app. Funciona sin conexión y sin correo — p. ej. Google Authenticator o Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "Crear nuevos códigos de respaldo — los anteriores dejan de valer.",
    'zfa.passwortWeiter': "Introduzca su contraseña",
    'zfa.weiter': "Continuar",
    'zfa.schritt1': "Abra una app de autenticación (p. ej. Google Authenticator) y elija «Añadir código».",
    'zfa.scannen': "Escanee este código QR.",
    'zfa.schritt3': "Introduzca abajo el código de 6 dígitos de la app.",
    'zfa.manuell': "¿No puede escanear? Introduzca esta clave:",
    'zfa.appCode': "Código de la app",
    'zfa.ersatzTitel': "Sus códigos de respaldo",
    'zfa.ersatzHinweis': "Si pierde el móvil: cada código sustituye una vez al de la app. Guárdelos en lugar seguro — solo se muestran ahora.",
    'zfa.kopieren': "Copiar",
    'zfa.kopiert': "Copiado",
    'zfa.herunterladen': "Guardar como archivo",
    'zfa.gesichert': "He guardado los códigos",
    'zfa.fertig': "Listo",
    'zfa.appText': "Abra su app de autenticación e introduzca el código de 6 dígitos de LagerPro.",
    'zfa.ersatzText': "Introduzca uno de sus códigos de respaldo. Cada uno vale una vez.",
    'zfa.ersatzCode': "Código de respaldo",
    'zfa.ersatzNutzen': "¿Sin móvil? Usar un código de respaldo",
    'zfa.appCodeNutzen': "Usar el código de la app",
    'nav.app': "App",
    'start.nav.app': "App",
    'desktop.titel': "LagerPro como app",
    'desktop.text': "Ventana e icono propios en el Dock o la barra de tareas, se abre con un clic. Inicie sesión una vez — seguirá conectado 90 días.",
    'desktop.inApp': "Está usando la app de escritorio",
    'desktop.inAppText': "Versión {version}. La interfaz se actualiza sola.",
    'desktop.alsWebApp': "LagerPro está instalado como app en este equipo.",
    'desktop.installiert': "Instalado — ahora encontrará LagerPro entre sus aplicaciones.",
    'desktop.direktTitel': "Instalar directamente desde el navegador",
    'desktop.schnell': "segundos",
    'desktop.direktText': "Sin descarga, sin advertencias, siempre actualizado.",
    'desktop.safari': "En Safari: menú «Archivo» → «Añadir al Dock» (macOS Sonoma o posterior).",
    'desktop.firefox': "Firefox no puede instalar apps web — use Chrome o Edge, o descargue abajo.",
    'desktop.chromeLeiste': "Haga clic en el icono «Instalar» de la barra de direcciones — o menú ⋮ → «Instalar LagerPro».",
    'desktop.andererBrowser': "Lo más fácil con Chrome, Edge o Safari — o descargue la app abajo.",
    'desktop.jetztInstallieren': "Instalar ahora",
    'desktop.downloadTitel': "O descargar",
    'desktop.version': "Versión {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 y 11, 64 bits",
    'desktop.macApple': "Mac con chip de Apple",
    'desktop.macAppleUnter': "M1, M2, M3, M4 · desde 2020",
    'desktop.macIntel': "Mac con chip Intel",
    'desktop.macIntelUnter': "Mac anteriores hasta 2020",
    'desktop.passtZuIhnen': "Adecuado para este equipo",
    'desktop.laden': "Descargar",
    'desktop.bald': "Próximamente",
    'desktop.warnungTitel': "¿Advertencia al abrir por primera vez?",
    'desktop.warnungText': "Las descargas aún no están registradas en Microsoft y Apple, por eso su equipo pregunta una vez al primer inicio.",
    'desktop.warnungWindows': "En «Windows protegió su PC», haga clic en «Más información» y luego «Ejecutar de todas formas».",
    'desktop.warnungMac': "Arrastre LagerPro a «Aplicaciones». Si aparece un aviso, vaya a «Ajustes del Sistema → Privacidad y seguridad» y pulse «Abrir igualmente» (macOS anterior: clic derecho → «Abrir»).",
    'desktop.anmeldung': "En ambos casos: inicie sesión una vez con contraseña (y código de dos factores), luego 90 días sin escribir. Cerrar sesión lo termina de inmediato.",
    'desktop.mehrInfos': "Todo sobre la app",

    'erinnerung.titel': 'Recordatorios de pago',
    'erinnerung.erklaerung': 'Si una factura está vencida, LagerPro envía automáticamente un recordatorio amable a su cliente — en su nombre y con su dirección para respuestas.',
    'erinnerung.nachTagen': 'Recordar tras … días de retraso',
    'erinnerung.wiederholung': 'Repetir tras … días',
    'erinnerung.maxAnzahl': 'Como máximo … recordatorios por factura',
    'erinnerung.faellig': 'Afectado en la próxima ejecución',
    'erinnerung.jetzt': 'Enviar ahora',
    'erinnerung.keineFaellig': 'Ninguna factura está vencida ahora.',
    'erinnerung.firmenmailFehlt': 'Añada su correo en «Empresa» — de lo contrario sus clientes no podrán responder.',
    'erinnerung.keineFuerKunden': 'Sin recordatorios a este cliente',
  },

  tr: {
    'start.nav.funktionen': 'Özellikler',
    'start.nav.belegkette': 'Belge zinciri',
    'start.nav.preise': 'Fiyatlar',
    'start.kostenlosStarten': 'Ücretsiz başla',
    'start.themaDunkel': 'Koyu tema',
    'start.themaHell': 'Açık tema',
    'start.sprache': 'Dil',
    'start.hero.marke': 'GoBD uyumlu · Sunucular AB’de',
    'start.hero.titel1': 'Depo, kasa ve muhasebe.',
    'start.hero.titel2': 'Beş sistem yerine bir tane.',
    'start.hero.text': 'LagerPro işletmenizi tekliften vergi beyanına kadar yürütür: stok, satış, belgeler ve muhasebe birbirini besler. Dışa aktarma yok, yeniden yazma yok, hiçbir şeyi iki yerde tutmak yok.',
    'start.hero.konto': 'Hesabım var',
    'start.hero.fussnote': 'Kredi kartı gerekmez · İki dakikada kurulur · Aylık iptal edilebilir',
    'start.vorschau.umsatz': 'Bugünkü ciro',
    'start.vorschau.offen': 'Açık faturalar',
    'start.vorschau.lagerwert': 'Stok değeri',
    'start.vorschau.knapp': 'Asgarinin altında',
    'start.vorschau.gruss': 'Günaydın, Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'Son belgeler',
    'start.vorschau.angebot': 'Teklif',
    'start.vorschau.lieferschein': 'İrsaliye',
    'start.vorschau.rechnung': 'Fatura',
    'start.vertrauen1t': 'GoBD uyumlu',
    'start.vertrauen1x': 'Boşluksuz numaralandırma, değiştirilemez belgeler, özet değerle zincirlenmiş kayıtlar — Alman muhasebe standardı',
    'start.vertrauen2t': 'GDPdU dışa aktarımı',
    'start.vertrauen2x': 'index.xml ile Z3 veri teslimi — denetim geldiğinde hazır',
    'start.vertrauen3t': 'Sunucular AB’de',
    'start.vertrauen3x': 'Veri aktarımı yok, reklam izleyicisi yok, ilk günden KVKK/GDPR uyumu',
    'start.vertrauen4t': '10 yıl saklama',
    'start.vertrauen4x': 'Alman vergi kanunu § 147 gereği, otomatik olarak — aklınızda tutmanız gereken bir şey yok',
    'start.f1t': 'Belgeyi fotoğrafla, bitti',
    'start.f1x': 'İrsaliyeyi veya faturayı telefonla çekin — asistan kalemleri, miktarları ve tedarikçiyi okur. Bir kez kontrol edersiniz, mal depoya girer. Stok ve rakamlarla ilgili soruları yazılı ya da sesli yanıtlar.',
    'start.f2t': 'Depo kontrol altında',
    'start.f2x': 'Anlık stok, asgari stok uyarısı, mal kabulü ve beş boyutta barkodlu etiket.',
    'start.f3t': 'Tezgâh için kasa',
    'start.f3x': 'Müşteriyi seç, ürünleri okut, tahsil et. Nakit satış da olur, stok kendiliğinden düşer.',
    'start.f4t': 'Bütün belgeler',
    'start.f4x': 'Teklif, sipariş onayı, irsaliye, fatura, iade faturası ve iptal — kendi logonuzla PDF olarak.',
    'start.f5t': 'Tutan muhasebe',
    'start.f5x': 'Form satırlarıyla gelir-gider hesabı, KDV beyanı, araç defteri ve mali müşaviriniz için DATEV dışa aktarımı.',
    'start.f6t': 'Karmaşasız iadeler',
    'start.f6x': 'Her zaman fatura üzerinden: müşteri, belge, kalemler. Mal depoya döner, alacak müşteri hesabına geçer.',
    'start.f7t': 'Ekip de dahil',
    'start.f7x': 'Zamanlayıcılı görevler, bildirimli sohbet, çevrimiçi durumu — ikinci bir uygulamaya gerek kalmadan.',
    'start.funktionen.titel': 'İşletmenin her gün ihtiyaç duyduğu her şey',
    'start.funktionen.text': 'Her alan diğerlerinin verisini bilir. Bir şey satarsınız: stok düşer, belge oluşur ve muhasebe kaydı hazırdır — aynı anda.',
    'start.kette.titel': 'Tek iş, dört belge, sıfır yeniden yazma',
    'start.kette.text': 'Tek tıkla teklif sipariş onayına, o irsaliyeye, o da faturaya dönüşür. Kalemler ve fiyatlar birlikte taşınır. Mal stoktan tam olarak bir kez çıkar.',
    'start.kette.schritt': 'Adım',
    'start.kette1t': 'Teklif',
    'start.kette1x': 'Maliyeti ne olacak',
    'start.kette2t': 'Sipariş',
    'start.kette2x': 'Müşteri onaylıyor',
    'start.kette3t': 'İrsaliye',
    'start.kette3x': 'Mal çıkıyor',
    'start.kette4t': 'Fatura',
    'start.kette4x': 'Para giriyor',
    'start.kette.fuss': 'Bir şey geri dönerse iade faturası ve iptal faturası devreye girer — vergi dairesinin istediği ters kayıtla birlikte.',
    'start.preise.titel': 'Küçük puntosuz fiyatlar',
    'start.preise.text': "Aylık iptal edilebilir, tüm fiyatlara KDV eklenir. Her paket 30 gün ücretsiz başlar — banka bilgisi gerekmez.",
    'start.preise.laedt': 'Paketler yükleniyor …',
    'start.preise.fehler': 'Paketler şu anda görüntülenemiyor. Bize yazın, koşulları iletelim.',
    'start.preise.top': 'En çok seçilen',
    'start.preise.proMonat': '/ ay',
    'start.preise.sofort': "30 gün ücretsiz dene",
    'start.preise.waehlen': "{tarif} paketini 30 gün ücretsiz dene",
    'start.ende.titel': 'Bugün başlayın, yarın tabloyu görün',
    'start.ende.text': 'Hesabı açın, ürünleri girin, ilk faturayı yazın. Mevcut verilerinizi sonra aktarabilirsiniz.',
    'start.fuss.hinweis': 'Sunucular AB’de · GoBD uyumlu',
    'nav.support': 'Destek',
    'support.vonAnbieter': 'LagerPro\'dan',
    'support.titel': 'Destek',
    'support.untertitel': 'Sorular, hatalar, istekler — bize buradan yazın. Tüm yazışma tek bir yerde kalır.',
    'support.neu': 'Yeni talep',
    'support.betreff': 'Konu',
    'support.kategorie': 'Ne ile ilgili?',
    'support.kat.frage': 'Soru',
    'support.kat.fehler': 'Bir şey çalışmıyor',
    'support.kat.wunsch': 'İstek veya fikir',
    'support.kat.abrechnung': 'Fatura ve paket',
    'support.kat.sonstiges': 'Diğer',
    'support.nachricht': 'Mesaj',
    'support.nachrichtPlatzhalter': 'Ne olduğunu ve ne beklediğinizi olabildiğince açık anlatın.',
    'support.anhang': 'Ekran görüntüsü veya dosya ekle',
    'support.anhangHinweis': 'En fazla 5 dosya, her biri 25 MB',
    'support.senden': 'Talebi gönder',
    'support.gesendet': '{nummer} numaralı talebiniz ulaştı. Yanıt gelir gelmez e-posta alacaksınız.',
    'support.leer': 'Henüz talep yok',
    'support.leerText': 'Bir şey çalışmıyorsa ya da sorunuz varsa bize yazın.',
    'support.status.offen': 'Alındı',
    'support.status.in_arbeit': 'İşlemde',
    'support.status.wartet': 'Yanıt var',
    'support.status.erledigt': 'Çözüldü',
    'support.zurueck': 'Tüm talepler',
    'support.antworten': 'Yanıtla',
    'support.antwortPlatzhalter': 'Mesajınız …',
    'support.erledigt': 'Çözüldü olarak işaretle',
    'support.erledigtHinweis': 'Çözüldü. Hâlâ bir şey varsa yazmaya devam edin — talep yeniden açılır.',
    'support.sie': 'Siz',
    'support.team': 'LagerPro Destek',
    'support.ungelesen': 'yeni',
    'support.glocke': 'Destekten yanıt',
    'support.anhaengen': 'Ekle',
    'einheit.stueck': 'Adet',
    'einheit.paar': 'Çift',
    'einheit.satz': 'Takım',
    'einheit.paket': 'Paket',
    'einheit.karton': 'Koli',
    'einheit.palette': 'Palet',
    'einheit.rolle': 'Rulo',
    'einheit.flasche': 'Şişe',
    'einheit.dose': 'Kutu',
    'einheit.beutel': 'Torba',
    'einheit.minute': 'dk',
    'einheit.stunde': 'sa',
    'einheit.tag': 'Gün',
    'einheit.woche': 'Hafta',
    'einheit.monat': 'Ay',
    'einheit.jahr': 'Yıl',
    'einheit.pauschale': 'Götürü',
    'einheit.gruppe.anzahl': 'Adet ve ambalaj',
    'einheit.gruppe.gewicht': 'Ağırlık',
    'einheit.gruppe.volumen': 'Hacim',
    'einheit.gruppe.laenge': 'Uzunluk ve alan',
    'einheit.gruppe.zeit': 'Zaman',
    'einheit.gruppe.sonstiges': 'Diğer',
    'einheit.andere': 'Başka birim …',
    'einheit.eigene': 'Özel birim',
    'einheit.eigenePlatzhalter': 'ör. demet, kasa',
    'positionen.umgerechnet': '= depodan {menge}',
    'positionen.passtNicht': 'Bu ürün {einheit} cinsinden tutuluyor — bu birim dönüştürülemez.',
    'positionen.jeEinheit': '{einheit} başına',
    'nav.assistent': 'Yapay zekâ asistanı',
    'assistent.datenschutz': 'Değerlendirme için belge ve sorunuz yapay zekâ sağlayıcımıza iletilir ve yalnızca yanıtlamak için işlenir; model eğitiminde kullanılmaz. Stok verileriniz sunucumuzda kalır.',
    'pruef.titel': '{art} kontrolü',
    'pruef.beleg': 'Belge',
    'pruef.lieferschein': 'İrsaliye',
    'pruef.eingangsrechnung': 'Alış faturası',
    'pruef.gutschrift': 'İade faturası',
    'pruef.bestellung': 'Sipariş',
    'pruef.kassenbon': 'Kasa fişi',
    'pruef.erkannt': '% okundu',
    'pruef.ohneNummer': 'Numara ve tarih yok',
    'pruef.schwerLesbar': 'Bu belge zor okundu',
    'pruef.schwerLesbarText': 'Lütfen her satırı gözden geçirin. Bulanık veya eğri fotoğrafta rakamlar karışır. İyi ışıkta çekilmiş yeni bir fotoğraf düzeltmekten çoğu zaman daha iyidir.',
    'pruef.nichtAusgewertet': 'Değerlendirilmedi',
    'pruef.nurBilder': 'yalnızca fotoğraf ve PDF okunabilir',
    'pruef.vorhandener': 'Mevcut',
    'pruef.neuAnlegen': 'Yeni oluştur',
    'pruef.ohne': 'Yok',
    'pruef.bitteWaehlen': '— seçiniz —',
    'pruef.gewaehlt': '{b} kalemden {a} seçili',
    'pruef.alleUebernehmen': 'Tümünü seç',
    'pruef.keine': 'Hiçbiri',
    'pruef.keinePositionen': 'Belgede hiçbir kalem tanınmadı.',
    'pruef.warenwert': 'Seçimin mal değeri',
    'pruef.rechnungsbetrag': 'Fatura tutarı',
    'pruef.brauchtBuchhaltung': 'Gideri kaydetmek için muhasebe gerekir — Professional tarifesinden itibaren dahildir.',
    'pruef.alsAusgabe': 'Tutarı işletme gideri olarak kaydet',
    'pruef.alsAusgabeText': 'Belge kanıt olarak eklidir; böylece gider muhasebe için eksiksizdir.',
    'pruef.brutto': 'Brüt tutar',
    'pruef.steuersatz': 'KDV oranı',
    'pruef.belegnummer': 'Belge numarası',
    'pruef.belegdatum': 'Belge tarihi',
    'pruef.lastschrift': 'Otomatik ödeme',
    'pruef.keineWare': 'Mal değil',
    'pruef.keineWareText': 'Fatura tutarına dahildir ama depoya girmez.',
    'pruef.grund.keineWare': 'Kargo, ücret veya iskonto — stoklanabilir ürün değil',
    'pruef.privat': 'Şahsen karşılandı',
    'pruef.erstMitKnopf': 'Bu düğmeye basılana kadar hiçbir şey kaydedilmez.',
    'pruef.buchen': 'Onayla ve kaydet',
    'pruef.wirdGebucht': 'Kaydediliyor',
    'pruef.zugeordnet': 'Eşleşti',
    'pruef.vermutet': 'Muhtemel',
    'pruef.neu': 'Yeni',
    'pruef.schlechtLesbar': 'Zor okunuyor',
    'pruef.aendern': 'Değiştir',
    'pruef.zuklappen': 'Kapat',
    'pruef.wirdAngelegt': 'yeni ürün olarak oluşturulacak',
    'pruef.artikel': 'Ürün',
    'pruef.nichtZugeordnet': '— eşleşmedi —',
    'pruef.alsNeuenArtikel': 'Yeni ürün olarak oluştur',
    'pruef.einkaufspreis': 'Alış fiyatı',
    'pruef.verkaufspreis': 'Satış fiyatı',
    'pruef.spaeterErgaenzen': 'Sonra eklenebilir',
    'pruef.ean': 'EAN',
    'pruef.je': 'adet başına',
    'pruef.grund.ean': 'EAN eşleşiyor',
    'pruef.grund.nummer': 'Ürün numarası eşleşiyor',
    'pruef.grund.name': 'Tanım birebir eşleşiyor',
    'pruef.grund.ustId': 'Vergi numarası eşleşiyor',
    'pruef.grund.aehnlich': 'Benzer tanım — lütfen kontrol edin',
    'pruef.grund.keiner': 'Depoda eşleşen ürün yok',
    'pruef.kat.wareneinkauf': 'Mal alımı',
    'pruef.kat.buero': 'Kırtasiye',
    'pruef.kat.fahrzeug': 'Araç giderleri',
    'pruef.kat.werbung': 'Reklam giderleri',
    'pruef.kat.kommunikation': 'Telefon ve internet',
    'pruef.kat.raumkosten': 'Kira ve işyeri',
    'pruef.kat.sonstiges': 'Diğer işletme giderleri',
    'assistent.titel': 'Yapay zekâ asistanı',
    'assistent.rest': 'Bu ay kalan',
    'assistent.scans': 'belge taraması',
    'assistent.fragen': 'soru',
    'assistent.testmodus': 'Test modu',
    'assistent.testmodusText': 'Yapay zekâ erişimi tanımlı değil. Belgeler örnek veri döndürür, yanıtlar yer tutucudur. Erişim tanımlandığında asistan gerçek verilerinizle çalışır.',
    'assistent.verlaufLoeschen': 'Geçmişi sil',
    'assistent.denkt': 'Bir saniye',
    'assistent.platzhalter': 'Soru sorun veya belge fotoğraflayın',
    'assistent.hoertZu': 'Dinliyorum',
    'assistent.sprechen': 'Konuş',
    'assistent.vorlesen': 'Sesli oku',
    'assistent.stopp': 'Dur',
    'assistent.senden': 'Gönder',
    'assistent.fotografieren': 'Belge fotoğrafla',
    'assistent.datei': 'Dosya yükle',
    'assistent.belegAuswerten': 'Belgeyi oku',
    'assistent.wertetAus': 'Belge okunuyor',
    'assistent.bestaetigen': 'Onayla',
    'assistent.gebucht': 'Kaydedildi',
    'assistent.verworfen': 'Vazgeçildi',
    'assistent.willkommen': 'Nasıl yardımcı olabilirim?',
    'assistent.willkommenText': 'Stok ve rakamları sorun ya da bir irsaliye fotoğraflayın — kalemler siz kontrol ettikten sonra depoya girer.',
    'assistent.beispiel1': 'Hangi ürünler azaldı?',
    'assistent.beispiel2': 'Bu ay ne kadar ciro yaptım?',
    'assistent.beispiel3': 'Hangi faturalar hâlâ açık?',
    'assistent.beispiel4': 'Stok değerim ne kadar?',
    'allg.entfernen': 'Kaldır',
    'bestaetigung.titel': 'E-posta adresinizi doğrulayın',
    'bestaetigung.text': 'Size bir ileti gönderdik. İçindeki bağlantıyı açın, sonrası kolay.',
    'bestaetigung.warum': 'Doğrulanmamış adrese ne giriş kodu, ne fatura, ne de otomatik ödeme ön bildirimi ulaşır.',
    'bestaetigung.schonBestaetigt': 'Doğruladım — devam',
    'bestaetigung.erneut': 'İletiyi yeniden gönder',
    'bestaetigung.erneutGesendet': 'İleti yolda.',
    'bestaetigung.versandFehlt': 'Şu anda gönderim mümkün değil. Lütfen destekle iletişime geçin.',
    'bestaetigung.fehlgeschlagen': 'Bu işe yaramadı.',
    'bestaetigung.spam': 'Hiçbir şey gelmedi mi? Lütfen gereksiz klasörüne bakın.',
    'bestaetigung.pruefe': 'Bir saniye …',
    'bestaetigung.fertigTitel': 'Adres doğrulandı',
    'bestaetigung.fertigText': 'E-posta adresiniz doğrulandı. Çalışmaya devam edebilirsiniz.',
    'bestaetigung.zurApp': 'Uygulamaya git',
    'bestaetigung.abgelaufenTitel': 'Bağlantının süresi doldu',
    'bestaetigung.abgelaufenText': 'Bu bağlantı artık geçerli değil. Giriş yapıp yeni bir ileti isteyin.',
    'bestaetigung.ungueltigTitel': 'Bağlantı geçersiz',
    'bestaetigung.ungueltigText': 'Bu bağlantı kullanılamıyor. Adres zaten doğrulanmış olabilir.',
    'mandat.pflichtTitel': 'SEPA otomatik ödeme talimatı',
    'mandat.pflichtText': 'Devam etmek için SEPA otomatik ödeme talimatı gerekir. İki dakika sürer. Verileriniz olduğu gibi kalır.',
    'mandat.einleitung': 'Talimatı hesap sahibi ve IBAN ile verirsiniz. Kâğıt üzerinde imzaya gerek yok.',
    'mandat.kontoinhaber': 'Hesap sahibi',
    'mandat.kontoinhaberHinweis': 'Bankanızdaki adla birebir aynı.',
    'mandat.bicHinweis': 'SEPA alanında gerekli değil.',
    'mandat.ibanFalsch': 'Bu IBAN’ın kontrol hanesi tutmuyor.',
    'mandat.wortlautTitel': 'Talimatın metni',
    'mandat.glaeubiger': 'Alacaklı',
    'mandat.glaeubigerId': 'Alacaklı kimlik numarası',
    'mandat.referenz': 'Talimat referansı',
    'mandat.referenzSpaeter': 'talimat verildikten sonra atanır ve ilk tahsilattan önce bildirilir',
    'mandat.zustimmung': 'Yukarıdaki otomatik ödeme talimatını kabul ediyorum.',
    'mandat.erteilen': 'Talimatı ver',
    'mandat.erteilt': 'Talimat verildi. Teşekkürler.',
    'mandat.liegtVor': 'Geçerli bir talimat kayıtlı.',
    'mandat.nochNichtBereit': 'Otomatik ödeme henüz kuruluyor. Lütfen sonra deneyin ya da destekle iletişime geçin.',
    'mandat.ladefehler': 'Bilgiler yüklenemedi.',
    'mandat.fehlgeschlagen': 'Talimat verilemedi.',
    'allg.optional': 'isteğe bağlı',
    'sperre.titel': 'LagerPro kilitli',
    'sperre.entsperren': 'Kilidi aç',
    'sperre.abgebrochen': 'Doğrulama iptal edildi.',
    'geraete.titel': 'Oturum açık cihazlar',
    'geraete.hinweis': 'Oturumunuzun açık kaldığı cihazlar.',
    'geraete.hinweisAdmin': 'İşletmenizdeki tüm cihazlar. Kaybolan ya da ayrılan birinin cihazını buradan kapatın.',
    'geraete.keine': 'Sürekli oturum açık cihaz yok.',
    'geraete.dieses': 'bu cihaz',
    'geraete.zuletzt': 'son görülme',
    'geraete.abmelden': 'Oturumu kapat',
    'geraete.abmeldenFehler': 'Cihazın oturumu kapatılamadı.',
    'geraete.ladefehler': 'Cihazlar yüklenemedi.',
    'geraete.nichtGemerkt': 'Bu cihaz hatırlanmıyor. Bir kez parolayla giriş yapın, sonrasında parmak iziniz yeter.',
    'geraete.sperreTitel': 'Açılışta kilit',
    'geraete.sperreAn': 'Uygulamayı {art} ile kilitle',
    'geraete.sperreText': 'Açılışta ve arka planda beş dakika sonra yeniden sorulur.',
    'geraete.sperreAusWarnung': 'Kilit olmadan, kilidi açık telefonunuzu eline alan herkes işletme verilerinize ulaşır.',
    'geraete.alleAnderen': "Diğerlerinin hepsinden çık",
    'geraete.gueltigBis': "{datum} tarihine kadar oturum açık",
    'geraete.browserTitel': "Tarayıcıda oturum açık",
    'geraete.diese': "bu oturum",
    'geraete.browserHinweis': "Tarayıcı oturumları 24 saat sonra kendiliğinden biter — veya burada hemen.",
    'app.name': 'LagerPro',
    'app.untertitel': 'Profesyonel depo yönetimi',
    'benachrichtigung.titel': 'Bildirimler',
    'benachrichtigung.leer': 'Yeni bir şey yok',
    'benachrichtigung.neu': 'Yeni bildirim',
    'benachrichtigung.neueVorgaenge': 'yeni öğe',
    'benachrichtigung.anAlle': 'herkese',
    'nav.uebersicht': 'Genel bakış',
    'nav.mehr': 'Daha fazla',
    'allg.rechnung': 'fatura',
    'app.gruss': 'Merhaba, {name}',
    'app.gruppe.tagesgeschaeft': 'Günlük işler',
    'app.gruppe.kontakte': 'Kişiler',
    'app.gruppe.zusammenarbeit': 'İş birliği',
    'app.gruppe.auswertung': 'Değerlendirme',
    'app.gruppe.betrieb': 'İşletme',
    'app.nochmalBeenden': 'Çıkmak için tekrar basın',
    'app.keineVerbindung': 'Bağlantı yok — veriler eksik',
    'app.konto': 'Hesap',
    'app.darstellung': 'Görünüm',
    'nav.kasse': 'Kasa',
    'nav.lager': 'Depo',
    'nav.belege': 'Belgeler',
    'nav.rechnungen': 'Faturalar',
    'nav.kunden': 'Müşteriler',
    'nav.lieferanten': 'Tedarikçiler',
    'nav.retouren': 'İadeler',
    'nav.buchhaltung': 'Muhasebe',
    'nav.ausgaben': 'Giderler',
    'nav.bank': 'Banka',
    'nav.fahrtenbuch': 'Seyir defteri',
    'nav.aufgaben': "Pano",
    'nav.chat': 'Sohbet',
    'nav.team': 'Ekip',
    'nav.firma': 'Firma',
    'nav.gobd': 'GoBD ve denetim',
    'nav.tarif': 'Tarife',
    'nav.uebernahme': 'Veri aktarımı',
    'nav.einstellungen': 'Ayarlar',
    'nav.owner': 'Müşteri yönetimi',
    'nav.mandanten': 'Kiracılar',
    'nav.gruppe.verkauf': 'Satış',
    'nav.gruppe.waren': 'Ürünler',
    'nav.gruppe.kontakte': 'Kişiler',
    'nav.gruppe.auswertung': 'Raporlar',
    'nav.gruppe.zusammenarbeit': 'İşbirliği',
    'nav.gruppe.verwaltung': 'Yönetim',
    'aktion.anlegen': 'Oluştur',
    'aktion.speichern': 'Kaydet',
    'aktion.abbrechen': 'İptal',
    'aktion.bearbeiten': 'Düzenle',
    'aktion.loeschen': 'Sil',
    'aktion.schliessen': 'Kapat',
    'aktion.suchen': 'Ara',
    'aktion.anmelden': 'Giriş yap',
    'aktion.abmelden': 'Çıkış yap',
    'aktion.drucken': 'Yazdır',
    'aktion.herunterladen': 'İndir',
    'allg.suche': 'Ara',
    'allg.laedt': 'Yükleniyor',
    'allg.keineTreffer': 'Sonuç bulunamadı',
    'allg.darstellung': 'Görünüm',
    'allg.sprache': 'Dil',
    'allg.thema.system': 'Sistem',
    'allg.thema.hell': 'Açık',
    'allg.thema.dunkel': 'Koyu',
    'allg.rolle.inhaber': 'Sahip',
    'allg.rolle.admin': 'Yönetici',
    'allg.rolle.mitarbeiter': 'Çalışan',
    'allg.nichtImTarif': 'Tarifenizde bulunmuyor',
    'allg.tarif': 'Tarife',
    'allg.menue': 'Menü',
    'allg.schnellzugriff': 'Hızlı erişim',
    'allg.schnellzugriffHinweis': "Sayfa, müşteri veya belge no …",
    'anmelden.willkommen': 'Tekrar hoş geldiniz',
    'anmelden.hinweis': 'Devam etmek için giriş yapın',
    'anmelden.kennung': 'Kullanıcı adı veya e-posta',
    'anmelden.passwort': 'Şifre',
    'anmelden.fehler': 'Giriş başarısız.',
    'anmelden.keinKonto': 'Hesabınız yok mu?',
    'anmelden.jetztRegistrieren': 'Ücretsiz kayıt olun',
    'passwort.vergessenLink': "Şifrenizi mi unuttunuz?",
    'passwort.vergessenTitel': "Şifremi unuttum",
    'passwort.vergessenText': "Kullanıcı adınızı veya e-posta adresinizi girin. Yeni şifre belirlemeniz için bir bağlantı göndereceğiz.",
    'passwort.linkSenden': "Bağlantı gönder",
    'passwort.gesendetTitel': "Gelen kutunuzu kontrol edin",
    'passwort.gesendetText': "E-posta adresi kayıtlı bir hesap eşleşirse bağlantı yoldadır. {min} dakika geçerlidir. Kayıtlı adres yoksa yöneticiniz yardımcı olur.",
    'passwort.zurAnmeldung': "Girişe dön",
    'passwort.neuTitel': "Yeni şifre belirle",
    'passwort.fuer': "{name} hesabı için",
    'passwort.regeln': "En az 8 karakter; büyük ve küçük harf ile bir rakam içermeli.",
    'passwort.ungleich': "İki şifre eşleşmiyor.",
    'passwort.speichern': "Şifreyi kaydet",
    'passwort.fertigTitel': "Şifre değiştirildi",
    'passwort.fertigText': "Şimdi yeni şifrenizle giriş yapın. Tüm cihazlarda oturum kapatıldı.",
    'passwort.abgelaufenTitel': "Bağlantının süresi doldu",
    'passwort.abgelaufenText': "Bağlantı yalnızca 30 dakika geçerlidir. Yeni bir tane isteyin.",
    'passwort.ungueltigTitel': "Geçersiz bağlantı",
    'passwort.ungueltigText': "Bu bağlantı geçersiz veya zaten kullanılmış.",
    'passwort.neuAnfordern': "Yeni bağlantı iste",
    'passwort.pruefe': "Bağlantı kontrol ediliyor …",
    'uebersicht.begruessung': 'Merhaba',
    'uebersicht.offeneAufgaben': 'Açık görevler',
    'uebersicht.laufendeTimer': 'Çalışan zamanlayıcılar',
    'uebersicht.teamOnline': 'Ekip çevrimiçi',
    'uebersicht.benutzerImTarif': 'Tarifedeki kullanıcılar',
    'uebersicht.naechsteAufgaben': 'Sıradaki görevler',
    'uebersicht.alleAnsehen': 'Tümünü gör',
    'uebersicht.unbegrenzt': 'sınırsız',
    'uebersicht.von': '/',
    'uebersicht.insgesamt': 'toplam',
    'uebersicht.keineOffenen': 'Açık görev yok.',

    'feld.name': 'Ad',
    'feld.nummer': 'Numara',
    'feld.telefon': 'Telefon',
    'feld.email': 'E-posta',
    'feld.strasse': 'Sokak',
    'feld.plz': 'Posta kodu',
    'feld.ort': 'Şehir',
    'feld.land': 'Ülke',
    'feld.notiz': 'Not',
    'feld.ansprechpartner': 'İlgili kişi',
    'feld.ustId': 'KDV No',
    'feld.menge': 'Miktar',
    'feld.einzelpreis': 'Birim fiyat',
    'feld.bezeichnung': 'Açıklama',
    'feld.artikel': 'Ürün',
    'feld.kunde': 'Müşteri',
    'feld.lieferant': 'Tedarikçi',
    'feld.datum': 'Tarih',
    'feld.betrag': 'Tutar',
    'feld.grund': 'Sebep',
    'feld.status': 'Durum',
    'feld.einheit': 'Birim',
    'feld.bestand': 'Stok',
    'feld.gesamt': 'Toplam',
    'feld.netto': 'Net',
    'feld.rabatt': 'İndirim',
    'feld.position': 'Kalem',
    'feld.positionen': 'Kalemler',
    'aktion.hinzufuegen': 'Ekle',
    'aktion.entfernen': 'Kaldır',
    'aktion.zurueck': 'Geri',
    'aktion.weiter': 'Devam',
    'kunden.titel': 'Müşteriler',
    'kunden.neu': 'Müşteri ekle',
    'kunden.nummer': 'Müşteri no',
    'kunden.suche': 'Ad, numara, e-posta veya şehir …',
    'kunden.leer': 'Henüz müşteri yok',
    'kunden.leerText': 'İlk müşterinizi ekleyin.',
    'kunden.akte': "Müşteri dosyası",
    'kunden.alle': "Tüm müşteriler",
    'kunden.nichtGefunden': "Müşteri bulunamadı.",
    'kunden.gespeichert': "Müşteri kaydedildi.",
    'kunden.geloescht': "{name} silindi.",
    'kunden.loeschenFrage': "“{name}” gerçekten silinsin mi?",
    'kunden.einKunde': "1 müşteri",
    'kunden.anzahl': "{n} müşteri",
    'kunden.sortieren': "Sırala",
    'kunden.letzterKaufSort': "Son alım",
    'kunden.keineKontakt': "İletişim bilgisi yok",
    'kunden.nochNichts': "henüz alım yok",
    'kunden.zuletzt': "son {datum}",
    'kunden.rechnungSchreiben': "Fatura yaz",
    'kunden.umsatzJahr': "Ciro {jahr}",
    'kunden.netto': "net",
    'kunden.vorjahr': "önceki yıl {betrag}",
    'kunden.offen': "Açık",
    'kunden.ueberfaellig': "{betrag} gecikmiş ({n})",
    'kunden.ueberfaelligKurz': "{betrag} gecikmiş",
    'kunden.offenAnzahl': "{n} açık fatura",
    'kunden.offenBetrag': "{betrag} açık",
    'kunden.meistgekauft': "En çok alınan",
    'kunden.malGekauft': "{n} kez alındı",
    'kunden.keineProdukte': "Henüz bir şey almadı",
    'kunden.zahlung': "Ödeme davranışı",
    'kunden.zahltNach': "ort. {n} gün",
    'kunden.puenktlich': "%{n} zamanında ödendi",
    'kunden.nochKeineZahlung': "henüz ödeme yok",
    'kunden.seit': "{datum} tarihinden beri müşteri",
    'kunden.letzterKauf': "Son alım {datum}",
    'kunden.gesamt': "Toplam ciro {betrag}",
    'kunden.durchschnitt': "Ort. fatura {betrag}",
    'kunden.guthaben': "Alacak {betrag}",
    'kunden.verlauf': "Son 12 ayın cirosu",
    'kunden.diagrammLabel': "Aylık ciro",
    'kunden.keinUmsatz': "Son 12 ayda ciro yok",
    'kunden.rechnungenAnzahl': "{n} fatura",
    'kunden.produkte': "Alınan ürünler",
    'kunden.kaeufe': "Alım",
    'kunden.menge': "Miktar",
    'kunden.umsatz': "Ciro",
    'kunden.mehr': "{n} tümünü göster",
    'kunden.weniger': "Daha az göster",
    'kunden.dokumente': "Belgeler",
    'kunden.dokumenteHinweis': "toplam {n}, en yenisi önce",
    'kunden.alleDokumente': "Tümü",
    'kunden.nurOffene': "Açık",
    'kunden.keineDokumente': "Belge yok",
    'kunden.zu': "{nummer} için",
    'kunden.entwurf': "Taslak",
    'kunden.faellig': "vade {datum}",
    'kunden.gueltigBis': "{datum} tarihine kadar geçerli",
    'kunden.ansehen': "Görüntüle",
    'kunden.oeffnen': "Aç",
    'kunden.art.rechnung': "Fatura",
    'kunden.art.lieferschein': "İrsaliye",
    'kunden.art.angebot': "Teklif",
    'kunden.art.auftrag': "Sipariş",
    'kunden.art.gutschrift': "Alacak dekontu",
    'kunden.art.retoure': "İade",
    'kunden.arten.rechnung': "Faturalar",
    'kunden.arten.lieferschein': "İrsaliyeler",
    'kunden.arten.angebot': "Teklifler",
    'kunden.arten.auftrag': "Siparişler",
    'kunden.arten.gutschrift': "Alacak dekontları",
    'kunden.arten.retoure': "İadeler",
    'kunden.status.entwurf': "Taslak",
    'kunden.status.gestellt': "Açık",
    'kunden.status.bezahlt': "Ödendi",
    'kunden.status.storniert': "İptal",
    'kunden.status.gesendet': "Gönderildi",
    'kunden.status.angenommen': "Kabul edildi",
    'kunden.status.abgelehnt': "Reddedildi",
    'kunden.status.abgelaufen': "Süresi doldu",
    'kunden.status.offen': "Açık",
    'kunden.status.inArbeit': "Hazırlanıyor",
    'kunden.status.erledigt': "Tamamlandı",
    'kunden.status.geliefert': "Teslim edildi",
    'kunden.status.eingelagert': "Stoğa alındı",
    'kunden.status.erfasst': "Kaydedildi",
    'kunden.status.nichtGeliefert': "Teslim edilmedi",
    'kunden.status.ausgestellt': "Düzenlendi",
    'kunden.status.teilbezahlt': "Kısmen ödendi",
    'lieferanten.titel': 'Tedarikçiler',
    'lieferanten.neu': 'Tedarikçi ekle',
    'lieferanten.nummer': 'Tedarikçi no',
    'lieferanten.suche': 'Ad, ilgili kişi veya şehir …',
    'lieferanten.akte': 'Dosya',
    'lieferanten.alle': 'Tüm tedarikçiler',
    'lieferanten.artikel': 'Ürünler',
    'lieferanten.lieferungen': 'Teslimatlar',
    'lieferanten.rechnungen': 'Faturalar',
    'lieferanten.offen': 'Açık',
    'lieferanten.ueberfaellig': 'gecikmiş',
    'lieferanten.einkaufJahr': '{jahr} alımları',
    'lieferanten.letzteLieferung': 'Son teslimat',
    'lieferanten.keineArtikel': 'Bu tedarikçiden henüz ürün yok.',
    'lieferanten.keineLieferungen': 'Henüz mal girişi yok.',
    'lieferanten.keineRechnungen': 'Henüz fatura yok.',
    'lieferanten.rechnungErfassen': 'Fatura kaydet',
    'lieferanten.alsBezahlt': 'Ödendi olarak işaretle',
    'lieferanten.bezahltAm': 'ödendi {datum}',
    'lieferanten.faellig': 'vade {datum}',
    'lieferanten.betrag': 'Tutar (brüt)',
    'lieferanten.rechnungsnummer': 'Fatura numarası',
    'lieferanten.rechnungsdatum': 'Fatura tarihi',
    'lieferanten.faelligAm': 'Vade tarihi',
    'lieferanten.schonBezahlt': 'Zaten ödendi',
    'lieferanten.wert': 'Değer',
    'lieferanten.wiederOffen': 'Yeniden aç',
    'lieferanten.gespeichert': 'Kaydedildi.',
    'lieferanten.bearbeiten': 'Düzenle',
    'lieferanten.loeschen': 'Sil',
    'lager.zugang': 'Mal girişi',
    'lager.abgang': 'Çıkış',
    'lager.buchen': 'Kaydet',
    'lager.rechnungLiegtBei': 'Tedarikçi faturası ekte',
    'lager.rechnungBezahlt': 'Fatura zaten ödendi',
    'lager.gebucht': '“{name}” stoğu kaydedildi.',
    'lager.ohneLieferant': 'tedarikçisiz',
    'lieferanten.leer': 'Henüz tedarikçi yok',
    'lieferanten.leerText': 'İlk tedarikçinizi ekleyin.',
    'allg.andereSuche': 'Farklı arama terimleri deneyin.',

    'lager.titel': 'Depo',
    'lager.neu': 'Ürün ekle',
    'lager.artikelnummer': 'Ürün no',
    'lager.ean': 'EAN / barkod',
    'lager.kategorie': 'Kategori',
    'lager.lagerort': 'Depo yeri',
    'lager.einkaufspreis': 'Alış fiyatı (net)',
    'lager.verkaufspreis': 'Satış fiyatı (net)',
    'lager.mindestbestand': 'Asgari stok',
    'lager.anfangsbestand': 'Başlangıç stoğu',
    'lager.wareneingang': 'Mal girişi olarak kaydedilir.',
    'lager.suche': 'Açıklama, numara veya EAN …',
    'lager.leer': 'Ürün yok',
    'lager.leerText': 'İlk ürününüzü ekleyin veya verilerinizi aktarın.',
    'lager.verlauf': 'Geçmiş',
    'lager.keineBewegungen': 'Hareket yok',
    'lager.etikett': 'Etiket',
    'lager.etikettenDrucken': 'Etiket yazdır',
    'lager.format': 'Format',
    'lager.stueckJeArtikel': 'Ürün başına adet',
    'lager.mehrfachHinweis': 'Mal girişinde aynı etiket sıkça birden çok kez gerekir.',
    'lager.knapp': 'az',
    'lager.auswahl': 'Seçim',
    'lager.auswahlAufheben': 'Seçimi kaldır',
    'lager.alle': 'Tümü',
    'lager.keinLieferant': '— tedarikçi yok —',
    'kasse.titel': 'Kasa',
    'kasse.schritt1': 'Adım 1/2 — satış kimin için?',
    'kasse.kundeWaehlen': 'Müşteri seç',
    'kasse.kundeSuchen': 'Müşteri ara',
    'kasse.kundeSuche': 'Ad, müşteri no veya şehir …',
    'kasse.kundeWechseln': 'Müşteriyi değiştir',
    'kasse.ohneKonto': 'Müşteri hesabı olmadan',
    'kasse.historieHinweis': 'Satış, fatura geçmişinde görünür',
    'kasse.artikelSuchen': 'Ürün ara',
    'kasse.artikelSuche': 'Açıklama, numara veya barkod okut …',
    'kasse.keinArtikel': 'Ürün bulunamadı',
    'kasse.warenkorb': 'Sepet',
    'kasse.warenkorbLeeren': 'Sepeti boşalt',
    'kasse.leer': 'Henüz bir şey yok',
    'kasse.leerText': 'Soldan bir ürüne tıklayın.',
    'kasse.zahlart': 'Ödeme türü',
    'kasse.bezahlt': 'Ödendi mi?',
    'kasse.vollstaendig': 'Tamamen',
    'kasse.teilweise': 'Kısmen',
    'kasse.nochNicht': 'Henüz değil',
    'kasse.gegeben': 'Verilen (para üstü için)',
    'kasse.rueckgeld': 'Para üstü: {betrag}',
    'kasse.erhalten': 'Alınan',
    'kasse.kundePflicht': 'Açık tutar için lütfen bir müşteri seçin — aksi halde kimin ödeyeceği bilinmez.',
    'kasse.buchen': 'Kaydet · {erhalten} alındı, {offen} açık',
    'kasse.kassieren': 'Tahsil et · {betrag}',
    'kasse.offenBis': 'Açık: {betrag} · vade {datum}',
    'kasse.bezahltMit': 'Ödendi · {zahlart}',
    'kasse.naechster': 'Sonraki satış',
    'kasse.bar': 'Nakit',
    'kasse.karte': 'Kart',
    'kasse.ueberweisung': 'Havale',
    'kasse.abgeschlossen': 'Satış tamamlandı',
    'kasse.alle': "Tümü",
    'kasse.anzahlung': "Ön ödeme",
    'kasse.aufRechnung': "Faturaya",
    'kasse.barAnteil': "Nakit kısmı",
    'kasse.barKassieren': "Nakit tahsil et · {betrag}",
    'kasse.beleg': "Fiş",
    'kasse.betragBrutto': "Tutar (brüt)",
    'kasse.bezahlen': "Öde",
    'kasse.bezahlenBetrag': "Öde · {betrag}",
    'kasse.bonNr': "Fiş {n}",
    'kasse.bonRabatt': "Fiş indirimi",
    'kasse.bonVerwerfen': "Fişi iptal et",
    'kasse.codeUnbekannt': "{code} kodlu ürün yok",
    'kasse.enthaltenSteuer': "{satz} % {name} dahil",
    'kasse.fehlgeschlagen': "Satış kaydedilemedi.",
    'kasse.freiHinweis': "Ürün olmayan her şey için: hizmet, depozito, diğer.",
    'kasse.freiStandard': "Diğer",
    'kasse.freierBetrag': "Serbest tutar",
    'kasse.gegebenKurz': "Verilen",
    'kasse.gespart': "tasarruf",
    'kasse.geteilt': "Bölünmüş",
    'kasse.geteiltKassieren': "Tahsil et · {bar} nakit + {karte} kart",
    'kasse.haeufig': "Sık satılan",
    'kasse.kameraFehler': "Kamera kullanılamıyor — izin verin veya el tarayıcısı kullanın.",
    'kasse.karteBezahlt': "Kartla ödendi · {betrag}",
    'kasse.karteHinweis': "Tutarı kart terminalinde çekin, sonra burada onaylayın.",
    'kasse.karteRest': "Kalan kartla",
    'kasse.keinRabatt': "yok",
    'kasse.keineArtikelAngelegt': "Henüz ürün yok.",
    'kasse.kundeEntfernen': "Müşteriyi kaldır",
    'kasse.kundeZuordnen': "Müşteri ata",
    'kasse.laufkunde': "Gelen müşteri",
    'kasse.leerNeu': "Bir ürüne dokunun veya barkod okutun.",
    'kasse.neuerBon': "Yeni fiş",
    'kasse.nichtAufLager': "\"{name}\" stokta yok.",
    'kasse.nichtVorraetig': "stokta yok",
    'kasse.nurNoch': "\"{name}\" için yalnızca {menge} mevcut.",
    'kasse.parkenHinweis': "Bu fişi beklet ve yenisini başlat",
    'kasse.passend': "tam",
    'kasse.positionen': "Kalem: {n}",
    'kasse.preis': "Fiyat",
    'kasse.preisAendern': "Fiyatı değiştir",
    'kasse.rabatt': "İndirim",
    'kasse.rabattText': "%{p} indirim",
    'kasse.rechnungBuchen': "Faturaya işle · {offen} açık",
    'kasse.rueckgeldGross': "Para üstü",
    'kasse.scannen': "Barkod okut",
    'kasse.scannenHinweis': "Kodu kameraya tutun.",
    'kasse.wofuer': "Ne için?",
    'kasse.zuWenig': "{betrag} eksik.",

    'belege.titel': 'Belgeler',
    'belege.angebote': 'Teklifler',
    'belege.auftraege': 'Siparişler',
    'belege.lieferscheine': 'İrsaliyeler',
    'belege.gutschriften': 'İade faturaları',
    'belege.offeneAngebote': 'Açık teklifler',
    'belege.laufendeAuftraege': 'Devam eden siparişler',
    'belege.lieferscheineOffen': 'Bekleyen irsaliyeler',
    'belege.interessent': 'İlgili kişinin adı',
    'belege.notiz': 'Not (belgede görünür)',
    'belege.rabatt': 'İndirim %',
    'belege.druckenPdf': 'Yazdır / PDF',
    'belege.entwurfLoeschen': 'Taslağı sil',
    'belege.gesamtBrutto': 'Toplam brüt',
    'belege.einzel': 'Birim',
    'rechnungen.titel': 'Faturalar',
    'rechnungen.neu': 'Yeni fatura',
    'rechnungen.schreiben': 'Fatura oluştur',
    'rechnungen.stellen': 'Faturayı kes',
    'rechnungen.entwurfSpeichern': 'Taslak olarak kaydet',
    'rechnungen.entwurfHinweis': 'Taslak henüz stoktan düşmez.',
    'rechnungen.bezahltMarkieren': 'Ödendi olarak işaretle',
    'rechnungen.teilbezahlt': 'kısmen ödendi · kalan {betrag}',
    'rechnungen.perBank': 'banka ile eşleştirildi',
    'rechnungen.zahlungen': 'Ödemeler',
    'rechnungen.nochOffen': 'Kalan',
    'rechnungen.bankeingang': 'Banka girişi',
    'rechnungen.zahlung': 'Ödeme',
    'rechnungen.zahlart': 'Ödeme şekli',
    'rechnungen.zahlweg.ueberweisung': 'Havale',
    'rechnungen.zahlweg.bar': 'Nakit',
    'rechnungen.zahlweg.karte': 'Kart',
    'rechnungen.zahlungsziel': 'Ödeme vadesi',
    'rechnungen.zielKunde': 'Müşterideki gibi ({tage} gün)',
    'rechnungen.zielSofort': 'Hemen vadeli',
    'rechnungen.zielTage': '{tage} gün',
    'rechnungen.zielDatum': 'Özel tarih',
    'rechnungen.faelligAm': 'Vade tarihi',
    'rechnungen.bezahlung': 'Ödendi mi?',
    'rechnungen.offenLassen': 'Henüz ödenmedi',
    'rechnungen.schonBezahlt': 'Zaten ödendi',
    'rechnungen.lieferscheinMit': 'İrsaliyeyi de oluştur',
    'rechnungen.lieferschein': 'İrsaliye',
    'rechnungen.lieferscheinDrucken': 'İrsaliyeyi yazdır',
    'rechnungen.lieferscheinErstellen': 'İrsaliye oluştur',
    'rechnungen.zahlungErfassen': 'Ödeme kaydet',
    'rechnungen.betrag': 'Tutar',
    'rechnungen.zahltag': 'Ödeme tarihi',
    'rechnungen.alsBezahltSpeichern': 'Ödendi olarak kaydet',
    'rechnungen.teilzahlungSpeichern': 'Kısmi ödemeyi kaydet',
    'rechnungen.ratenHinweis': 'Açık tutardan az mı? Kalan açık kalır — taksitler böyle kaydedilir.',
    'rechnungen.zahlungEntfernen': 'Ödemeyi sil',
    'rechnungen.sofort': 'hemen vadeli',
    'rechnungen.gestelltMitLs': 'Fatura kesildi. {nummer} numaralı irsaliye yazdırmaya hazır.',
    'rechnungen.zahlungGespeichert': 'Ödeme kaydedildi.',
    'rechnungen.vollBezahlt': 'Fatura tamamen ödendi.',
    'rechnungen.guthaben': 'Müşteri alacağı',
    'rechnungen.gutschriftTitel': 'Bu faturaya iade faturası',
    'rechnungen.gutschriftMenge': 'İade edilecek miktar',
    'rechnungen.gutschriftGrund': 'Gerekçe (iade faturasında görünür)',
    'rechnungen.gutschriftStellen': 'İade faturası kes',
    'rechnungen.gutschriftHinweis': 'Fatura hâlâ açıksa iade faturası ondan düşülür. Değilse müşteri alacağı olur. Fatura başına bir iade faturası.',
    'rechnungen.gutschriftErstellt': 'İade faturası {nummer} kesildi.',
    'rechnungen.gutschriftVorhanden': 'İade faturası {nummer}',
    'rechnungen.perGutschrift': 'iade faturasıyla kapatıldı',
    'rechnungen.gutschriftDrucken': 'İade faturasını yazdır',
    'rechnungen.gutschriftSumme': '{betrag} tutarında iade faturası',
    'rechnungen.stornieren': 'İptal et',
    'rechnungen.gutschriftErstellen': 'İade faturası oluştur',
    'rechnungen.drucken': 'Belgeyi yazdır / PDF',
    'rechnungen.stempelBezahlt': 'Ödendi',
    'rechnungen.stempelAm': '{datum} tarihinde',
    'rechnungen.keinGirocode': 'GiroCode yok',
    'rechnungen.girocodeText': 'Banka uygulamasıyla tarayın — tutar ve açıklama hazır gelir.',
    'neuigkeiten.titel': 'Yenilikler',
    'neuigkeiten.auchNeu': 'Ayrıca yeni',
    'neuigkeiten.fortschritt': 'LagerPro ile ilerlemeniz',
    'neuigkeiten.updates': 'güncelleme',
    'neuigkeiten.verbesserungen': 'iyileştirme',
    'neuigkeiten.seit': '{datum} tarihinden beri',
    'neuigkeiten.mailHinweis': 'Yenilik e-postalarını Ayarlar’dan kapatabilirsiniz.',
    'neuigkeiten.verstanden': 'Tamam',
    'rechnungen.barverkauf': 'Nakit satış / hesapsız',
    'rechnungen.leer': 'Fatura yok',
    'rechnungen.leerText': 'İlk faturanızı oluşturun.',
    'status.alle': 'Tümü',
    'status.entwurf': 'Taslak',
    'status.entwuerfe': 'Taslaklar',
    'status.offen': 'Açık',
    'status.bezahlt': 'Ödendi',
    'status.storniert': 'İptal',
    'retouren.titel': 'İadeler',
    'retouren.erfassen': 'İade kaydet',
    'retouren.alle': 'Tüm iadeler',
    'retouren.leer': 'Henüz iade yok',
    'retouren.leerText': 'İade her zaman bir faturaya bağlıdır: müşteri, fatura ve kalemleri seçin.',
    'retouren.schritt1': '1. Müşteri',
    'retouren.schritt2': '2. Fatura',
    'retouren.schritt3': '3. Ne iade ediliyor?',
    'retouren.grund': 'Sebep (isteğe bağlı)',
    'retouren.grundBeispiel': 'ör. nakliye hasarı, yanlış teslimat',
    'retouren.berechnet': 'Faturalanan',
    'retouren.zurueck': 'İade',
    'retouren.gutschrift': 'İade faturası',
    'retouren.einlagern': 'Malı stoğa geri al',
    'retouren.kundeWaehlen': 'Müşteri seç …',

    'team.titel': 'Ekip',
    'team.mitglieder': 'Üyeler',
    'team.neu': 'Çalışan ekle',
    'team.benutzername': 'Kullanıcı adı',
    'team.emailOptional': 'E-posta (isteğe bağlı)',
    'team.rolle': 'Rol',
    'team.passwortHinweis': 'En az 8 karakter. Çalışan sonradan değiştirebilir.',
    'team.limitErreicht': 'Kullanıcı sınırına ulaşıldı',
    'team.leer': 'Henüz üye yok',
    'team.leerText': 'İlk çalışanınızı ekleyin.',
    'team.gesperrt': 'Engelli',
    'team.alle': "Tüm ekip",
    'team.nichtGefunden': "Kişi bulunamadı.",
    'team.sie': "siz",
    'team.online': "Çevrimiçi",
    'team.offline': "Çevrimdışı",
    'team.bearbeiten': "Profili düzenle",
    'team.gespeichert': "Profil kaydedildi.",
    'team.kontakt': "İletişim ve iş",
    'team.position': "Pozisyon",
    'team.positionBeispiel': "örn. Depo sorumlusu",
    'team.abteilung': "Departman",
    'team.mobil': "Cep",
    'team.arbeitszeit': "Çalışma saatleri",
    'team.arbeitszeitBeispiel': "örn. Pzt–Cum 8–16",
    'team.eintritt': "İşe giriş",
    'team.jahre': "{n} yıldır ekipte",
    'team.einJahr': "1 yıldır ekipte",
    'team.ersteJahr': "ilk yılında",
    'team.privat': "Kişisel",
    'team.privatHinweis': "Yalnızca kişinin kendisi ve yöneticiler görür",
    'team.freiwillig': "İsteğe bağlı — yalnızca kişi ve yöneticiler görür",
    'team.geburtstag': "Doğum günü",
    'team.alter': "{n} yaşında",
    'team.anschrift': "Adres",
    'team.notfall': "Acil durum kişisi",
    'team.notfallName': "Acil durum kişisi (ad)",
    'team.notfallTelefon': "Acil durum kişisi (telefon)",
    'team.notfallBeispiel': "örn. Anna Meyer (eşi)",
    'team.notiz': "Dahili not",
    'team.notizHinweis': "Yalnızca yöneticiler görür",
    'team.keineAngaben': "Henüz bilgi yok",
    'team.offeneAufgaben': "Açık görevler",
    'team.keineAufgaben': "Açık görev yok",
    'team.aktualisiert': "Son değişiklik {datum}",
    'team.nurAdminFelder': "Pozisyon, departman, çalışma saatleri ve giriş tarihini yönetici düzenler.",
    'uebersicht.umsatzHeute': 'Bugünkü ciro',
    'uebersicht.offeneForderungen': 'Açık alacaklar',
    'uebersicht.lagerwert': 'Stok değeri',
    'uebersicht.unterMindest': 'Asgari stok altı',
    'uebersicht.nachbestellen': 'Yeniden sipariş',
    'uebersicht.ueberfaellig': 'Vadesi geçmiş faturalar',
    'uebersicht.zahlungsziel': 'Ödeme vadesi aşıldı',
    'uebersicht.umsatzMonat': 'Aylara göre ciro',
    'uebersicht.schnell': 'Hızlı işlemler',
    'uebersicht.kassieren': 'Satış tahsil et',
    'uebersicht.wareneingang': 'Mal girişi kaydet',
    'aufgaben.titel': 'Görevler',
    'aufgaben.neu': 'Yeni görev',
    'aufgaben.wasZuTun': 'Ne yapılmalı?',
    'aufgaben.ansichtHeute': 'Bugün',
    'aufgaben.ansichtBoard': 'Pano',
    'aufgaben.ansichtAlle': 'Tümü',
    'aufgaben.vomBetrieb': 'İşletmeden',
    'aufgaben.vomBetriebHinweis': 'Verilerinizden çıkar ve yapıldığında kendiliğinden kaybolur.',
    'aufgaben.meine': 'Görevlerim',
    'aufgaben.nurMeine': 'Sadece benimkiler',
    'aufgaben.ueberfaellig': 'Gecikmiş',
    'aufgaben.heute': 'Bugün',
    'aufgaben.morgen': 'Yarın',
    'aufgaben.dieseWoche': 'Bu hafta',
    'aufgaben.spaeter': 'Daha sonra',
    'aufgaben.ohneDatum': 'Tarihsiz',
    'aufgaben.spalteOffen': 'Açık',
    'aufgaben.spalteArbeit': 'Devam ediyor',
    'aufgaben.spalteErledigt': 'Tamamlandı',
    'aufgaben.schnell': 'Yeni görev — örn. “Yarın Meyer’i ara !yüksek @Mia”',
    'aufgaben.schnellHilfe': 'bugün · yarın · Pazartesi · 15.11. · !yüksek · !düşük · @İsim',
    'aufgaben.oeffnen': 'Aç',
    'aufgaben.zurueckstellen': 'Ertele',
    'aufgaben.tage': '{n} gün',
    'aufgaben.uebernehmen': 'Göreve dönüştür',
    'aufgaben.beschreibung': 'Açıklama',
    'aufgaben.faellig': 'Vade',
    'aufgaben.prioritaet': 'Öncelik',
    'aufgaben.niedrig': 'Düşük',
    'aufgaben.normal': 'Normal',
    'aufgaben.zustaendig': 'Sorumlu',
    'aufgaben.niemand': 'Kimse',
    'aufgaben.wiederholung': 'Tekrar',
    'aufgaben.keine': 'Yok',
    'aufgaben.taeglich': 'Günlük',
    'aufgaben.woechentlich': 'Haftalık',
    'aufgaben.monatlich': 'Aylık',
    'aufgaben.checkliste': 'Kontrol listesi',
    'aufgaben.punktNeu': 'Madde ekle',
    'aufgaben.loeschen': 'Sil',
    'aufgaben.loeschenFrage': 'Bu görev silinsin mi?',
    'aufgaben.timerStart': 'Zamanlayıcıyı başlat',
    'aufgaben.timerStopp': 'Zamanlayıcıyı durdur',
    'aufgaben.allesErledigt': 'Acil bir şey yok — işler yolunda.',
    'aufgaben.keineEigenen': 'Açık görev yok.',
    'aufgaben.folge': 'Sonraki {datum}',
    'aufgaben.zieheHier': 'Buraya bırak',
    'aufgaben.erledigtHeute': 'Bugün tamamlandı',
    'aufgaben.schliessen': 'Kapat',
    'aufgaben.aktionGeliefert': 'Teslim edildi olarak işaretle',
    'aufgaben.aktionBezahlt': 'Ödendi olarak işaretle',
    'aufgaben.erledigtMeldung': 'Tamamlandı.',
    'aufgaben.zurueckgestellt': '{datum} tarihine kadar ertelendi.',
    'aufgaben.uebernommen': 'Göreve dönüştürüldü.',
    'aufgaben.auto.nachbestellen': '{artikel} yeniden sipariş et',
    'aufgaben.auto.nachbestellenText': 'Stok {bestand} {einheit}, asgari {mindestbestand}',
    'aufgaben.auto.bei': '{lieferant} tedarikçisinden',
    'aufgaben.auto.ausliefern': '{nummer} irsaliyesini teslim et',
    'aufgaben.auto.ausliefernText': '{kunde} müşterisine',
    'aufgaben.auto.nachfassen': '{nummer} teklifini takip et',
    'aufgaben.auto.nachfassenText': '{kunde} · {tage} gündür yanıt yok',
    'aufgaben.auto.entwurf': 'Fatura taslağını kes',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · {tage} gündür bekliyor',
    'aufgaben.auto.support': 'Destek yanıtını oku',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'Taranan belgeleri kaydet',
    'aufgaben.auto.belegscanText': '{anzahl} belge onayınızı bekliyor',
    'aufgaben.auto.mahnen': 'Ödeme hatırlat: {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} açık · {tage} gün gecikmiş',
    'aufgaben.auto.bezahlen': 'Tedarikçiye öde: {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · vade {datum}',
    'aufgaben.auto.zuordnen': 'Gelen ödemeleri eşleştir',
    'aufgaben.auto.zuordnenText': '{anzahl} ödeme bekliyor',
    'aufgaben.auto.bank': 'Bankayı yeniden bağla: {bank}',
    'aufgaben.auto.bankAbgelaufen': 'Onayın süresi doldu — artık hareket gelmiyor.',
    'aufgaben.auto.bankEndet': 'Onay {datum} tarihinde sona eriyor.',
    'aufgaben.auto.ustva': 'KDV beyannamesi {monat}',
    'aufgaben.auto.ustvaText': 'Ödenecek {betrag} · {datum} tarihine kadar',
    'tafel.titel': "Pano",
    'tafel.untertitel': "Görevler, randevular ve son tarihler bir bakışta",
    'tafel.ansicht.woche': "Hafta",
    'tafel.ansicht.monat': "Ay",
    'tafel.ansicht.liste': "Liste",
    'tafel.neuerZettel': "Yeni not",
    'tafel.zurueck': "Önceki",
    'tafel.vor': "Sonraki",
    'tafel.kw': "{n}. hafta",
    'tafel.naechste30': "Önümüzdeki 30 gün",
    'tafel.art.aufgabe': "Görev",
    'tafel.art.termin': "Randevu",
    'tafel.art.frist': "Son tarih",
    'tafel.liegenGeblieben': "Geciken",
    'tafel.ablage': "Kutu",
    'tafel.ablageHinweis': "Günsüz — fırsat olunca",
    'tafel.leerTag': "+ Not",
    'tafel.weitere': "+{n} daha",
    'tafel.erledigt': "Tamamlandı",
    'tafel.wiederOffen': "Yeniden aç",
    'tafel.ziehen': "Başka bir güne taşımak için sürükleyin",
    'tafel.automatisch': "otomatik",
    'tafel.schnell': "Ne yapılacak?",
    'tafel.schnellGross': "Ne yapılacak? örn. \"14:30 Meyer randevu\" veya \"teklif gönder !yüksek\"",
    'tafel.schnellHilfe': "bugün · yarın · cuma · 15.11. · 14:30 · !yüksek · @isim",
    'tafel.inEinerWoche': "Bir hafta sonra",
    'tafel.artWaehlen': "Tür seçin",
    'tafel.bisWann': "Ne zamana kadar?",
    'tafel.wann': "Ne zaman?",
    'tafel.von': "Başlangıç",
    'tafel.bis': "Bitiş",
    'tafel.ort': "Yer",
    'tafel.notiz': "Not",
    'tafel.mehr': "Diğer seçenekler",
    'tafel.anheften': "Tuttur",
    'tafel.fristHinweis': "Verilerinizden gelir, iş bitince kendiliğinden kaybolur.",
    'tafel.kurzTitel': "Panom",
    'tafel.kurzLiegen': "{n} geciken",
    'tafel.zurTafel': "Panoya git",
    'tafel.heuteFrei': "Plan yok.",
    'tafel.frist.angebot': "Teklif süresi doluyor: {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "Teslimat: {nummer}",
    'tafel.frist.lieferungText': "{kunde} için",
    'tafel.frist.zahlungseingang': "Beklenen ödeme: {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "Tedarikçi faturası vadesi: {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "KDV beyannamesi",
    'tafel.frist.ustvaText': "{monat} için",
    'aufgaben.leer': 'Henüz görev yok',
    'aufgaben.leerText': 'İlkini yukarıdan ekleyin.',
    'aufgaben.hoch': 'Yüksek',
    'aufgaben.laeuft': 'çalışıyor',
    'chat.titel': 'Sohbet',
    'chat.wemSchreiben': 'Kime yazılsın?',
    'chat.leer': 'Henüz mesaj yok',
    'chat.senden': 'Gönder',
    'chat.dateiAnhaengen': 'Dosya ekle',
    'chat.anhangEntfernen': 'Eki kaldır',
    'chat.zurListe': 'Listeye dön',
    'chat.alleImTeam': 'Tüm ekip',
    'chat.herunterladenTipp': 'indirmek için dokunun',

    'basis.tarifAb': 'Şu paketten itibaren',
    'basis.tarifWechsel': 'Paketi „Paket ve faturalama" altından değiştirebilirsiniz.',
    'basis.support': 'Bu özelliği açmak için destek ile iletişime geçin.',
    'positionen.hinzufuegen': 'Kalem ekle',
    'positionen.freiText': '— serbest giriş —',
    'passwort.festlegen': 'Şifre belirle',
    'passwort.uebermittelt': 'Size iletilen şifre',
    'passwort.neu': 'Yeni şifre',
    'passwort.wiederholen': 'Yeni şifreyi tekrarlayın',
    'tarif.titel': 'Paket ve faturalama',
    'tarif.aktuell': 'Mevcut paket',
    'tarif.enthalten': 'Dahil',
    'tarif.nichtEnthalten': 'Dahil değil',
    'tarif.proMonat': '/ ay',
    'tarif.sonderkondition': 'Özel koşul',
    'tarif.verlauf': 'Paket geçmişi',
    'tarif.mandat': 'Otomatik ödeme talimatı',
    'tarif.angebot': 'Size özel teklif',
    'kopie.titel': "Veri kopyası",
    'kopie.text': "Verilerinizin bir kopyasını fesihten itibaren sözleşme bitiminden 30 gün sonrasına kadar isteyebilirsiniz.",
    'kopie.frist': "{datum} tarihine kadar mümkün.",
    'kopie.anfordern': "Veri kopyası iste",
    'kopie.angefordert': "{datum} tarihinde istendi. Kopya hazır olduğunda e-posta alacaksınız.",
    'kopie.bereit': "Veri kopyanız hazır: {format}.",
    'kopie.passwort': "Giriş şifreniz",
    'kopie.passwortHinweis': "ZIP dosyası bu şifreyle şifrelenir — aynı şifreyle çift tıklayarak açarsınız.",
    'kopie.herunterladen': "İndir",
    'kopie.geladen': "İndirildi. Dosyayı güvenli bir yerde saklayın.",
    'kopie.formatLesbar': "PDF ve tablolar (Excel)",
    'kopie.formatWiederherstellung': "Geri yükleme dosyası",
    'kopie.nurAdmin': "Veri kopyasını işletmenizin bir yöneticisi ister.",
    'abo.agbAnsehen': "Koşulları görüntüle",
    'abo.agbZustimmung': "Genel koşulları okudum ve kabul ediyorum.",
    'abo.aufbewahrung': "Saklama yükümlülüklerinizi unutmayın: faturalar ve muhasebe belgeleri 8 yıl, defterler ve bilançolar 10 yıl.",
    'abo.aufpreisHinweis': "Sonraki faturada: {datum} tarihli paket değişikliği için {betrag} net ek ücret.",
    'abo.bestaetigen': "Değişikliği onayla",
    'abo.erstZuruecknehmen': "Sözleşme feshedildi. Paketi değiştirmek için önce feshi geri alın.",
    'abo.gekuendigt': "{datum} itibarıyla feshedildi. O zamana kadar LagerPro’yu her zamanki gibi kullanabilirsiniz.",
    'abo.gekuendigtMeldung': "{datum} itibarıyla feshedildi. Onay e-posta ile gönderiliyor.",
    'abo.gewechselt': "Artık {plan} kullanıyorsunuz.",
    'abo.hochstufen': "Yükselt",
    'abo.ihrTarif': "Paketiniz",
    'abo.jetztKuendigen': "Şimdi feshet",
    'abo.kostenpflichtig': "Ücretli değiştir",
    'abo.kuendigen': "Sözleşmeyi feshet",
    'abo.kuendigenText': "Fesih {datum} tarihinde geçerli olur. O zamana kadar LagerPro’yu her zamanki gibi kullanın; sonrasında 30 gün boyunca verilerinizin bir kopyasını isteyebilirsiniz.",
    'abo.kuendigenTextTest': "Ücretsiz ayınızdasınız: sözleşme {datum} tarihinde onunla birlikte sona erer, hiçbir ücret alınmaz.",
    'abo.kuendigenTitel': "Sözleşmeyi feshet",
    'abo.mandatAendern': "Banka bilgilerini değiştir",
    'abo.mandatBisTestende': "Lütfen o zamana kadar bir SEPA otomatik ödeme talimatı verin — aksi halde LagerPro ücretsiz aydan sonra durur.",
    'abo.nurAdmin': "Paket ve sözleşme işletmenizin bir yöneticisi tarafından yönetilir.",
    'abo.runterstufen': "Düşür",
    'abo.sonderkondition': "Size özel bir anlaşmanız var. Paket değişikliği için lütfen destekle iletişime geçin.",
    'abo.testDanach': "Sonrasında {plan} aylık {preis} tutarındadır.",
    'abo.testLaeuft': "Ücretsiz ay: {datum} dahil {n} gün kaldı.",
    'abo.vorgemerkt': "{datum} itibarıyla {plan} paketine geçiş planlandı.",
    'abo.vorgemerktMeldung': "{datum} itibarıyla {plan} paketine geçiş planlandı.",
    'abo.vormerkungWeg': "Planlanan değişiklik geri alındı.",
    'abo.vormerkungZurueck': "Planlanan değişikliği geri al",
    'abo.waehlen': "Seç",
    'abo.wechselHoch': "Hemen geçerli olur. Bu ay zaten faturalandıysa, kalan günlerin farkı sonraki faturaya eklenir. Gelecek aydan itibaren aylık {preis}.",
    'abo.wechselRunter': "{datum} tarihinden itibaren geçerli. O zamana kadar {plan} paketinin tüm özelliklerini kullanırsınız.",
    'abo.wechselTest': "Hemen geçerli olur. Ücretsiz ayda ücretsizdir; sonrasında aylık {preis}.",
    'abo.wechselTitel': "{plan} paketine geç",
    'abo.wechselnHinweis': "Yükseltme hemen, düşürme ay sonunda geçerli olur.",
    'abo.wechselnTitel': "Paketi değiştir",
    'abo.zurueckMeldung': "Fesih geri alındı — sözleşmeniz devam ediyor.",
    'abo.zuruecknehmen': "Feshi geri al",
    'abo.beendetTitel': "Sözleşmeniz sona erdi",
    'abo.beendetText': "LagerPro sözleşmeniz {datum} tarihinde sona erdi.",
    'abo.beendetKopie': "{bis} tarihine kadar verilerinizin bir kopyasını isteyebilirsiniz — sonrasında silinir.",
    'firma.titel': 'Firma',
    'firma.anschrift': 'Adres ve iletişim',
    'firma.steuerRegister': 'Vergi ve sicil',
    'firma.girocode': 'Faturaya ödeme kodu bas',
    'firma.girocodeText': 'Müşterinin bankacılık uygulamasıyla okuttuğu bir QR kod — alıcı, tutar ve açıklama hazır gelir. Yalnızca havaleyle ödenecek açık faturalarda görünür.',
    'firma.ibanFehlt': 'Ödeme kodu için IBAN gerekir.',
    'firma.ibanUngueltig': 'Bu IBAN’ın kontrol hanesi tutmuyor. Lütfen kontrol edin; o zamana kadar kod basılmaz.',
    'firma.bank': 'Banka bilgileri',
    'firma.fusstext': 'Alt bilgi metni',
    'firma.logo': 'Logo',
    'firma.logoWaehlen': 'Logo seç',
    'firma.logoEntfernen': 'Logoyu kaldır',
    'firma.speichern': 'Firma bilgilerini kaydet',
    'firma.aufRechnung': 'Her faturada görünür',
    'firma.pflichtangaben': 'Faturada zorunlu bilgiler',
    'firma.imFuss': 'Fatura alt bilgisinde görünür',
    'passwort.willkommen': 'Hoş geldiniz',
    'passwort.warnung': 'Mevcut şifreniz size iletildi ve bu nedenle başka biri tarafından biliniyor. Lütfen kendi şifrenizi belirleyin.',
    'passwort.regel8': 'En az 8 karakter',
    'passwort.regelGross': 'Büyük ve küçük harf',
    'passwort.regelZiffer': 'En az bir rakam',
    'passwort.regelGleich': 'İki giriş eşleşiyor',
    'passwort.regelAnders': 'Size iletilen şifreden farklı',
    'passwort.fortfahren': 'Şifreyi belirle ve devam et',
    'lager.anzahlArtikel': 'ürün',
    'lager.nurKnappe': 'Sadece azalanlar',
    'lager.etiketten': 'Etiketler',

    'zfa.titel': 'Doğrulama',
    'zfa.gesendet': 'Size bir kod gönderdik:',
    'zfa.code': 'E-postadaki kod',
    'zfa.bestaetigen': 'Doğrula',
    'zfa.gueltig': 'Kod {min} dakika geçerlidir.',
    'zfa.andererZugang': 'Farklı giriş',
    'zfa.einstellung': 'İki adımlı giriş',
    'zfa.erklaerung': 'Şifrenin yanı sıra her girişte e-postanıza bir kod gönderilir. Şifrenizi bilen biri yine de giremez.',
    'zfa.aktiv': 'Açık',
    'zfa.aus': 'Kapalı',
    'zfa.einschalten': 'Aç',
    'zfa.ausschalten': 'Kapat',
    'zfa.mailFrage': 'Kodlar hangi adrese gitsin?',
    'zfa.codeSenden': 'Kod gönder',
    'zfa.passwortFrage': 'Kapatmak için şifrenizi girin',
    'zfa.keinVersand': 'Bu sistemde henüz e-posta gönderimi kurulmadı. Onsuz kod iletilemez.',
    'zfa.eingeschaltet': 'İki adımlı giriş açık.',
    'zfa.ausgeschaltet': 'İki adımlı giriş kapalı.',
    'zfa.methodeApp': "Doğrulayıcı uygulama",
    'zfa.methodeMail': "E-posta kodu",
    'zfa.erklaerungBeide': "LagerPro her girişte şifreye ek olarak bir kod ister — telefonunuzdaki doğrulayıcı uygulamadan veya e-postayla. Yalnızca şifreyi bilen giremez.",
    'zfa.ersatzUebrig': "{n} yedek kod kaldı",
    'zfa.mitApp': "Doğrulayıcı uygulama ile aç",
    'zfa.mitMail': "E-posta ile aç",
    'zfa.zurApp': "Doğrulayıcı uygulamaya geç",
    'zfa.ersatzNeu': "Yeni yedek kodlar",
    'zfa.appEmpfohlen': "Önerilen: uygulama. İnternetsiz ve e-postasız çalışır — örn. Google Authenticator veya Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "Yeni yedek kodlar oluştur — eskiler hemen geçersiz olur.",
    'zfa.passwortWeiter': "Lütfen şifrenizi girin",
    'zfa.weiter': "Devam",
    'zfa.schritt1': "Telefonda doğrulayıcı uygulamayı açın (örn. Google Authenticator) ve “Kod ekle”yi seçin.",
    'zfa.scannen': "Bu QR kodunu tarayın.",
    'zfa.schritt3': "Uygulamadaki 6 haneli kodu aşağıya girin.",
    'zfa.manuell': "Taranamıyor mu? Bu anahtarı elle girin:",
    'zfa.appCode': "Uygulamadaki kod",
    'zfa.ersatzTitel': "Yedek kodlarınız",
    'zfa.ersatzHinweis': "Telefonunuzu kaybederseniz: her kod uygulama kodunun yerine bir kez geçer. Güvenle saklayın — yalnızca şimdi gösterilir.",
    'zfa.kopieren': "Kopyala",
    'zfa.kopiert': "Kopyalandı",
    'zfa.herunterladen': "Dosya olarak kaydet",
    'zfa.gesichert': "Kodları güvenle sakladım",
    'zfa.fertig': "Tamam",
    'zfa.appText': "Doğrulayıcı uygulamanızı açın ve LagerPro için 6 haneli kodu girin.",
    'zfa.ersatzText': "Yedek kodlarınızdan birini girin. Her biri bir kez geçerlidir.",
    'zfa.ersatzCode': "Yedek kod",
    'zfa.ersatzNutzen': "Telefon yanınızda değil mi? Yedek kod kullanın",
    'zfa.appCodeNutzen': "Uygulamadaki kodu kullan",
    'nav.app': "Uygulama",
    'start.nav.app': "Uygulama",
    'desktop.titel': "Uygulama olarak LagerPro",
    'desktop.text': "Kendi penceresi, Dock veya görev çubuğunda kendi simgesi, tek tıkla açılır. Bir kez giriş yapın — 90 gün oturumunuz açık kalır.",
    'desktop.inApp': "Masaüstü uygulamasını kullanıyorsunuz",
    'desktop.inAppText': "Sürüm {version}. Arayüz kendini günceller.",
    'desktop.alsWebApp': "LagerPro bu bilgisayarda uygulama olarak yüklü.",
    'desktop.installiert': "Yüklendi — LagerPro artık uygulamalarınız arasında.",
    'desktop.direktTitel': "Doğrudan tarayıcıdan yükleyin",
    'desktop.schnell': "saniyeler",
    'desktop.direktText': "İndirme yok, güvenlik uyarısı yok, hep güncel.",
    'desktop.safari': "Safari’de: “Dosya” menüsü → “Dock’a Ekle” (macOS Sonoma ve sonrası).",
    'desktop.firefox': "Firefox web uygulaması yükleyemez — Chrome veya Edge kullanın ya da aşağıdan indirin.",
    'desktop.chromeLeiste': "Adres çubuğunun sağındaki “Yükle” simgesine tıklayın — veya menü ⋮ → “LagerPro’yu yükle”.",
    'desktop.andererBrowser': "En kolayı Chrome, Edge veya Safari ile — ya da uygulamayı aşağıdan indirin.",
    'desktop.jetztInstallieren': "Şimdi yükle",
    'desktop.downloadTitel': "Ya da indirin",
    'desktop.version': "Sürüm {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 ve 11, 64 bit",
    'desktop.macApple': "Apple çipli Mac",
    'desktop.macAppleUnter': "M1, M2, M3, M4 · 2020’den itibaren",
    'desktop.macIntel': "Intel çipli Mac",
    'desktop.macIntelUnter': "2020’ye kadar eski Mac’ler",
    'desktop.passtZuIhnen': "Bu bilgisayara uygun",
    'desktop.laden': "İndir",
    'desktop.bald': "Yakında",
    'desktop.warnungTitel': "İlk açılışta uyarı mı?",
    'desktop.warnungText': "İndirmeler henüz Microsoft ve Apple’a kayıtlı değil; bu yüzden bilgisayarınız ilk açılışta bir kez sorar.",
    'desktop.warnungWindows': "“Windows bilgisayarınızı korudu” uyarısında “Ek bilgi”ye, sonra “Yine de çalıştır”a tıklayın.",
    'desktop.warnungMac': "LagerPro’yu “Uygulamalar” klasörüne sürükleyin. Uyarı çıkarsa “Sistem Ayarları → Gizlilik ve Güvenlik”te “Yine de Aç”a tıklayın (eski macOS: sağ tık → “Aç”).",
    'desktop.anmeldung': "Her iki yolda da: bir kez şifre (ve iki aşamalı kod) ile giriş, ardından 90 gün girişsiz. Çıkış yapmak bunu hemen sonlandırır.",
    'desktop.mehrInfos': "Uygulama hakkında her şey",

    'erinnerung.titel': 'Ödeme hatırlatmaları',
    'erinnerung.erklaerung': 'Bir fatura gecikirse, LagerPro müşterinize otomatik olarak nazik bir hatırlatma gönderir — sizin adınıza, yanıt adresi olarak sizin adresinizle.',
    'erinnerung.nachTagen': '… gün gecikmeden sonra hatırlat',
    'erinnerung.wiederholung': '… gün sonra tekrar',
    'erinnerung.maxAnzahl': 'Fatura başına en fazla … hatırlatma',
    'erinnerung.faellig': 'Sonraki çalışmada etkilenecek',
    'erinnerung.jetzt': 'Şimdi gönder',
    'erinnerung.keineFaellig': 'Şu anda gecikmiş fatura yok.',
    'erinnerung.firmenmailFehlt': '„Firma" altında e-postanızı girin — aksi halde müşterileriniz yanıt veremez.',
    'erinnerung.keineFuerKunden': 'Bu müşteriye hatırlatma gönderme',
  },

  ar: {
    'start.nav.funktionen': 'المزايا',
    'start.nav.belegkette': 'سلسلة المستندات',
    'start.nav.preise': 'الأسعار',
    'start.kostenlosStarten': 'ابدأ مجاناً',
    'start.themaDunkel': 'المظهر الداكن',
    'start.themaHell': 'المظهر الفاتح',
    'start.sprache': 'اللغة',
    'start.hero.marke': 'متوافق مع GoBD · خوادم في الاتحاد الأوروبي',
    'start.hero.titel1': 'المخزن والصندوق والمحاسبة.',
    'start.hero.titel2': 'نظام واحد بدل خمسة.',
    'start.hero.text': 'يدير LagerPro عملك من عرض السعر حتى الإقرار الضريبي: المخزون والمبيعات والمستندات والمحاسبة يغذّي بعضها بعضاً. لا تصدير، لا إعادة إدخال، ولا بيانات تُحفظ مرتين.',
    'start.hero.konto': 'لديّ حساب',
    'start.hero.fussnote': 'بدون بطاقة ائتمان · الإعداد خلال دقيقتين · يمكن الإلغاء شهرياً',
    'start.vorschau.umsatz': 'مبيعات اليوم',
    'start.vorschau.offen': 'فواتير مستحقة',
    'start.vorschau.lagerwert': 'قيمة المخزون',
    'start.vorschau.knapp': 'تحت الحد الأدنى',
    'start.vorschau.gruss': 'صباح الخير، بترا',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': 'آخر المستندات',
    'start.vorschau.angebot': 'عرض سعر',
    'start.vorschau.lieferschein': 'إشعار تسليم',
    'start.vorschau.rechnung': 'فاتورة',
    'start.vertrauen1t': 'متوافق مع GoBD',
    'start.vertrauen1x': 'ترقيم بلا فجوات، مستندات غير قابلة للتعديل، سجلات مترابطة بالبصمة — المعيار المحاسبي الألماني',
    'start.vertrauen2t': 'تصدير GDPdU',
    'start.vertrauen2x': 'تسليم بيانات Z3 مع index.xml — جاهز متى جاء التدقيق',
    'start.vertrauen3t': 'خوادم في الاتحاد الأوروبي',
    'start.vertrauen3x': 'لا مشاركة للبيانات، لا متتبعات إعلانية، التزام بلائحة حماية البيانات منذ اليوم الأول',
    'start.vertrauen4t': 'حفظ لعشر سنوات',
    'start.vertrauen4x': 'وفق المادة 147 من قانون الضرائب الألماني، تلقائياً — لا شيء عليك تذكّره',
    'start.f1t': 'صوّر المستند، وانتهى الأمر',
    'start.f1x': 'التقط صورة لإشعار التسليم أو الفاتورة بهاتفك — يستخرج المساعد البنود والكميات والمورّد. تراجعها مرة واحدة فتدخل البضاعة إلى المخزن. ويجيب عن أسئلتك حول المخزون والأرقام، كتابةً أو صوتاً.',
    'start.f2t': 'المخزن تحت السيطرة',
    'start.f2x': 'أرصدة لحظية، تنبيه عند الحد الأدنى، إدخال بضاعة وملصقات باركود بخمسة أحجام.',
    'start.f3t': 'صندوق للمحل',
    'start.f3x': 'اختر العميل، امسح الأصناف، حصّل المبلغ. البيع النقدي يبقى ممكناً، والمخزون يُخصم تلقائياً.',
    'start.f4t': 'كل المستندات',
    'start.f4x': 'عرض سعر، تأكيد طلب، إشعار تسليم، فاتورة، إشعار دائن وإلغاء — بصيغة PDF وبشعارك.',
    'start.f5t': 'محاسبة مضبوطة',
    'start.f5x': 'حساب الإيرادات والمصروفات بسطوره النظامية، إقرار ضريبة القيمة المضافة، دفتر الرحلات وتصدير DATEV لمحاسبك.',
    'start.f6t': 'مرتجعات بلا فوضى',
    'start.f6x': 'دائماً عبر الفاتورة: العميل، المستند، البنود. البضاعة تعود إلى المخزن والرصيد إلى حساب العميل.',
    'start.f7t': 'الفريق معك',
    'start.f7x': 'مهام بمؤقّت، محادثة بإشعارات، وحالة الحضور — دون الحاجة إلى تطبيق ثانٍ.',
    'start.funktionen.titel': 'كل ما يحتاجه العمل يومياً',
    'start.funktionen.text': 'كل جزء يعرف بيانات الأجزاء الأخرى. تبيع شيئاً فينخفض المخزون، وينشأ المستند، وتصبح القيود جاهزة — في اللحظة نفسها.',
    'start.kette.titel': 'معاملة واحدة، أربعة مستندات، بلا إعادة كتابة',
    'start.kette.text': 'بنقرة يتحول عرض السعر إلى تأكيد طلب، ومنه إلى إشعار تسليم، ومنه إلى فاتورة. البنود والأسعار تنتقل معها. والبضاعة تخرج من المخزن مرة واحدة فقط.',
    'start.kette.schritt': 'الخطوة',
    'start.kette1t': 'عرض سعر',
    'start.kette1x': 'كم ستكون التكلفة',
    'start.kette2t': 'طلب',
    'start.kette2x': 'العميل يوافق',
    'start.kette3t': 'إشعار تسليم',
    'start.kette3x': 'البضاعة تخرج',
    'start.kette4t': 'فاتورة',
    'start.kette4x': 'المال يدخل',
    'start.kette.fuss': 'وإن عاد شيء، فهناك الإشعار الدائن وفاتورة الإلغاء — مع القيد العكسي الذي تطلبه مصلحة الضرائب.',
    'start.preise.titel': 'أسعار بلا بنود صغيرة',
    'start.preise.text': "قابل للإلغاء شهريًا، جميع الأسعار غير شاملة للضريبة. كل باقة تبدأ بـ 30 يومًا مجانًا — دون بيانات مصرفية.",
    'start.preise.laedt': 'جارٍ تحميل الباقات …',
    'start.preise.fehler': 'الباقات غير متاحة حالياً. راسلنا وسنوافيك بالشروط.',
    'start.preise.top': 'الأكثر اختياراً',
    'start.preise.proMonat': '/ شهرياً',
    'start.preise.sofort': "جرّب 30 يومًا مجانًا",
    'start.preise.waehlen': "جرّب {tarif} 30 يومًا مجانًا",
    'start.ende.titel': 'ابدأ اليوم، واحصل على الصورة الكاملة غداً',
    'start.ende.text': 'أنشئ الحساب، أدخل الأصناف، واكتب أول فاتورة. ويمكنك نقل بياناتك السابقة لاحقاً.',
    'start.fuss.hinweis': 'خوادم في الاتحاد الأوروبي · متوافق مع GoBD',
    'nav.support': 'الدعم',
    'support.vonAnbieter': 'من LagerPro',
    'support.titel': 'الدعم',
    'support.untertitel': 'أسئلة أو أعطال أو اقتراحات — راسلنا هنا. تبقى المحادثة كاملة في مكان واحد.',
    'support.neu': 'طلب جديد',
    'support.betreff': 'الموضوع',
    'support.kategorie': 'بخصوص ماذا؟',
    'support.kat.frage': 'سؤال',
    'support.kat.fehler': 'شيء لا يعمل',
    'support.kat.wunsch': 'اقتراح أو فكرة',
    'support.kat.abrechnung': 'الفوترة والباقة',
    'support.kat.sonstiges': 'أخرى',
    'support.nachricht': 'الرسالة',
    'support.nachrichtPlatzhalter': 'صف بأكبر قدر من الدقة ما حدث وما كنت تتوقعه.',
    'support.anhang': 'إرفاق لقطة شاشة أو ملف',
    'support.anhangHinweis': 'حتى 5 ملفات، 25 ميغابايت لكل ملف',
    'support.senden': 'إرسال الطلب',
    'support.gesendet': 'وصل طلبك رقم {nummer}. ستصلك رسالة بريد إلكتروني فور وجود رد.',
    'support.leer': 'لا توجد طلبات بعد',
    'support.leerText': 'إذا لم يعمل شيء أو كان لديك سؤال — راسلنا.',
    'support.status.offen': 'تم الاستلام',
    'support.status.in_arbeit': 'قيد المعالجة',
    'support.status.wartet': 'يوجد رد',
    'support.status.erledigt': 'تم الحل',
    'support.zurueck': 'كل الطلبات',
    'support.antworten': 'رد',
    'support.antwortPlatzhalter': 'رسالتك …',
    'support.erledigt': 'تحديد كمحلول',
    'support.erledigtHinweis': 'تم الحل. إن بقي شيء فتابع الكتابة ببساطة — سيُعاد فتح الطلب.',
    'support.sie': 'أنت',
    'support.team': 'دعم LagerPro',
    'support.ungelesen': 'جديد',
    'support.glocke': 'رد من الدعم',
    'support.anhaengen': 'إرفاق',
    'einheit.stueck': 'قطعة',
    'einheit.paar': 'زوج',
    'einheit.satz': 'طقم',
    'einheit.paket': 'عبوة',
    'einheit.karton': 'كرتونة',
    'einheit.palette': 'منصة',
    'einheit.rolle': 'لفة',
    'einheit.flasche': 'زجاجة',
    'einheit.dose': 'علبة',
    'einheit.beutel': 'كيس',
    'einheit.minute': 'دقيقة',
    'einheit.stunde': 'ساعة',
    'einheit.tag': 'يوم',
    'einheit.woche': 'أسبوع',
    'einheit.monat': 'شهر',
    'einheit.jahr': 'سنة',
    'einheit.pauschale': 'مبلغ مقطوع',
    'einheit.gruppe.anzahl': 'العدد والتعبئة',
    'einheit.gruppe.gewicht': 'الوزن',
    'einheit.gruppe.volumen': 'الحجم',
    'einheit.gruppe.laenge': 'الطول والمساحة',
    'einheit.gruppe.zeit': 'الوقت',
    'einheit.gruppe.sonstiges': 'أخرى',
    'einheit.andere': 'وحدة أخرى …',
    'einheit.eigene': 'وحدة مخصصة',
    'einheit.eigenePlatzhalter': 'مثلاً حزمة، صندوق',
    'positionen.umgerechnet': '= {menge} من المخزون',
    'positionen.passtNicht': 'يُدار هذا الصنف بوحدة {einheit} — لا يمكن تحويل هذه الوحدة.',
    'positionen.jeEinheit': 'لكل {einheit}',
    'nav.assistent': 'مساعد الذكاء الاصطناعي',
    'assistent.datenschutz': 'لأغراض التحليل يُرسل المستند وسؤالك إلى مزوّد الذكاء الاصطناعي لدينا ويُعالج هناك للإجابة فقط، لا لتدريب النماذج. تبقى بيانات مخزونك على خادمنا.',
    'pruef.titel': 'مراجعة {art}',
    'pruef.beleg': 'المستند',
    'pruef.lieferschein': 'إشعار التسليم',
    'pruef.eingangsrechnung': 'فاتورة الشراء',
    'pruef.gutschrift': 'إشعار دائن',
    'pruef.bestellung': 'أمر الشراء',
    'pruef.kassenbon': 'إيصال الصندوق',
    'pruef.erkannt': '٪ تم التعرف',
    'pruef.ohneNummer': 'بدون رقم أو تاريخ',
    'pruef.schwerLesbar': 'كانت قراءة هذا المستند صعبة',
    'pruef.schwerLesbarText': 'راجع كل سطر. في الصورة غير الواضحة أو المائلة يخلط التعرّف بين الأرقام. غالباً ما تكون صورة جديدة بإضاءة جيدة أفضل من التصحيح.',
    'pruef.nichtAusgewertet': 'لم يُحلَّل',
    'pruef.nurBilder': 'يمكن قراءة الصور وملفات PDF فقط',
    'pruef.vorhandener': 'موجود',
    'pruef.neuAnlegen': 'إنشاء جديد',
    'pruef.ohne': 'بدون',
    'pruef.bitteWaehlen': '— اختر —',
    'pruef.gewaehlt': 'تم اختيار {a} من {b}',
    'pruef.alleUebernehmen': 'اختيار الكل',
    'pruef.keine': 'لا شيء',
    'pruef.keinePositionen': 'لم يتم التعرف على أي بند في المستند.',
    'pruef.warenwert': 'قيمة المحدد',
    'pruef.rechnungsbetrag': 'قيمة الفاتورة',
    'pruef.brauchtBuchhaltung': 'تسجيل المصروف يتطلب وحدة المحاسبة، وهي متاحة اعتباراً من باقة Professional.',
    'pruef.alsAusgabe': 'تسجيل المبلغ كمصروف تشغيلي',
    'pruef.alsAusgabeText': 'يبقى المستند مرفقاً كإثبات، فيكتمل المصروف للمحاسبة.',
    'pruef.brutto': 'المبلغ الإجمالي',
    'pruef.steuersatz': 'نسبة الضريبة',
    'pruef.belegnummer': 'رقم المستند',
    'pruef.belegdatum': 'تاريخ المستند',
    'pruef.lastschrift': 'خصم مباشر',
    'pruef.keineWare': 'ليست بضاعة',
    'pruef.keineWareText': 'تُحتسب ضمن قيمة الفاتورة لكنها لا تدخل المخزن.',
    'pruef.grund.keineWare': 'شحن أو رسوم أو خصم — ليس صنفاً قابلاً للتخزين',
    'pruef.privat': 'دُفع شخصياً',
    'pruef.erstMitKnopf': 'لا يُسجَّل شيء قبل الضغط على هذا الزر.',
    'pruef.buchen': 'اعتماد وتسجيل',
    'pruef.wirdGebucht': 'جارٍ التسجيل',
    'pruef.zugeordnet': 'مطابق',
    'pruef.vermutet': 'مرجَّح',
    'pruef.neu': 'جديد',
    'pruef.schlechtLesbar': 'صعب القراءة',
    'pruef.aendern': 'تعديل',
    'pruef.zuklappen': 'طيّ',
    'pruef.wirdAngelegt': 'سيُنشأ كصنف جديد',
    'pruef.artikel': 'الصنف',
    'pruef.nichtZugeordnet': '— غير مطابق —',
    'pruef.alsNeuenArtikel': 'إنشاء كصنف جديد',
    'pruef.einkaufspreis': 'سعر الشراء',
    'pruef.verkaufspreis': 'سعر البيع',
    'pruef.spaeterErgaenzen': 'يمكن إضافته لاحقاً',
    'pruef.ean': 'EAN',
    'pruef.je': 'لكل',
    'pruef.grund.ean': 'رمز EAN مطابق',
    'pruef.grund.nummer': 'رقم الصنف مطابق',
    'pruef.grund.name': 'التسمية مطابقة تماماً',
    'pruef.grund.ustId': 'الرقم الضريبي مطابق',
    'pruef.grund.aehnlich': 'تسمية مشابهة — يُرجى التحقق',
    'pruef.grund.keiner': 'لا يوجد صنف مطابق في المخزن',
    'pruef.kat.wareneinkauf': 'شراء بضاعة',
    'pruef.kat.buero': 'لوازم مكتبية',
    'pruef.kat.fahrzeug': 'مصاريف المركبات',
    'pruef.kat.werbung': 'مصاريف الدعاية',
    'pruef.kat.kommunikation': 'الهاتف والإنترنت',
    'pruef.kat.raumkosten': 'الإيجار والمقر',
    'pruef.kat.sonstiges': 'مصاريف تشغيلية أخرى',
    'assistent.titel': 'مساعد الذكاء الاصطناعي',
    'assistent.rest': 'المتبقي هذا الشهر',
    'assistent.scans': 'مسح مستندات',
    'assistent.fragen': 'أسئلة',
    'assistent.testmodus': 'وضع الاختبار',
    'assistent.testmodusText': 'لم يتم إعداد وصول الذكاء الاصطناعي. المستندات تعيد بيانات نموذجية والإجابات مؤقتة. بمجرد إعداد الوصول، سيعمل المساعد ببياناتك الحقيقية.',
    'assistent.verlaufLoeschen': 'حذف السجل',
    'assistent.denkt': 'لحظة من فضلك',
    'assistent.platzhalter': 'اطرح سؤالاً أو صوّر مستنداً',
    'assistent.hoertZu': 'أستمع إليك',
    'assistent.sprechen': 'تحدث',
    'assistent.vorlesen': 'اقرأ بصوت عالٍ',
    'assistent.stopp': 'إيقاف',
    'assistent.senden': 'إرسال',
    'assistent.fotografieren': 'تصوير مستند',
    'assistent.datei': 'رفع ملف',
    'assistent.belegAuswerten': 'تحليل المستند',
    'assistent.wertetAus': 'جارٍ قراءة المستند',
    'assistent.bestaetigen': 'تأكيد',
    'assistent.gebucht': 'تم التسجيل',
    'assistent.verworfen': 'تم الإلغاء',
    'assistent.willkommen': 'كيف يمكنني المساعدة؟',
    'assistent.willkommenText': 'اسأل عن المخزون والأرقام، أو صوّر إشعار تسليم — تدخل البنود إلى المخزن بعد مراجعتك لها.',
    'assistent.beispiel1': 'ما الأصناف التي أوشكت على النفاد؟',
    'assistent.beispiel2': 'كم بلغت مبيعاتي هذا الشهر؟',
    'assistent.beispiel3': 'ما الفواتير التي ما زالت مستحقة؟',
    'assistent.beispiel4': 'ما قيمة مخزوني؟',
    'allg.entfernen': 'إزالة',
    'bestaetigung.titel': 'أكّد عنوان بريدك الإلكتروني',
    'bestaetigung.text': 'أرسلنا إليك رسالة. افتح الرابط الموجود فيها لتتابع.',
    'bestaetigung.warum': 'بدون عنوان مؤكَّد لن تصلك رموز الدخول ولا الفواتير ولا الإشعار المسبق قبل الخصم المباشر.',
    'bestaetigung.schonBestaetigt': 'لقد أكّدت — متابعة',
    'bestaetigung.erneut': 'إعادة إرسال الرسالة',
    'bestaetigung.erneutGesendet': 'الرسالة في طريقها إليك.',
    'bestaetigung.versandFehlt': 'الإرسال غير متاح حالياً. يُرجى التواصل مع الدعم.',
    'bestaetigung.fehlgeschlagen': 'لم ينجح ذلك.',
    'bestaetigung.spam': 'لم يصلك شيء؟ تحقّق من مجلد الرسائل غير المرغوبة.',
    'bestaetigung.pruefe': 'لحظة من فضلك …',
    'bestaetigung.fertigTitel': 'تم تأكيد العنوان',
    'bestaetigung.fertigText': 'تم تأكيد بريدك الإلكتروني. يمكنك المتابعة.',
    'bestaetigung.zurApp': 'إلى التطبيق',
    'bestaetigung.abgelaufenTitel': 'انتهت صلاحية الرابط',
    'bestaetigung.abgelaufenText': 'لم يعد هذا الرابط صالحاً. سجّل الدخول واطلب رسالة جديدة.',
    'bestaetigung.ungueltigTitel': 'رابط غير صالح',
    'bestaetigung.ungueltigText': 'لا يمكن استخدام هذا الرابط. ربما تم تأكيد العنوان مسبقاً.',
    'mandat.pflichtTitel': 'تفويض الخصم المباشر SEPA',
    'mandat.pflichtText': 'للمتابعة يلزم تفويض خصم مباشر SEPA. يستغرق دقيقتين، وتبقى بياناتك كما هي.',
    'mandat.einleitung': 'تمنح التفويض باسم صاحب الحساب ورقم IBAN. لا حاجة إلى توقيع ورقي.',
    'mandat.kontoinhaber': 'صاحب الحساب',
    'mandat.kontoinhaberHinweis': 'بالصيغة نفسها المسجّلة لدى مصرفك تماماً.',
    'mandat.bicHinweis': 'غير مطلوب داخل منطقة SEPA.',
    'mandat.ibanFalsch': 'رقم IBAN هذا لا يجتاز خانة التحقق.',
    'mandat.wortlautTitel': 'نص التفويض',
    'mandat.glaeubiger': 'الدائن',
    'mandat.glaeubigerId': 'رقم تعريف الدائن',
    'mandat.referenz': 'مرجع التفويض',
    'mandat.referenzSpaeter': 'يُمنح بعد التوقيع ويُبلَّغ إليك قبل أول خصم',
    'mandat.zustimmung': 'أوافق على تفويض الخصم المباشر الوارد أعلاه.',
    'mandat.erteilen': 'منح التفويض',
    'mandat.erteilt': 'تم منح التفويض. شكراً لك.',
    'mandat.liegtVor': 'يوجد تفويض ساري المفعول.',
    'mandat.nochNichtBereit': 'يجري إعداد نظام الخصم المباشر. حاول لاحقاً أو تواصل مع الدعم.',
    'mandat.ladefehler': 'تعذّر تحميل البيانات.',
    'mandat.fehlgeschlagen': 'تعذّر منح التفويض.',
    'allg.optional': 'اختياري',
    'sperre.titel': 'تطبيق LagerPro مقفل',
    'sperre.entsperren': 'إلغاء القفل',
    'sperre.abgebrochen': 'تم إلغاء التحقق.',
    'geraete.titel': 'الأجهزة المسجّلة',
    'geraete.hinweis': 'الأجهزة التي تبقى فيها مسجَّل الدخول.',
    'geraete.hinweisAdmin': 'جميع أجهزة منشأتك. سجّل خروج أي جهاز عند فقدانه أو عند مغادرة أحد الموظفين.',
    'geraete.keine': 'لا يوجد جهاز مسجَّل بشكل دائم.',
    'geraete.dieses': 'هذا الجهاز',
    'geraete.zuletzt': 'آخر ظهور',
    'geraete.abmelden': 'تسجيل الخروج',
    'geraete.abmeldenFehler': 'تعذّر تسجيل خروج الجهاز.',
    'geraete.ladefehler': 'تعذّر تحميل الأجهزة.',
    'geraete.nichtGemerkt': 'هذا الجهاز غير محفوظ. سجّل الدخول مرة واحدة بكلمة المرور، وبعدها تكفي بصمتك.',
    'geraete.sperreTitel': 'القفل عند الفتح',
    'geraete.sperreAn': 'اقفل التطبيق بواسطة {art}',
    'geraete.sperreText': 'يُطلب عند الفتح وبعد خمس دقائق في الخلفية.',
    'geraete.sperreAusWarnung': 'بدون القفل، يصل إلى بيانات منشأتك كل من يمسك هاتفك وهو غير مقفل.',
    'geraete.alleAnderen': "تسجيل خروج الجميع الآخرين",
    'geraete.gueltigBis': "مسجل حتى {datum}",
    'geraete.browserTitel': "مسجل في متصفح",
    'geraete.diese': "هذه الجلسة",
    'geraete.browserHinweis': "تنتهي جلسات المتصفح تلقائيًا بعد 24 ساعة — أو فورًا من هنا.",
    'app.name': 'LagerPro',
    'app.untertitel': 'إدارة مستودعات احترافية',
    'benachrichtigung.titel': 'الإشعارات',
    'benachrichtigung.leer': 'لا جديد',
    'benachrichtigung.neu': 'إشعار جديد',
    'benachrichtigung.neueVorgaenge': 'عناصر جديدة',
    'benachrichtigung.anAlle': 'للجميع',
    'nav.uebersicht': 'نظرة عامة',
    'nav.mehr': 'المزيد',
    'allg.rechnung': 'فاتورة',
    'app.gruss': 'مرحباً، {name}',
    'app.gruppe.tagesgeschaeft': 'العمل اليومي',
    'app.gruppe.kontakte': 'جهات الاتصال',
    'app.gruppe.zusammenarbeit': 'التعاون',
    'app.gruppe.auswertung': 'التقارير',
    'app.gruppe.betrieb': 'الشركة',
    'app.nochmalBeenden': 'اضغط مرة أخرى للخروج',
    'app.keineVerbindung': 'لا يوجد اتصال — الأرقام غير متاحة',
    'app.konto': 'الحساب',
    'app.darstellung': 'المظهر',
    'nav.kasse': 'نقطة البيع',
    'nav.lager': 'المستودع',
    'nav.belege': 'المستندات',
    'nav.rechnungen': 'الفواتير',
    'nav.kunden': 'العملاء',
    'nav.lieferanten': 'الموردون',
    'nav.retouren': 'المرتجعات',
    'nav.buchhaltung': 'المحاسبة',
    'nav.ausgaben': 'المصروفات',
    'nav.bank': 'البنك',
    'nav.fahrtenbuch': 'سجل الرحلات',
    'nav.aufgaben': "اللوحة",
    'nav.chat': 'المحادثة',
    'nav.team': 'الفريق',
    'nav.firma': 'الشركة',
    'nav.gobd': 'الامتثال والتدقيق',
    'nav.tarif': 'الخطة',
    'nav.uebernahme': 'نقل البيانات',
    'nav.einstellungen': 'الإعدادات',
    'nav.owner': 'إدارة العملاء',
    'nav.mandanten': 'المستأجرون',
    'nav.gruppe.verkauf': 'المبيعات',
    'nav.gruppe.waren': 'السلع',
    'nav.gruppe.kontakte': 'جهات الاتصال',
    'nav.gruppe.auswertung': 'التقارير',
    'nav.gruppe.zusammenarbeit': 'التعاون',
    'nav.gruppe.verwaltung': 'الإدارة',
    'aktion.anlegen': 'إنشاء',
    'aktion.speichern': 'حفظ',
    'aktion.abbrechen': 'إلغاء',
    'aktion.bearbeiten': 'تعديل',
    'aktion.loeschen': 'حذف',
    'aktion.schliessen': 'إغلاق',
    'aktion.suchen': 'بحث',
    'aktion.anmelden': 'تسجيل الدخول',
    'aktion.abmelden': 'تسجيل الخروج',
    'aktion.drucken': 'طباعة',
    'aktion.herunterladen': 'تنزيل',
    'allg.suche': 'بحث',
    'allg.laedt': 'جار التحميل',
    'allg.keineTreffer': 'لا توجد نتائج',
    'allg.darstellung': 'المظهر',
    'allg.sprache': 'اللغة',
    'allg.thema.system': 'النظام',
    'allg.thema.hell': 'فاتح',
    'allg.thema.dunkel': 'داكن',
    'allg.rolle.inhaber': 'المالك',
    'allg.rolle.admin': 'مدير',
    'allg.rolle.mitarbeiter': 'موظف',
    'allg.nichtImTarif': 'غير مشمول في خطتك',
    'allg.tarif': 'الخطة',
    'allg.menue': 'القائمة',
    'allg.schnellzugriff': 'وصول سريع',
    'allg.schnellzugriffHinweis': "صفحة أو عميل أو رقم مستند …",
    'anmelden.willkommen': 'مرحباً بعودتك',
    'anmelden.hinweis': 'سجّل الدخول للمتابعة',
    'anmelden.kennung': 'اسم المستخدم أو البريد الإلكتروني',
    'anmelden.passwort': 'كلمة المرور',
    'anmelden.fehler': 'فشل تسجيل الدخول.',
    'anmelden.keinKonto': 'ليس لديك حساب بعد؟',
    'anmelden.jetztRegistrieren': 'سجّل مجاناً',
    'passwort.vergessenLink': "هل نسيت كلمة المرور؟",
    'passwort.vergessenTitel': "نسيت كلمة المرور",
    'passwort.vergessenText': "أدخل اسم المستخدم أو بريدك الإلكتروني. سنرسل إليك رابطًا لتعيين كلمة مرور جديدة.",
    'passwort.linkSenden': "إرسال الرابط",
    'passwort.gesendetTitel': "تحقق من بريدك الوارد",
    'passwort.gesendetText': "إذا وُجد حساب ببريد إلكتروني مطابق، فالرابط في الطريق. صالح لمدة {min} دقيقة. إن لم يكن هناك بريد مسجّل، فسيساعدك المسؤول.",
    'passwort.zurAnmeldung': "العودة لتسجيل الدخول",
    'passwort.neuTitel': "تعيين كلمة مرور جديدة",
    'passwort.fuer': "للحساب {name}",
    'passwort.regeln': "8 أحرف على الأقل، بأحرف كبيرة وصغيرة ورقم.",
    'passwort.ungleich': "كلمتا المرور غير متطابقتين.",
    'passwort.speichern': "حفظ كلمة المرور",
    'passwort.fertigTitel': "تم تغيير كلمة المرور",
    'passwort.fertigText': "سجّل الدخول الآن بكلمة المرور الجديدة. تم تسجيل الخروج من جميع الأجهزة.",
    'passwort.abgelaufenTitel': "انتهت صلاحية الرابط",
    'passwort.abgelaufenText': "الرابط صالح لمدة 30 دقيقة فقط. اطلب رابطًا جديدًا.",
    'passwort.ungueltigTitel': "رابط غير صالح",
    'passwort.ungueltigText': "هذا الرابط غير صالح أو تم استخدامه من قبل.",
    'passwort.neuAnfordern': "طلب رابط جديد",
    'passwort.pruefe': "جارٍ التحقق من الرابط …",
    'uebersicht.begruessung': 'مرحباً',
    'uebersicht.offeneAufgaben': 'المهام المفتوحة',
    'uebersicht.laufendeTimer': 'المؤقتات النشطة',
    'uebersicht.teamOnline': 'الفريق متصل',
    'uebersicht.benutzerImTarif': 'المستخدمون في الخطة',
    'uebersicht.naechsteAufgaben': 'المهام القادمة',
    'uebersicht.alleAnsehen': 'عرض الكل',
    'uebersicht.unbegrenzt': 'غير محدود',
    'uebersicht.von': 'من',
    'uebersicht.insgesamt': 'الإجمالي',
    'uebersicht.keineOffenen': 'لا توجد مهام مفتوحة.',

    'feld.name': 'الاسم',
    'feld.nummer': 'الرقم',
    'feld.telefon': 'الهاتف',
    'feld.email': 'البريد الإلكتروني',
    'feld.strasse': 'الشارع',
    'feld.plz': 'الرمز البريدي',
    'feld.ort': 'المدينة',
    'feld.land': 'الدولة',
    'feld.notiz': 'ملاحظة',
    'feld.ansprechpartner': 'جهة الاتصال',
    'feld.ustId': 'الرقم الضريبي',
    'feld.menge': 'الكمية',
    'feld.einzelpreis': 'سعر الوحدة',
    'feld.bezeichnung': 'الوصف',
    'feld.artikel': 'الصنف',
    'feld.kunde': 'العميل',
    'feld.lieferant': 'المورّد',
    'feld.datum': 'التاريخ',
    'feld.betrag': 'المبلغ',
    'feld.grund': 'السبب',
    'feld.status': 'الحالة',
    'feld.einheit': 'الوحدة',
    'feld.bestand': 'المخزون',
    'feld.gesamt': 'الإجمالي',
    'feld.netto': 'الصافي',
    'feld.rabatt': 'الخصم',
    'feld.position': 'البند',
    'feld.positionen': 'البنود',
    'aktion.hinzufuegen': 'إضافة',
    'aktion.entfernen': 'إزالة',
    'aktion.zurueck': 'رجوع',
    'aktion.weiter': 'متابعة',
    'kunden.titel': 'العملاء',
    'kunden.neu': 'عميل جديد',
    'kunden.nummer': 'رقم العميل',
    'kunden.suche': 'الاسم أو الرقم أو البريد أو المدينة …',
    'kunden.leer': 'لا يوجد عملاء بعد',
    'kunden.leerText': 'أضف أول عميل.',
    'kunden.akte': "ملف العميل",
    'kunden.alle': "كل العملاء",
    'kunden.nichtGefunden': "لم يتم العثور على العميل.",
    'kunden.gespeichert': "تم حفظ العميل.",
    'kunden.geloescht': "تم حذف {name}.",
    'kunden.loeschenFrage': "هل تريد حذف «{name}» فعلًا؟",
    'kunden.einKunde': "عميل واحد",
    'kunden.anzahl': "{n} عملاء",
    'kunden.sortieren': "ترتيب حسب",
    'kunden.letzterKaufSort': "آخر شراء",
    'kunden.keineKontakt': "لا توجد بيانات اتصال",
    'kunden.nochNichts': "لا مشتريات بعد",
    'kunden.zuletzt': "آخرها {datum}",
    'kunden.rechnungSchreiben': "إنشاء فاتورة",
    'kunden.umsatzJahr': "المبيعات {jahr}",
    'kunden.netto': "صافي",
    'kunden.vorjahr': "العام السابق {betrag}",
    'kunden.offen': "مستحق",
    'kunden.ueberfaellig': "{betrag} متأخر ({n})",
    'kunden.ueberfaelligKurz': "{betrag} متأخر",
    'kunden.offenAnzahl': "{n} فواتير مستحقة",
    'kunden.offenBetrag': "{betrag} مستحق",
    'kunden.meistgekauft': "الأكثر شراءً",
    'kunden.malGekauft': "اشتُري {n} مرة",
    'kunden.keineProdukte': "لم يشترِ شيئًا بعد",
    'kunden.zahlung': "سلوك الدفع",
    'kunden.zahltNach': "متوسط {n} يوم",
    'kunden.puenktlich': "{n} % دُفعت في الموعد",
    'kunden.nochKeineZahlung': "لا مدفوعات بعد",
    'kunden.seit': "عميل منذ {datum}",
    'kunden.letzterKauf': "آخر شراء {datum}",
    'kunden.gesamt': "إجمالي المبيعات {betrag}",
    'kunden.durchschnitt': "متوسط الفاتورة {betrag}",
    'kunden.guthaben': "رصيد {betrag}",
    'kunden.verlauf': "المبيعات في آخر 12 شهرًا",
    'kunden.diagrammLabel': "المبيعات شهريًا",
    'kunden.keinUmsatz': "لا مبيعات في آخر 12 شهرًا",
    'kunden.rechnungenAnzahl': "{n} فواتير",
    'kunden.produkte': "المنتجات المشتراة",
    'kunden.kaeufe': "مرات الشراء",
    'kunden.menge': "الكمية",
    'kunden.umsatz': "المبيعات",
    'kunden.mehr': "عرض الكل ({n})",
    'kunden.weniger': "عرض أقل",
    'kunden.dokumente': "المستندات",
    'kunden.dokumenteHinweis': "{n} إجمالًا، الأحدث أولًا",
    'kunden.alleDokumente': "الكل",
    'kunden.nurOffene': "مستحقة",
    'kunden.keineDokumente': "لا توجد مستندات",
    'kunden.zu': "مرتبط بـ {nummer}",
    'kunden.entwurf': "مسودة",
    'kunden.faellig': "تستحق {datum}",
    'kunden.gueltigBis': "صالح حتى {datum}",
    'kunden.ansehen': "عرض",
    'kunden.oeffnen': "فتح",
    'kunden.art.rechnung': "فاتورة",
    'kunden.art.lieferschein': "إشعار تسليم",
    'kunden.art.angebot': "عرض سعر",
    'kunden.art.auftrag': "طلب",
    'kunden.art.gutschrift': "إشعار دائن",
    'kunden.art.retoure': "مرتجع",
    'kunden.arten.rechnung': "الفواتير",
    'kunden.arten.lieferschein': "إشعارات التسليم",
    'kunden.arten.angebot': "عروض الأسعار",
    'kunden.arten.auftrag': "الطلبات",
    'kunden.arten.gutschrift': "الإشعارات الدائنة",
    'kunden.arten.retoure': "المرتجعات",
    'kunden.status.entwurf': "مسودة",
    'kunden.status.gestellt': "صادرة",
    'kunden.status.bezahlt': "مدفوعة",
    'kunden.status.storniert': "ملغاة",
    'kunden.status.gesendet': "مُرسل",
    'kunden.status.angenommen': "مقبول",
    'kunden.status.abgelehnt': "مرفوض",
    'kunden.status.abgelaufen': "منتهي",
    'kunden.status.offen': "مفتوح",
    'kunden.status.inArbeit': "قيد التنفيذ",
    'kunden.status.erledigt': "منجز",
    'kunden.status.geliefert': "تم التسليم",
    'kunden.status.eingelagert': "أُعيد للمخزون",
    'kunden.status.erfasst': "مُسجل",
    'kunden.status.nichtGeliefert': "لم يُسلَّم",
    'kunden.status.ausgestellt': "صادر",
    'kunden.status.teilbezahlt': "مدفوعة جزئيًا",
    'lieferanten.titel': 'الموردون',
    'lieferanten.neu': 'مورّد جديد',
    'lieferanten.nummer': 'رقم المورّد',
    'lieferanten.suche': 'الاسم أو جهة الاتصال أو المدينة …',
    'lieferanten.akte': 'الملف',
    'lieferanten.alle': 'كل الموردين',
    'lieferanten.artikel': 'الأصناف',
    'lieferanten.lieferungen': 'التوريدات',
    'lieferanten.rechnungen': 'الفواتير',
    'lieferanten.offen': 'مفتوح',
    'lieferanten.ueberfaellig': 'متأخر',
    'lieferanten.einkaufJahr': 'مشتريات {jahr}',
    'lieferanten.letzteLieferung': 'آخر توريد',
    'lieferanten.keineArtikel': 'لا أصناف من هذا المورد بعد.',
    'lieferanten.keineLieferungen': 'لا توجد مداخيل بضاعة بعد.',
    'lieferanten.keineRechnungen': 'لا توجد فواتير بعد.',
    'lieferanten.rechnungErfassen': 'تسجيل فاتورة',
    'lieferanten.alsBezahlt': 'وضع علامة مدفوعة',
    'lieferanten.bezahltAm': 'مدفوعة {datum}',
    'lieferanten.faellig': 'مستحقة {datum}',
    'lieferanten.betrag': 'المبلغ (إجمالي)',
    'lieferanten.rechnungsnummer': 'رقم الفاتورة',
    'lieferanten.rechnungsdatum': 'تاريخ الفاتورة',
    'lieferanten.faelligAm': 'تاريخ الاستحقاق',
    'lieferanten.schonBezahlt': 'مدفوعة بالفعل',
    'lieferanten.wert': 'القيمة',
    'lieferanten.wiederOffen': 'إعادة فتح',
    'lieferanten.gespeichert': 'تم الحفظ.',
    'lieferanten.bearbeiten': 'تعديل',
    'lieferanten.loeschen': 'حذف',
    'lager.zugang': 'إدخال بضاعة',
    'lager.abgang': 'إخراج',
    'lager.buchen': 'تسجيل',
    'lager.rechnungLiegtBei': 'فاتورة المورد مرفقة',
    'lager.rechnungBezahlt': 'الفاتورة مدفوعة بالفعل',
    'lager.gebucht': 'تم تسجيل مخزون «{name}».',
    'lager.ohneLieferant': 'بدون مورد',
    'lieferanten.leer': 'لا يوجد موردون بعد',
    'lieferanten.leerText': 'أضف أول مورّد.',
    'allg.andereSuche': 'جرّب كلمات بحث أخرى.',

    'lager.titel': 'المستودع',
    'lager.neu': 'صنف جديد',
    'lager.artikelnummer': 'رقم الصنف',
    'lager.ean': 'EAN / الباركود',
    'lager.kategorie': 'الفئة',
    'lager.lagerort': 'الموقع',
    'lager.einkaufspreis': 'سعر الشراء (صافي)',
    'lager.verkaufspreis': 'سعر البيع (صافي)',
    'lager.mindestbestand': 'الحد الأدنى للمخزون',
    'lager.anfangsbestand': 'المخزون الافتتاحي',
    'lager.wareneingang': 'يُسجَّل كإدخال بضاعة.',
    'lager.suche': 'الوصف أو الرقم أو EAN …',
    'lager.leer': 'لا توجد أصناف',
    'lager.leerText': 'أضف أول صنف أو استورد بياناتك.',
    'lager.verlauf': 'السجل',
    'lager.keineBewegungen': 'لا توجد حركات',
    'lager.etikett': 'الملصق',
    'lager.etikettenDrucken': 'طباعة الملصقات',
    'lager.format': 'المقاس',
    'lager.stueckJeArtikel': 'نسخ لكل صنف',
    'lager.mehrfachHinweis': 'عند استلام البضاعة غالبًا ما تحتاج الملصق نفسه عدة مرات.',
    'lager.knapp': 'منخفض',
    'lager.auswahl': 'التحديد',
    'lager.auswahlAufheben': 'إلغاء التحديد',
    'lager.alle': 'الكل',
    'lager.keinLieferant': '— بدون مورّد —',
    'kasse.titel': 'نقطة البيع',
    'kasse.schritt1': 'الخطوة 1 من 2 — لمن هذا البيع؟',
    'kasse.kundeWaehlen': 'اختر العميل',
    'kasse.kundeSuchen': 'ابحث عن عميل',
    'kasse.kundeSuche': 'الاسم أو رقم العميل أو المدينة …',
    'kasse.kundeWechseln': 'تغيير العميل',
    'kasse.ohneKonto': 'بدون حساب عميل',
    'kasse.historieHinweis': 'ستظهر عملية البيع في سجل فواتيره',
    'kasse.artikelSuchen': 'ابحث عن صنف',
    'kasse.artikelSuche': 'الوصف أو الرقم أو امسح الباركود …',
    'kasse.keinArtikel': 'لم يتم العثور على صنف',
    'kasse.warenkorb': 'السلة',
    'kasse.warenkorbLeeren': 'إفراغ السلة',
    'kasse.leer': 'لم تتم إضافة شيء بعد',
    'kasse.leerText': 'انقر على صنف من اليسار.',
    'kasse.zahlart': 'طريقة الدفع',
    'kasse.bezahlt': 'مدفوع؟',
    'kasse.vollstaendig': 'بالكامل',
    'kasse.teilweise': 'جزئيًا',
    'kasse.nochNicht': 'ليس بعد',
    'kasse.gegeben': 'المبلغ المسلَّم (للباقي)',
    'kasse.rueckgeld': 'الباقي: {betrag}',
    'kasse.erhalten': 'المستلَم',
    'kasse.kundePflicht': 'لمبلغ مفتوح يُرجى اختيار عميل — وإلا لا يعرف أحد من عليه الدفع.',
    'kasse.buchen': 'تسجيل · {erhalten} مستلَم، {offen} مفتوح',
    'kasse.kassieren': 'تحصيل · {betrag}',
    'kasse.offenBis': 'مفتوح: {betrag} · مستحق {datum}',
    'kasse.bezahltMit': 'مدفوع · {zahlart}',
    'kasse.naechster': 'البيع التالي',
    'kasse.bar': 'نقدًا',
    'kasse.karte': 'بطاقة',
    'kasse.ueberweisung': 'تحويل بنكي',
    'kasse.abgeschlossen': 'تم إتمام البيع',
    'kasse.alle': "الكل",
    'kasse.anzahlung': "دفعة مقدمة",
    'kasse.aufRechnung': "على الحساب",
    'kasse.barAnteil': "الجزء النقدي",
    'kasse.barKassieren': "تحصيل نقدًا · {betrag}",
    'kasse.beleg': "إيصال",
    'kasse.betragBrutto': "المبلغ (شامل)",
    'kasse.bezahlen': "دفع",
    'kasse.bezahlenBetrag': "دفع · {betrag}",
    'kasse.bonNr': "فاتورة {n}",
    'kasse.bonRabatt': "خصم على الفاتورة",
    'kasse.bonVerwerfen': "إلغاء الفاتورة",
    'kasse.codeUnbekannt': "لا يوجد صنف بالرمز {code}",
    'kasse.enthaltenSteuer': "يشمل {satz} % {name}",
    'kasse.fehlgeschlagen': "تعذر تسجيل البيع.",
    'kasse.freiHinweis': "لكل ما ليس صنفًا: خدمة، تأمين، أخرى.",
    'kasse.freiStandard': "أخرى",
    'kasse.freierBetrag': "مبلغ حر",
    'kasse.gegebenKurz': "المدفوع",
    'kasse.gespart': "وفّرت",
    'kasse.geteilt': "مقسّم",
    'kasse.geteiltKassieren': "تحصيل · {bar} نقدًا + {karte} بطاقة",
    'kasse.haeufig': "الأكثر مبيعًا",
    'kasse.kameraFehler': "الكاميرا غير متاحة — اسمح بالوصول أو استخدم ماسحًا يدويًا.",
    'kasse.karteBezahlt': "دُفع بالبطاقة · {betrag}",
    'kasse.karteHinweis': "اخصم المبلغ على جهاز البطاقة ثم أكّد هنا.",
    'kasse.karteRest': "الباقي بالبطاقة",
    'kasse.keinRabatt': "بدون",
    'kasse.keineArtikelAngelegt': "لا توجد أصناف بعد.",
    'kasse.kundeEntfernen': "إزالة العميل",
    'kasse.kundeZuordnen': "تعيين عميل",
    'kasse.laufkunde': "عميل عابر",
    'kasse.leerNeu': "المس صنفًا أو امسح رمزًا شريطيًا.",
    'kasse.neuerBon': "فاتورة جديدة",
    'kasse.nichtAufLager': "«{name}» غير متوفر.",
    'kasse.nichtVorraetig': "غير متوفر",
    'kasse.nurNoch': "المتوفر من «{name}» {menge} فقط.",
    'kasse.parkenHinweis': "تعليق هذه الفاتورة وبدء أخرى",
    'kasse.passend': "بالضبط",
    'kasse.positionen': "البنود: {n}",
    'kasse.preis': "السعر",
    'kasse.preisAendern': "تغيير السعر",
    'kasse.rabatt': "خصم",
    'kasse.rabattText': "خصم {p} %",
    'kasse.rechnungBuchen': "تسجيل على الحساب · {offen} مستحق",
    'kasse.rueckgeldGross': "الباقي للعميل",
    'kasse.scannen': "مسح الرمز الشريطي",
    'kasse.scannenHinweis': "ضع الرمز أمام الكاميرا.",
    'kasse.wofuer': "لأي غرض؟",
    'kasse.zuWenig': "ينقص {betrag}.",

    'belege.titel': 'المستندات',
    'belege.angebote': 'عروض الأسعار',
    'belege.auftraege': 'الطلبات',
    'belege.lieferscheine': 'سندات التسليم',
    'belege.gutschriften': 'إشعارات دائنة',
    'belege.offeneAngebote': 'عروض مفتوحة',
    'belege.laufendeAuftraege': 'طلبات جارية',
    'belege.lieferscheineOffen': 'تسليمات معلقة',
    'belege.interessent': 'اسم العميل المحتمل',
    'belege.notiz': 'ملاحظة (تظهر على المستند)',
    'belege.rabatt': 'الخصم %',
    'belege.druckenPdf': 'طباعة / PDF',
    'belege.entwurfLoeschen': 'حذف المسودة',
    'belege.gesamtBrutto': 'الإجمالي شاملاً',
    'belege.einzel': 'الوحدة',
    'rechnungen.titel': 'الفواتير',
    'rechnungen.neu': 'فاتورة جديدة',
    'rechnungen.schreiben': 'إنشاء فاتورة',
    'rechnungen.stellen': 'إصدار الفاتورة',
    'rechnungen.entwurfSpeichern': 'حفظ كمسودة',
    'rechnungen.entwurfHinweis': 'المسودة لا تخصم من المخزون بعد.',
    'rechnungen.bezahltMarkieren': 'وضع علامة مدفوعة',
    'rechnungen.teilbezahlt': 'مدفوعة جزئيًا · المتبقي {betrag}',
    'rechnungen.perBank': 'مطابقة عبر البنك',
    'rechnungen.zahlungen': 'المدفوعات',
    'rechnungen.nochOffen': 'المتبقي',
    'rechnungen.bankeingang': 'إيداع بنكي',
    'rechnungen.zahlung': 'الدفع',
    'rechnungen.zahlart': 'طريقة الدفع',
    'rechnungen.zahlweg.ueberweisung': 'تحويل بنكي',
    'rechnungen.zahlweg.bar': 'نقدًا',
    'rechnungen.zahlweg.karte': 'بطاقة',
    'rechnungen.zahlungsziel': 'مهلة الدفع',
    'rechnungen.zielKunde': 'كما لدى العميل ({tage} يومًا)',
    'rechnungen.zielSofort': 'مستحق فورًا',
    'rechnungen.zielTage': '{tage} يومًا',
    'rechnungen.zielDatum': 'تاريخ مخصص',
    'rechnungen.faelligAm': 'تاريخ الاستحقاق',
    'rechnungen.bezahlung': 'مدفوعة؟',
    'rechnungen.offenLassen': 'لم تُدفع بعد',
    'rechnungen.schonBezahlt': 'مدفوعة بالفعل',
    'rechnungen.lieferscheinMit': 'إنشاء إشعار التسليم أيضًا',
    'rechnungen.lieferschein': 'إشعار التسليم',
    'rechnungen.lieferscheinDrucken': 'طباعة إشعار التسليم',
    'rechnungen.lieferscheinErstellen': 'إنشاء إشعار التسليم',
    'rechnungen.zahlungErfassen': 'تسجيل دفعة',
    'rechnungen.betrag': 'المبلغ',
    'rechnungen.zahltag': 'تاريخ الدفع',
    'rechnungen.alsBezahltSpeichern': 'حفظ كمدفوعة',
    'rechnungen.teilzahlungSpeichern': 'حفظ دفعة جزئية',
    'rechnungen.ratenHinweis': 'أقل من المبلغ المستحق؟ يبقى الباقي مفتوحًا — هكذا تُسجَّل الأقساط.',
    'rechnungen.zahlungEntfernen': 'حذف الدفعة',
    'rechnungen.sofort': 'مستحق فورًا',
    'rechnungen.gestelltMitLs': 'تم إصدار الفاتورة. إشعار التسليم {nummer} جاهز للطباعة.',
    'rechnungen.zahlungGespeichert': 'تم تسجيل الدفعة.',
    'rechnungen.vollBezahlt': 'الفاتورة مدفوعة بالكامل.',
    'rechnungen.guthaben': 'رصيد العميل',
    'rechnungen.gutschriftTitel': 'إشعار دائن لهذه الفاتورة',
    'rechnungen.gutschriftMenge': 'الكمية المراد إرجاعها',
    'rechnungen.gutschriftGrund': 'السبب (يظهر على الإشعار)',
    'rechnungen.gutschriftStellen': 'إصدار الإشعار الدائن',
    'rechnungen.gutschriftHinweis': 'إذا كانت الفاتورة ما زالت مفتوحة، يُخصم الإشعار منها. وإلا يصبح رصيدًا للعميل. إشعار واحد لكل فاتورة.',
    'rechnungen.gutschriftErstellt': 'تم إصدار الإشعار الدائن {nummer}.',
    'rechnungen.gutschriftVorhanden': 'إشعار دائن {nummer}',
    'rechnungen.perGutschrift': 'سُدِّدت بإشعار دائن',
    'rechnungen.gutschriftDrucken': 'طباعة الإشعار الدائن',
    'rechnungen.gutschriftSumme': 'إشعار دائن بقيمة {betrag}',
    'rechnungen.stornieren': 'إلغاء',
    'rechnungen.gutschriftErstellen': 'إنشاء إشعار دائن',
    'rechnungen.drucken': 'طباعة المستند / PDF',
    'rechnungen.stempelBezahlt': 'مدفوعة',
    'rechnungen.stempelAm': 'بتاريخ {datum}',
    'rechnungen.keinGirocode': 'لا يوجد GiroCode',
    'rechnungen.girocodeText': 'امسح الرمز بتطبيق البنك — المبلغ والبيان معبآن مسبقًا.',
    'neuigkeiten.titel': 'ما الجديد',
    'neuigkeiten.auchNeu': 'جديد أيضًا',
    'neuigkeiten.fortschritt': 'تقدمك مع LagerPro',
    'neuigkeiten.updates': 'تحديثات',
    'neuigkeiten.verbesserungen': 'تحسينات',
    'neuigkeiten.seit': 'منذ {datum}',
    'neuigkeiten.mailHinweis': 'يمكنك إيقاف رسائل الأخبار من الإعدادات.',
    'neuigkeiten.verstanden': 'حسنًا',
    'rechnungen.barverkauf': 'بيع نقدي / بدون حساب',
    'rechnungen.leer': 'لا توجد فواتير',
    'rechnungen.leerText': 'أنشئ أول فاتورة.',
    'status.alle': 'الكل',
    'status.entwurf': 'مسودة',
    'status.entwuerfe': 'مسودات',
    'status.offen': 'مفتوح',
    'status.bezahlt': 'مدفوعة',
    'status.storniert': 'ملغاة',
    'retouren.titel': 'المرتجعات',
    'retouren.erfassen': 'تسجيل مرتجع',
    'retouren.alle': 'كل المرتجعات',
    'retouren.leer': 'لا توجد مرتجعات بعد',
    'retouren.leerText': 'يرتبط المرتجع دائمًا بفاتورة: اختر العميل ثم الفاتورة ثم البنود.',
    'retouren.schritt1': '1. العميل',
    'retouren.schritt2': '2. الفاتورة',
    'retouren.schritt3': '3. ما الذي يُعاد؟',
    'retouren.grund': 'السبب (اختياري)',
    'retouren.grundBeispiel': 'مثال: تلف أثناء النقل، تسليم خاطئ',
    'retouren.berechnet': 'مفوتر',
    'retouren.zurueck': 'المرتجع',
    'retouren.gutschrift': 'إشعار دائن',
    'retouren.einlagern': 'إعادة البضاعة إلى المخزون',
    'retouren.kundeWaehlen': 'اختر العميل …',

    'team.titel': 'الفريق',
    'team.mitglieder': 'الأعضاء',
    'team.neu': 'إضافة موظف',
    'team.benutzername': 'اسم المستخدم',
    'team.emailOptional': 'البريد (اختياري)',
    'team.rolle': 'الدور',
    'team.passwortHinweis': '8 أحرف على الأقل. يمكن للموظف تغييرها لاحقًا.',
    'team.limitErreicht': 'تم بلوغ حد المستخدمين',
    'team.leer': 'لا يوجد أعضاء بعد',
    'team.leerText': 'أضف أول موظف.',
    'team.gesperrt': 'محظور',
    'team.alle': "كل الفريق",
    'team.nichtGefunden': "لم يتم العثور على الشخص.",
    'team.sie': "أنت",
    'team.online': "متصل",
    'team.offline': "غير متصل",
    'team.bearbeiten': "تعديل الملف",
    'team.gespeichert': "تم حفظ الملف.",
    'team.kontakt': "الاتصال والعمل",
    'team.position': "المنصب",
    'team.positionBeispiel': "مثلًا: مدير المستودع",
    'team.abteilung': "القسم",
    'team.mobil': "الجوال",
    'team.arbeitszeit': "ساعات العمل",
    'team.arbeitszeitBeispiel': "مثلًا: الإثنين–الجمعة 8–16",
    'team.eintritt': "تاريخ الالتحاق",
    'team.jahre': "{n} سنوات في الفريق",
    'team.einJahr': "سنة في الفريق",
    'team.ersteJahr': "في السنة الأولى",
    'team.privat': "شخصي",
    'team.privatHinweis': "يراه الشخص نفسه والمسؤولون فقط",
    'team.freiwillig': "اختياري — يراه الشخص نفسه والمسؤولون فقط",
    'team.geburtstag': "تاريخ الميلاد",
    'team.alter': "{n} سنة",
    'team.anschrift': "العنوان",
    'team.notfall': "جهة اتصال للطوارئ",
    'team.notfallName': "جهة الطوارئ (الاسم)",
    'team.notfallTelefon': "جهة الطوارئ (الهاتف)",
    'team.notfallBeispiel': "مثلًا: آنا ماير (الزوجة)",
    'team.notiz': "ملاحظة داخلية",
    'team.notizHinweis': "مرئية للمسؤولين فقط",
    'team.keineAngaben': "لا توجد بيانات بعد",
    'team.offeneAufgaben': "المهام المفتوحة",
    'team.keineAufgaben': "لا توجد مهام مفتوحة",
    'team.aktualisiert': "آخر تعديل في {datum}",
    'team.nurAdminFelder': "المنصب والقسم وساعات العمل وتاريخ الالتحاق يحددها المسؤول.",
    'uebersicht.umsatzHeute': 'إيراد اليوم',
    'uebersicht.offeneForderungen': 'مستحقات قائمة',
    'uebersicht.lagerwert': 'قيمة المخزون',
    'uebersicht.unterMindest': 'أقل من الحد الأدنى',
    'uebersicht.nachbestellen': 'إعادة الطلب',
    'uebersicht.ueberfaellig': 'فواتير متأخرة',
    'uebersicht.zahlungsziel': 'تجاوز موعد السداد',
    'uebersicht.umsatzMonat': 'الإيراد شهريًا',
    'uebersicht.schnell': 'إجراءات سريعة',
    'uebersicht.kassieren': 'تحصيل بيع',
    'uebersicht.wareneingang': 'تسجيل إدخال بضاعة',
    'aufgaben.titel': 'المهام',
    'aufgaben.neu': 'مهمة جديدة',
    'aufgaben.wasZuTun': 'ما المطلوب؟',
    'aufgaben.ansichtHeute': 'اليوم',
    'aufgaben.ansichtBoard': 'اللوحة',
    'aufgaben.ansichtAlle': 'الكل',
    'aufgaben.vomBetrieb': 'من نشاطك',
    'aufgaben.vomBetriebHinweis': 'مستخلصة من بياناتك وتختفي تلقائيًا بمجرد إنجازها.',
    'aufgaben.meine': 'مهامي',
    'aufgaben.nurMeine': 'مهامي فقط',
    'aufgaben.ueberfaellig': 'متأخرة',
    'aufgaben.heute': 'اليوم',
    'aufgaben.morgen': 'غدًا',
    'aufgaben.dieseWoche': 'هذا الأسبوع',
    'aufgaben.spaeter': 'لاحقًا',
    'aufgaben.ohneDatum': 'بلا تاريخ',
    'aufgaben.spalteOffen': 'مفتوحة',
    'aufgaben.spalteArbeit': 'قيد التنفيذ',
    'aufgaben.spalteErledigt': 'منجزة',
    'aufgaben.schnell': 'مهمة جديدة — مثل «غدًا الاتصال بماير !عالية @Mia»',
    'aufgaben.schnellHilfe': 'اليوم · غدًا · الاثنين · 15.11. · !عالية · !منخفضة · @الاسم',
    'aufgaben.oeffnen': 'فتح',
    'aufgaben.zurueckstellen': 'تأجيل',
    'aufgaben.tage': '{n} أيام',
    'aufgaben.uebernehmen': 'تحويل إلى مهمة',
    'aufgaben.beschreibung': 'الوصف',
    'aufgaben.faellig': 'الاستحقاق',
    'aufgaben.prioritaet': 'الأولوية',
    'aufgaben.niedrig': 'منخفضة',
    'aufgaben.normal': 'عادية',
    'aufgaben.zustaendig': 'المسؤول',
    'aufgaben.niemand': 'لا أحد',
    'aufgaben.wiederholung': 'التكرار',
    'aufgaben.keine': 'بدون',
    'aufgaben.taeglich': 'يوميًا',
    'aufgaben.woechentlich': 'أسبوعيًا',
    'aufgaben.monatlich': 'شهريًا',
    'aufgaben.checkliste': 'قائمة التحقق',
    'aufgaben.punktNeu': 'إضافة بند',
    'aufgaben.loeschen': 'حذف',
    'aufgaben.loeschenFrage': 'حذف هذه المهمة؟',
    'aufgaben.timerStart': 'تشغيل المؤقت',
    'aufgaben.timerStopp': 'إيقاف المؤقت',
    'aufgaben.allesErledigt': 'لا شيء عاجل — العمل يسير.',
    'aufgaben.keineEigenen': 'لا توجد مهام مفتوحة.',
    'aufgaben.folge': 'التالية في {datum}',
    'aufgaben.zieheHier': 'أفلت هنا',
    'aufgaben.erledigtHeute': 'أُنجزت اليوم',
    'aufgaben.schliessen': 'إغلاق',
    'aufgaben.aktionGeliefert': 'وضع علامة تم التسليم',
    'aufgaben.aktionBezahlt': 'وضع علامة مدفوعة',
    'aufgaben.erledigtMeldung': 'تم.',
    'aufgaben.zurueckgestellt': 'مؤجلة حتى {datum}.',
    'aufgaben.uebernommen': 'تم تحويلها إلى مهمة.',
    'aufgaben.auto.nachbestellen': 'إعادة طلب {artikel}',
    'aufgaben.auto.nachbestellenText': 'المخزون {bestand} {einheit}، الحد الأدنى {mindestbestand}',
    'aufgaben.auto.bei': 'من {lieferant}',
    'aufgaben.auto.ausliefern': 'تسليم إشعار التسليم {nummer}',
    'aufgaben.auto.ausliefernText': 'إلى {kunde}',
    'aufgaben.auto.nachfassen': 'متابعة العرض {nummer}',
    'aufgaben.auto.nachfassenText': '{kunde} · بلا رد منذ {tage} يومًا',
    'aufgaben.auto.entwurf': 'إصدار مسودة الفاتورة',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · منذ {tage} أيام',
    'aufgaben.auto.support': 'قراءة رد الدعم',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': 'تسجيل الإيصالات الممسوحة',
    'aufgaben.auto.belegscanText': '{anzahl} بانتظار مراجعتك',
    'aufgaben.auto.mahnen': 'المطالبة بالدفع: {nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · {betrag} مفتوح · متأخر {tage} يومًا',
    'aufgaben.auto.bezahlen': 'الدفع للمورد: {lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · مستحق {datum}',
    'aufgaben.auto.zuordnen': 'مطابقة المدفوعات الواردة',
    'aufgaben.auto.zuordnenText': '{anzahl} دفعات بالانتظار',
    'aufgaben.auto.bank': 'إعادة ربط البنك: {bank}',
    'aufgaben.auto.bankAbgelaufen': 'انتهت الموافقة — لم تعد تصل حركات.',
    'aufgaben.auto.bankEndet': 'تنتهي الموافقة في {datum}.',
    'aufgaben.auto.ustva': 'إقرار ضريبة القيمة المضافة {monat}',
    'aufgaben.auto.ustvaText': 'المستحق {betrag} · حتى {datum}',
    'tafel.titel': "اللوحة",
    'tafel.untertitel': "المهام والمواعيد والمهل في لمحة",
    'tafel.ansicht.woche': "أسبوع",
    'tafel.ansicht.monat': "شهر",
    'tafel.ansicht.liste': "قائمة",
    'tafel.neuerZettel': "ملاحظة جديدة",
    'tafel.zurueck': "السابق",
    'tafel.vor': "التالي",
    'tafel.kw': "الأسبوع {n}",
    'tafel.naechste30': "الأيام الثلاثون القادمة",
    'tafel.art.aufgabe': "مهمة",
    'tafel.art.termin': "موعد",
    'tafel.art.frist': "مهلة",
    'tafel.liegenGeblieben': "متأخرة",
    'tafel.ablage': "الصندوق",
    'tafel.ablageHinweis': "بلا يوم — عند توفر الوقت",
    'tafel.leerTag': "+ ملاحظة",
    'tafel.weitere': "+{n} أخرى",
    'tafel.erledigt': "تم",
    'tafel.wiederOffen': "إعادة فتح",
    'tafel.ziehen': "اسحب لنقلها إلى يوم آخر",
    'tafel.automatisch': "تلقائي",
    'tafel.schnell': "ما المطلوب؟",
    'tafel.schnellGross': "ما المطلوب؟ مثلًا «14:30 موعد ماير» أو «إرسال عرض !عالية»",
    'tafel.schnellHilfe': "اليوم · غدًا · الجمعة · 15.11. · 14:30 · !عالية · @الاسم",
    'tafel.inEinerWoche': "بعد أسبوع",
    'tafel.artWaehlen': "اختر النوع",
    'tafel.bisWann': "حتى متى؟",
    'tafel.wann': "متى؟",
    'tafel.von': "من",
    'tafel.bis': "إلى",
    'tafel.ort': "المكان",
    'tafel.notiz': "ملاحظة",
    'tafel.mehr': "خيارات أخرى",
    'tafel.anheften': "تثبيت",
    'tafel.fristHinweis': "مأخوذ من بياناتك ويختفي تلقائيًا عند إنجازه.",
    'tafel.kurzTitel': "لوحتي",
    'tafel.kurzLiegen': "{n} متأخرة",
    'tafel.zurTafel': "إلى اللوحة",
    'tafel.heuteFrei': "لا شيء مخطط.",
    'tafel.frist.angebot': "ينتهي العرض: {nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "تسليم: {nummer}",
    'tafel.frist.lieferungText': "إلى {kunde}",
    'tafel.frist.zahlungseingang': "دفعة متوقعة: {nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "فاتورة مورد مستحقة: {lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "الإقرار الضريبي المسبق",
    'tafel.frist.ustvaText': "عن {monat}",
    'aufgaben.leer': 'لا توجد مهام بعد',
    'aufgaben.leerText': 'أضف أول مهمة أعلاه.',
    'aufgaben.hoch': 'عالية',
    'aufgaben.laeuft': 'قيد التشغيل',
    'chat.titel': 'المحادثة',
    'chat.wemSchreiben': 'إلى مَن تكتب؟',
    'chat.leer': 'لا توجد رسائل بعد',
    'chat.senden': 'إرسال',
    'chat.dateiAnhaengen': 'إرفاق ملف',
    'chat.anhangEntfernen': 'إزالة المرفق',
    'chat.zurListe': 'العودة إلى القائمة',
    'chat.alleImTeam': 'كل الفريق',
    'chat.herunterladenTipp': 'اضغط للتنزيل',

    'basis.tarifAb': 'متاح ابتداءً من الباقة',
    'basis.tarifWechsel': 'يمكنك تغيير الباقة من «الباقة والفوترة».',
    'basis.support': 'يرجى التواصل مع الدعم لتفعيل هذه الميزة.',
    'positionen.hinzufuegen': 'إضافة بند',
    'positionen.freiText': '— إدخال حر —',
    'passwort.festlegen': 'تعيين كلمة المرور',
    'passwort.uebermittelt': 'كلمة المرور المستلمة',
    'passwort.neu': 'كلمة المرور الجديدة',
    'passwort.wiederholen': 'أعد إدخال كلمة المرور',
    'tarif.titel': 'الباقة والفوترة',
    'tarif.aktuell': 'الباقة الحالية',
    'tarif.enthalten': 'مشمول',
    'tarif.nichtEnthalten': 'غير مشمول',
    'tarif.proMonat': '/ شهر',
    'tarif.sonderkondition': 'شرط خاص',
    'tarif.verlauf': 'سجل الباقات',
    'tarif.mandat': 'تفويض الخصم المباشر',
    'tarif.angebot': 'عرضك الشخصي',
    'kopie.titel': "نسخة البيانات",
    'kopie.text': "يمكنك طلب نسخة من بياناتك من تاريخ الإلغاء حتى 30 يومًا بعد انتهاء العقد.",
    'kopie.frist': "متاح حتى {datum}.",
    'kopie.anfordern': "طلب نسخة البيانات",
    'kopie.angefordert': "تم الطلب في {datum}. ستصلك رسالة بريد إلكتروني عندما تصبح النسخة جاهزة.",
    'kopie.bereit': "نسخة بياناتك جاهزة: {format}.",
    'kopie.passwort': "كلمة مرور تسجيل الدخول",
    'kopie.passwortHinweis': "تُشفَّر ملف ZIP بهذه الكلمة — افتحه بنقرة مزدوجة وبالكلمة نفسها.",
    'kopie.herunterladen': "تنزيل",
    'kopie.geladen': "تم التنزيل. احفظ الملف في مكان آمن.",
    'kopie.formatLesbar': "PDF وجداول (Excel)",
    'kopie.formatWiederherstellung': "ملف الاستعادة",
    'kopie.nurAdmin': "يطلب نسخة البيانات مسؤولٌ في منشأتك.",
    'abo.agbAnsehen': "عرض الشروط",
    'abo.agbZustimmung': "لقد قرأت الشروط العامة وأوافق عليها.",
    'abo.aufbewahrung': "تذكّر واجبات الحفظ: الفواتير ومستندات القيد 8 سنوات، والدفاتر والقوائم الختامية 10 سنوات.",
    'abo.aufpreisHinweis': "في الفاتورة القادمة: {betrag} صافيًا رسومًا إضافية لتغيير الباقة بتاريخ {datum}.",
    'abo.bestaetigen': "تأكيد التغيير",
    'abo.erstZuruecknehmen': "تم إلغاء العقد. لتغيير الباقة، تراجع عن الإلغاء أولًا.",
    'abo.gekuendigt': "أُلغي اعتبارًا من {datum}. حتى ذلك الحين استخدم LagerPro كالمعتاد.",
    'abo.gekuendigtMeldung': "أُلغي اعتبارًا من {datum}. التأكيد في طريقه إليك بالبريد الإلكتروني.",
    'abo.gewechselt': "أنت الآن على باقة {plan}.",
    'abo.hochstufen': "ترقية",
    'abo.ihrTarif': "باقتك",
    'abo.jetztKuendigen': "إلغاء الآن",
    'abo.kostenpflichtig': "تغيير مدفوع",
    'abo.kuendigen': "إلغاء العقد",
    'abo.kuendigenText': "يسري الإلغاء في {datum}. حتى ذلك الحين استخدم LagerPro كالمعتاد؛ وبعدها يمكنك طلب نسخة من بياناتك لمدة 30 يومًا.",
    'abo.kuendigenTextTest': "أنت في الشهر المجاني: ينتهي العقد معه في {datum} ولا يُحتسب أي مبلغ.",
    'abo.kuendigenTitel': "إلغاء العقد",
    'abo.mandatAendern': "تغيير البيانات المصرفية",
    'abo.mandatBisTestende': "يرجى تقديم تفويض خصم مباشر SEPA قبل ذلك — وإلا يتوقف LagerPro بعد الشهر المجاني.",
    'abo.nurAdmin': "يدير الباقة والعقد مسؤولٌ في منشأتك.",
    'abo.runterstufen': "تخفيض",
    'abo.sonderkondition': "لديك شروط خاصة. لتغيير الباقة يرجى التواصل مع الدعم.",
    'abo.testDanach': "بعد ذلك تكلّف باقة {plan} مبلغ {preis} شهريًا.",
    'abo.testLaeuft': "الشهر المجاني: متبقٍ {n} يومًا، حتى {datum} ضمنًا.",
    'abo.vorgemerkt': "التحويل إلى {plan} مجدول اعتبارًا من {datum}.",
    'abo.vorgemerktMeldung': "التحويل إلى {plan} مجدول اعتبارًا من {datum}.",
    'abo.vormerkungWeg': "تم التراجع عن التغيير المجدول.",
    'abo.vormerkungZurueck': "التراجع عن التغيير المجدول",
    'abo.waehlen': "اختيار",
    'abo.wechselHoch': "يسري فورًا. إذا كانت فاتورة هذا الشهر قد صدرت، يُضاف الفرق عن الأيام المتبقية إلى الفاتورة التالية. من الشهر القادم {preis} شهريًا.",
    'abo.wechselRunter': "يسري من {datum}. حتى ذلك الحين تحتفظ بجميع مزايا {plan}.",
    'abo.wechselTest': "يسري فورًا. مجاني خلال الشهر المجاني؛ ثم {preis} شهريًا.",
    'abo.wechselTitel': "التحويل إلى {plan}",
    'abo.wechselnHinweis': "الترقية فورية، والتخفيض في نهاية الشهر.",
    'abo.wechselnTitel': "تغيير الباقة",
    'abo.zurueckMeldung': "تم التراجع عن الإلغاء — عقدك مستمر.",
    'abo.zuruecknehmen': "التراجع عن الإلغاء",
    'abo.beendetTitel': "انتهى عقدك",
    'abo.beendetText': "انتهى عقد LagerPro الخاص بك في {datum}.",
    'abo.beendetKopie': "يمكنك طلب نسخة من بياناتك حتى {bis} — بعد ذلك تُحذف.",
    'firma.titel': 'الشركة',
    'firma.anschrift': 'العنوان والتواصل',
    'firma.steuerRegister': 'الضرائب والسجل',
    'firma.girocode': 'طباعة رمز الدفع على الفاتورة',
    'firma.girocodeText': 'رمز QR يمسحه العميل بتطبيق مصرفه، فتُملأ بيانات المستفيد والمبلغ والغرض تلقائياً. يظهر فقط على الفواتير غير المسددة المستحقة بالتحويل.',
    'firma.ibanFehlt': 'رمز الدفع يحتاج إلى رقم IBAN.',
    'firma.ibanUngueltig': 'رقم IBAN هذا لا يجتاز خانة التحقق. يُرجى مراجعته؛ حتى ذلك الحين لن يُطبع أي رمز.',
    'firma.bank': 'بيانات البنك',
    'firma.fusstext': 'نص التذييل',
    'firma.logo': 'الشعار',
    'firma.logoWaehlen': 'اختر الشعار',
    'firma.logoEntfernen': 'إزالة الشعار',
    'firma.speichern': 'حفظ بيانات الشركة',
    'firma.aufRechnung': 'يظهر في كل فاتورة',
    'firma.pflichtangaben': 'بيانات إلزامية في الفواتير',
    'firma.imFuss': 'يظهر في تذييل الفاتورة',
    'passwort.willkommen': 'مرحبًا',
    'passwort.warnung': 'تم تسليم كلمة المرور الحالية إليك، لذا يعرفها شخص آخر. يرجى تعيين كلمة مرور خاصة بك الآن.',
    'passwort.regel8': '8 أحرف على الأقل',
    'passwort.regelGross': 'أحرف كبيرة وصغيرة',
    'passwort.regelZiffer': 'رقم واحد على الأقل',
    'passwort.regelGleich': 'الإدخالان متطابقان',
    'passwort.regelAnders': 'مختلفة عن كلمة المرور المستلمة',
    'passwort.fortfahren': 'تعيين كلمة المرور والمتابعة',
    'lager.anzahlArtikel': 'أصناف',
    'lager.nurKnappe': 'المنخفض فقط',
    'lager.etiketten': 'الملصقات',

    'zfa.titel': 'التأكيد',
    'zfa.gesendet': 'أرسلنا لك رمزًا إلى',
    'zfa.code': 'الرمز من البريد',
    'zfa.bestaetigen': 'تأكيد',
    'zfa.gueltig': 'الرمز صالح لمدة {min} دقيقة.',
    'zfa.andererZugang': 'تسجيل دخول آخر',
    'zfa.einstellung': 'تسجيل الدخول بخطوتين',
    'zfa.erklaerung': 'بالإضافة إلى كلمة المرور، يُرسل رمز إلى بريدك عند كل تسجيل دخول. معرفة كلمة المرور وحدها لا تكفي.',
    'zfa.aktiv': 'مُفعّل',
    'zfa.aus': 'مُعطّل',
    'zfa.einschalten': 'تفعيل',
    'zfa.ausschalten': 'تعطيل',
    'zfa.mailFrage': 'إلى أي عنوان تُرسل الرموز؟',
    'zfa.codeSenden': 'إرسال الرمز',
    'zfa.passwortFrage': 'أدخل كلمة المرور للتعطيل',
    'zfa.keinVersand': 'لم يتم إعداد إرسال البريد بعد. بدونه لا يمكن تسليم أي رمز.',
    'zfa.eingeschaltet': 'تم تفعيل تسجيل الدخول بخطوتين.',
    'zfa.ausgeschaltet': 'تم تعطيل تسجيل الدخول بخطوتين.',
    'zfa.methodeApp': "تطبيق المصادقة",
    'zfa.methodeMail': "رمز عبر البريد",
    'zfa.erklaerungBeide': "بالإضافة إلى كلمة المرور يطلب LagerPro رمزًا عند كل تسجيل دخول — من تطبيق مصادقة على هاتفك أو عبر البريد. معرفة كلمة المرور وحدها لا تكفي.",
    'zfa.ersatzUebrig': "تبقى {n} رموز احتياطية",
    'zfa.mitApp': "التفعيل بتطبيق المصادقة",
    'zfa.mitMail': "التفعيل عبر البريد",
    'zfa.zurApp': "التحويل إلى تطبيق المصادقة",
    'zfa.ersatzNeu': "رموز احتياطية جديدة",
    'zfa.appEmpfohlen': "الموصى به: التطبيق. يعمل دون اتصال ودون بريد — مثل Google Authenticator أو Microsoft Authenticator.",
    'zfa.ersatzNeuFrage': "إنشاء رموز احتياطية جديدة — تتوقف القديمة فورًا.",
    'zfa.passwortWeiter': "يرجى إدخال كلمة المرور",
    'zfa.weiter': "متابعة",
    'zfa.schritt1': "افتح تطبيق المصادقة على هاتفك (مثل Google Authenticator) واختر «إضافة رمز».",
    'zfa.scannen': "امسح رمز QR هذا.",
    'zfa.schritt3': "أدخل الرمز المكون من 6 أرقام من التطبيق أدناه.",
    'zfa.manuell': "لا يمكن المسح؟ أدخل هذا المفتاح يدويًا:",
    'zfa.appCode': "الرمز من التطبيق",
    'zfa.ersatzTitel': "رموزك الاحتياطية",
    'zfa.ersatzHinweis': "إذا فقدت هاتفك: كل رمز يحل محل رمز التطبيق مرة واحدة. احفظها بأمان — تُعرض الآن فقط.",
    'zfa.kopieren': "نسخ",
    'zfa.kopiert': "تم النسخ",
    'zfa.herunterladen': "حفظ كملف",
    'zfa.gesichert': "لقد حفظت الرموز بأمان",
    'zfa.fertig': "تم",
    'zfa.appText': "افتح تطبيق المصادقة وأدخل الرمز المكون من 6 أرقام لـ LagerPro.",
    'zfa.ersatzText': "أدخل أحد رموزك الاحتياطية. كل رمز يصلح مرة واحدة.",
    'zfa.ersatzCode': "رمز احتياطي",
    'zfa.ersatzNutzen': "الهاتف ليس معك؟ استخدم رمزًا احتياطيًا",
    'zfa.appCodeNutzen': "استخدم الرمز من التطبيق",
    'nav.app': "التطبيق",
    'start.nav.app': "التطبيق",
    'desktop.titel': "LagerPro كتطبيق",
    'desktop.text': "نافذة وأيقونة خاصة في Dock أو شريط المهام، يفتح بنقرة واحدة. سجّل الدخول مرة — وتبقى مسجلًا 90 يومًا.",
    'desktop.inApp': "أنت تستخدم تطبيق سطح المكتب",
    'desktop.inAppText': "الإصدار {version}. الواجهة تتحدث تلقائيًا.",
    'desktop.alsWebApp': "LagerPro مثبّت كتطبيق على هذا الجهاز.",
    'desktop.installiert': "تم التثبيت — ستجد LagerPro الآن بين تطبيقاتك.",
    'desktop.direktTitel': "التثبيت مباشرة من المتصفح",
    'desktop.schnell': "ثوانٍ",
    'desktop.direktText': "دون تنزيل ودون تحذير أمني، ومحدّث دائمًا.",
    'desktop.safari': "في Safari: قائمة «ملف» ← «إضافة إلى Dock» (macOS Sonoma أو أحدث).",
    'desktop.firefox': "لا يمكن لـ Firefox تثبيت تطبيقات الويب — استخدم Chrome أو Edge أو نزّل من الأسفل.",
    'desktop.chromeLeiste': "انقر رمز «تثبيت» في يمين شريط العنوان — أو القائمة ⋮ ← «تثبيت LagerPro».",
    'desktop.andererBrowser': "الأسهل باستخدام Chrome أو Edge أو Safari — أو نزّل التطبيق من الأسفل.",
    'desktop.jetztInstallieren': "ثبّت الآن",
    'desktop.downloadTitel': "أو نزّل",
    'desktop.version': "الإصدار {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 و11، 64 بت",
    'desktop.macApple': "Mac بشريحة Apple",
    'desktop.macAppleUnter': "M1، M2، M3، M4 · منذ 2020",
    'desktop.macIntel': "Mac بشريحة Intel",
    'desktop.macIntelUnter': "أجهزة Mac الأقدم حتى 2020",
    'desktop.passtZuIhnen': "مناسب لهذا الجهاز",
    'desktop.laden': "تنزيل",
    'desktop.bald': "قريبًا",
    'desktop.warnungTitel': "تحذير عند الفتح لأول مرة؟",
    'desktop.warnungText': "التنزيلات غير مسجلة بعد لدى Microsoft وApple، لذا يسأل جهازك مرة واحدة عند أول تشغيل.",
    'desktop.warnungWindows': "عند «حمى Windows جهاز الكمبيوتر» انقر «مزيد من المعلومات» ثم «تشغيل على أي حال».",
    'desktop.warnungMac': "اسحب LagerPro إلى «التطبيقات». إذا ظهر تنبيه فافتح «إعدادات النظام ← الخصوصية والأمان» وانقر «الفتح على أي حال» (macOS أقدم: انقر بزر الفأرة الأيمن ← «فتح»).",
    'desktop.anmeldung': "في الحالتين: سجّل الدخول مرة بكلمة المرور (ورمز التحقق) ثم 90 يومًا دون إدخال. تسجيل الخروج ينهي ذلك فورًا.",
    'desktop.mehrInfos': "كل شيء عن التطبيق",

    'erinnerung.titel': 'تذكيرات الدفع',
    'erinnerung.erklaerung': 'عند تأخر فاتورة، يرسل LagerPro تذكيرًا مهذبًا لعميلك تلقائيًا — باسمك، وبعنوانك للرد.',
    'erinnerung.nachTagen': 'التذكير بعد … يومًا من التأخر',
    'erinnerung.wiederholung': 'التكرار بعد … يومًا',
    'erinnerung.maxAnzahl': 'بحد أقصى … تذكيرات لكل فاتورة',
    'erinnerung.faellig': 'ستُشمل في التشغيل القادم',
    'erinnerung.jetzt': 'إرسال الآن',
    'erinnerung.keineFaellig': 'لا توجد فواتير متأخرة حاليًا.',
    'erinnerung.firmenmailFehlt': 'أضف بريدك ضمن «الشركة» — وإلا لن يتمكن عملاؤك من الرد.',
    'erinnerung.keineFuerKunden': 'بدون تذكيرات لهذا العميل',
  },
  zh: {
    'start.nav.funktionen': '功能',
    'start.nav.belegkette': '单据链',
    'start.nav.preise': '价格',
    'start.kostenlosStarten': '免费开始',
    'start.themaDunkel': '深色主题',
    'start.themaHell': '浅色主题',
    'start.sprache': '语言',
    'start.hero.marke': '符合 GoBD · 服务器位于欧盟',
    'start.hero.titel1': '库存、收银与记账。',
    'start.hero.titel2': '一套系统，取代五套。',
    'start.hero.text': 'LagerPro 从报价一路管到报税：库存、销售、单据与账务彼此衔接。无需导出，无需重复录入，不用在两处维护同一份数据。',
    'start.hero.konto': '我已有账号',
    'start.hero.fussnote': '无需信用卡 · 两分钟完成设置 · 按月可取消',
    'start.vorschau.umsatz': '今日营业额',
    'start.vorschau.offen': '未付发票',
    'start.vorschau.lagerwert': '库存价值',
    'start.vorschau.knapp': '低于安全库存',
    'start.vorschau.gruss': '您好，Petra',
    'start.vorschau.firma': 'Werkzeug Nord GmbH',
    'start.vorschau.letzte': '最近单据',
    'start.vorschau.angebot': '报价单',
    'start.vorschau.lieferschein': '送货单',
    'start.vorschau.rechnung': '发票',
    'start.vertrauen1t': '符合 GoBD',
    'start.vertrauen1x': '编号无断号、单据不可更改、哈希链式日志——德国法定账簿标准',
    'start.vertrauen2t': 'GDPdU 导出',
    'start.vertrauen2x': '带 index.xml 的 Z3 数据移交——税务稽查随时可以来',
    'start.vertrauen3t': '服务器位于欧盟',
    'start.vertrauen3x': '不转交数据、无广告追踪，从第一天起符合 GDPR',
    'start.vertrauen4t': '保存十年',
    'start.vertrauen4x': '按德国税法第 147 条自动执行——您无需操心',
    'start.f1t': '拍张照，就好了',
    'start.f1x': '用手机拍下送货单或发票——助手会读出明细、数量和供应商。您核对一次，货就进了库。有关库存和数据的问题，它也能用文字或语音回答。',
    'start.f2t': '库存尽在掌握',
    'start.f2x': '实时库存、低库存提醒、入库登记，以及五种尺寸的条码标签。',
    'start.f3t': '柜台用的收银台',
    'start.f3x': '选客户、扫商品、收款。现金照样能用，库存自动扣减。',
    'start.f4t': '所有单据',
    'start.f4x': '报价单、订单确认、送货单、发票、贷记单和作废单——印上您的标志，导出 PDF。',
    'start.f5t': '算得清的账',
    'start.f5x': '带报表行次的收支结算、增值税申报、行车记录，以及给会计师的 DATEV 导出。',
    'start.f6t': '退货不再混乱',
    'start.f6x': '始终基于发票：客户、单据、明细。货回库存，款项记入客户账户。',
    'start.f7t': '团队一起用',
    'start.f7x': '带计时的任务、带提醒的聊天、在线状态——不必再装第二个应用。',
    'start.funktionen.titel': '生意每天都要用到的一切',
    'start.funktionen.text': '各个模块共享同一份数据。卖出一件商品，库存随之减少、单据随之生成、账务随之入账——就在同一刻。',
    'start.kette.titel': '一笔业务，四张单据，无需重敲',
    'start.kette.text': '一次点击，报价单变订单确认，订单确认变送货单，送货单变发票。明细和价格一路带着走。货物只出库一次。',
    'start.kette.schritt': '第',
    'start.kette1t': '报价单',
    'start.kette1x': '先说清多少钱',
    'start.kette2t': '订单',
    'start.kette2x': '客户点头',
    'start.kette3t': '送货单',
    'start.kette3x': '货发出去',
    'start.kette4t': '发票',
    'start.kette4x': '钱收进来',
    'start.kette.fuss': '若有退回，还有贷记单和作废发票——并带上税务局要求的冲销分录。',
    'start.preise.titel': '价格没有小字',
    'start.preise.text': "按月可解约，所有价格均不含增值税。每个套餐都从 30 天免费试用开始——无需银行信息。",
    'start.preise.laedt': '正在加载套餐 …',
    'start.preise.fehler': '套餐暂时无法获取。来信联系我们，我们会告知具体条件。',
    'start.preise.top': '最多人选',
    'start.preise.proMonat': '/ 月',
    'start.preise.sofort': "免费试用 30 天",
    'start.preise.waehlen': "免费试用 {tarif} 30 天",
    'start.ende.titel': '今天开始，明天就有全局',
    'start.ende.text': '开通账号、录入商品、开出第一张发票。原有数据之后再迁入也不迟。',
    'start.fuss.hinweis': '服务器位于欧盟 · 符合 GoBD',
    'nav.support': '支持',
    'support.vonAnbieter': '来自 LagerPro',
    'support.titel': '客户支持',
    'support.untertitel': '问题、故障、建议——在这里写给我们。整个对话都保存在一处。',
    'support.neu': '新建请求',
    'support.betreff': '主题',
    'support.kategorie': '关于什么？',
    'support.kat.frage': '咨询',
    'support.kat.fehler': '功能异常',
    'support.kat.wunsch': '需求或想法',
    'support.kat.abrechnung': '账单与套餐',
    'support.kat.sonstiges': '其他',
    'support.nachricht': '内容',
    'support.nachrichtPlatzhalter': '请尽量具体地描述发生了什么，以及您原本期待的结果。',
    'support.anhang': '附加截图或文件',
    'support.anhangHinweis': '最多 5 个文件，每个 25 MB',
    'support.senden': '发送请求',
    'support.gesendet': '您的请求 {nummer} 已收到。一有回复，您就会收到邮件。',
    'support.leer': '暂无请求',
    'support.leerText': '遇到问题或有疑问——欢迎给我们写信。',
    'support.status.offen': '已收到',
    'support.status.in_arbeit': '处理中',
    'support.status.wartet': '已回复',
    'support.status.erledigt': '已解决',
    'support.zurueck': '全部请求',
    'support.antworten': '回复',
    'support.antwortPlatzhalter': '您的消息 …',
    'support.erledigt': '标记为已解决',
    'support.erledigtHinweis': '已解决。如仍有问题，直接继续留言即可——请求会重新打开。',
    'support.sie': '您',
    'support.team': 'LagerPro 支持',
    'support.ungelesen': '新',
    'support.glocke': '支持团队的回复',
    'support.anhaengen': '添加附件',
    'einheit.stueck': '件',
    'einheit.paar': '双',
    'einheit.satz': '套',
    'einheit.paket': '包',
    'einheit.karton': '箱',
    'einheit.palette': '托盘',
    'einheit.rolle': '卷',
    'einheit.flasche': '瓶',
    'einheit.dose': '罐',
    'einheit.beutel': '袋',
    'einheit.minute': '分钟',
    'einheit.stunde': '小时',
    'einheit.tag': '天',
    'einheit.woche': '周',
    'einheit.monat': '月',
    'einheit.jahr': '年',
    'einheit.pauschale': '一口价',
    'einheit.gruppe.anzahl': '数量与包装',
    'einheit.gruppe.gewicht': '重量',
    'einheit.gruppe.volumen': '体积',
    'einheit.gruppe.laenge': '长度与面积',
    'einheit.gruppe.zeit': '时间',
    'einheit.gruppe.sonstiges': '其他',
    'einheit.andere': '其他单位 …',
    'einheit.eigene': '自定义单位',
    'einheit.eigenePlatzhalter': '例如 捆、筐',
    'positionen.umgerechnet': '= 库存 {menge}',
    'positionen.passtNicht': '该商品以 {einheit} 计量——此单位无法换算。',
    'positionen.jeEinheit': '每 {einheit}',
    'nav.assistent': 'AI 助手',
    'assistent.datenschutz': '为进行识别，单据和问题会发送给我们的 AI 服务商，仅用于生成回答，不用于训练模型。您的库存数据保留在我们的服务器上。',
    'pruef.titel': '核对{art}',
    'pruef.beleg': '单据',
    'pruef.lieferschein': '送货单',
    'pruef.eingangsrechnung': '采购发票',
    'pruef.gutschrift': '贷记单',
    'pruef.bestellung': '订单',
    'pruef.kassenbon': '收银小票',
    'pruef.erkannt': '% 识别度',
    'pruef.ohneNummer': '无编号与日期',
    'pruef.schwerLesbar': '这份单据不易识别',
    'pruef.schwerLesbarText': '请逐行核对。照片模糊或倾斜时，识别容易混淆数字。光线充足时重拍一张，往往比逐项修改更省事。',
    'pruef.nichtAusgewertet': '未识别',
    'pruef.nurBilder': '仅支持照片和 PDF',
    'pruef.vorhandener': '已有供应商',
    'pruef.neuAnlegen': '新建',
    'pruef.ohne': '不指定',
    'pruef.bitteWaehlen': '— 请选择 —',
    'pruef.gewaehlt': '已选 {a} / {b}',
    'pruef.alleUebernehmen': '全选',
    'pruef.keine': '全不选',
    'pruef.keinePositionen': '未在单据上识别到任何明细。',
    'pruef.warenwert': '所选货值',
    'pruef.rechnungsbetrag': '发票金额',
    'pruef.brauchtBuchhaltung': '登记支出需要会计模块，Professional 套餐起包含。',
    'pruef.alsAusgabe': '将金额登记为经营支出',
    'pruef.alsAusgabeText': '单据作为凭证一同保存，支出记录即为完整。',
    'pruef.brutto': '含税金额',
    'pruef.steuersatz': '税率',
    'pruef.belegnummer': '单据编号',
    'pruef.belegdatum': '单据日期',
    'pruef.lastschrift': '自动扣款',
    'pruef.keineWare': '非商品',
    'pruef.keineWareText': '计入发票金额，但不入库。',
    'pruef.grund.keineWare': '运费、手续费或折扣 — 不是可入库商品',
    'pruef.privat': '个人垫付',
    'pruef.erstMitKnopf': '点击此按钮之前，不会有任何入账。',
    'pruef.buchen': '确认并入账',
    'pruef.wirdGebucht': '正在入账',
    'pruef.zugeordnet': '已匹配',
    'pruef.vermutet': '疑似',
    'pruef.neu': '新增',
    'pruef.schlechtLesbar': '难以辨识',
    'pruef.aendern': '修改',
    'pruef.zuklappen': '收起',
    'pruef.wirdAngelegt': '将新建为商品',
    'pruef.artikel': '商品',
    'pruef.nichtZugeordnet': '— 未匹配 —',
    'pruef.alsNeuenArtikel': '新建为商品',
    'pruef.einkaufspreis': '进货价',
    'pruef.verkaufspreis': '销售价',
    'pruef.spaeterErgaenzen': '可稍后补充',
    'pruef.ean': 'EAN',
    'pruef.je': '每',
    'pruef.grund.ean': 'EAN 一致',
    'pruef.grund.nummer': '货号一致',
    'pruef.grund.name': '名称完全一致',
    'pruef.grund.ustId': '税号一致',
    'pruef.grund.aehnlich': '名称相近，请核对',
    'pruef.grund.keiner': '库存中没有对应商品',
    'pruef.kat.wareneinkauf': '商品采购',
    'pruef.kat.buero': '办公用品',
    'pruef.kat.fahrzeug': '车辆费用',
    'pruef.kat.werbung': '广告费用',
    'pruef.kat.kommunikation': '电话与网络',
    'pruef.kat.raumkosten': '房租与场地',
    'pruef.kat.sonstiges': '其他经营支出',
    'assistent.titel': 'AI 助手',
    'assistent.rest': '本月剩余',
    'assistent.scans': '次单据扫描',
    'assistent.fragen': '个问题',
    'assistent.testmodus': '测试模式',
    'assistent.testmodusText': '尚未配置 AI 访问权限。单据返回示例数据，回答为占位内容。配置完成后，助手将使用您的真实数据。',
    'assistent.verlaufLoeschen': '清除记录',
    'assistent.denkt': '请稍候',
    'assistent.platzhalter': '提问或拍摄单据',
    'assistent.hoertZu': '正在聆听',
    'assistent.sprechen': '语音输入',
    'assistent.vorlesen': '朗读',
    'assistent.stopp': '停止',
    'assistent.senden': '发送',
    'assistent.fotografieren': '拍摄单据',
    'assistent.datei': '上传文件',
    'assistent.belegAuswerten': '识别单据',
    'assistent.wertetAus': '正在识别单据',
    'assistent.bestaetigen': '确认',
    'assistent.gebucht': '已入账',
    'assistent.verworfen': '已放弃',
    'assistent.willkommen': '需要什么帮助？',
    'assistent.willkommenText': '询问库存和数据，或拍摄送货单——核对之后，明细即可入库。',
    'assistent.beispiel1': '哪些商品快用完了？',
    'assistent.beispiel2': '本月营业额是多少？',
    'assistent.beispiel3': '还有哪些未付款的发票？',
    'assistent.beispiel4': '我的库存价值是多少？',
    'allg.entfernen': '移除',
    'bestaetigung.titel': '确认您的电子邮箱',
    'bestaetigung.text': '我们已向您发送邮件。点击其中的链接即可继续。',
    'bestaetigung.warum': '未确认邮箱将收不到登录验证码、发票，以及扣款前的预先通知。',
    'bestaetigung.schonBestaetigt': '我已确认 — 继续',
    'bestaetigung.erneut': '重新发送邮件',
    'bestaetigung.erneutGesendet': '邮件已发出。',
    'bestaetigung.versandFehlt': '目前无法发送，请联系客服。',
    'bestaetigung.fehlgeschlagen': '未能完成。',
    'bestaetigung.spam': '没有收到？请查看垃圾邮件文件夹。',
    'bestaetigung.pruefe': '请稍候 …',
    'bestaetigung.fertigTitel': '邮箱已确认',
    'bestaetigung.fertigText': '您的电子邮箱已确认，可以继续使用了。',
    'bestaetigung.zurApp': '进入应用',
    'bestaetigung.abgelaufenTitel': '链接已过期',
    'bestaetigung.abgelaufenText': '此链接已失效。请登录后重新申请一封邮件。',
    'bestaetigung.ungueltigTitel': '链接无效',
    'bestaetigung.ungueltigText': '此链接无法使用，邮箱可能已经确认过了。',
    'mandat.pflichtTitel': 'SEPA 直接扣款授权',
    'mandat.pflichtText': '继续使用需要一份 SEPA 直接扣款授权，两分钟即可完成。您的数据不受任何影响。',
    'mandat.einleitung': '填写账户持有人和 IBAN 即可授权，无需纸质签名。',
    'mandat.kontoinhaber': '账户持有人',
    'mandat.kontoinhaberHinweis': '须与银行登记的名称完全一致。',
    'mandat.bicHinweis': '在 SEPA 区域内无需填写。',
    'mandat.ibanFalsch': '该 IBAN 校验位不正确。',
    'mandat.wortlautTitel': '授权条款',
    'mandat.glaeubiger': '收款方',
    'mandat.glaeubigerId': '收款方识别码',
    'mandat.referenz': '授权编号',
    'mandat.referenzSpaeter': '授权后生成，并在首次扣款前告知您',
    'mandat.zustimmung': '我同意上述直接扣款授权。',
    'mandat.erteilen': '提交授权',
    'mandat.erteilt': '授权已提交，谢谢。',
    'mandat.liegtVor': '已有有效授权。',
    'mandat.nochNichtBereit': '扣款功能正在配置中，请稍后再试或联系客服。',
    'mandat.ladefehler': '无法加载相关信息。',
    'mandat.fehlgeschlagen': '未能提交授权。',
    'allg.optional': '选填',
    'sperre.titel': 'LagerPro 已锁定',
    'sperre.entsperren': '解锁',
    'sperre.abgebrochen': '验证已取消。',
    'geraete.titel': '已登录设备',
    'geraete.hinweis': '保持登录状态的设备。',
    'geraete.hinweisAdmin': '企业内的全部设备。设备丢失或员工离职时，在此退出登录。',
    'geraete.keine': '没有设备保持登录。',
    'geraete.dieses': '当前设备',
    'geraete.zuletzt': '最近活动',
    'geraete.abmelden': '退出登录',
    'geraete.abmeldenFehler': '无法退出该设备的登录。',
    'geraete.ladefehler': '无法加载设备列表。',
    'geraete.nichtGemerkt': '尚未记住此设备。用密码登录一次，之后指纹即可。',
    'geraete.sperreTitel': '打开时锁定',
    'geraete.sperreAn': '使用{art}锁定应用',
    'geraete.sperreText': '打开时以及退到后台五分钟后会再次验证。',
    'geraete.sperreAusWarnung': '不加锁定，任何人拿到您已解锁的手机都能看到企业数据。',
    'geraete.alleAnderen': "退出其他所有登录",
    'geraete.gueltigBis': "登录有效至 {datum}",
    'geraete.browserTitel': "在浏览器中登录",
    'geraete.diese': "当前登录",
    'geraete.browserHinweis': "浏览器登录 24 小时后自动结束——也可在此立即结束。",
    'app.name': 'LagerPro',
    'app.untertitel': '专业仓储管理',

    'benachrichtigung.titel': '通知',
    'benachrichtigung.leer': '暂无新消息',
    'benachrichtigung.neu': '新通知',
    'benachrichtigung.neueVorgaenge': '条新动态',
    'benachrichtigung.anAlle': '发给所有人',
    'nav.uebersicht': '总览',
    'nav.mehr': '更多',
    'allg.rechnung': '发票',
    'app.gruss': '您好，{name}',
    'app.gruppe.tagesgeschaeft': '日常业务',
    'app.gruppe.kontakte': '联系人',
    'app.gruppe.zusammenarbeit': '协作',
    'app.gruppe.auswertung': '分析',
    'app.gruppe.betrieb': '公司',
    'app.nochmalBeenden': '再按一次退出',
    'app.keineVerbindung': '无连接 — 数据不可用',
    'app.konto': '账户',
    'app.darstellung': '外观',
    'nav.kasse': '收银',
    'nav.lager': '仓库',
    'nav.belege': '单据',
    'nav.rechnungen': '发票',
    'nav.kunden': '客户',
    'nav.lieferanten': '供应商',
    'nav.retouren': '退货',
    'nav.buchhaltung': '账务',
    'nav.ausgaben': '支出',
    'nav.bank': '银行',
    'nav.fahrtenbuch': '行车记录',
    'nav.aufgaben': "看板",
    'nav.chat': '聊天',
    'nav.team': '团队',
    'nav.firma': '公司',
    'nav.gobd': 'GoBD 与稽核',
    'nav.tarif': '套餐',
    'nav.uebernahme': '导入数据',
    'nav.einstellungen': '设置',
    'nav.owner': '客户管理',
    'nav.mandanten': '租户',
    'nav.gruppe.verkauf': '销售',
    'nav.gruppe.waren': '商品',
    'nav.gruppe.kontakte': '联系人',
    'nav.gruppe.auswertung': '分析',
    'nav.gruppe.zusammenarbeit': '协作',
    'nav.gruppe.verwaltung': '管理',

    'aktion.anlegen': '新建',
    'aktion.speichern': '保存',
    'aktion.abbrechen': '取消',
    'aktion.bearbeiten': '编辑',
    'aktion.loeschen': '删除',
    'aktion.schliessen': '关闭',
    'aktion.suchen': '搜索',
    'aktion.anmelden': '登录',
    'aktion.abmelden': '退出登录',
    'aktion.drucken': '打印',
    'aktion.herunterladen': '下载',

    'allg.suche': '搜索',
    'allg.laedt': '加载中',
    'allg.keineTreffer': '没有找到结果',
    'allg.darstellung': '显示',
    'allg.sprache': '语言',
    'allg.thema.system': '跟随系统',
    'allg.thema.hell': '浅色',
    'allg.thema.dunkel': '深色',
    'allg.rolle.inhaber': '所有者',
    'allg.rolle.admin': '管理员',
    'allg.rolle.mitarbeiter': '员工',
    'allg.nichtImTarif': '当前套餐不包含此功能',
    'allg.tarif': '套餐',
    'allg.menue': '菜单',
    'allg.schnellzugriff': '快速跳转',
    'allg.schnellzugriffHinweis': "页面、客户或单据号 …",

    'anmelden.willkommen': '欢迎回来',
    'anmelden.hinweis': '请登录以继续',
    'anmelden.kennung': '用户名或邮箱',
    'anmelden.passwort': '密码',
    'anmelden.fehler': '登录失败。',
    'anmelden.keinKonto': '还没有账户？',
    'anmelden.jetztRegistrieren': '免费注册',
    'passwort.vergessenLink': "忘记密码？",
    'passwort.vergessenTitel': "忘记密码",
    'passwort.vergessenText': "请输入用户名或电子邮箱。我们会发送一个链接，供您设置新密码。",
    'passwort.linkSenden': "发送链接",
    'passwort.gesendetTitel': "请查看收件箱",
    'passwort.gesendetText': "如果有绑定邮箱的账户与之匹配，链接已发出。有效期 {min} 分钟。未绑定邮箱时，请联系管理员。",
    'passwort.zurAnmeldung': "返回登录",
    'passwort.neuTitel': "设置新密码",
    'passwort.fuer': "账户 {name}",
    'passwort.regeln': "至少 8 个字符，包含大小写字母和数字。",
    'passwort.ungleich': "两次输入的密码不一致。",
    'passwort.speichern': "保存密码",
    'passwort.fertigTitel': "密码已更改",
    'passwort.fertigText': "请使用新密码登录。所有设备均已退出登录。",
    'passwort.abgelaufenTitel': "链接已过期",
    'passwort.abgelaufenText': "链接仅 30 分钟内有效。请重新申请。",
    'passwort.ungueltigTitel': "链接无效",
    'passwort.ungueltigText': "此链接无效或已被使用。",
    'passwort.neuAnfordern': "申请新链接",
    'passwort.pruefe': "正在检查链接 …",

    'uebersicht.begruessung': '您好',
    'uebersicht.offeneAufgaben': '待办任务',
    'uebersicht.laufendeTimer': '进行中的计时',
    'uebersicht.teamOnline': '在线同事',
    'uebersicht.benutzerImTarif': '套餐内用户数',
    'uebersicht.naechsteAufgaben': '接下来的任务',
    'uebersicht.alleAnsehen': '查看全部',
    'uebersicht.unbegrenzt': '不限',
    'uebersicht.von': '/',
    'uebersicht.insgesamt': '共计',
    'uebersicht.keineOffenen': '没有待办任务。',

    'feld.name': '名称',
    'feld.nummer': '编号',
    'feld.telefon': '电话',
    'feld.email': '邮箱',
    'feld.strasse': '街道',
    'feld.plz': '邮编',
    'feld.ort': '城市',
    'feld.land': '国家',
    'feld.notiz': '备注',
    'feld.ansprechpartner': '联系人',
    'feld.ustId': '税号',
    'feld.menge': '数量',
    'feld.einzelpreis': '单价',
    'feld.bezeichnung': '品名',
    'feld.artikel': '商品',
    'feld.kunde': '客户',
    'feld.lieferant': '供应商',
    'feld.datum': '日期',
    'feld.betrag': '金额',
    'feld.grund': '原因',
    'feld.status': '状态',
    'feld.einheit': '单位',
    'feld.bestand': '库存',
    'feld.gesamt': '合计',
    'feld.netto': '净额',
    'feld.rabatt': '折扣',
    'feld.position': '项目',
    'feld.positionen': '项目',
    'aktion.hinzufuegen': '添加',
    'aktion.entfernen': '移除',
    'aktion.zurueck': '返回',
    'aktion.weiter': '继续',
    'kunden.titel': '客户',
    'kunden.neu': '新建客户',
    'kunden.nummer': '客户编号',
    'kunden.suche': '姓名、编号、邮箱或城市 …',
    'kunden.leer': '暂无客户',
    'kunden.leerText': '请添加第一位客户。',
    'kunden.akte': "客户档案",
    'kunden.alle': "全部客户",
    'kunden.nichtGefunden': "未找到客户。",
    'kunden.gespeichert': "客户已保存。",
    'kunden.geloescht': "已删除 {name}。",
    'kunden.loeschenFrage': "确定删除“{name}”吗？",
    'kunden.einKunde': "1 位客户",
    'kunden.anzahl': "{n} 位客户",
    'kunden.sortieren': "排序方式",
    'kunden.letzterKaufSort': "最近购买",
    'kunden.keineKontakt': "无联系方式",
    'kunden.nochNichts': "尚无购买",
    'kunden.zuletzt': "最近 {datum}",
    'kunden.rechnungSchreiben': "开具发票",
    'kunden.umsatzJahr': "{jahr} 年营业额",
    'kunden.netto': "净额",
    'kunden.vorjahr': "上一年 {betrag}",
    'kunden.offen': "未付",
    'kunden.ueberfaellig': "逾期 {betrag}（{n}）",
    'kunden.ueberfaelligKurz': "逾期 {betrag}",
    'kunden.offenAnzahl': "{n} 张未付发票",
    'kunden.offenBetrag': "未付 {betrag}",
    'kunden.meistgekauft': "购买最多",
    'kunden.malGekauft': "购买 {n} 次",
    'kunden.keineProdukte': "尚未购买",
    'kunden.zahlung': "付款习惯",
    'kunden.zahltNach': "平均 {n} 天",
    'kunden.puenktlich': "{n}% 按时付款",
    'kunden.nochKeineZahlung': "尚无付款",
    'kunden.seit': "自 {datum} 起为客户",
    'kunden.letzterKauf': "最近购买 {datum}",
    'kunden.gesamt': "总营业额 {betrag}",
    'kunden.durchschnitt': "平均发票 {betrag}",
    'kunden.guthaben': "余额 {betrag}",
    'kunden.verlauf': "近 12 个月营业额",
    'kunden.diagrammLabel': "每月营业额",
    'kunden.keinUmsatz': "近 12 个月无营业额",
    'kunden.rechnungenAnzahl': "{n} 张发票",
    'kunden.produkte': "已购产品",
    'kunden.kaeufe': "次数",
    'kunden.menge': "数量",
    'kunden.umsatz': "营业额",
    'kunden.mehr': "显示全部 {n} 项",
    'kunden.weniger': "收起",
    'kunden.dokumente': "单据",
    'kunden.dokumenteHinweis': "共 {n} 份，最新在前",
    'kunden.alleDokumente': "全部",
    'kunden.nurOffene': "未付",
    'kunden.keineDokumente': "没有单据",
    'kunden.zu': "关联 {nummer}",
    'kunden.entwurf': "草稿",
    'kunden.faellig': "到期 {datum}",
    'kunden.gueltigBis': "有效期至 {datum}",
    'kunden.ansehen': "查看",
    'kunden.oeffnen': "打开",
    'kunden.art.rechnung': "发票",
    'kunden.art.lieferschein': "送货单",
    'kunden.art.angebot': "报价单",
    'kunden.art.auftrag': "订单",
    'kunden.art.gutschrift': "贷记单",
    'kunden.art.retoure': "退货",
    'kunden.arten.rechnung': "发票",
    'kunden.arten.lieferschein': "送货单",
    'kunden.arten.angebot': "报价单",
    'kunden.arten.auftrag': "订单",
    'kunden.arten.gutschrift': "贷记单",
    'kunden.arten.retoure': "退货",
    'kunden.status.entwurf': "草稿",
    'kunden.status.gestellt': "已开具",
    'kunden.status.bezahlt': "已付",
    'kunden.status.storniert': "已作废",
    'kunden.status.gesendet': "已发送",
    'kunden.status.angenommen': "已接受",
    'kunden.status.abgelehnt': "已拒绝",
    'kunden.status.abgelaufen': "已过期",
    'kunden.status.offen': "待处理",
    'kunden.status.inArbeit': "处理中",
    'kunden.status.erledigt': "已完成",
    'kunden.status.geliefert': "已送达",
    'kunden.status.eingelagert': "已入库",
    'kunden.status.erfasst': "已登记",
    'kunden.status.nichtGeliefert': "未送达",
    'kunden.status.ausgestellt': "已开具",
    'kunden.status.teilbezahlt': "部分付款",
    'lieferanten.titel': '供应商',
    'lieferanten.neu': '新建供应商',
    'lieferanten.nummer': '供应商编号',
    'lieferanten.suche': '名称、联系人或城市 …',
    'lieferanten.akte': '档案',
    'lieferanten.alle': '所有供应商',
    'lieferanten.artikel': '商品',
    'lieferanten.lieferungen': '到货',
    'lieferanten.rechnungen': '发票',
    'lieferanten.offen': '未付',
    'lieferanten.ueberfaellig': '已逾期',
    'lieferanten.einkaufJahr': '{jahr} 年采购',
    'lieferanten.letzteLieferung': '最近到货',
    'lieferanten.keineArtikel': '该供应商暂无商品。',
    'lieferanten.keineLieferungen': '暂无入库记录。',
    'lieferanten.keineRechnungen': '暂无发票。',
    'lieferanten.rechnungErfassen': '登记发票',
    'lieferanten.alsBezahlt': '标记为已付款',
    'lieferanten.bezahltAm': '已付 {datum}',
    'lieferanten.faellig': '到期 {datum}',
    'lieferanten.betrag': '金额（含税）',
    'lieferanten.rechnungsnummer': '发票号',
    'lieferanten.rechnungsdatum': '发票日期',
    'lieferanten.faelligAm': '到期日',
    'lieferanten.schonBezahlt': '已付款',
    'lieferanten.wert': '价值',
    'lieferanten.wiederOffen': '重新打开',
    'lieferanten.gespeichert': '已保存。',
    'lieferanten.bearbeiten': '编辑',
    'lieferanten.loeschen': '删除',
    'lager.zugang': '入库',
    'lager.abgang': '出库',
    'lager.buchen': '记账',
    'lager.rechnungLiegtBei': '附有供应商发票',
    'lager.rechnungBezahlt': '发票已付款',
    'lager.gebucht': '“{name}”库存已记账。',
    'lager.ohneLieferant': '无供应商',
    'lieferanten.leer': '暂无供应商',
    'lieferanten.leerText': '请添加第一家供应商。',
    'allg.andereSuche': '请尝试其他搜索词。',

    'lager.titel': '仓库',
    'lager.neu': '新建商品',
    'lager.artikelnummer': '商品编号',
    'lager.ean': 'EAN / 条码',
    'lager.kategorie': '类别',
    'lager.lagerort': '库位',
    'lager.einkaufspreis': '进价（净额）',
    'lager.verkaufspreis': '售价（净额）',
    'lager.mindestbestand': '最低库存',
    'lager.anfangsbestand': '期初库存',
    'lager.wareneingang': '将作为入库记账。',
    'lager.suche': '品名、编号或 EAN …',
    'lager.leer': '暂无商品',
    'lager.leerText': '请添加第一个商品或导入数据。',
    'lager.verlauf': '记录',
    'lager.keineBewegungen': '暂无出入库记录',
    'lager.etikett': '标签',
    'lager.etikettenDrucken': '打印标签',
    'lager.format': '规格',
    'lager.stueckJeArtikel': '每个商品的数量',
    'lager.mehrfachHinweis': '入库时经常需要同一标签多份。',
    'lager.knapp': '偏低',
    'lager.auswahl': '已选',
    'lager.auswahlAufheben': '取消选择',
    'lager.alle': '全部',
    'lager.keinLieferant': '— 无供应商 —',
    'kasse.titel': '收银台',
    'kasse.schritt1': '第 1 步 / 共 2 步 — 这笔销售给谁？',
    'kasse.kundeWaehlen': '选择客户',
    'kasse.kundeSuchen': '搜索客户',
    'kasse.kundeSuche': '姓名、客户编号或城市 …',
    'kasse.kundeWechseln': '更换客户',
    'kasse.ohneKonto': '不使用客户账户',
    'kasse.historieHinweis': '该笔销售将出现在其发票记录中',
    'kasse.artikelSuchen': '搜索商品',
    'kasse.artikelSuche': '品名、编号或扫描条码 …',
    'kasse.keinArtikel': '未找到商品',
    'kasse.warenkorb': '购物车',
    'kasse.warenkorbLeeren': '清空购物车',
    'kasse.leer': '尚未添加任何商品',
    'kasse.leerText': '请点击左侧商品。',
    'kasse.zahlart': '支付方式',
    'kasse.bezahlt': '已付款？',
    'kasse.vollstaendig': '全额',
    'kasse.teilweise': '部分',
    'kasse.nochNicht': '尚未',
    'kasse.gegeben': '收取金额（用于找零）',
    'kasse.rueckgeld': '找零：{betrag}',
    'kasse.erhalten': '已收',
    'kasse.kundePflicht': '有未付金额时请选择客户——否则无人知道谁欠款。',
    'kasse.buchen': '记账 · 已收 {erhalten}，未付 {offen}',
    'kasse.kassieren': '收款 · {betrag}',
    'kasse.offenBis': '未付：{betrag} · 到期 {datum}',
    'kasse.bezahltMit': '已付 · {zahlart}',
    'kasse.naechster': '下一笔销售',
    'kasse.bar': '现金',
    'kasse.karte': '刷卡',
    'kasse.ueberweisung': '银行转账',
    'kasse.abgeschlossen': '销售已完成',
    'kasse.alle': "全部",
    'kasse.anzahlung': "预付款",
    'kasse.aufRechnung': "记账",
    'kasse.barAnteil': "现金部分",
    'kasse.barKassieren': "现金收款 · {betrag}",
    'kasse.beleg': "小票",
    'kasse.betragBrutto': "金额（含税）",
    'kasse.bezahlen': "付款",
    'kasse.bezahlenBetrag': "付款 · {betrag}",
    'kasse.bonNr': "小票 {n}",
    'kasse.bonRabatt': "整单折扣",
    'kasse.bonVerwerfen': "作废小票",
    'kasse.codeUnbekannt': "没有条码为 {code} 的商品",
    'kasse.enthaltenSteuer': "含 {satz} % {name}",
    'kasse.fehlgeschlagen': "销售未能记账。",
    'kasse.freiHinweis': "用于非商品项目：服务、押金、其他。",
    'kasse.freiStandard': "其他",
    'kasse.freierBetrag': "自定义金额",
    'kasse.gegebenKurz': "实收",
    'kasse.gespart': "已省",
    'kasse.geteilt': "拆分",
    'kasse.geteiltKassieren': "收款 · 现金 {bar} + 刷卡 {karte}",
    'kasse.haeufig': "常用",
    'kasse.kameraFehler': "摄像头不可用——请允许访问或使用扫码枪。",
    'kasse.karteBezahlt': "刷卡已付 · {betrag}",
    'kasse.karteHinweis': "先在刷卡机上收款，再在此确认。",
    'kasse.karteRest': "其余刷卡",
    'kasse.keinRabatt': "无",
    'kasse.keineArtikelAngelegt': "尚未创建商品。",
    'kasse.kundeEntfernen': "移除客户",
    'kasse.kundeZuordnen': "指定客户",
    'kasse.laufkunde': "散客",
    'kasse.leerNeu': "点击商品或扫描条码。",
    'kasse.neuerBon': "新小票",
    'kasse.nichtAufLager': "“{name}”无库存。",
    'kasse.nichtVorraetig': "缺货",
    'kasse.nurNoch': "“{name}”仅剩 {menge}。",
    'kasse.parkenHinweis': "挂起此小票并开始新的",
    'kasse.passend': "正好",
    'kasse.positionen': "项目：{n}",
    'kasse.preis': "价格",
    'kasse.preisAendern': "改价",
    'kasse.rabatt': "折扣",
    'kasse.rabattText': "{p} % 折扣",
    'kasse.rechnungBuchen': "记账 · 未付 {offen}",
    'kasse.rueckgeldGross': "找零",
    'kasse.scannen': "扫描条码",
    'kasse.scannenHinweis': "将条码对准摄像头。",
    'kasse.wofuer': "用途？",
    'kasse.zuWenig': "还差 {betrag}。",

    'belege.titel': '单据',
    'belege.angebote': '报价单',
    'belege.auftraege': '订单',
    'belege.lieferscheine': '送货单',
    'belege.gutschriften': '贷项通知单',
    'belege.offeneAngebote': '未结报价',
    'belege.laufendeAuftraege': '进行中的订单',
    'belege.lieferscheineOffen': '待发送货单',
    'belege.interessent': '潜在客户名称',
    'belege.notiz': '备注（显示在单据上）',
    'belege.rabatt': '折扣 %',
    'belege.druckenPdf': '打印 / PDF',
    'belege.entwurfLoeschen': '删除草稿',
    'belege.gesamtBrutto': '含税合计',
    'belege.einzel': '单价',
    'rechnungen.titel': '发票',
    'rechnungen.neu': '新建发票',
    'rechnungen.schreiben': '开具发票',
    'rechnungen.stellen': '开出发票',
    'rechnungen.entwurfSpeichern': '存为草稿',
    'rechnungen.entwurfHinweis': '草稿尚不扣减库存。',
    'rechnungen.bezahltMarkieren': '标记为已付款',
    'rechnungen.teilbezahlt': '部分已付 · 未付 {betrag}',
    'rechnungen.perBank': '已通过银行匹配',
    'rechnungen.zahlungen': '付款',
    'rechnungen.nochOffen': '尚未支付',
    'rechnungen.bankeingang': '银行入账',
    'rechnungen.zahlung': '付款',
    'rechnungen.zahlart': '付款方式',
    'rechnungen.zahlweg.ueberweisung': '银行转账',
    'rechnungen.zahlweg.bar': '现金',
    'rechnungen.zahlweg.karte': '刷卡',
    'rechnungen.zahlungsziel': '付款期限',
    'rechnungen.zielKunde': '按客户设置（{tage} 天）',
    'rechnungen.zielSofort': '立即到期',
    'rechnungen.zielTage': '{tage} 天',
    'rechnungen.zielDatum': '自定义日期',
    'rechnungen.faelligAm': '到期日',
    'rechnungen.bezahlung': '已付款？',
    'rechnungen.offenLassen': '尚未付款',
    'rechnungen.schonBezahlt': '已付款',
    'rechnungen.lieferscheinMit': '同时创建送货单',
    'rechnungen.lieferschein': '送货单',
    'rechnungen.lieferscheinDrucken': '打印送货单',
    'rechnungen.lieferscheinErstellen': '创建送货单',
    'rechnungen.zahlungErfassen': '记录付款',
    'rechnungen.betrag': '金额',
    'rechnungen.zahltag': '付款日期',
    'rechnungen.alsBezahltSpeichern': '保存为已付款',
    'rechnungen.teilzahlungSpeichern': '保存部分付款',
    'rechnungen.ratenHinweis': '少于未付金额？余额保持未付——分期付款就是这样记录的。',
    'rechnungen.zahlungEntfernen': '删除付款',
    'rechnungen.sofort': '立即到期',
    'rechnungen.gestelltMitLs': '发票已开具。送货单 {nummer} 可以打印了。',
    'rechnungen.zahlungGespeichert': '付款已记录。',
    'rechnungen.vollBezahlt': '发票已全部付清。',
    'rechnungen.guthaben': '客户余额',
    'rechnungen.gutschriftTitel': '此发票的贷项通知单',
    'rechnungen.gutschriftMenge': '退还数量',
    'rechnungen.gutschriftGrund': '原因（显示在贷项通知单上）',
    'rechnungen.gutschriftStellen': '开具贷项通知单',
    'rechnungen.gutschriftHinweis': '如果发票仍未付清，贷项通知单将与之抵销；否则转为客户余额。每张发票只能开一张。',
    'rechnungen.gutschriftErstellt': '贷项通知单 {nummer} 已开具。',
    'rechnungen.gutschriftVorhanden': '贷项通知单 {nummer}',
    'rechnungen.perGutschrift': '已用贷项通知单结清',
    'rechnungen.gutschriftDrucken': '打印贷项通知单',
    'rechnungen.gutschriftSumme': '金额 {betrag} 的贷项通知单',
    'rechnungen.stornieren': '作废',
    'rechnungen.gutschriftErstellen': '创建贷项通知单',
    'rechnungen.drucken': '打印单据 / PDF',
    'rechnungen.stempelBezahlt': '已付款',
    'rechnungen.stempelAm': '{datum}',
    'rechnungen.keinGirocode': '无 GiroCode',
    'rechnungen.girocodeText': '用银行App扫码——金额和用途已自动填好。',
    'neuigkeiten.titel': '新功能',
    'neuigkeiten.auchNeu': '其他新内容',
    'neuigkeiten.fortschritt': '您使用 LagerPro 的进展',
    'neuigkeiten.updates': '次更新',
    'neuigkeiten.verbesserungen': '项改进',
    'neuigkeiten.seit': '自 {datum} 起',
    'neuigkeiten.mailHinweis': '可在“设置”中关闭新闻邮件。',
    'neuigkeiten.verstanden': '知道了',
    'rechnungen.barverkauf': '现金销售 / 无客户账户',
    'rechnungen.leer': '暂无发票',
    'rechnungen.leerText': '请开具第一张发票。',
    'status.alle': '全部',
    'status.entwurf': '草稿',
    'status.entwuerfe': '草稿',
    'status.offen': '未结',
    'status.bezahlt': '已付款',
    'status.storniert': '已作废',
    'retouren.titel': '退货',
    'retouren.erfassen': '登记退货',
    'retouren.alle': '全部退货',
    'retouren.leer': '暂无退货',
    'retouren.leerText': '退货必须关联一张发票：先选客户，再选发票，然后选择退回的项目。',
    'retouren.schritt1': '1. 客户',
    'retouren.schritt2': '2. 发票',
    'retouren.schritt3': '3. 退回哪些商品？',
    'retouren.grund': '原因（可选）',
    'retouren.grundBeispiel': '例如：运输损坏、错发',
    'retouren.berechnet': '已开票',
    'retouren.zurueck': '退回',
    'retouren.gutschrift': '贷项通知单',
    'retouren.einlagern': '将商品退回库存',
    'retouren.kundeWaehlen': '选择客户 …',

    'team.titel': '团队',
    'team.mitglieder': '成员',
    'team.neu': '添加员工',
    'team.benutzername': '用户名',
    'team.emailOptional': '邮箱（可选）',
    'team.rolle': '角色',
    'team.passwortHinweis': '至少 8 位。员工之后可自行修改。',
    'team.limitErreicht': '已达用户数上限',
    'team.leer': '暂无成员',
    'team.leerText': '请添加第一位员工。',
    'team.gesperrt': '已停用',
    'team.alle': "全体成员",
    'team.nichtGefunden': "未找到该成员。",
    'team.sie': "您",
    'team.online': "在线",
    'team.offline': "离线",
    'team.bearbeiten': "编辑资料",
    'team.gespeichert': "资料已保存。",
    'team.kontakt': "联系与工作",
    'team.position': "职位",
    'team.positionBeispiel': "例如：仓库主管",
    'team.abteilung': "部门",
    'team.mobil': "手机",
    'team.arbeitszeit': "工作时间",
    'team.arbeitszeitBeispiel': "例如：周一至周五 8–16 点",
    'team.eintritt': "入职日期",
    'team.jahre': "在团队 {n} 年",
    'team.einJahr': "在团队 1 年",
    'team.ersteJahr': "入职第一年",
    'team.privat': "个人信息",
    'team.privatHinweis': "仅本人和管理员可见",
    'team.freiwillig': "选填 — 仅本人和管理员可见",
    'team.geburtstag': "生日",
    'team.alter': "{n} 岁",
    'team.anschrift': "地址",
    'team.notfall': "紧急联系人",
    'team.notfallName': "紧急联系人（姓名）",
    'team.notfallTelefon': "紧急联系人（电话）",
    'team.notfallBeispiel': "例如：Anna Meyer（伴侣）",
    'team.notiz': "内部备注",
    'team.notizHinweis': "仅管理员可见",
    'team.keineAngaben': "暂无信息",
    'team.offeneAufgaben': "未完成任务",
    'team.keineAufgaben': "没有未完成任务",
    'team.aktualisiert': "最后修改于 {datum}",
    'team.nurAdminFelder': "职位、部门、工作时间和入职日期由管理员维护。",
    'uebersicht.umsatzHeute': '今日营业额',
    'uebersicht.offeneForderungen': '应收账款',
    'uebersicht.lagerwert': '库存价值',
    'uebersicht.unterMindest': '低于最低库存',
    'uebersicht.nachbestellen': '需补货',
    'uebersicht.ueberfaellig': '逾期发票',
    'uebersicht.zahlungsziel': '已超过付款期限',
    'uebersicht.umsatzMonat': '按月营业额',
    'uebersicht.schnell': '快捷操作',
    'uebersicht.kassieren': '收银结账',
    'uebersicht.wareneingang': '登记入库',
    'aufgaben.titel': '任务',
    'aufgaben.neu': '新建任务',
    'aufgaben.wasZuTun': '需要做什么？',
    'aufgaben.ansichtHeute': '今天',
    'aufgaben.ansichtBoard': '看板',
    'aufgaben.ansichtAlle': '全部',
    'aufgaben.vomBetrieb': '来自业务',
    'aufgaben.vomBetriebHinweis': '根据您的数据生成，完成后自动消失。',
    'aufgaben.meine': '我的任务',
    'aufgaben.nurMeine': '只看我的',
    'aufgaben.ueberfaellig': '已逾期',
    'aufgaben.heute': '今天',
    'aufgaben.morgen': '明天',
    'aufgaben.dieseWoche': '本周',
    'aufgaben.spaeter': '以后',
    'aufgaben.ohneDatum': '无日期',
    'aufgaben.spalteOffen': '待办',
    'aufgaben.spalteArbeit': '进行中',
    'aufgaben.spalteErledigt': '已完成',
    'aufgaben.schnell': '新任务 — 例如“明天 给 Meyer 打电话 !高 @Mia”',
    'aufgaben.schnellHilfe': '今天 · 明天 · 星期一 · 15.11. · !高 · !低 · @姓名',
    'aufgaben.oeffnen': '打开',
    'aufgaben.zurueckstellen': '推迟',
    'aufgaben.tage': '{n} 天',
    'aufgaben.uebernehmen': '转为任务',
    'aufgaben.beschreibung': '描述',
    'aufgaben.faellig': '到期',
    'aufgaben.prioritaet': '优先级',
    'aufgaben.niedrig': '低',
    'aufgaben.normal': '普通',
    'aufgaben.zustaendig': '负责人',
    'aufgaben.niemand': '无人',
    'aufgaben.wiederholung': '重复',
    'aufgaben.keine': '不重复',
    'aufgaben.taeglich': '每天',
    'aufgaben.woechentlich': '每周',
    'aufgaben.monatlich': '每月',
    'aufgaben.checkliste': '检查清单',
    'aufgaben.punktNeu': '添加项目',
    'aufgaben.loeschen': '删除',
    'aufgaben.loeschenFrage': '删除此任务？',
    'aufgaben.timerStart': '开始计时',
    'aufgaben.timerStopp': '停止计时',
    'aufgaben.allesErledigt': '没有紧急事项——一切正常。',
    'aufgaben.keineEigenen': '没有未完成的任务。',
    'aufgaben.folge': '下次：{datum}',
    'aufgaben.zieheHier': '拖到这里',
    'aufgaben.erledigtHeute': '今日完成',
    'aufgaben.schliessen': '关闭',
    'aufgaben.aktionGeliefert': '标记为已送达',
    'aufgaben.aktionBezahlt': '标记为已付款',
    'aufgaben.erledigtMeldung': '已完成。',
    'aufgaben.zurueckgestellt': '已推迟到 {datum}。',
    'aufgaben.uebernommen': '已转为任务。',
    'aufgaben.auto.nachbestellen': '补货：{artikel}',
    'aufgaben.auto.nachbestellenText': '库存 {bestand} {einheit}，最低 {mindestbestand}',
    'aufgaben.auto.bei': '供应商 {lieferant}',
    'aufgaben.auto.ausliefern': '送货：送货单 {nummer}',
    'aufgaben.auto.ausliefernText': '送至 {kunde}',
    'aufgaben.auto.nachfassen': '跟进报价 {nummer}',
    'aufgaben.auto.nachfassenText': '{kunde} · {tage} 天未回复',
    'aufgaben.auto.entwurf': '开具发票草稿',
    'aufgaben.auto.entwurfText': '{kunde} · {betrag} · 已搁置 {tage} 天',
    'aufgaben.auto.support': '阅读客服回复',
    'aufgaben.auto.supportText': '{nummer} · {betreff}',
    'aufgaben.auto.belegscan': '登记扫描的单据',
    'aufgaben.auto.belegscanText': '{anzahl} 份待您审核',
    'aufgaben.auto.mahnen': '催款：{nummer}',
    'aufgaben.auto.mahnenText': '{kunde} · 未付 {betrag} · 逾期 {tage} 天',
    'aufgaben.auto.bezahlen': '付款给供应商：{lieferant}',
    'aufgaben.auto.bezahlenText': '{nummer} · {betrag} · 到期 {datum}',
    'aufgaben.auto.zuordnen': '匹配收款',
    'aufgaben.auto.zuordnenText': '{anzahl} 笔收款待匹配',
    'aufgaben.auto.bank': '重新连接银行：{bank}',
    'aufgaben.auto.bankAbgelaufen': '授权已过期——不再收到交易。',
    'aufgaben.auto.bankEndet': '授权于 {datum} 结束。',
    'aufgaben.auto.ustva': '增值税申报 {monat}',
    'aufgaben.auto.ustvaText': '应缴 {betrag} · 截止 {datum}',
    'tafel.titel': "看板",
    'tafel.untertitel': "任务、约会和期限一目了然",
    'tafel.ansicht.woche': "周",
    'tafel.ansicht.monat': "月",
    'tafel.ansicht.liste': "列表",
    'tafel.neuerZettel': "新便签",
    'tafel.zurueck': "上一个",
    'tafel.vor': "下一个",
    'tafel.kw': "第 {n} 周",
    'tafel.naechste30': "未来 30 天",
    'tafel.art.aufgabe': "任务",
    'tafel.art.termin': "约会",
    'tafel.art.frist': "期限",
    'tafel.liegenGeblieben': "逾期未办",
    'tafel.ablage': "收纳",
    'tafel.ablageHinweis': "无日期 — 有空再做",
    'tafel.leerTag': "+ 便签",
    'tafel.weitere': "还有 {n} 项",
    'tafel.erledigt': "已完成",
    'tafel.wiederOffen': "重新打开",
    'tafel.ziehen': "拖动到其他日期",
    'tafel.automatisch': "自动",
    'tafel.schnell': "要做什么？",
    'tafel.schnellGross': "要做什么？例如“14:30 约见 Meyer”或“发送报价 !高”",
    'tafel.schnellHilfe': "今天 · 明天 · 周五 · 15.11. · 14:30 · !高 · @姓名",
    'tafel.inEinerWoche': "一周后",
    'tafel.artWaehlen': "选择类型",
    'tafel.bisWann': "截止何时？",
    'tafel.wann': "何时？",
    'tafel.von': "从",
    'tafel.bis': "到",
    'tafel.ort': "地点",
    'tafel.notiz': "备注",
    'tafel.mehr': "更多选项",
    'tafel.anheften': "贴上",
    'tafel.fristHinweis': "来自您的数据，处理完毕后会自动消失。",
    'tafel.kurzTitel': "我的看板",
    'tafel.kurzLiegen': "{n} 项逾期",
    'tafel.zurTafel': "打开看板",
    'tafel.heuteFrei': "暂无安排。",
    'tafel.frist.angebot': "报价即将到期：{nummer}",
    'tafel.frist.angebotText': "{kunde}",
    'tafel.frist.lieferung': "送货：{nummer}",
    'tafel.frist.lieferungText': "给 {kunde}",
    'tafel.frist.zahlungseingang': "预计收款：{nummer}",
    'tafel.frist.zahlungseingangText': "{kunde} · {betrag}",
    'tafel.frist.zahlungsausgang': "供应商发票到期：{lieferant}",
    'tafel.frist.zahlungsausgangText': "{nummer} · {betrag}",
    'tafel.frist.ustva': "增值税预申报",
    'tafel.frist.ustvaText': "{monat}",
    'aufgaben.leer': '暂无任务',
    'aufgaben.leerText': '请在上方添加第一条。',
    'aufgaben.hoch': '高',
    'aufgaben.laeuft': '进行中',
    'chat.titel': '聊天',
    'chat.wemSchreiben': '发给谁？',
    'chat.leer': '暂无消息',
    'chat.senden': '发送',
    'chat.dateiAnhaengen': '添加附件',
    'chat.anhangEntfernen': '移除附件',
    'chat.zurListe': '返回列表',
    'chat.alleImTeam': '全体成员',
    'chat.herunterladenTipp': '点击下载',

    'basis.tarifAb': '此功能需要套餐',
    'basis.tarifWechsel': '您可在“套餐与账单”中更换套餐。',
    'basis.support': '请联系客服以开通此功能。',
    'positionen.hinzufuegen': '添加项目',
    'positionen.freiText': '— 自由输入 —',
    'passwort.festlegen': '设置密码',
    'passwort.uebermittelt': '收到的密码',
    'passwort.neu': '新密码',
    'passwort.wiederholen': '再次输入新密码',
    'tarif.titel': '套餐与账单',
    'tarif.aktuell': '当前套餐',
    'tarif.enthalten': '已包含',
    'tarif.nichtEnthalten': '不包含',
    'tarif.proMonat': '/ 月',
    'tarif.sonderkondition': '特别价格',
    'tarif.verlauf': '套餐记录',
    'tarif.mandat': '直接扣款授权',
    'tarif.angebot': '您的专属报价',
    'kopie.titel': "数据副本",
    'kopie.text': "自解约起至合同结束后 30 天内，您可以申请数据副本。",
    'kopie.frist': "可申请至 {datum}。",
    'kopie.anfordern': "申请数据副本",
    'kopie.angefordert': "已于 {datum} 申请。副本准备好后您将收到邮件。",
    'kopie.bereit': "您的数据副本已准备好：{format}。",
    'kopie.passwort': "您的登录密码",
    'kopie.passwortHinweis': "ZIP 文件将用此密码加密——双击并输入相同密码即可打开。",
    'kopie.herunterladen': "下载",
    'kopie.geladen': "已下载。请妥善保存文件。",
    'kopie.formatLesbar': "PDF 和表格（Excel）",
    'kopie.formatWiederherstellung': "恢复文件",
    'kopie.nurAdmin': "数据副本由您企业的管理员申请。",
    'abo.agbAnsehen': "查看条款",
    'abo.agbZustimmung': "我已阅读并同意通用条款。",
    'abo.aufbewahrung': "请注意您的保存义务：发票和记账凭证 8 年，账簿和年度报表 10 年。",
    'abo.aufpreisHinweis': "下一张发票：{datum} 套餐变更的附加费 {betrag}（不含税）。",
    'abo.bestaetigen': "确认变更",
    'abo.erstZuruecknehmen': "合同已解约。如需更换套餐，请先撤回解约。",
    'abo.gekuendigt': "解约生效日为 {datum}。在此之前可照常使用 LagerPro。",
    'abo.gekuendigtMeldung': "解约生效日为 {datum}。确认邮件已发出。",
    'abo.gewechselt': "您现在使用 {plan}。",
    'abo.hochstufen': "升级",
    'abo.ihrTarif': "您的套餐",
    'abo.jetztKuendigen': "立即解约",
    'abo.kostenpflichtig': "付费变更",
    'abo.kuendigen': "解约合同",
    'abo.kuendigenText': "解约于 {datum} 生效。在此之前照常使用 LagerPro；之后 30 天内可申请数据副本。",
    'abo.kuendigenTextTest': "您正处于免费月：合同将于 {datum} 随免费月一起结束，不收取任何费用。",
    'abo.kuendigenTitel': "解约合同",
    'abo.mandatAendern': "更改银行信息",
    'abo.mandatBisTestende': "请在此之前提交 SEPA 直接借记授权——否则免费月结束后 LagerPro 将暂停。",
    'abo.nurAdmin': "套餐和合同由您企业的管理员管理。",
    'abo.runterstufen': "降级",
    'abo.sonderkondition': "您享有特殊条件。如需更换套餐，请联系客服。",
    'abo.testDanach': "之后 {plan} 每月 {preis}。",
    'abo.testLaeuft': "免费月：还剩 {n} 天，至 {datum}（含）。",
    'abo.vorgemerkt': "已预约自 {datum} 起切换到 {plan}。",
    'abo.vorgemerktMeldung': "已预约自 {datum} 起切换到 {plan}。",
    'abo.vormerkungWeg': "预约变更已撤回。",
    'abo.vormerkungZurueck': "撤回预约变更",
    'abo.waehlen': "选择",
    'abo.wechselHoch': "立即生效。若本月已开票，剩余天数的差额将计入下一张发票。自下月起每月 {preis}。",
    'abo.wechselRunter': "自 {datum} 起生效。在此之前您保留 {plan} 的全部功能。",
    'abo.wechselTest': "立即生效。免费月内免费；之后每月 {preis}。",
    'abo.wechselTitel': "切换到 {plan}",
    'abo.wechselnHinweis': "升级立即生效，降级在月底生效。",
    'abo.wechselnTitel': "更换套餐",
    'abo.zurueckMeldung': "解约已撤回——合同继续有效。",
    'abo.zuruecknehmen': "撤回解约",
    'abo.beendetTitel': "您的合同已结束",
    'abo.beendetText': "您的 LagerPro 合同已于 {datum} 结束。",
    'abo.beendetKopie': "您可在 {bis} 之前申请数据副本——之后数据将被删除。",
    'firma.titel': '公司',
    'firma.anschrift': '地址与联系方式',
    'firma.steuerRegister': '税务与登记',
    'firma.girocode': '在发票上打印付款码',
    'firma.girocodeText': '客户用银行 App 扫一下，收款方、金额和用途就自动填好。仅出现在待付、以转账结算的发票上。',
    'firma.ibanFehlt': '付款码需要填写 IBAN。',
    'firma.ibanUngueltig': '该 IBAN 校验位不正确，请核对；在此之前不会打印付款码。',
    'firma.bank': '银行信息',
    'firma.fusstext': '页脚文字',
    'firma.logo': '标志',
    'firma.logoWaehlen': '选择标志',
    'firma.logoEntfernen': '移除标志',
    'firma.speichern': '保存公司信息',
    'firma.aufRechnung': '显示在每张发票上',
    'firma.pflichtangaben': '发票必填信息',
    'firma.imFuss': '显示在发票页脚',
    'passwort.willkommen': '欢迎',
    'passwort.warnung': '您当前的密码由他人转交，因此他人知晓。请立即设置自己的密码。',
    'passwort.regel8': '至少 8 位',
    'passwort.regelGross': '包含大小写字母',
    'passwort.regelZiffer': '至少一个数字',
    'passwort.regelGleich': '两次输入一致',
    'passwort.regelAnders': '与收到的密码不同',
    'passwort.fortfahren': '设置密码并继续',
    'lager.anzahlArtikel': '件商品',
    'lager.nurKnappe': '仅低库存',
    'lager.etiketten': '标签',

    'zfa.titel': '验证',
    'zfa.gesendet': '我们已将验证码发送至',
    'zfa.code': '邮件中的验证码',
    'zfa.bestaetigen': '确认',
    'zfa.gueltig': '验证码有效期 {min} 分钟。',
    'zfa.andererZugang': '换个账号登录',
    'zfa.einstellung': '两步验证登录',
    'zfa.erklaerung': '除密码外，每次登录都会向您的邮箱发送验证码。仅知道密码将无法登录。',
    'zfa.aktiv': '已开启',
    'zfa.aus': '已关闭',
    'zfa.einschalten': '开启',
    'zfa.ausschalten': '关闭',
    'zfa.mailFrage': '验证码发送到哪个邮箱？',
    'zfa.codeSenden': '发送验证码',
    'zfa.passwortFrage': '请输入密码以关闭',
    'zfa.keinVersand': '本系统尚未配置邮件发送。没有它则无法送达验证码。',
    'zfa.eingeschaltet': '两步验证已开启。',
    'zfa.ausgeschaltet': '两步验证已关闭。',
    'zfa.methodeApp': "身份验证器应用",
    'zfa.methodeMail': "邮件验证码",
    'zfa.erklaerungBeide': "除密码外，LagerPro 每次登录都会要求输入验证码——来自手机上的身份验证器应用或邮件。仅知道密码无法登录。",
    'zfa.ersatzUebrig': "剩余 {n} 个备用码",
    'zfa.mitApp': "使用身份验证器应用开启",
    'zfa.mitMail': "通过邮件开启",
    'zfa.zurApp': "改用身份验证器应用",
    'zfa.ersatzNeu': "生成新备用码",
    'zfa.appEmpfohlen': "推荐使用应用：无需网络和邮件即可使用——例如 Google Authenticator 或 Microsoft Authenticator。",
    'zfa.ersatzNeuFrage': "生成新的备用码——旧码立即失效。",
    'zfa.passwortWeiter': "请输入您的密码",
    'zfa.weiter': "继续",
    'zfa.schritt1': "在手机上打开身份验证器应用（如 Google Authenticator），选择“添加代码”。",
    'zfa.scannen': "扫描此二维码。",
    'zfa.schritt3': "在下方输入应用中的 6 位验证码。",
    'zfa.manuell': "无法扫描？请手动输入此密钥：",
    'zfa.appCode': "应用中的验证码",
    'zfa.ersatzTitel': "您的备用码",
    'zfa.ersatzHinweis': "如果手机丢失：每个备用码可代替一次应用验证码。请妥善保存——仅显示这一次。",
    'zfa.kopieren': "复制",
    'zfa.kopiert': "已复制",
    'zfa.herunterladen': "保存为文件",
    'zfa.gesichert': "我已妥善保存这些代码",
    'zfa.fertig': "完成",
    'zfa.appText': "打开身份验证器应用，输入 LagerPro 的 6 位验证码。",
    'zfa.ersatzText': "输入一个备用码。每个仅可使用一次。",
    'zfa.ersatzCode': "备用码",
    'zfa.ersatzNutzen': "手机不在身边？使用备用码",
    'zfa.appCodeNutzen': "使用应用中的验证码",
    'nav.app': "应用",
    'start.nav.app': "应用",
    'desktop.titel': "LagerPro 桌面应用",
    'desktop.text': "独立窗口，Dock 或任务栏中有专属图标，一键启动。登录一次——之后 90 天内保持登录。",
    'desktop.inApp': "您正在使用桌面应用",
    'desktop.inAppText': "版本 {version}。界面会自动更新。",
    'desktop.alsWebApp': "LagerPro 已作为应用安装在此电脑上。",
    'desktop.installiert': "已安装——现在可在应用程序中找到 LagerPro。",
    'desktop.direktTitel': "直接从浏览器安装",
    'desktop.schnell': "几秒钟",
    'desktop.direktText': "无需下载，没有安全警告，始终保持最新。",
    'desktop.safari': "在 Safari 中：菜单“文件”→“添加到程序坞”（macOS Sonoma 或更高版本）。",
    'desktop.firefox': "Firefox 无法安装网页应用——请使用 Chrome 或 Edge，或在下方下载。",
    'desktop.chromeLeiste': "点击地址栏右侧的“安装”图标——或菜单 ⋮ →“安装 LagerPro”。",
    'desktop.andererBrowser': "使用 Chrome、Edge 或 Safari 最方便——或在下方下载应用。",
    'desktop.jetztInstallieren': "立即安装",
    'desktop.downloadTitel': "或下载",
    'desktop.version': "版本 {version}",
    'desktop.windows': "Windows",
    'desktop.windowsUnter': "Windows 10 和 11，64 位",
    'desktop.macApple': "搭载 Apple 芯片的 Mac",
    'desktop.macAppleUnter': "M1、M2、M3、M4 · 2020 年起",
    'desktop.macIntel': "搭载 Intel 芯片的 Mac",
    'desktop.macIntelUnter': "2020 年及更早的 Mac",
    'desktop.passtZuIhnen': "适合此电脑",
    'desktop.laden': "下载",
    'desktop.bald': "即将推出",
    'desktop.warnungTitel': "首次打开时出现警告？",
    'desktop.warnungText': "下载文件尚未在 Microsoft 和 Apple 注册，因此首次启动时电脑会询问一次，之后不再询问。",
    'desktop.warnungWindows': "出现“Windows 已保护你的电脑”时，点击“更多信息”，再点击“仍要运行”。",
    'desktop.warnungMac': "将 LagerPro 拖入“应用程序”。首次打开出现提示时，前往“系统设置 → 隐私与安全性”点击“仍要打开”（旧版 macOS：右键点击 →“打开”）。",
    'desktop.anmeldung': "两种方式都一样：用密码（及双重验证码）登录一次，之后 90 天无需输入。退出登录会立即结束记住的登录。",
    'desktop.mehrInfos': "应用详细信息",

    'erinnerung.titel': '付款提醒',
    'erinnerung.erklaerung': '发票逾期时，LagerPro 会以您的名义自动向客户发送礼貌提醒，回复地址为您的邮箱。',
    'erinnerung.nachTagen': '逾期 … 天后提醒',
    'erinnerung.wiederholung': '… 天后再次提醒',
    'erinnerung.maxAnzahl': '每张发票最多 … 次提醒',
    'erinnerung.faellig': '下次运行将涉及',
    'erinnerung.jetzt': '立即发送',
    'erinnerung.keineFaellig': '当前没有逾期发票。',
    'erinnerung.firmenmailFehlt': '请在“公司”中填写邮箱，否则客户无法回复提醒。',
    'erinnerung.keineFuerKunden': '不向该客户发送付款提醒',
  },
} as const;

export type Schluessel = keyof typeof TEXTE.de;

const SPEICHER = 'lp_sprache';

function gespeicherteSprache(): SprachCode {
  try {
    const w = localStorage.getItem(SPEICHER);
    if (w && SPRACHEN.some(s => s.code === w)) return w as SprachCode;
  } catch { /* egal */ }
  // Sonst die Browsersprache, wenn wir sie anbieten.
  const browser = (navigator.language || 'de').slice(0, 2);
  return (SPRACHEN.find(s => s.code === browser)?.code ?? 'de') as SprachCode;
}

type Ctx = {
  sprache: SprachCode;
  setSprache: (s: SprachCode) => void;
  rtl: boolean;
  t: (schluessel: Schluessel) => string;
};

const SprachKontext = createContext<Ctx | null>(null);

export function SprachProvider({ children }: { children: ReactNode }) {
  const [sprache, setSpracheRoh] = useState<SprachCode>(gespeicherteSprache);
  const rtl = SPRACHEN.find(s => s.code === sprache)?.rtl ?? false;

  useEffect(() => {
    document.documentElement.lang = sprache;
    document.documentElement.dir = rtl ? 'rtl' : 'ltr';
    try { localStorage.setItem(SPEICHER, sprache); } catch { /* egal */ }
  }, [sprache, rtl]);

  const setSprache = useCallback((s: SprachCode) => setSpracheRoh(s), []);

  const t = useCallback((schluessel: Schluessel) => {
    const tabelle = TEXTE[sprache] as Record<string, string>;
    // Fehlt eine Übersetzung, ist Deutsch besser als ein leeres Feld.
    return tabelle[schluessel] ?? TEXTE.de[schluessel] ?? schluessel;
  }, [sprache]);

  const wert = useMemo(() => ({ sprache, setSprache, rtl, t }), [sprache, setSprache, rtl, t]);
  return <SprachKontext.Provider value={wert}>{children}</SprachKontext.Provider>;
}

export function useSprache() {
  const c = useContext(SprachKontext);
  if (!c) throw new Error('useSprache benötigt einen SprachProvider');
  return c;
}
